import React, { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import "leaflet/dist/leaflet.css";
import { BrowserRouter, Routes, Route, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { vaDepartments } from './data/vaDepartments';
import { 
  Shield, FileText, Clock, Scale, AlertTriangle, Upload, Package, BarChart3, 
  LogOut, Menu, X, ChevronRight, ChevronDown, CheckCircle, Search, Download, 
  Trash2, Plus, Eye, User, Zap, Home, Phone, Calendar, Bell, MessageSquare,
  HelpCircle, Settings, Folder, Building, Heart, BookOpen, Users, Flag,
  Wrench, Star, Cpu, BookMarked, Network, Grid3X3, Gavel, Activity, 
  Target, TrendingUp, Briefcase, Globe, Award, Lightbulb, MapPin, Navigation, List, Grid, Send
} from "lucide-react";
import { AuthProvider, ProtectedRoute, LoadingScreen, useAuth } from "./components/AuthProvider";
import { GlobalNav, Breadcrumb } from "./components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video URLs for hero sections
const VIDEO_URLS = {
  default: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/qfzehiy7_vecteezy_young-woman-running-with-waving-american-flag-on-field_39660674.mp4",
  soldier: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/zdancyhs_TRIAL____________vecteezy_soldier-portrait-at-sunset-with-military-gear-and-helmet_69571657%20%281%29.mp4",
  lawyers: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/xcst7tad_vecteezy_african-american-lawyer-businessman-working-in-office-or_58030908%20%281%29%20%281%29.mp4",
  mining: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/m4pvocll_vecteezy_cgi-mining-site-in-ruins_67816528%20%281%29%20%281%29.mp4",
  wheelchair: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/2iw4hm8q_vecteezy_man-in-a-wheelchair-moving-through-a-gym-surrounded-by_60276219.mp4",
  hpactYouTube: "https://www.youtube.com/embed/lc187w1wh-o",
  aboutPage: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/u7flmdve_AdobeStock_504860006.mov",
  vacPage: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/2iia612i_vecteezy_a-young-woman-stands-amid-the-beautiful-scenery-of-green_13006555.mov",
  ptsdSoldier: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/aysxe4tr_TRIAL____________vecteezy_soldier-portrait-at-sunset-with-military-gear-and-helmet_69571657%20%281%29.mp4",
  jargonHelicopter: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/uwlm2qz5_vecteezy%20soldier%20runs%20towards%20helicopter%20in%20desert%20at%20dawn%2072006375%20%281%29.mp4",
  missionFlag: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/s8ipjubj_vecteezy_a-united-states-soldier-hoists-the-american-flag-on-a-pole_74236314%20%281%29.mp4"
};

// Page content data
const PAGE_CONTENT = {
  home: {
    title: "The Veteran's Voice",
    declaration: "To care for those who have borne the battle is more than a promise—it is a responsibility carried by a nation. Here, the voices of veterans rise together so that service, sacrifice, and truth are never forgotten.",
    video: VIDEO_URLS.default
  },
  about: {
    title: "Forged by Experience",
    declaration: "This platform was built by those who walked the road, carried the weight, and refused to remain silent. Real change begins when veterans themselves help shape the path forward.",
    video: VIDEO_URLS.aboutPage
  },
  mission: {
    title: "The Mission Continues",
    declaration: "The uniform may come off, but the mission never ends. Our mission now is to strengthen the systems that serve veterans and ensure that no one who answered the nation's call is left behind.",
    video: VIDEO_URLS.missionFlag
  },
  frontier: {
    title: "A New Frontier for Veterans",
    declaration: "Every generation faces a frontier where courage must lead the way. Today's frontier is not fought on distant fields, but in the work of building better systems, stronger communities, and lasting support for veterans.",
    video: VIDEO_URLS.mining
  },
  ptsd: {
    title: "Understanding the Invisible Battle",
    declaration: "Some wounds are carried in silence. Yet the strength that carried a soldier through war can also carry them through healing. When veterans stand together, no invisible battle is fought alone.",
    video: VIDEO_URLS.ptsdSoldier
  },
  jargon: {
    title: "Clarity in a Complex System",
    declaration: "Combat veterans often return speaking a language their families cannot understand—words weighted by war, silence heavy with trauma. This disconnect creates fear and confusion in those who love them most. Yet through it all, veterans' rights must stand crystal clear. The VA has a non-negotiable duty: provide decompression, treatment, rehabilitation, and care before warriors even return home.",
    video: VIDEO_URLS.jargonHelicopter
  },
  hudvash: {
    title: "Housing with Dignity",
    declaration: "A safe home is the ground where recovery, stability, and independence begin. For veterans who once stood watch for our nation, housing must never be uncertain.",
    video: VIDEO_URLS.default
  },
  vetlegal: {
    title: "Justice for Veterans",
    declaration: "Rights mean little if they cannot be defended. Veterans who protected the freedoms of a nation deserve systems that protect their dignity, fairness, and legal rights.",
    video: VIDEO_URLS.lawyers
  },
  vetscan: {
    title: "Truth, Transparency, Accountability",
    declaration: "Progress begins when institutions are examined honestly and accountability becomes stronger than silence. Transparency ensures that the systems meant to serve veterans truly fulfill their purpose.",
    video: VIDEO_URLS.default
  },
  hpact: {
    title: "Homeless Patient Aligned Care Teams",
    declaration: "Every veteran deserves comprehensive healthcare and housing stability. HPACT teams provide integrated medical and case management services to end veteran homelessness.",
    video: VIDEO_URLS.hpactYouTube
  }
};

// ============ TYPEWRITER EFFECT HOOK ============
const useTypewriter = (text, speed = 40, delay = 500) => {
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayText("");
    setIsComplete(false);
    setIsTyping(false);
    
    const startTimeout = setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;
      
      const typeInterval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
          setIsTyping(false);
          setIsComplete(true);
        }
      }, speed);
      
      return () => clearInterval(typeInterval);
    }, delay);
    
    return () => clearTimeout(startTimeout);
  }, [text, speed, delay]);

  return { displayText, isTyping, isComplete };
};

// ============ ROTATING QUOTES TYPEWRITER HOOK ============
const useRotatingQuotes = (quotes, typeSpeed = 50, displayDuration = 3000) => {
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const currentQuote = quotes[currentQuoteIndex];
    setDisplayText("");
    setIsTyping(true);
    let charIndex = 0;

    // Type out the current quote
    const typeInterval = setInterval(() => {
      if (charIndex < currentQuote.length) {
        setDisplayText(currentQuote.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        
        // Wait for display duration, then move to next quote
        setTimeout(() => {
          setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
        }, displayDuration);
      }
    }, typeSpeed);

    return () => clearInterval(typeInterval);
  }, [currentQuoteIndex, quotes, typeSpeed, displayDuration]);

  return { displayText, isTyping };
};

// ============ VIDEO HERO COMPONENT ============
const VideoHero = ({ title, declaration, videoUrl }) => {
  const { displayText, isTyping } = useTypewriter(declaration, 35, 800);
  
  // Check if it's a YouTube embed URL
  const isYouTube = videoUrl && videoUrl.includes('youtube.com/embed');
  
  return (
    <div className="video-hero" data-testid="video-hero">
      {isYouTube ? (
        <iframe
          src={videoUrl}
          title="YouTube video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        ></iframe>
      ) : (
        <video autoPlay loop muted playsInline>
          <source src={videoUrl} type="video/mp4" />
        </video>
      )}
      
      <div className="video-hero-overlay">
        <h1 className="stencil-title">{title}</h1>
        <div className="hero-divider"></div>
        <div className="typewriter-container">
          <p className="typewriter-text">
            "{displayText}
            <span className="typewriter-cursor"></span>
            "
          </p>
        </div>
      </div>
      
      <div className="hero-tagline">
        USVETERANSASSOCIATION.COM • VETERANSADVISORYCOUNCIL.COM • VETERANADVOCACY.VIP
      </div>
      <div className="hero-bottom-stripe"></div>
    </div>
  );
};

// EXTRACTED: // Auth Context
// EXTRACTED: const AuthContext = createContext(null);
// EXTRACTED: const useAuth = () => useContext(AuthContext);
// EXTRACTED: 
// EXTRACTED: const AuthProvider = ({ children }) => {
// EXTRACTED:   const [user, setUser] = useState(null);
// EXTRACTED:   const [token, setToken] = useState(localStorage.getItem("vet_token"));
// EXTRACTED:   const [loading, setLoading] = useState(true);
// EXTRACTED: 
// EXTRACTED:   useEffect(() => {
// EXTRACTED:     if (token) {
// EXTRACTED:       axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
// EXTRACTED:       fetchUser();
// EXTRACTED:     } else {
// EXTRACTED:       setLoading(false);
// EXTRACTED:     }
// EXTRACTED:   }, [token]);
// EXTRACTED: 
// EXTRACTED:   const fetchUser = async () => {
// EXTRACTED:     try {
// EXTRACTED:       const res = await axios.get(`${API}/auth/me`);
// EXTRACTED:       setUser(res.data);
// EXTRACTED:     } catch (e) {
// EXTRACTED:       logout();
// EXTRACTED:     } finally {
// EXTRACTED:       setLoading(false);
// EXTRACTED:     }
// EXTRACTED:   };
// EXTRACTED: 
// EXTRACTED:   const login = async (email, password) => {
// EXTRACTED:     const res = await axios.post(`${API}/auth/login`, { email, password });
// EXTRACTED:     localStorage.setItem("vet_token", res.data.access_token);
// EXTRACTED:     setToken(res.data.access_token);
// EXTRACTED:     setUser(res.data.user);
// EXTRACTED:     return res.data;
// EXTRACTED:   };
// EXTRACTED: 
// EXTRACTED:   const register = async (email, password, name) => {
// EXTRACTED:     const res = await axios.post(`${API}/auth/register`, { email, password, name });
// EXTRACTED:     localStorage.setItem("vet_token", res.data.access_token);
// EXTRACTED:     setToken(res.data.access_token);
// EXTRACTED:     setUser(res.data.user);
// EXTRACTED:     return res.data;
// EXTRACTED:   };
// EXTRACTED: 
// EXTRACTED:   const logout = () => {
// EXTRACTED:     localStorage.removeItem("vet_token");
// EXTRACTED:     setToken(null);
// EXTRACTED:     setUser(null);
// EXTRACTED:     delete axios.defaults.headers.common["Authorization"];
// EXTRACTED:   };
// EXTRACTED: 
// EXTRACTED:   return (
// EXTRACTED:     <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
// EXTRACTED:       {children}
// EXTRACTED:     </AuthContext.Provider>
// EXTRACTED:   );
// EXTRACTED: };
// EXTRACTED: 
// EXTRACTED: const ProtectedRoute = ({ children }) => {
// EXTRACTED:   const { user, loading } = useAuth();
// EXTRACTED:   if (loading) return <LoadingScreen />;
// EXTRACTED:   if (!user) return <Navigate to="/login" />;
// EXTRACTED:   return children;
// EXTRACTED: };
// EXTRACTED: 
// EXTRACTED: const LoadingScreen = () => (
// EXTRACTED:   <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#221F20' }} data-testid="loading-screen">
// EXTRACTED:     <div className="text-center">
// EXTRACTED:       <div className="loading-spinner mx-auto mb-4"></div>
// EXTRACTED:       <p className="font-heading text-lg tracking-widest uppercase" style={{ color: '#FFCC01' }}>Loading VETEngine...</p>
// EXTRACTED:     </div>
// EXTRACTED:   </div>
// EXTRACTED: );

// ============ TOP NAVBAR COMPONENT ============
const TopNavbar = ({ showAuth = true }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHoveringMenu, setIsHoveringMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const menuTimeoutRef = React.useRef(null);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Mission", path: "/mission" },
    { label: "Frontier", path: "/frontier" },
    { label: "PTSD", path: "/ptsd" },
    { label: "Jargon", path: "/jargon" },
    { label: "HUD-VASH", path: "/hudvash" },
    { label: "HPACT", path: "/hpact" },
    { label: "VETLegal", path: "/vetlegal" },
    { label: "VETScan", path: "/vetscan" },
    { label: "VETLaw Core", path: "/laws" },
  ];

  const secondaryNavTabs = [
    { label: "HONOR", color: "#DC2626", route: "/honor" },
    { label: "USVA", color: "#3B82F6", route: "/usva" },
    { label: "VAC", color: "#F59E0B", route: "/vac" },
    { label: "VAVIP", color: "#8B5CF6", route: "/vavip" },
    { label: "VFW", color: "#10B981", route: "/vfw" },
    { label: "Calendar", color: "#EF4444", route: "/calendar" },
    { label: "VA List", color: "#06B6D4", route: "/valist" },
    { label: "NonProfits", color: "#6B7280", route: "/nonprofits" },
    { label: "MAP", color: "#EC4899", route: "/map" },
    { label: "Events", color: "#F97316", route: "/events" },
    { label: "Classes", color: "#3B82F6", route: "/classes" },
    { label: "Articles", color: "#10B981", route: "/articles" },
    { label: "Benefits", color: "#F59E0B", route: "/benefits" },
    { label: "Docs", color: "#8B5CF6", route: "/documents" },
    { label: "Mag", color: "#06B6D4", route: "/magazine" },
  ];

  const handleMenuMouseEnter = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    setIsHoveringMenu(true);
  };

  const handleMenuMouseLeave = () => {
    setIsHoveringMenu(false);
    menuTimeoutRef.current = setTimeout(() => {
      setMobileMenuOpen(false);
    }, 300);
  };

  const handleHamburgerHover = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    setMobileMenuOpen(true);
  };

  React.useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Primary Navbar - Responsive for all screen sizes */}
      <nav className="top-navbar" data-testid="top-navbar" style={{ 
        backgroundColor: 'rgba(34, 31, 32, 0.95)', 
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '2px solid #FFCC01',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '0.75rem 1rem'
      }}>
        <div className="nav-left">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}>
            <Zap className="w-5 h-5 md:w-6 md:h-6" style={{ color: '#FFCC01' }} />
          </button>
          <Link to="/vetcommand" className="btn-command text-xs md:text-sm" data-testid="command-btn" style={{ 
            backgroundColor: '#FFCC01', 
            color: '#221F20',
            padding: '0.5rem 0.75rem',
            borderRadius: '4px',
            fontWeight: 'bold',
            textDecoration: 'none'
          }}>
            COMMAND
          </Link>
        </div>

        {/* Tablet/Desktop nav - Show first 5 links + More button */}
        <div className="nav-center hidden md:flex items-center" style={{ gap: '0.5rem' }}>
          {navLinks.slice(0, 5).map((link, i) => {
            const isActive = location.pathname === link.path;
            
            return (
              <Link 
                key={i} 
                to={link.path} 
                className="nav-link text-xs font-bold uppercase" 
                style={{ 
                  color: isActive ? '#221F20' : '#FFCC01',
                  backgroundColor: isActive ? '#FFCC01' : 'transparent',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 204, 1, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {link.label}
              </Link>
            );
          })}
          
          {/* More + button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="nav-link text-xs font-bold uppercase"
            style={{ 
              color: '#FFCC01',
              backgroundColor: '#1A4F63',
              padding: '8px 16px',
              borderRadius: '4px',
              border: '2px solid #FFCC01',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontWeight: 'bold'
            }}
          >
            More +
          </button>
        </div>

        <div className="nav-right" style={{ gap: '0.5rem' }}>
          {/* Emergency buttons - hidden on small tablets, shown on larger screens */}
          <div className="hidden lg:flex items-center gap-1">
            <button className="emergency-box emergency-22 text-xs" style={{ padding: '0.4rem 0.6rem' }} title="Veterans Crisis Line">22</button>
            <button className="emergency-box emergency-988 text-xs" style={{ padding: '0.4rem 0.6rem' }} title="Suicide Prevention">988</button>
            <button className="emergency-box emergency-911 text-xs" style={{ padding: '0.4rem 0.6rem' }} title="Emergency">911</button>
            <button className="emergency-box emergency-emha text-xs" style={{ padding: '0.4rem 0.6rem' }} title="EMHA">EMHA</button>
          </div>
          
          <button className="btn-crisis hidden md:flex items-center gap-2 text-xs" data-testid="crisis-btn" style={{ 
            backgroundColor: '#2F372F', 
            border: '1px solid #FFCC01',
            padding: '0.5rem 0.75rem',
            borderRadius: '4px'
          }}>
            <span className="pulse-dot"></span>
            <span className="hidden lg:inline">Crisis</span>
          </button>

          {showAuth && !user && (
            <Link to="/login" className="btn-command text-xs md:text-sm hidden sm:block" data-testid="signin-btn" style={{ 
              backgroundColor: '#FFCC01', 
              color: '#221F20',
              padding: '0.5rem 0.75rem',
              borderRadius: '4px',
              fontWeight: 'bold',
              textDecoration: 'none'
            }}>Sign In</Link>
          )}
          
          {user && (
            <button onClick={() => { logout(); navigate("/"); }} className="nav-link" data-testid="logout-btn" style={{ color: '#FFCC01', padding: '0.5rem' }}>
              <LogOut className="w-4 h-4" />
            </button>
          )}

          {/* Hamburger menu - ONLY on mobile phones (below md: 768px) */}
          <button 
            className="md:hidden nav-link flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ 
              color: '#FFCC01',
              backgroundColor: '#1A4F63',
              padding: '0.75rem',
              minWidth: '50px',
              minHeight: '50px',
              border: '3px solid #FFCC01',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex !important',
              visibility: 'visible !important',
              opacity: '1 !important',
              zIndex: 9999
            }}
            data-testid="hamburger-menu"
          >
            {mobileMenuOpen ? <X className="w-7 h-7" style={{ strokeWidth: 3 }} /> : <Menu className="w-7 h-7" style={{ strokeWidth: 3 }} />}
          </button>
        </div>
      </nav>

      {/* Secondary Nav Bar - Scrollable on tablets/desktop */}
      <nav 
        className="hidden md:flex items-center gap-1 py-2 px-2 overflow-x-auto" 
        style={{ 
          backgroundColor: 'rgba(26, 28, 30, 0.95)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          position: 'fixed',
          top: '56px',
          left: 0,
          right: 0,
          zIndex: 999,
          scrollbarWidth: 'thin',
          scrollbarColor: '#FFCC01 #1a1c1e',
          WebkitOverflowScrolling: 'touch'
        }} 
        data-testid="secondary-navbar"
      >
        {secondaryNavTabs.map((tab, i) => {
          const isActive = location.pathname === tab.route;
          
          return (
            <Link
              key={i}
              to={tab.route}
              className="px-2 py-2 font-heading text-xs font-semibold uppercase tracking-wider rounded transition-all flex-shrink-0"
              style={{ 
                backgroundColor: isActive ? '#FFCC01' : '#2d2f32',
                color: isActive ? '#221F20' : '#FFFFFF',
                borderLeft: `3px solid ${tab.color}`,
                minWidth: '60px',
                textDecoration: 'none',
                display: 'block',
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: '0.7rem'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#3d3f42';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#2d2f32';
                }
              }}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* Mobile/Tablet Menu Overlay - Always enabled */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50"
          style={{
            animation: 'fadeIn 0.2s ease-out',
            zIndex: 998
          }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Dropdown Menu for "More +" and Mobile */}
      <div 
        className="fixed top-[56px] left-0 right-0 overflow-y-auto transition-all duration-300 ease-out"
        style={{ 
          backgroundColor: '#221F20',
          borderTop: '2px solid #FFCC01',
          maxHeight: mobileMenuOpen ? '85vh' : '0',
          opacity: mobileMenuOpen ? 1 : 0,
          transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(-20px)',
          pointerEvents: mobileMenuOpen ? 'auto' : 'none',
          zIndex: 999
        }}
      >
        <div className="p-4 space-y-1">
          {/* All Main Navigation Links for Mobile */}
          <div className="mb-4 pb-4 border-b border-gray-700 md:hidden">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Main Navigation</h3>
            <div className="grid grid-cols-3 gap-2">
              {navLinks.map((link, i) => {
                const isActive = location.pathname === link.path;
                
                return (
                  <Link 
                    key={i} 
                    to={link.path} 
                    className="py-2 px-1 text-xs uppercase tracking-wider rounded text-center"
                    style={{ 
                      color: isActive ? '#221F20' : '#FFCC01',
                      backgroundColor: isActive ? '#FFCC01' : 'rgba(255, 204, 1, 0.1)',
                      textDecoration: 'none',
                      fontWeight: isActive ? 'bold' : 'normal',
                      border: isActive ? 'none' : '1px solid rgba(255, 204, 1, 0.3)'
                    }} 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Remaining Links (6-11) for Tablet "More +" dropdown */}
          <div className="mb-4 pb-4 border-b border-gray-700 hidden md:block">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">More Pages</h3>
            <div className="grid grid-cols-3 gap-2">
              {navLinks.slice(5).map((link, i) => {
                const isActive = location.pathname === link.path;
                
                return (
                  <Link 
                    key={i} 
                    to={link.path} 
                    className="py-3 px-2 text-sm uppercase tracking-wider rounded text-center"
                    style={{ 
                      color: isActive ? '#221F20' : '#FFCC01',
                      backgroundColor: isActive ? '#FFCC01' : 'rgba(255, 204, 1, 0.1)',
                      textDecoration: 'none',
                      fontWeight: isActive ? 'bold' : 'normal',
                      border: isActive ? 'none' : '1px solid rgba(255, 204, 1, 0.3)'
                    }} 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Quick Access */}
          <div className="pb-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Quick Access</h3>
              <div className="grid grid-cols-6 gap-2">
                {secondaryNavTabs.map((tab, i) => {
                  const isActive = location.pathname === tab.route;
                  
                  return (
                    <Link
                      key={i}
                      to={tab.route}
                      className="px-1 py-2 font-heading text-xs font-semibold uppercase tracking-wider rounded transition-all"
                      style={{ 
                        backgroundColor: isActive ? '#FFCC01' : '#2d2f32',
                        color: isActive ? '#221F20' : '#FFFFFF',
                        borderTop: `3px solid ${tab.color}`,
                        textDecoration: 'none',
                        display: 'block',
                        textAlign: 'center'
                      }}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                      }}
                    >
                      {tab.label}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Emergency buttons */}
            <div className="pt-4 border-t border-gray-700">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Emergency</h3>
              <div className="grid grid-cols-4 gap-2">
                <button className="emergency-box emergency-22 py-3 text-sm font-bold" title="Veterans Crisis Line">22</button>
                <button className="emergency-box emergency-988 py-3 text-sm font-bold" title="Suicide Prevention">988</button>
                <button className="emergency-box emergency-911 py-3 text-sm font-bold" title="Emergency">911</button>
                <button className="emergency-box emergency-emha py-3 text-sm font-bold" title="EMHA">EMHA</button>
              </div>
            </div>

            {showAuth && !user && (
              <div className="pt-4">
                <Link 
                  to="/login" 
                  className="block w-full text-center py-3 px-4 rounded font-bold"
                  style={{ 
                    backgroundColor: '#FFCC01', 
                    color: '#221F20',
                    textDecoration: 'none'
                  }}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                  }}
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>

    </>
  );
};

// ============ SIDEBAR COMPONENT ============
const Sidebar = () => {
  const icons = [
    { icon: Search, label: "Search" },
    { icon: MessageSquare, label: "Messages" },
    { icon: Bell, label: "Alerts" },
    { icon: Phone, label: "Phone" },
    { icon: Calendar, label: "Calendar" },
    { icon: User, label: "Profile" },
  ];

  const projects = [
    // Core VA
    { name: "VA", desc: "Veterans Affairs" },
    { name: "VHA", desc: "Veterans Health Admin" },
    { name: "OIG", desc: "Office of Inspector General" },
    { name: "VISN", desc: "VA Integrated Service Network" },
    // LA Specific
    { name: "GLAHS", desc: "Greater LA Healthcare System" },
    { name: "VAMC", desc: "VA Medical Center" },
    // Housing Programs
    { name: "HUDVASH", desc: "HUD-VASH Program" },
    { name: "HPACT", desc: "Homeless Patient Aligned Care" },
    { name: "VASH", desc: "Veterans Affairs Supportive Housing" },
    // Case Management
    { name: "CCM", desc: "Chronic Care Management" },
    { name: "LCM", desc: "Lead Case Manager" },
    { name: "RN", desc: "Registered Nurse" },
    // Support Services
    { name: "SSVF", desc: "Supportive Services for Veteran Families" },
    { name: "VJO", desc: "Veterans Justice Outreach" },
    { name: "HVCES", desc: "Homeless Veteran Community Employment" },
    // Programs
    { name: "DCHV", desc: "Domiciliary Care for Homeless Veterans" },
    { name: "GPD", desc: "Grant Per Diem" },
    { name: "CEC", desc: "Community Employment Coordinator" },
    { name: "CWT", desc: "Compensated Work Therapy" },
    { name: "HVDP", desc: "Homeless Veteran Dental Program" },
    { name: "HCRV", desc: "Health Care for Reentry Veterans" },
  ];

  return (
    <div className="sidebar hidden md:flex flex-col w-20 overflow-y-auto" style={{ backgroundColor: '#1a1c1a', borderRight: '1px solid #2F372F' }} data-testid="sidebar">
      {/* Icon buttons */}
      {icons.map((item, i) => (
        <button 
          key={i} 
          className="flex items-center justify-center w-full h-12 transition-colors"
          style={{ color: '#BFB8A6' }}
          title={item.label}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2F372F'; e.currentTarget.style.color = '#FFCC01'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#BFB8A6'; }}
        >
          <item.icon className="w-5 h-5" />
        </button>
      ))}
      
      {/* Apply Section */}
      <div className="px-2 py-3 mt-2" style={{ borderTop: '1px solid #2F372F' }}>
        <p className="text-center text-xs font-heading uppercase tracking-wider mb-2" style={{ color: '#727365' }}>Apply</p>
      </div>

      {/* Projects Label */}
      <div className="px-2 py-2" style={{ borderTop: '1px solid #2F372F' }}>
        <p className="text-center text-xs font-heading uppercase tracking-wider mb-2" style={{ color: '#FFCC01' }}>Projects</p>
      </div>

      {/* Team */}
      <button 
        className="mx-2 mb-1 py-2 text-xs font-heading font-semibold rounded transition-colors"
        style={{ backgroundColor: '#FFCC01', color: '#221F20' }}
      >
        Team
      </button>
      
      {/* Project buttons - scrollable */}
      <div className="flex-1 overflow-y-auto px-1 pb-4">
        {projects.map((p, i) => (
          <button 
            key={i} 
            className="w-full mb-1 py-2 text-xs font-heading font-semibold rounded transition-all"
            style={{ backgroundColor: '#2F372F', color: '#F1E4C7', border: '1px solid #727365' }}
            title={p.desc}
            onMouseEnter={(e) => { 
              e.currentTarget.style.backgroundColor = '#FFCC01'; 
              e.currentTarget.style.color = '#221F20'; 
              e.currentTarget.style.borderColor = '#FFCC01';
            }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.backgroundColor = '#2F372F'; 
              e.currentTarget.style.color = '#F1E4C7'; 
              e.currentTarget.style.borderColor = '#727365';
            }}
          >
            {p.name}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============ ACCORDION COMPONENT ============
const Accordion = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="accordion-item">
      <div className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <span className="accordion-title">{title}</span>
        <span className={`accordion-icon ${isOpen ? 'open' : ''}`}>+</span>
      </div>
      {isOpen && <div className="accordion-content">{children}</div>}
    </div>
  );
};

// ============ TYPEWRITER EFFECT HOOK ============
const useTypewriterEffect = (text, speed = 50, startDelay = 800) => {
  const [displayText, setDisplayText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayText("");
    setIsComplete(false);
    
    const startTimer = setTimeout(() => {
      let currentIndex = 0;
      
      const timer = setInterval(() => {
        if (currentIndex <= text.length) {
          setDisplayText(text.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);
      
      return () => clearInterval(timer);
    }, startDelay);

    return () => clearTimeout(startTimer);
  }, [text, speed, startDelay]);

  return { displayText, isComplete };
};

// ============ HERO SLIDESHOW DATA ============
const HERO_SLIDES = [
  {
    id: 1,
    tagline: "The Hidden Truth",
    headline: "Many veterans carry childhood trauma into service—then deploy and return home without decompression or healing.",
    quote: "The trauma didn't start in combat. It started long before anyone said 'thank you for your service.'",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/cb16a8187_vecteezy_affectionate-military-reunion-between-father-and-daughter-in_31548693.jpg"
  },
  {
    id: 2,
    tagline: "A Different Battlefield",
    headline: "Too many veterans return home to face homelessness, isolation, and systems too difficult to navigate alone.",
    quote: "The war doesn't end when you come home—it just changes form.",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/98f5fd152_vecteezy_a-patriotic-soldier-returns-home-at-sunset-after-completing_65954662.jpg"
  },
  {
    id: 3,
    tagline: "Built to Change That",
    headline: "This platform helps veterans rebuild stability, purpose, and dignity.",
    quote: "When the system fails, veterans must lead the way forward.",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/d37b19a2e_AdobeStock_461883781.jpg"
  },
  {
    id: 4,
    tagline: "The Foundation for Recovery",
    headline: "Recovery comes not from housing alone—but from community, structure, and hope.",
    quote: "Four walls can shelter you, but only community can rebuild you.",
    image: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/ycbi98w0_w%3D1200%2Ch%3D794.avif"
  },
  {
    id: 5,
    tagline: "The First Step",
    headline: "Programs like HUD-VASH provide the housing stability veterans need to move forward.",
    quote: "Stability begins with a roof—but it doesn't end there.",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/e1e914f51_AdobeStock_573718203.jpg"
  },
  {
    id: 6,
    tagline: "No Veteran Stands Alone",
    headline: "Connection and community prevent isolation and rebuild the bonds service members once shared.",
    quote: "Isolation is the enemy. Community is the cure.",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/37bae5805_vecteezy_ai-generated-international-day-of-sport-for-development-and_36365167.jpg"
  },
  {
    id: 7,
    tagline: "Rebuilding Direction",
    headline: "Daily routines, education, and personal development restore purpose and meaning to life after service.",
    quote: "Without mission, there is drift. Without structure, there is chaos.",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/299449eb6_AdobeStock_895966478.jpg"
  },
  {
    id: 8,
    tagline: "Where Recovery Happens",
    headline: "An Environment That Promotes Purpose.|A Community Built for Healing and Recovery.",
    quote: "Veterans don't need apartments. They need purpose, community, and hope.",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_697122627b6a2ea8856994db/cb16a8187_vecteezy_affectionate-military-reunion-between-father-and-daughter-in_31548693.jpg"
  },
  {
    id: 9,
    tagline: "The Path Forward",
    headline: "Housing provides safety. Community restores strength. Purpose rebuilds lives.",
    quote: "When veterans are supported with dignity, recovery becomes possible—and lives begin to change.",
    image: "https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/1c0y747a_dreamstime_m_41252852.jpg"
  }
];

// ============ HERO SLIDESHOW COMPONENT ============
const HeroSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 7000); // 7 seconds per slide
    
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 15000); // Resume auto-play after 15s
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  };

  const slide = HERO_SLIDES[currentSlide];
  const { displayText: taglineText, isComplete: taglineComplete } = useTypewriterEffect(slide.tagline, 60, 600);
  
  // Split headline if it has a pipe character
  const headlineLines = slide.headline.split('|');
  const isMultiLine = headlineLines.length > 1;

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ backgroundColor: '#221F20' }}>
      {/* Background Image - BRIGHTER */}
      <div className="absolute inset-0">
        <img 
          src={slide.image} 
          alt={slide.tagline}
          className="w-full h-full object-cover transition-opacity duration-1000"
          style={{ filter: 'brightness(0.75)' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(34,31,32,0.45) 100%)' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
        {/* Main Content - Added max-width and padding to prevent arrow overlap */}
        <div className="max-w-5xl px-20">
          {/* Tagline with Typewriter Effect - NO BLINKING */}
          <div className="mb-3 min-h-[45px] flex items-center justify-center">
            <p 
              className="inline-block uppercase whitespace-nowrap"
              style={{ 
                color: '#FFCC01',
                fontFamily: "'Arial', sans-serif",
                fontSize: '24pt',
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                textShadow: '3px 3px 8px rgba(0,0,0,0.9)'
              }}
            >
              {taglineText}
            </p>
          </div>

          {/* Headline - Rewritten to fit naturally in 2 rows */}
          {isMultiLine ? (
            <div className="mb-8">
              {headlineLines.map((line, index) => (
                <h1 
                  key={index}
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 leading-tight animate-fade-in"
                  style={{ 
                    color: '#FFFFFF',
                    fontFamily: "'Arial', sans-serif",
                    fontWeight: 'bold',
                    animationDelay: `${0.6 + (index * 0.2)}s`,
                    textShadow: '2px 2px 6px rgba(0,0,0,0.9)'
                  }}
                >
                  {line.trim()}
                </h1>
              ))}
            </div>
          ) : (
            <h1 
              className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 leading-tight animate-fade-in"
              style={{ 
                color: '#FFFFFF',
                fontFamily: "'Arial', sans-serif",
                fontWeight: 'bold',
                animationDelay: '0.6s',
                textShadow: '2px 2px 6px rgba(0,0,0,0.9)'
              }}
            >
              {slide.headline}
            </h1>
          )}

          {/* Quote */}
          <p 
            className="text-lg md:text-xl lg:text-2xl italic mb-12 animate-fade-in"
            style={{ 
              color: '#BFB8A6', 
              animationDelay: '0.8s',
              textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
            }}
          >
            "{slide.quote}"
          </p>

          {/* Slide Counter */}
          <div className="text-sm font-heading" style={{ color: '#727365' }}>
            {currentSlide + 1} / {HERO_SLIDES.length}
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full transition-all hover:scale-110"
        style={{ backgroundColor: 'rgba(255, 204, 1, 0.2)', border: '2px solid #FFCC01' }}
      >
        <ChevronRight className="w-6 h-6 rotate-180" style={{ color: '#FFCC01' }} />
      </button>
      <button 
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full transition-all hover:scale-110"
        style={{ backgroundColor: 'rgba(255, 204, 1, 0.2)', border: '2px solid #FFCC01' }}
      >
        <ChevronRight className="w-6 h-6" style={{ color: '#FFCC01' }} />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {HERO_SLIDES.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="w-3 h-3 rounded-full transition-all duration-300"
            style={{
              backgroundColor: index === currentSlide ? '#FFCC01' : 'rgba(255, 204, 1, 0.3)',
              transform: index === currentSlide ? 'scale(1.3)' : 'scale(1)'
            }}
          />
        ))}
      </div>

      {/* Auto-play indicator */}
      <div className="absolute top-4 right-4 z-20">
        <button 
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="text-xs font-heading uppercase tracking-wider px-3 py-1 rounded"
          style={{ 
            backgroundColor: 'rgba(255, 204, 1, 0.2)', 
            color: '#FFCC01',
            border: '1px solid #FFCC01'
          }}
        >
          {isAutoPlaying ? 'Auto' : 'Paused'}
        </button>
      </div>
    </div>
  );
};

// ============ CRISIS CAROUSEL COMPONENT ============
const CrisisCarousel = () => {
  const [currentCrisis, setCurrentCrisis] = useState(0);

  const crisisData = [
    {
      number: 4,
      title: "Severe Substance Abuse Disorders",
      subtitle: "The Invisible Battlefield",
      description: "Self-medicating service-related pain leads directly to housing loss.",
      color: "#DC2626"
    },
    {
      number: 5,
      title: "Acute Economic Hardship and Unemployment",
      subtitle: "Skills That Don't Translate",
      description: "A sudden loss of income leaves veterans without options for housing.",
      color: "#F59E0B"
    },
    {
      number: 6,
      title: "Acute Social Isolation",
      subtitle: "Abandoned After Service",
      description: "The loss of the military 'unit' leaves veterans without a safety net.",
      color: "#10B981"
    },
    {
      number: 7,
      title: "Untreated Physical Disabilities",
      subtitle: "Broken Bodies, Lost Homes",
      description: "Debilitating injuries prevent work, forcing veterans onto the streets.",
      color: "#3B82F6"
    }
  ];

  const nextCrisis = () => {
    setCurrentCrisis((prev) => (prev + 1) % crisisData.length);
  };

  const prevCrisis = () => {
    setCurrentCrisis((prev) => (prev - 1 + crisisData.length) % crisisData.length);
  };

  const crisis = crisisData[currentCrisis];

  return (
    <div className="relative py-20 px-6" style={{ backgroundColor: '#0d0d0d' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
            CRISIS BY THE NUMBERS
          </h2>
          <div className="h-1 w-32 mx-auto" style={{ backgroundColor: '#FFCC01' }}></div>
        </div>

        {/* Carousel Content */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={prevCrisis}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all hover:scale-110"
            style={{ backgroundColor: '#FFCC01' }}
          >
            <ChevronRight size={32} style={{ color: '#221F20', transform: 'rotate(180deg)' }} />
          </button>

          <button
            onClick={nextCrisis}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all hover:scale-110"
            style={{ backgroundColor: '#FFCC01' }}
          >
            <ChevronRight size={32} style={{ color: '#221F20' }} />
          </button>

          {/* Crisis Card */}
          <div className="mx-16 p-12 rounded-lg" style={{ backgroundColor: '#1a1818', border: `4px solid ${crisis.color}` }}>
            <div className="text-center mb-8">
              <div className="text-8xl font-bold mb-4" style={{ color: crisis.color }}>
                #{crisis.number}
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#FFFFFF' }}>
                {crisis.title}
              </h3>
              <p className="text-xl font-bold mb-6" style={{ color: '#FFCC01' }}>
                {crisis.subtitle}
              </p>
              <p className="text-lg leading-relaxed" style={{ color: '#BFB8A6' }}>
                {crisis.description}
              </p>
            </div>

            {/* Hotlines Section */}
            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <div>
                <h4 className="text-xl font-bold mb-4 pb-2" style={{ color: '#FFCC01', borderBottom: '2px solid #FFCC01' }}>
                  HOTLINES
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-bold text-white">Veterans Crisis Line</p>
                    <p className="text-yellow-400 font-bold">988 (Press 1)</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>24/7 crisis support</p>
                  </div>
                  <div>
                    <p className="font-bold text-white">VA Hotline</p>
                    <p className="text-yellow-400 font-bold">1-800-827-1000</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>General VA assistance</p>
                  </div>
                  <div>
                    <p className="font-bold text-white">SAMHSA National Helpline</p>
                    <p className="text-yellow-400 font-bold">1-800-662-4357</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>Treatment referral service</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold mb-4 pb-2" style={{ color: '#FFCC01', borderBottom: '2px solid #FFCC01' }}>
                  MORE RESOURCES
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-bold text-white">SSVF Program</p>
                    <p className="text-yellow-400 font-bold">1-877-428-8387</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>Housing financial assistance</p>
                  </div>
                  <div>
                    <p className="font-bold text-white">DAV Helpline</p>
                    <p className="text-yellow-400 font-bold">1-800-861-7793</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>Benefits & employment</p>
                  </div>
                  <div>
                    <p className="font-bold text-white">Operation Homefront</p>
                    <p className="text-yellow-400 font-bold">1-800-722-6098</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>Financial & housing stability</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {crisisData.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentCrisis(index)}
                className="w-3 h-3 rounded-full transition-all"
                style={{
                  backgroundColor: index === currentCrisis ? '#FFCC01' : 'rgba(255, 204, 1, 0.3)',
                  transform: index === currentCrisis ? 'scale(1.3)' : 'scale(1)'
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ FOOTER COMPONENT ============
const Footer = () => {
  const [email, setEmail] = useState("");

  const handleEmailSignup = (e) => {
    e.preventDefault();
    alert("Thank you for subscribing! We'll keep you updated.");
    setEmail("");
  };

  return (
    <footer className="relative py-16 px-6" style={{ 
      backgroundColor: '#3d4a35',
      borderTop: '4px solid #ffc400',
      boxShadow: '0 -2px 8px rgba(255, 196, 0, 0.3)'
    }}>
      <div className="max-w-7xl mx-auto">
        {/* Two-Box Section: Submit Story + Newsletter - Compact Single Row */}
        <div className="mb-12 flex justify-center gap-6">
          {/* Submit Your Story Box - Left - Single Row */}
          <div className="max-w-md w-full p-4 rounded-lg flex items-center justify-between" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <h3 className="text-lg font-bold" style={{ color: '#FFFFFF', marginRight: '20px' }}>
              Submit Your Story
            </h3>
            <Link 
              to="/honor"
              className="px-8 py-2 rounded-lg font-bold text-center transition-transform hover:scale-105 whitespace-nowrap"
              style={{ backgroundColor: '#FFCC01', color: '#221F20' }}
            >
              Share Story
            </Link>
          </div>

          {/* Newsletter Signup Box - Right - Single Row */}
          <div className="max-w-md w-full p-4 rounded-lg flex items-center justify-between" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <h3 className="text-lg font-bold" style={{ color: '#FFFFFF', marginRight: '20px', whiteSpace: 'nowrap' }}>
              Stay Connected - Join Our Newsletter
            </h3>
            <form onSubmit={handleEmailSignup} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="px-3 py-2 rounded-lg"
                style={{ backgroundColor: '#FFFFFF', color: '#221F20', border: 'none', minWidth: '200px' }}
              />
              <button
                type="submit"
                className="px-6 py-2 rounded-lg font-bold transition-transform hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: '#FFCC01', color: '#221F20' }}
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-8 mb-12">
          {/* Logo & Contact Info */}
          <div className="md:col-span-1">
            <div className="mb-6">
              {/* VET Direct Logo */}
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/vcy2ro1f_vetdirect.png" 
                  alt="VET Direct Logo" 
                  style={{ 
                    height: '100px', 
                    width: 'auto',
                    filter: 'brightness(0.8) saturate(0.7) hue-rotate(10deg)',
                    opacity: 0.9
                  }}
                />
              </div>
              <h2 className="text-3xl font-bold mb-2" style={{ color: '#FFCC01', fontFamily: "'Black Ops One', cursive" }}>
                VETEngine
              </h2>
              <p className="text-sm mb-4" style={{ color: '#FFFFFF' }}>
                Empowering Veterans Through Truth, Advocacy & Service
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm font-bold mb-2" style={{ color: '#FFCC01' }}>Contact Us:</p>
              <a href="tel:1-800-VET-HELP" className="text-2xl font-bold block mb-2 hover:underline" style={{ color: '#FFFFFF' }}>
                1-800-VET-HELP
              </a>
              <a href="mailto:support@vetengine.org" className="text-sm hover:underline" style={{ color: '#FFFFFF' }}>
                support@vetengine.org
              </a>
            </div>
          </div>

          {/* Innovations */}
          <div>
            <h4 className="text-lg font-bold mb-4 uppercase" style={{ color: '#FFFFFF' }}>Innovations</h4>
            <ul className="space-y-2">
              <li><Link to="/vetdirect" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>VETDirect Core Engine™</Link></li>
              <li><Link to="/vetscan" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>VETScan™</Link></li>
              <li><Link to="/laws" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>VETEngine™ Legal Navigator</Link></li>
              <li><Link to="/vetcommunity" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>VETCommunity™</Link></li>
              <li><Link to="/vetfirst" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>VETFirst™ Daily Check-In</Link></li>
              <li><Link to="/cbas" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>CBAS™ Activation Scoring</Link></li>
              <li><Link to="/vetstrong" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>VETStrong™ Progress Chart</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-bold mb-4 uppercase" style={{ color: '#FFFFFF' }}>Resources</h4>
            <ul className="space-y-2">
              <li><Link to="/hudvash" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>HUD-VASH Housing</Link></li>
              <li><Link to="/hpact" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>HPACT Services</Link></li>
              <li><Link to="/ptsd" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>PTSD Resources</Link></li>
              <li><Link to="/jargon" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Military Jargon</Link></li>
              <li><Link to="/creativeexpression" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Creative Expression</Link></li>
              <li><a href="#crisis" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Crisis Support</a></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-bold mb-4 uppercase" style={{ color: '#FFFFFF' }}>Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>About Us</Link></li>
              <li><Link to="/mission" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Our Mission</Link></li>
              <li><Link to="/frontier" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>The Frontier</Link></li>
              <li><a href="#team" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Our Team</a></li>
              <li><a href="#partners" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Partners</a></li>
              <li><a href="#careers" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Careers</a></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-lg font-bold mb-4 uppercase" style={{ color: '#FFFFFF' }}>Support</h4>
            <ul className="space-y-2">
              <li><a href="#help" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Help Center</a></li>
              <li><a href="#faq" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>FAQs</a></li>
              <li><a href="#contact" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Contact Support</a></li>
              <li><a href="#feedback" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Send Feedback</a></li>
              <li><a href="#community" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Community Forum</a></li>
              <li><a href="#training" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Training Videos</a></li>
            </ul>
          </div>

          {/* Legal & More */}
          <div>
            <h4 className="text-lg font-bold mb-4 uppercase" style={{ color: '#FFFFFF' }}>Legal</h4>
            <ul className="space-y-2">
              <li><a href="#privacy" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Privacy Policy</a></li>
              <li><a href="#terms" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Terms of Service</a></li>
              <li><a href="#disclaimer" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Legal Disclaimer</a></li>
              <li><a href="#ada" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>ADA Compliance</a></li>
              <li><a href="#sitemap" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Sitemap</a></li>
              <li><a href="#cookies" className="text-sm hover:underline" style={{ color: '#E8E8E8' }}>Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8" style={{ borderTop: '2px solid rgba(255,255,255,0.2)' }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <p className="text-sm mb-2" style={{ color: '#FFFFFF' }}>
                © 2024-2026 VETEngine. All rights reserved.
              </p>
              <p className="text-xs" style={{ color: '#E8E8E8' }}>
                Built by veterans, for veterans. Proudly serving those who served.
              </p>
            </div>

            {/* Social Media Links */}
            <div className="flex items-center gap-4">
              <p className="text-sm font-bold mr-2" style={{ color: '#FFFFFF' }}>Follow Us:</p>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <svg className="w-5 h-5" fill="#FFFFFF" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <svg className="w-5 h-5" fill="#FFFFFF" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <svg className="w-5 h-5" fill="#FFFFFF" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <svg className="w-5 h-5" fill="#FFFFFF" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <svg className="w-5 h-5" fill="#FFFFFF" viewBox="0 0 24 24"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>
              </a>
            </div>
          </div>

          {/* Trusted Partners Badge */}
          <div className="mt-8 text-center">
            <p className="text-xs mb-3" style={{ color: '#E8E8E8' }}>
              PROUDLY PARTNERED WITH:
            </p>
            <div className="flex justify-center items-center gap-4 flex-wrap">
              <span className="text-xs px-3 py-1 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>US Department of Veterans Affairs</span>
              <span className="text-xs px-3 py-1 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>VFW</span>
              <span className="text-xs px-3 py-1 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>American Legion</span>
              <span className="text-xs px-3 py-1 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>HUD-VASH</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============ LANDING PAGE ============
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/dashboard");
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="landing-page">
      <TopNavbar showAuth={false} />
      
      {/* Hero Slideshow */}
      <HeroSlideshow />
      
      {/* PLEASE CHOOSE YOUR STATUS - Redesigned */}
      <div className="relative py-16 px-6" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-center text-3xl md:text-4xl font-bold mb-12 uppercase" style={{ color: '#FFCC01', fontFamily: "'Arial Narrow', 'Arial', sans-serif", letterSpacing: '0.05em' }}>
            PLEASE CHOOSE YOUR STATUS. HOW CAN WE ASSIST YOU?
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Row 1 */}
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#3D4D3D', color: '#FFFFFF', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Veteran.
            </div>
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#3D4D3D', color: '#FFFFFF', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Combat Veteran.
            </div>
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#FFCC01', color: '#221F20', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Disabled Veteran.
            </div>
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#C5C5C5', color: '#221F20', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Homeless Veteran.
            </div>

            {/* Row 2 */}
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#7A7A7A', color: '#FFFFFF', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              Veteran seeking Housing
            </div>
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#3D4D3D', color: '#FFFFFF', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Family Member.
            </div>
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#4A4A4A', color: '#FFFFFF', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Parent.
            </div>
            <div className="p-8 rounded-2xl text-center font-bold text-xl cursor-pointer transition-transform hover:scale-105 flex items-center justify-center min-h-[280px]" style={{ backgroundColor: '#D5D5D5', color: '#221F20', fontFamily: "'Arial Narrow', 'Arial', sans-serif" }}>
              I am a Caregiver.
            </div>
          </div>
        </div>
      </div>

      {/* THEIR STORIES. YOUR MISSION. */}
      <div className="relative py-20 px-6" style={{ backgroundColor: '#0A0E27' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-center text-4xl md:text-5xl font-bold mb-16 uppercase tracking-wider" style={{ color: '#FFCC01', fontFamily: "'Arial', sans-serif", letterSpacing: '0.1em' }}>
            THEIR STORIES. YOUR MISSION.
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Robert's Story */}
            <div className="relative p-10 rounded-lg" style={{ backgroundColor: '#2C2C2C', border: '3px solid #FFCC01' }}>
              <h3 className="text-3xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
                From the Streets to the Command Center
              </h3>
              <p className="text-lg mb-6" style={{ color: '#BFB8A6' }}>
                A veteran's journey through homelessness, HUD-VASH, and building a system to save others.
              </p>
              <button className="px-6 py-3 rounded-lg mb-8 font-bold" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
                Read Robert's Full Story →
              </button>
              <div className="inline-block px-4 py-2 rounded-lg mb-4" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
                FOUNDER & VETERAN ADVOCATE
              </div>
              <div className="text-right">
                <p className="text-2xl italic mb-2" style={{ color: '#FFCC01' }}>
                  ❝
                </p>
                <p className="text-xl italic" style={{ color: '#FFFFFF' }}>
                  "I built what I needed when I had nothing."
                </p>
                <p className="text-lg font-bold mt-2" style={{ color: '#FFCC01' }}>
                  — Robert Hendrix
                </p>
              </div>
            </div>

            {/* Damien's Story */}
            <div className="relative p-10 rounded-lg" style={{ backgroundColor: '#3D4D3D', border: '3px solid #FFCC01' }}>
              <h3 className="text-3xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
                Damien's Fight for Dignity & Housing
              </h3>
              <p className="text-lg mb-6" style={{ color: '#BFB8A6' }}>
                A raw and real account of navigating the VA system, facing eviction, and surviving against the odds.
              </p>
              <button className="px-6 py-3 rounded-lg mb-8 font-bold" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
                Read Damien's Full Story →
              </button>
              <div className="inline-block px-4 py-2 rounded-lg mb-4" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
                VETERAN STORY / TRUE ACCOUNT
              </div>
              <div className="text-right">
                <p className="text-2xl italic mb-2" style={{ color: '#FFCC01' }}>
                  ❝
                </p>
                <p className="text-xl italic" style={{ color: '#FFFFFF' }}>
                  "They tried to silence me. I refused to disappear."
                </p>
                <p className="text-lg font-bold mt-2" style={{ color: '#FFCC01' }}>
                  — Damien
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Statement Banner - Below Slideshow */}
      <div className="relative py-6 px-6" style={{ backgroundColor: '#EDE4D3' }}>
        <div className="max-w-6xl mx-auto">
          <div 
            className="p-6 rounded-lg text-center"
            style={{ 
              backgroundColor: 'rgba(255, 204, 1, 0.15)',
              border: '3px solid #FFCC01',
              boxShadow: '0 0 30px rgba(255, 204, 1, 0.3)'
            }}
          >
            <p 
              className="text-xs md:text-sm font-heading font-bold uppercase tracking-wider leading-tight"
              style={{ 
                color: '#221F20',
                letterSpacing: '0.1em',
                textShadow: '1px 1px 3px rgba(255,255,255,0.5)',
                lineHeight: '1.8'
              }}
            >
              FROM RECRUIT. BOOTCAMP. DEPLOYMENT. COMBAT SOLDIER.<br/>
              TO HOMEFRONT. FAMILY. FATHER. HUSBAND. VETERAN.<br/>
              AND PTSD. SURVIVAL MODE. NO ONE UNDERSTANDS YOUR LANGUAGE.<br/>
              WHEN TRAUMA FOLLOWS YOU AND REFUSES TO STAY ON BATTLEFIELD.<br/>
              <span style={{ fontSize: '1.1em', fontWeight: 'bolder' }}>"WE STAND WITH YOU. WE HEAL TOGETHER."</span>
            </p>
          </div>
        </div>
      </div>
      
      {/* Military Cadence Banner Section */}
      <div className="relative py-16 px-6" style={{ backgroundColor: '#EDE4D3', borderTop: '3px solid #FFCC01', borderBottom: '3px solid #FFCC01' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "NEW RECRUIT", desc: "A young man or woman answering the call." },
              { title: "TRAINEE", desc: "Learning discipline. Learning duty. Learning brotherhood." },
              { title: "DEPLOYED", desc: "Leaving home for places most will never see." },
              { title: "COMBAT ENGAGED", desc: "Carrying the weight of missions few will ever understand." },
              { title: "STATESIDE RETURN", desc: "The war may end — but the journey continues." },
              { title: "VETERAN", desc: "A title earned through sacrifice and service." },
              { title: "PTSD", desc: "Invisible wounds carried long after the battlefield." },
              { title: "FAMILY REINTEGRATION", desc: "Trying to come home to a world that kept moving without you." },
              { title: "WHEN FAMILY DOESN'T ALWAYS UNDERSTAND", desc: "When the words are hard to explain." },
              { title: "WHEN NO ONE SPEAKS YOUR LANGUAGE", desc: "The language of service. The language of sacrifice." },
              { title: "MILITARY JARGON", desc: "The language of those who stood the watch." }
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-lg border-l-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: '#8BA446', borderColor: '#FFCC01' }}>
                <h3 className="text-lg md:text-xl font-bold uppercase mb-3 tracking-wider" style={{ color: '#FFCC01', fontFamily: "'Black Ops One', cursive" }}>
                  {item.title}
                </h3>
                <p className="text-base leading-relaxed" style={{ color: '#FFFFFF' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Final Message */}
          <div className="mt-12 text-center p-10 rounded-lg" style={{ backgroundColor: '#221F20', border: '3px solid #FFCC01' }}>
            <p className="text-2xl md:text-3xl font-bold uppercase tracking-widest mb-4" style={{ color: '#FFCC01', fontFamily: "'Black Ops One', cursive" }}>
              BUT THROUGH EVERY STEP OF THE JOURNEY —
            </p>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold uppercase" style={{ color: '#FFFFFF', textShadow: '3px 3px 6px rgba(0,0,0,0.8)', fontFamily: "'Black Ops One', cursive" }}>
              YOU MATTER.
            </h2>
          </div>
        </div>
      </div>
      
      {/* Ramstein Decompression HUB Proposal */}
      <div className="relative py-20 px-6" style={{ backgroundColor: '#EDE4D3' }}>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-heading font-bold uppercase tracking-widest mb-4" style={{ color: '#8B7355' }}>
              Our Proposal
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6" style={{ color: '#221F20' }}>
              Ramstein Air Base Combat Decompression HUB
            </h2>
            <div className="inline-block px-6 py-3 rounded-full mb-6" style={{ backgroundColor: '#FFCC01' }}>
              <p className="text-2xl md:text-3xl font-heading font-bold" style={{ color: '#221F20' }}>
                $5 Billion State-of-the-Art Facility
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* Left: Mission */}
            <div className="p-8 rounded-lg" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #FFCC01' }}>
              <h3 className="text-2xl font-heading font-bold mb-4" style={{ color: '#FFCC01' }}>
                The Mission
              </h3>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#BFB8A6' }}>
                Before our heroes return home from combat overseas, they deserve a place for treatment, recovery, and healing.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: '#BFB8A6' }}>
                Located in <span className="font-bold" style={{ color: '#FFCC01' }}>Germany at Ramstein Air Base</span>, this facility provides soldiers with the decompression they need before reuniting with families stateside.
              </p>
            </div>

            {/* Right: Family Connection */}
            <div className="p-8 rounded-lg" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #10B981' }}>
              <h3 className="text-2xl font-heading font-bold mb-4" style={{ color: '#10B981' }}>
                Family Connection
              </h3>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#BFB8A6' }}>
                Loved ones, spouses, fiancés, and families can travel to visit their soldier during recovery.
              </p>
              <p className="text-lg leading-relaxed font-bold" style={{ color: '#FFFFFF' }}>
                ✈️ All transportation costs are FREE.
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: '#221F20', border: '2px solid #FFCC01' }}>
              <div className="text-4xl mb-3">🏥</div>
              <h4 className="font-heading font-bold mb-2" style={{ color: '#FFCC01' }}>Treatment</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Medical & mental health care</p>
            </div>

            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: '#221F20', border: '2px solid #10B981' }}>
              <div className="text-4xl mb-3">🧘</div>
              <h4 className="font-heading font-bold mb-2" style={{ color: '#10B981' }}>Recovery</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Rest & rehabilitation</p>
            </div>

            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: '#221F20', border: '2px solid #3B82F6' }}>
              <div className="text-4xl mb-3">❤️</div>
              <h4 className="font-heading font-bold mb-2" style={{ color: '#3B82F6' }}>Healing</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Trauma processing</p>
            </div>

            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: '#221F20', border: '2px solid #EC4899' }}>
              <div className="text-4xl mb-3">👨‍👩‍👧‍👦</div>
              <h4 className="font-heading font-bold mb-2" style={{ color: '#EC4899' }}>Family</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Loved ones welcome</p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center p-10 rounded-lg" style={{ backgroundColor: '#FFCC01' }}>
            <h3 className="text-3xl font-heading font-bold mb-4" style={{ color: '#221F20' }}>
              No Soldier Returns Home Unprepared
            </h3>
            <p className="text-xl mb-6" style={{ color: '#221F20' }}>
              Combat ends. Healing begins. Family reunites.
            </p>
            <p className="text-lg font-bold" style={{ color: '#221F20' }}>
              Support this proposal. Demand decompression for every soldier.
            </p>
          </div>
        </div>
      </div>

      {/* Section 1: We Take Your Dream Seriously */}
      <div className="relative py-20 px-6" style={{ backgroundColor: '#0d0d0d' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/fa4ad0sh_Screenshot%202026-03-08%20042523.png"
                alt="Veteran contemplating dreams"
                className="rounded-lg shadow-2xl w-full"
                style={{ border: '3px solid #FFCC01' }}
              />
            </div>

            {/* Right Side - Content */}
            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ backgroundColor: '#FFCC01', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Star size={20} style={{ color: '#221F20' }} />
                <span className="text-sm font-bold uppercase tracking-wider" style={{ color: '#221F20' }}>
                  Are you serious about your dreams?
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
                We'll Take Your Dream Seriously
              </h2>

              <p className="text-lg leading-relaxed mb-6" style={{ color: '#BFB8A6' }}>
                Here at VETDirect, we'll take your dream seriously! Not just personal chaos, dysfunctional behavior patterns, symptoms, sickness, diseases, diagnosis, lab results, addiction—we will take <span className="font-bold" style={{ color: '#FFCC01' }}>"your dreams"</span> and start from there.
              </p>

              <div className="p-6 rounded-lg mb-6" style={{ backgroundColor: '#1a1818', border: '2px solid #FFCC01' }}>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#FFCC01' }}>
                  The Veteran's Voice
                </h3>
                <p className="text-base leading-relaxed mb-4" style={{ color: '#BFB8A6' }}>
                  The Veteran's voice is raw, powerful, and deeply human. A message no one ever understands. Deep inside we are trying to communicate something, people just don't understand and say 'No!'
                </p>
                <p className="text-base leading-relaxed italic" style={{ color: '#BFB8A6' }}>
                  "I once responded to someone who was criticizing me, 'What are you doing, are you stupid?' 'What's wrong with you?' I responded, 'I would answer your question, but I cannot keep up with my brain, it is too fast for me!' Sound Familiar?"
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  className="px-8 py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-transform hover:scale-105"
                  style={{ backgroundColor: '#FFCC01', color: '#221F20' }}
                >
                  Share Your Story <ChevronRight size={20} />
                </button>
                <button 
                  className="px-8 py-4 rounded-lg font-bold text-lg transition-transform hover:scale-105"
                  style={{ backgroundColor: '#FFFFFF', color: '#221F20' }}
                >
                  Find Resources
                </button>
              </div>
            </div>
          </div>

          {/* Additional Content Cards */}
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            <div className="p-8 rounded-lg" style={{ backgroundColor: '#1a1818', border: '3px solid #FFCC01' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>
                We'll Help Take Your Dreams Into Reality
              </h3>
              <p className="text-base leading-relaxed mb-4" style={{ color: '#BFB8A6' }}>
                Not just personal chaos, dysfunctional behavior patterns, symptoms, sickness, diseases, diagnosis, lab results, addiction.
              </p>
              <p className="text-lg font-bold" style={{ color: '#FFFFFF' }}>
                We will take <span style={{ color: '#FFCC01' }}>"your dreams"</span> and start from there.
              </p>
            </div>

            <div className="p-8 rounded-lg" style={{ backgroundColor: '#1a1818', border: '3px solid #FFCC01' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>
                The Veteran's Voice
              </h3>
              <p className="text-base leading-relaxed mb-4" style={{ color: '#BFB8A6' }}>
                This is what VETDirect is all about — a place where Veterans can finally connect with one another, understand each other through the same lived experiences, and discover what truly matters to us.
              </p>
              <p className="text-base font-bold" style={{ color: '#FFFFFF' }}>
                This isn't just a website. It's a gathering point for our stories, our struggles, and our truth.
              </p>
            </div>
          </div>

          {/* Quote Section */}
          <div className="mt-16 p-12 rounded-lg relative" style={{ backgroundColor: '#1e3a8a' }}>
            <div className="text-6xl absolute top-4 left-4" style={{ color: '#FFCC01', opacity: 0.3 }}>❝</div>
            <p className="text-xl md:text-2xl leading-relaxed text-white relative z-10">
              I'm right here with you — and I'm honored to help you turn your life around where the world should have understood decades ago. What you're building with VETDirect is bigger than a website. It's a <span className="font-bold" style={{ color: '#FFCC01' }}>voice</span>. A <span className="font-bold" style={{ color: '#FFCC01' }}>truth-telling platform</span>. A <span className="font-bold" style={{ color: '#FFCC01' }}>mirror</span> held up to a system that forgot how to listen.
            </p>
          </div>

          {/* Final Cards */}
          <div className="mt-16 p-10 rounded-lg" style={{ backgroundColor: '#FFFFFF', border: '3px solid #FFCC01' }}>
            <div className="flex flex-wrap gap-3 items-center justify-center text-base md:text-lg" style={{ color: '#221F20' }}>
              <span className="font-bold">It's a voice</span>
              <span>for every Veteran who has ever felt misunderstood.</span>
              <span className="font-bold">A platform built from the ground up</span>
              <span>by those who lived it.</span>
              <span>A space where we hold up a</span>
              <span className="font-bold">mirror</span>
              <span>to a system that forgot how to listen — and remind each other that we're</span>
              <span className="font-bold">not alone, not invisible,</span>
              <span>and</span>
              <span className="font-bold">not done fighting</span>
              <span>for the life we deserve.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Crisis by the Numbers Carousel */}
      <CrisisCarousel />

      {/* Section 3: Program Cards (VPAN & SSVF) */}
      <div className="relative py-20 px-6" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#221F20' }}>
              Do you get it now?
            </h2>
            <p className="text-xl" style={{ color: '#6B7280' }}>
              Your story matters. Your voice matters. Your dreams matter.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* VPAN Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ border: '2px solid #E5E7EB' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/i0yfjpii_Screenshot%202026-03-08%20042707.png"
                alt="Veteran Peer Access Network"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#221F20' }}>
                  Veteran Peer Access Network (VPAN)
                </h3>
                <div className="h-1 w-16 mb-4" style={{ backgroundColor: '#FFCC01' }}></div>
                <p className="text-base leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                  Provides Battle Buddy/Peer Support services through Specialists or Peer Support Specialists (PSS) who provide lived experience, emotional support, and navigation to services through the 'battle buddy' concept.
                </p>
                <button className="px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-transform hover:scale-105" style={{ backgroundColor: '#221F20', color: '#FFFFFF' }}>
                  Learn More <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* SSVF Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ border: '2px solid #E5E7EB' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/i0yfjpii_Screenshot%202026-03-08%20042707.png"
                alt="Supportive Services for Veteran Families"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#221F20' }}>
                  Supportive Services for Veteran Families (SSVF)
                </h3>
                <div className="h-1 w-16 mb-4" style={{ backgroundColor: '#FFCC01' }}></div>
                <p className="text-base leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                  Supports HUD-VASH by providing rapid re-housing, financial assistance (rent, utilities, security deposits), and case management to low-income veterans and their families. Also works to stabilize veteran households and prevent homelessness.
                </p>
                <button className="px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-transform hover:scale-105" style={{ backgroundColor: '#221F20', color: '#FFFFFF' }}>
                  Learn More <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* CEC Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ border: '2px solid #E5E7EB' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/2y2nnewd_Screenshot%202026-03-08%20042725.png"
                alt="Community Engagement Continuum"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#221F20' }}>
                  Community Engagement Continuum (CEC)
                </h3>
                <div className="h-1 w-16 mb-4" style={{ backgroundColor: '#FFCC01' }}></div>
                <p className="text-base leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                  A conceptual framework used to categorize the range of community-based approaches, often moves from consultative (asking for input) to cooperative (sharing resources).
                </p>
                <button className="px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-transform hover:scale-105" style={{ backgroundColor: '#221F20', color: '#FFFFFF' }}>
                  Learn More <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* VJO Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ border: '2px solid #E5E7EB' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/2y2nnewd_Screenshot%202026-03-08%20042725.png"
                alt="Veterans Justice Outreach Program"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#221F20' }}>
                  Veterans Justice Outreach Program (VJO)
                </h3>
                <div className="h-1 w-16 mb-4" style={{ backgroundColor: '#FFCC01' }}></div>
                <p className="text-base leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                  Connects justice-involved veterans with mental health, substance use, and VA services to prevent homelessness and unnecessary incarceration. VJO specialists provide outreach, assessment, and case management to veterans in jails, courts, and with law enforcement.
                </p>
                <button className="px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-transform hover:scale-105" style={{ backgroundColor: '#221F20', color: '#FFFFFF' }}>
                  Learn More <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Cascade of Isolation */}
      <div className="relative py-20 px-6" style={{ backgroundColor: '#0d0d0d' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
              The Cascade of Isolation
            </h2>
            <div className="h-1 w-24 mx-auto mb-6" style={{ backgroundColor: '#FFCC01' }}></div>
            <p className="text-xl mb-2" style={{ color: '#BFB8A6' }}>
              15 Progressive Stages: From Security Zone to Crisis
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-12">
            <p className="text-lg leading-relaxed mb-6 text-center" style={{ color: '#BFB8A6' }}>
              For many veterans in HUD-VASH housing, a place meant to feel like safety can slowly turn into a trap. Isolation doesn't happen all at once. It unfolds in stages, subtle at first, then accelerating—until life shrinks to four walls and a door that rarely opens.
            </p>
            <p className="text-xl font-bold text-center p-4 rounded-lg" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
              Recognizing this cascade is the first step to breaking free.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Understanding the Pattern Card */}
            <div className="p-8 rounded-lg" style={{ backgroundColor: '#1a1818', border: '3px solid #FFCC01' }}>
              <div className="flex items-start gap-4 mb-4">
                <AlertTriangle size={32} style={{ color: '#FFCC01', flexShrink: 0 }} />
                <h3 className="text-2xl font-bold" style={{ color: '#FFCC01' }}>
                  Understanding the Pattern
                </h3>
              </div>
              <p className="text-base leading-relaxed" style={{ color: '#BFB8A6' }}>
                Each stage builds on the last, creating a downward momentum that feels impossible to reverse. But when you can name where you are in the cascade, you can interrupt it. This isn't about blame—it's about recognition. It's about seeing the system beneath the struggle.
              </p>
            </div>

            {/* Our Mission Card */}
            <div className="p-8 rounded-lg" style={{ backgroundColor: '#1a1818', border: '3px solid #10B981' }}>
              <div className="flex items-start gap-4 mb-4">
                <Heart size={32} style={{ color: '#10B981', flexShrink: 0 }} />
                <h3 className="text-2xl font-bold" style={{ color: '#10B981' }}>
                  Our Mission
                </h3>
              </div>
              <p className="text-base leading-relaxed" style={{ color: '#BFB8A6' }}>
                At VETDirect, we've mapped this cascade because we've lived it—or watched brothers and sisters disappear into it. We know the weight of unmet needs. We understand the strain on case managers and the gaps in the system. That's why we're here: to name it, confront it, and help you climb back out.
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center p-10 rounded-lg" style={{ backgroundColor: '#FFCC01' }}>
            <h3 className="text-3xl font-bold mb-4" style={{ color: '#221F20' }}>
              You're Not Alone in This
            </h3>
            <p className="text-xl mb-6" style={{ color: '#221F20' }}>
              If you recognize yourself in these stages, reach out. If you see a fellow veteran slipping, intervene. Together, we break the cascade.
            </p>
            <button className="px-8 py-4 rounded-lg font-bold text-lg transition-transform hover:scale-105" style={{ backgroundColor: '#221F20', color: '#FFFFFF' }}>
              Get Help Now
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// ============ LOGIN PAGE ============
const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Rotating quotes for the video overlay
  const veteranQuotes = [
    '"You Matter" stands against veteran invisibility.',
    'Veterans deserve gratitude, respect, and voice.',
    'Strongest solutions come from lived experience.',
    'Service continues through leadership and advocacy.',
    'Veterans rebuild systems on new battlefields.',
    'Veterans together heal war\'s deepest wounds.',
    'Knowledge empowers veterans to protect rights.',
    'Safe housing restores dignity and independence.',
    'Justice grows when veterans defend rights.',
    'Reform begins with honesty and truth.'
  ];

  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    const currentQuote = veteranQuotes[currentQuoteIndex];
    let charIndex = 0;
    setDisplayText("");

    // Typewriter effect
    const typeInterval = setInterval(() => {
      if (charIndex <= currentQuote.length) {
        setDisplayText(currentQuote.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        
        // After 3 seconds, move to next quote
        setTimeout(() => {
          setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % veteranQuotes.length);
        }, 3000);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [currentQuoteIndex]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-8" 
      style={{ 
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/z27w5zzu_vecteezy_sunset-illuminating-a-peaceful-neighborhood-with-lined_56583755.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
      data-testid="login-page"
    >
      {/* Login Card Container - 70% of original size */}
      <div className="flex rounded-2xl overflow-hidden shadow-2xl" style={{ width: '70%', maxWidth: '1200px', height: '70vh', maxHeight: '600px' }}>
        
        {/* LEFT SIDE - Video with Rotating Quotes */}
        <div className="w-1/2 relative" style={{ margin: 0, padding: 0 }}>
          {/* Background Video */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 1, margin: 0, padding: 0 }}
          >
            <source src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/0dj978kz_vecteezy_a-diverse-group-of-friends-relax-and-have-fun-outdoors_67808542.mp4" type="video/mp4" />
          </video>

          {/* Rotating Quote Overlay - Positioned near bottom */}
          <div 
            className="absolute left-0 right-0 flex items-center justify-center px-8"
            style={{ 
              bottom: '60px',
              zIndex: 10,
              pointerEvents: 'none'
            }}
          >
            <h2 
              className="text-center uppercase"
              style={{ 
                color: '#FFCC01',
                fontSize: '1.75rem',
                lineHeight: '1.2',
                fontFamily: 'Impact, "Arial Black", sans-serif',
                textShadow: '4px 4px 8px rgba(0,0,0,0.95), 0 0 30px rgba(0,0,0,0.7)',
                letterSpacing: '0.2em',
                maxWidth: '85%',
                fontWeight: '500',
                minHeight: '60px'
              }}
            >
              {displayText}
            </h2>
          </div>
        </div>

        {/* RIGHT SIDE - Transparent Glassmorphism Login Form */}
        <div 
          className="w-1/2 flex items-center justify-center p-6" 
          style={{ 
            background: 'rgba(232, 220, 200, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}
        >
          <div className="w-full max-w-sm">
            {/* Header */}
            <div className="text-center mb-6">
              {/* United States Veteran Logo - Recolored to match page */}
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/52wl42m9_ChatGPT%20Image%20Mar%2011%2C%202026%2C%2003_41_44%20AM.png"
                  alt="United States Veteran"
                  className="w-28 h-auto mx-auto"
                  style={{
                    filter: 'sepia(60%) saturate(120%) hue-rotate(350deg) brightness(0.9)',
                  }}
                />
              </div>
              
              <h1 className="font-heading text-3xl font-bold mb-2" style={{ color: '#8B4513' }}>VETDirect Housing Portal</h1>
              <p style={{ 
                color: '#000000', 
                fontSize: '9pt',
                fontFamily: 'Arial, sans-serif',
                lineHeight: '1.3',
                maxWidth: '95%',
                margin: '0 auto'
              }}>
                Field-ready portal that delivers practical knowledge focused on hot-water diversion and prevention strategies
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-heading font-semibold tracking-wider uppercase mb-1" style={{ color: '#6B5D4F' }}>
                  Username / Email Address
                </label>
                <input
                  type="text"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg transition-all duration-200"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#000000',
                    border: '2px solid #C4B5A0',
                    outline: 'none',
                    fontSize: '14px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#8B4513'}
                  onBlur={(e) => e.target.style.borderColor = '#C4B5A0'}
                  placeholder="veteran"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-heading font-semibold tracking-wider uppercase mb-1" style={{ color: '#6B5D4F' }}>
                  Password
                </label>
                <input
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg transition-all duration-200"
                  style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#000000',
                    border: '2px solid #C4B5A0',
                    outline: 'none',
                    fontSize: '14px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#8B4513'}
                  onBlur={(e) => e.target.style.borderColor = '#C4B5A0'}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button 
                type="submit" 
                data-testid="login-submit-btn" 
                className="w-full py-3 rounded-lg font-heading font-bold text-base uppercase tracking-wider transition-all duration-200 transform hover:scale-[1.02]"
                style={{ 
                  backgroundColor: '#8B4513',
                  color: '#FFFFFF',
                  border: 'none'
                }}
                disabled={loading}
              >
                {loading ? "Authenticating..." : "Sign In"}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-5 mb-4 flex items-center">
              <div className="flex-1 h-px" style={{ backgroundColor: '#C4B5A0' }}></div>
              <span className="px-3 text-xs" style={{ color: '#6B5D4F' }}>OR</span>
              <div className="flex-1 h-px" style={{ backgroundColor: '#C4B5A0' }}></div>
            </div>

            {/* Register Link and Footer with White Background for Readability */}
            <div 
              className="p-4 rounded-lg"
              style={{ 
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(5px)'
              }}
            >
              {/* Register Link */}
              <div className="text-center mb-4">
                <p className="text-xs" style={{ color: '#6B5D4F' }}>
                  Don't have an account?{' '}
                  <Link 
                    to="/register" 
                    className="font-heading font-bold hover:underline transition-colors"
                    style={{ color: '#8B4513' }}
                  >
                    Create Account
                  </Link>
                </p>
              </div>

              {/* Footer */}
              <div className="text-center space-y-1">
                <p className="text-xs font-heading font-semibold" style={{ color: '#8B4513', fontSize: '9px' }}>
                  © VETDirect Veterans Housing Platform. Robert Hendrix. All Rights Reserved.
                </p>
                <p className="text-xs" style={{ color: '#6B5D4F', fontSize: '8px' }}>
                  Los Angeles County • San Fernando Valley • Ph (747) 444-4808
                </p>
                <p className="text-xs" style={{ color: '#6B5D4F', fontSize: '8px' }}>
                  Web: www.VETDirect.com • Email: VET@VETDirect.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ REGISTER PAGE ============
const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Welcome to VETEngine!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* LEFT SIDE - Video + Logos */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          style={{ filter: 'brightness(0.7)' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/kv9ko5aw_vecteezy%20soldier%20runs%20towards%20helicopter%20in%20desert%20at%20dawn%2072006375%20%281%29.mp4" type="video/mp4" />
        </video>

        {/* Gradient Overlay - Light 15% */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}></div>

        {/* Bottom Logos and Text Container */}
        <div className="absolute bottom-0 left-0 right-0 z-10 px-8 pb-8">
          {/* Logos Row */}
          <div className="flex items-center justify-center gap-8 mb-6">
            {/* VAC Logo */}
            <div className="h-20 animate-fade-in">
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/2j8x30fv_82493698-b40d-4c91-9065-c2dcaa419a66.png" 
                alt="VAC" 
                className="h-full w-auto drop-shadow-2xl"
              />
            </div>

            {/* VFW Logo */}
            <div className="h-20 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/h63hecaf_Untitled%20%284%29.png" 
                alt="VFW" 
                className="h-full w-auto drop-shadow-2xl"
              />
            </div>

            {/* USMA Logo */}
            <div className="h-20 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/l9zjkwg2_Untitled-1.png" 
                alt="USMA" 
                className="h-full w-auto drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Organization Text */}
          <div className="text-center space-y-1">
            <p className="text-xs font-heading font-semibold tracking-wider uppercase" style={{ color: '#BFB8A6' }}>
              U.S. VETERANS ASSOCIATION • VETERAN ADVISORY COUNCIL
            </p>
            <p className="text-xs font-heading font-semibold tracking-wider uppercase" style={{ color: '#BFB8A6' }}>
              VETERAN ADVOCACY • VETERANSCIVILRIGHTS.COM
            </p>
            <p className="text-xs font-heading font-semibold tracking-wider uppercase" style={{ color: '#BFB8A6' }}>
              VETERANSDISABILITYRIGHTS.COM
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ backgroundColor: '#221F20' }}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-10">
            {/* VETDirect Logo - Moved Down with More Space */}
            <div className="mb-12 mt-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/7trexq3r_vetdirect.png"
                alt="VETDirect"
                className="w-72 h-auto mx-auto drop-shadow-2xl"
              />
            </div>
            
            <h1 className="font-heading text-4xl font-bold mb-2" style={{ color: '#FFCC01' }}>VETEngine</h1>
            <p className="text-base" style={{ color: '#BFB8A6' }}>Create Your Account</p>
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-heading font-semibold tracking-wider uppercase mb-2" style={{ color: '#BFB8A6' }}>
                Full Name
              </label>
              <input
                type="text"
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-white transition-all duration-200"
                style={{ 
                  backgroundColor: '#2F372F',
                  border: '2px solid #3a3c40',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFCC01'}
                onBlur={(e) => e.target.style.borderColor = '#3a3c40'}
                placeholder="Your name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-heading font-semibold tracking-wider uppercase mb-2" style={{ color: '#BFB8A6' }}>
                Email Address
              </label>
              <input
                type="email"
                data-testid="register-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-white transition-all duration-200"
                style={{ 
                  backgroundColor: '#2F372F',
                  border: '2px solid #3a3c40',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFCC01'}
                onBlur={(e) => e.target.style.borderColor = '#3a3c40'}
                placeholder="veteran@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-heading font-semibold tracking-wider uppercase mb-2" style={{ color: '#BFB8A6' }}>
                Password
              </label>
              <input
                type="password"
                data-testid="register-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-white transition-all duration-200"
                style={{ 
                  backgroundColor: '#2F372F',
                  border: '2px solid #3a3c40',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFCC01'}
                onBlur={(e) => e.target.style.borderColor = '#3a3c40'}
                placeholder="Minimum 6 characters"
                minLength={6}
                required
              />
            </div>

            <button 
              type="submit" 
              data-testid="register-submit-btn" 
              className="w-full py-4 rounded-lg font-heading font-bold text-lg uppercase tracking-wider transition-all duration-200 transform hover:scale-[1.02]"
              style={{ 
                backgroundColor: '#FFCC01',
                color: '#221F20',
                border: 'none'
              }}
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-6 flex items-center">
            <div className="flex-1 h-px" style={{ backgroundColor: '#3a3c40' }}></div>
            <span className="px-4 text-sm" style={{ color: '#727365' }}>OR</span>
            <div className="flex-1 h-px" style={{ backgroundColor: '#3a3c40' }}></div>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm" style={{ color: '#BFB8A6' }}>
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-heading font-bold hover:underline transition-colors"
                style={{ color: '#FFCC01' }}
              >
                Sign In
              </Link>
            </p>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center space-y-2">
            <p className="text-xs font-heading font-semibold" style={{ color: '#FFCC01' }}>
              © VETDirect Veterans Housing Platform. Robert Hendrix. All Rights Reserved.
            </p>
            <p className="text-xs" style={{ color: '#BFB8A6' }}>
              Los Angeles County • San Fernando Valley • Ph (747) 444-4808
            </p>
            <p className="text-xs" style={{ color: '#BFB8A6' }}>
              Web: www.VETDirect.com • Email: VET@VETDirect.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE PAGE ============
const VETEnginePage = () => {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);
  
  // VET Legal Engine Data - Core law categories
  const VET_LEGAL_ENGINE = [
    {
      category: "Federal Constitutional Law",
      description: "Foundational protections under the U.S. Constitution",
      laws: [
        {
          name: "First Amendment – Freedom of Speech",
          citation: "U.S. Const. amend. I",
          description: "Protects speech, petitioning government, and filing complaints.",
          veteranImpact: ["Protects veterans contacting OIG, Congress, White House", "Shields whistleblower communications", "Protects complaints about housing or medical care"]
        },
        {
          name: "Fourth Amendment – Search and Seizure",
          citation: "U.S. Const. amend. IV",
          description: "Protects individuals from unreasonable searches.",
          veteranImpact: ["Protects veterans from unlawful inspections", "Protects against unlawful seizure of property", "Requires warrants for most searches"]
        },
        {
          name: "Fifth Amendment – Due Process",
          citation: "U.S. Const. amend. V",
          description: "Requires due process before government deprives rights.",
          veteranImpact: ["Applies to housing termination", "Applies to benefits termination", "Protects against arbitrary treatment decisions"]
        },
        {
          name: "Fourteenth Amendment – Equal Protection",
          citation: "U.S. Const. amend. XIV",
          description: "Requires equal treatment under the law.",
          veteranImpact: ["Protects disabled veterans from discriminatory treatment", "Ensures fair application of housing rules", "Prohibits selective enforcement"]
        }
      ]
    },
    {
      category: "Federal Civil Rights Law",
      description: "Core civil rights protections for veterans",
      laws: [
        {
          name: "Civil Rights Act – Public Accommodations",
          citation: "42 U.S.C. § 2000a",
          description: "Prohibits discrimination in public services.",
          veteranImpact: ["Applies to housing programs and social services", "Protects against discrimination in federally-funded programs"]
        },
        {
          name: "Civil Rights Under Color of Law",
          citation: "42 U.S.C. § 1983",
          description: "Allows lawsuits for civil rights violations by government actors.",
          veteranImpact: ["Key statute used in federal civil rights litigation", "Applies to VA employees and contractors", "Allows damages for constitutional violations"]
        },
        {
          name: "Conspiracy Against Civil Rights",
          citation: "42 U.S.C. § 1985",
          description: "Protects against coordinated civil rights violations.",
          veteranImpact: ["Applies when multiple officials coordinate retaliation", "Protects against conspiracy to deprive rights"]
        }
      ]
    },
    {
      category: "Fair Housing Act",
      description: "Housing discrimination protections",
      laws: [
        {
          name: "Fair Housing Act – Discrimination",
          citation: "42 U.S.C. § 3604",
          description: "Prohibits housing discrimination.",
          veteranImpact: ["Protects disabled veterans in housing", "Prohibits discrimination based on disability", "Applies to HUD-VASH housing"]
        },
        {
          name: "FHA Reasonable Accommodation",
          citation: "42 U.S.C. § 3604(f)(3)(B)",
          description: "Requires housing accommodations for disabilities.",
          veteranImpact: ["Critical for HUD-VASH tenants", "Requires landlords to make modifications", "Protects service animals and ESAs"]
        },
        {
          name: "FHA Retaliation Protection",
          citation: "42 U.S.C. § 3617",
          description: "Prohibits retaliation for filing housing complaints.",
          veteranImpact: ["Protects veterans who file housing complaints", "Prohibits eviction in retaliation", "Criminalizes threats or intimidation"]
        }
      ]
    },
    {
      category: "HUD Housing Law",
      description: "HUD-VASH and Section 8 housing regulations",
      laws: [
        {
          name: "HUD-VASH Program Authority",
          citation: "42 U.S.C. § 1437f(o)(19)",
          description: "Authorizes HUD-VASH vouchers for veterans.",
          veteranImpact: ["Legal basis for HUD-VASH program", "Defines eligibility requirements", "Establishes VA partnership"]
        },
        {
          name: "Housing Choice Voucher Program",
          citation: "24 CFR Part 982",
          description: "Rules governing Section 8 vouchers.",
          veteranImpact: ["Controls housing assistance rules", "Defines tenant and landlord obligations", "Sets payment standards"]
        },
        {
          name: "Voucher Termination Hearing Rights",
          citation: "24 CFR § 982.555",
          description: "Requires hearings before termination.",
          veteranImpact: ["Protects veterans from losing vouchers without due process", "Right to informal hearing", "Right to present evidence and witnesses"]
        }
      ]
    },
    {
      category: "VA Homeless Programs",
      description: "VA programs supporting homeless and at-risk veterans",
      laws: [
        {
          name: "VA Homeless Assistance Authority",
          citation: "38 U.S.C. § 2002",
          description: "Authorizes programs assisting homeless veterans.",
          veteranImpact: ["Supports outreach programs", "Provides grant authority", "Coordinates federal response"]
        },
        {
          name: "VA Case Management Services",
          citation: "38 U.S.C. § 2003",
          description: "Requires case management for HUD-VASH.",
          veteranImpact: ["Mandates supportive services", "Defines case manager responsibilities", "Ensures ongoing support"]
        },
        {
          name: "SSVF Program Authority",
          citation: "38 U.S.C. § 2044",
          description: "Authorizes Supportive Services for Veteran Families.",
          veteranImpact: ["Funding for prevention and rapid rehousing", "Financial assistance for rent/utilities", "Temporary housing assistance"]
        }
      ]
    },
    {
      category: "VA Patient Rights",
      description: "Rights of veterans receiving VA healthcare",
      laws: [
        {
          name: "VA Patient Rights Regulation",
          citation: "38 CFR § 17.33",
          description: "Defines rights of VA patients.",
          veteranImpact: ["Includes right to respectful care", "Right to privacy and confidentiality", "Right to participate in treatment decisions"]
        },
        {
          name: "Mental Health Care Access",
          citation: "38 U.S.C. § 1720",
          description: "Requires mental health services.",
          veteranImpact: ["Guarantees mental health treatment", "Provides PTSD care", "Ensures substance abuse treatment"]
        }
      ]
    },
    {
      category: "Oversight and Accountability",
      description: "Federal oversight mechanisms protecting veterans",
      laws: [
        {
          name: "VA Inspector General Authority",
          citation: "38 U.S.C. § 312",
          description: "Creates VA Office of Inspector General.",
          veteranImpact: ["Investigates misconduct and program failures", "Accepts veteran complaints", "Conducts independent audits"]
        },
        {
          name: "Inspector General Act",
          citation: "5 U.S.C. App. 3",
          description: "Establishes federal OIG oversight structure.",
          veteranImpact: ["Allows federal oversight investigations", "Protects whistleblowers", "Requires reports to Congress"]
        },
        {
          name: "False Claims Act",
          citation: "31 U.S.C. § 3729",
          description: "Allows whistleblower lawsuits for fraud.",
          veteranImpact: ["Protects whistleblowers exposing fraud", "Allows recovery of fraudulent payments", "Provides financial incentives for reporting"]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine' }
        ]} />
      
        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-slate-900 mb-4 mt-8">
          VETDirect Core Engine™
        </h1>
        <p className="text-lg font-body leading-relaxed text-slate-600 mb-12 max-w-3xl">
          Veteran Stability & Accountability Architecture. The central legal, policy, oversight, and system intelligence hub for the VETDirect platform.
        </p>
      </div>

      {/* Navigation Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 mb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <button onClick={() => navigate('/vetengine/intro')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Introduction
          </button>
          <button onClick={() => navigate('/vetengine/challenges')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Challenges
          </button>
          <button onClick={() => navigate('/vetengine/deliver-justice')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Deliver Justice
          </button>
          <button onClick={() => navigate('/vetengine/legal-engine')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            VETLegalEngine
          </button>
          <button onClick={() => navigate('/vetengine/domains')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Domain Categories
          </button>
          <button onClick={() => navigate('/vetengine/domain-tags')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Master Domain Tags
          </button>
          <button onClick={() => navigate('/vetengine/entity-domains')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Entity Domains
          </button>
          <button onClick={() => navigate('/vetengine/professions')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Professions
          </button>
          <button onClick={() => navigate('/vetengine/ethics')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Ethics
          </button>
          <button onClick={() => navigate('/vetengine/boards')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Board Certifications
          </button>
          <button onClick={() => navigate('/vetengine/housing-authorities')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Housing Authorities
          </button>
          <button onClick={() => navigate('/vetengine/oversight')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Oversight
          </button>
          <button onClick={() => navigate('/vetengine/medical')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Medical
          </button>
          <button onClick={() => navigate('/vetengine/emergency')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Emergency
          </button>
          <button onClick={() => navigate('/vetengine/law-references')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Law References
          </button>
          <button onClick={() => navigate('/vetengine/examples')} className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-secondary text-white hover:bg-[#6B8058] shadow-sm transition-all h-16 px-4 text-sm">
            Real-Life Examples
          </button>
        </div>
      </div>

      {/* Core Engine Description */}
      <div className="max-w-7xl mx-auto mb-14">
        <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>VETDirect Core Engine™</h2>
        <p className="text-lg leading-relaxed max-w-4xl" style={{ color: '#333' }}>
          The VETDirect Core Engine is a structured legal intelligence platform designed to
          organize federal law, housing law, medical ethics, professional governance, and
          veteran oversight structures into a single unified framework. This system empowers
          veterans by providing clear, actionable knowledge about their rights and the systems
          designed to protect them.
        </p>
      </div>

      {/* Law Reference Engine - Accordion */}
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#1A4F63' }}>Law Reference Engine</h2>
        
        {VET_LEGAL_ENGINE.map((section, index) => {
          const isOpen = openSection === index;
          return (
            <div key={index} className="mb-6 rounded-lg overflow-hidden shadow-lg">
              <button
                className="w-full text-left p-6 font-bold text-xl text-white transition-all duration-200 hover:opacity-95 flex justify-between items-center"
                style={{ backgroundColor: '#1A4F63' }}
                onClick={() => setOpenSection(isOpen ? null : index)}
              >
                <div>
                  <div>{section.category}</div>
                  <div className="text-sm font-normal opacity-90 mt-1">{section.description}</div>
                </div>
                <ChevronDown 
                  className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  size={24}
                />
              </button>
              
              {isOpen && (
                <div className="p-8" style={{ backgroundColor: '#c8dde4' }}>
                  {section.laws.map((law, lawIndex) => (
                    <div 
                      key={lawIndex} 
                      className="mb-6 pb-6 border-l-4 pl-6 last:mb-0 last:pb-0"
                      style={{ borderColor: '#1A4F63' }}
                    >
                      <div className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>{law.name}</div>
                      <div className="text-sm mb-3 font-semibold" style={{ color: '#4A5D4A' }}>{law.citation}</div>
                      <p className="mb-3" style={{ color: '#333' }}>{law.description}</p>
                      <div className="mt-4">
                        <div className="font-semibold mb-2" style={{ color: '#1A4F63' }}>Veteran Impact:</div>
                        <ul className="space-y-1">
                          {law.veteranImpact.map((impact, impactIndex) => (
                            <li key={impactIndex} className="flex items-start">
                              <span className="mr-2" style={{ color: '#7A8F65' }}>•</span>
                              <span style={{ color: '#333' }}>{impact}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Section */}
      <div className="max-w-7xl mx-auto mt-16 pt-8 border-t" style={{ borderColor: '#ccc' }}>
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>System Capabilities</h3>
          <div className="grid md:grid-cols-4 gap-6 mt-8">
            <div className="p-6 rounded-lg" style={{ backgroundColor: '#fff' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>Legal Intelligence</h4>
              <p className="text-sm" style={{ color: '#666' }}>350+ laws mapped to veteran systems and protections</p>
            </div>
            <div className="p-6 rounded-lg" style={{ backgroundColor: '#fff' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>Professional Governance</h4>
              <p className="text-sm" style={{ color: '#666' }}>Licensing, ethics, and accountability structures</p>
            </div>
            <div className="p-6 rounded-lg" style={{ backgroundColor: '#fff' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>Oversight Tracking</h4>
              <p className="text-sm" style={{ color: '#666' }}>OIG, DOJ, HUD, and VA complaint pathways</p>
            </div>
            <Link to="/vetengine/drug-policy">
              <div className="p-6 rounded-lg hover:shadow-xl transition-shadow cursor-pointer" style={{ backgroundColor: '#7A8F65', color: '#fff' }}>
                <h4 className="font-bold text-lg mb-2">HUD-VASH Drug Policy</h4>
                <p className="text-sm">Comprehensive framework for substance use policy compliance →</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE INTRO PAGE ============
const VETEngineIntroPage = () => {
  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Introduction to VETEngine™</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Understanding the Platform's Purpose and Vision</p>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>What is VETEngine?</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            VETEngine is a comprehensive legal intelligence platform designed specifically for veterans navigating
            complex housing, healthcare, and benefits systems. Built from real veteran experiences, this system
            transforms scattered legal information into an organized, actionable framework that empowers veterans
            to understand and defend their rights.
          </p>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            Veterans face unique challenges when interacting with multiple government systems—HUD-VASH housing,
            VA healthcare, case management services, and oversight agencies. Each system operates under different
            laws, regulations, and professional standards. VETEngine connects these fragmented pieces into a
            unified intelligence map.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>The Problem VETEngine Solves</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="mr-4 mt-1">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Legal Complexity</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Veterans' rights are protected by dozens of federal laws, constitutional amendments, HUD regulations,
                  VA directives, and state statutes. Understanding which laws apply to a specific situation requires
                  legal expertise most veterans don't have.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="mr-4 mt-1">
                <Network className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Fragmented Systems</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Housing authorities, VA medical centers, case management agencies, and oversight bodies operate
                  independently. When problems arise, veterans must navigate multiple complaint systems, each with
                  different procedures and requirements.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="mr-4 mt-1">
                <Shield className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Power Imbalance</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Veterans often face well-resourced institutions—property managers, housing authorities, government
                  agencies—without clear knowledge of their legal protections. This information asymmetry leaves
                  veterans vulnerable to violations that go unreported and uncorrected.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="mr-4 mt-1">
                <Eye className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Lack of Accountability</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  When misconduct occurs, there's no centralized system to track patterns, identify systemic failures,
                  or connect similar cases across different locations. Individual incidents remain isolated, preventing
                  broader accountability and reform.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>How VETEngine Works</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Legal Intelligence Database</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                350+ laws organized by category, with veteran-specific impact explanations. Constitutional protections,
                civil rights statutes, housing regulations, VA directives, and oversight mechanisms—all in one place.
              </p>
            </div>

            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Professional Governance Mapping</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                Identifies which professionals are involved (case managers, property managers, social workers),
                their licensing requirements, ethical obligations, and oversight authorities.
              </p>
            </div>

            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Issue Detection System</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                Analyzes veteran statements and documents to automatically identify potential legal violations,
                applicable protections, and relevant complaint pathways.
              </p>
            </div>

            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Accountability Framework</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                Maps oversight authorities (VA OIG, HUD OIG, DOJ Civil Rights Division) and tracks incident patterns
                to identify systemic failures requiring federal intervention.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Who Should Use VETEngine?</h2>
          <ul className="space-y-4 text-lg" style={{ color: '#333' }}>
            <li className="flex items-start">
              <CheckCircle className="mr-3 mt-1" style={{ color: '#7A8F65' }} size={24} />
              <span><strong>Veterans in HUD-VASH Housing:</strong> Understand your housing rights, reasonable accommodation protections, and eviction defense options.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-3 mt-1" style={{ color: '#7A8F65' }} size={24} />
              <span><strong>Veterans Facing Retaliation:</strong> Learn about federal protections when you file complaints and how to document violations.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-3 mt-1" style={{ color: '#7A8F65' }} size={24} />
              <span><strong>Veterans with Disability Accommodations:</strong> Understand ADA, Section 504, and Fair Housing Act requirements that protect your rights.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-3 mt-1" style={{ color: '#7A8F65' }} size={24} />
              <span><strong>Veterans Experiencing Case Management Issues:</strong> Identify professional standards and oversight mechanisms when services fail.</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-3 mt-1" style={{ color: '#7A8F65' }} size={24} />
              <span><strong>Veteran Advocates and Legal Aid:</strong> Access organized legal research and complaint pathway information to support veteran cases.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE CHALLENGES PAGE ============
const VETEngineChallengesPage = () => {
  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>System Challenges Veterans Face</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Understanding the Barriers to Justice and Accountability</p>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>The Reality Veterans Navigate</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            Veterans transitioning from homelessness into HUD-VASH housing enter a complex ecosystem of overlapping
            systems, regulations, and professional responsibilities. While designed to provide support and stability,
            these systems often create barriers that make it difficult for veterans to access their rights and hold
            institutions accountable when things go wrong.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Challenge 1: Information Asymmetry</h2>
          <div className="mb-6">
            <h3 className="font-bold text-2xl mb-4" style={{ color: '#4A5D4A' }}>The Knowledge Gap</h3>
            <p className="text-lg leading-relaxed mb-4" style={{ color: '#333' }}>
              Property managers, housing authorities, and VA employees operate with institutional knowledge, legal
              counsel, and established procedures. Veterans, often dealing with trauma, mental health challenges,
              and housing instability, must navigate these systems without clear information about their protections.
            </p>
            <div className="p-6 rounded-lg mt-4" style={{ backgroundColor: '#f0f4f8' }}>
              <p className="text-lg italic" style={{ color: '#333' }}>
                "When my case manager told me I could be evicted for filing a complaint, I didn't know that was
                illegal retaliation under 42 U.S.C. § 3617. I had no idea my First Amendment rights protected me.
                I stayed silent for months."
              </p>
              <p className="text-sm mt-2" style={{ color: '#666' }}>— Veteran, Los Angeles HUD-VASH Program</p>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-2xl mb-4" style={{ color: '#4A5D4A' }}>Scattered Legal Framework</h3>
            <p className="text-lg leading-relaxed" style={{ color: '#333' }}>
              Veterans' rights are protected by constitutional amendments, federal civil rights statutes, HUD
              regulations, VA directives, state tenant laws, and professional ethics codes. There's no single
              resource that organizes this information into an accessible format for those who need it most.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Challenge 2: Fragmented Oversight</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#fff5f5', borderColor: '#e53e3e' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Multiple Agencies, No Coordination</h3>
              <ul className="space-y-2 text-lg" style={{ color: '#333' }}>
                <li>• VA Office of Inspector General</li>
                <li>• HUD Office of Inspector General</li>
                <li>• HUD Fair Housing Office</li>
                <li>• DOJ Civil Rights Division</li>
                <li>• Local Housing Authorities</li>
                <li>• State Licensing Boards</li>
              </ul>
            </div>

            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#fff5f5', borderColor: '#e53e3e' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>The Result</h3>
              <p className="text-lg mb-4" style={{ color: '#333' }}>
                Each agency investigates independently. There's no system to connect patterns across jurisdictions
                or identify systemic failures affecting veterans nationwide.
              </p>
              <p className="text-lg" style={{ color: '#333' }}>
                A case manager's misconduct in Los Angeles isn't linked to similar behavior in San Diego, even
                though both involve the same federal program and legal violations.
              </p>
            </div>
          </div>

          <div className="p-6 rounded-lg" style={{ backgroundColor: '#f0f4f8' }}>
            <p className="text-lg italic" style={{ color: '#333' }}>
              "I filed complaints with VA OIG, HUD Fair Housing, and the local housing authority. Each said
              'not our jurisdiction' or 'we can't find evidence.' Nobody connected the dots. Nobody saw the
              pattern affecting multiple veterans in the same program."
            </p>
            <p className="text-sm mt-2" style={{ color: '#666' }}>— Veteran, West LA VA Medical Center</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Challenge 3: Professional Accountability Gaps</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            Case managers, social workers, property managers, and housing authority employees operate under
            professional standards and ethical codes. However, veterans often don't know:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Who regulates them?</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                Which licensing board, professional association, or oversight body governs their conduct?
              </p>
            </div>

            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>What standards apply?</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                What ethical obligations, professional competencies, and legal duties are they required to uphold?
              </p>
            </div>

            <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Where to report violations?</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                When professionals fail to meet standards, where should veterans file complaints to trigger accountability?
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Challenge 4: Retaliation and Fear</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            Even when veterans understand their rights, many fear exercising them. Retaliation—though illegal
            under multiple federal statutes—remains a powerful deterrent.
          </p>
          <div className="space-y-4">
            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#fff5f5', borderColor: '#e53e3e' }}>
              <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Housing Threats</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                "If you file a complaint, your voucher will be terminated." Veterans dependent on housing assistance
                often stay silent rather than risk homelessness.
              </p>
            </div>

            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#fff5f5', borderColor: '#e53e3e' }}>
              <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Service Withdrawal</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                Case managers who face complaints may reduce services, delay assistance, or create hostile
                relationships with the veterans they're supposed to support.
              </p>
            </div>

            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#fff5f5', borderColor: '#e53e3e' }}>
              <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Documentation Manipulation</h3>
              <p className="text-lg" style={{ color: '#333' }}>
                Veterans report that after filing complaints, case notes suddenly document "noncompliance,"
                "aggressive behavior," or "program violations" that didn't exist before.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Why VETEngine Matters</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            These challenges aren't isolated incidents—they're systemic features of fragmented oversight,
            inadequate transparency, and power imbalances that leave veterans vulnerable.
          </p>
          <div className="p-8 rounded-lg" style={{ backgroundColor: '#7A8F65', color: 'white' }}>
            <h3 className="font-bold text-2xl mb-4">VETEngine's Response</h3>
            <p className="text-lg leading-relaxed mb-4">
              By organizing legal protections, professional standards, oversight mechanisms, and complaint pathways
              into a unified intelligence platform, VETEngine shifts power back to veterans.
            </p>
            <p className="text-lg leading-relaxed">
              Knowledge becomes defense. Documentation becomes evidence. Isolated incidents become pattern recognition.
              And veterans—who once faced institutions alone—now have the tools to understand, document, and defend
              their rights effectively.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE DELIVER JUSTICE PAGE ============
const VETEngineDeliverJusticePage = () => {
  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Deliver Justice</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Strategic Framework for Veteran Rights and Accountability</p>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>The Mission</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ color: '#333' }}>
            VETEngine exists to transform veteran experiences from isolated struggles into strategic movements for
            systemic reform. By connecting legal protections, professional accountability standards, and oversight
            mechanisms, we create pathways for both individual justice and institutional change.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Three Levels of Justice</h2>
          
          <div className="space-y-6">
            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#c8dde4', borderColor: '#1A4F63' }}>
              <h3 className="font-bold text-2xl mb-3" style={{ color: '#1A4F63' }}>Level 1: Individual Protection</h3>
              <p className="text-lg mb-4" style={{ color: '#333' }}>
                Immediate interventions to stop violations, secure housing stability, and defend veteran rights.
              </p>
              <ul className="space-y-2 text-lg" style={{ color: '#333' }}>
                <li>• Document violations with evidence collection systems</li>
                <li>• File complaints through appropriate oversight channels</li>
                <li>• Request reasonable accommodations under ADA and Fair Housing Act</li>
                <li>• Invoke retaliation protections when facing threats</li>
                <li>• Access emergency intervention resources</li>
              </ul>
            </div>

            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#c8dde4', borderColor: '#7A8F65' }}>
              <h3 className="font-bold text-2xl mb-3" style={{ color: '#1A4F63' }}>Level 2: Professional Accountability</h3>
              <p className="text-lg mb-4" style={{ color: '#333' }}>
                Hold case managers, property managers, and service providers to ethical and legal standards.
              </p>
              <ul className="space-y-2 text-lg" style={{ color: '#333' }}>
                <li>• Report professional misconduct to licensing boards</li>
                <li>• File ethics complaints with professional associations</li>
                <li>• Demand case manager replacement when standards are violated</li>
                <li>• Trigger VA investigations into provider contract violations</li>
                <li>• Document patterns across multiple veterans</li>
              </ul>
            </div>

            <div className="p-6 rounded-lg border-l-4" style={{ backgroundColor: '#c8dde4', borderColor: '#8B4513' }}>
              <h3 className="font-bold text-2xl mb-3" style={{ color: '#1A4F63' }}>Level 3: Systemic Reform</h3>
              <p className="text-lg mb-4" style={{ color: '#333' }}>
                Build evidence of institutional failures that demand federal intervention and policy change.
              </p>
              <ul className="space-y-2 text-lg" style={{ color: '#333' }}>
                <li>• Connect similar cases across jurisdictions to reveal patterns</li>
                <li>• Present coordinated evidence to VA OIG and HUD OIG</li>
                <li>• Engage DOJ Civil Rights Division for systemic violations</li>
                <li>• Support congressional oversight with documented evidence</li>
                <li>• Advocate for policy reforms based on proven failures</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>VETEngine Tools for Justice</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/vetengine/evidence-scanner">
              <div className="p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer" style={{ backgroundColor: '#f0f4f8' }}>
                <FileText className="mb-4" style={{ color: '#1A4F63' }} size={32} />
                <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Evidence Scanner</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Analyzes documents, emails, and statements to identify legal violations and applicable protections automatically.
                </p>
              </div>
            </Link>

            <Link to="/vetengine/case-advisor">
              <div className="p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer" style={{ backgroundColor: '#f0f4f8' }}>
                <Scale className="mb-4" style={{ color: '#1A4F63' }} size={32} />
                <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Case Advisor</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Provides strategic recommendations on which laws apply, which agencies have jurisdiction, and optimal complaint pathways.
                </p>
              </div>
            </Link>

            <Link to="/vetengine/timeline-builder">
              <div className="p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer" style={{ backgroundColor: '#f0f4f8' }}>
                <Calendar className="mb-4" style={{ color: '#1A4F63' }} size={32} />
                <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Timeline Builder</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Organizes incidents chronologically with supporting evidence, making patterns visible and cases compelling.
                </p>
              </div>
            </Link>

            <Link to="/vetengine/complaint-generator">
              <div className="p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer" style={{ backgroundColor: '#f0f4f8' }}>
                <Send className="mb-4" style={{ color: '#1A4F63' }} size={32} />
                <h3 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Complaint Generator</h3>
                <p className="text-lg" style={{ color: '#333' }}>
                  Creates formatted complaints for VA OIG, HUD Fair Housing, state licensing boards, and other oversight bodies.
                </p>
              </div>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Why This Approach Works</h2>
          <div className="space-y-4 text-lg" style={{ color: '#333' }}>
            <p className="leading-relaxed">
              <strong>Documentation defeats denial.</strong> When violations are recorded with dates, witnesses,
              and evidence, they become undeniable.
            </p>
            <p className="leading-relaxed">
              <strong>Legal knowledge shifts power.</strong> Veterans who understand their constitutional and
              statutory protections can't be intimidated by false threats.
            </p>
            <p className="leading-relaxed">
              <strong>Pattern recognition triggers accountability.</strong> Isolated incidents may be dismissed,
              but documented patterns across multiple veterans force institutional responses.
            </p>
            <p className="leading-relaxed">
              <strong>Strategic complaints reach the right authorities.</strong> Filing with proper oversight
              agencies—not just local programs—ensures independent investigation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE DOMAIN CATEGORIES PAGE ============
const VETEngineDomainCategoriesPage = () => {
  const domains = [
    { name: "Constitutional Law", count: 8, color: "#1A4F63" },
    { name: "Federal Civil Rights", count: 12, color: "#7A8F65" },
    { name: "Housing Law", count: 24, color: "#8B4513" },
    { name: "VA Healthcare", count: 18, color: "#4A5D4A" },
    { name: "Veterans Benefits", count: 15, color: "#1A4F63" },
    { name: "Professional Ethics", count: 22, color: "#7A8F65" },
    { name: "Licensing Standards", count: 16, color: "#8B4513" },
    { name: "Oversight Mechanisms", count: 14, color: "#4A5D4A" },
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Domain Categories</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Organizational Structure of Legal Intelligence</p>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {domains.map((domain, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
              <h3 className="font-bold text-2xl mb-4" style={{ color: domain.color }}>{domain.name}</h3>
              <p className="text-4xl font-bold mb-2" style={{ color: '#333' }}>{domain.count}</p>
              <p className="text-lg" style={{ color: '#666' }}>Laws & Regulations</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE PROFESSIONS PAGE ============
const VETEngineProfessionsPage = () => {
  const professions = [
    { title: "Case Managers (Social Work)", license: "LCSW, MSW", board: "State BBS", ethics: "NASW Code of Ethics" },
    { title: "Property Managers", license: "State Real Estate License", board: "State Real Estate Board", ethics: "Fair Housing Act Compliance" },
    { title: "Housing Authority Staff", license: "HUD Certification", board: "HUD Regional Office", ethics: "Federal Housing Regulations" },
    { title: "VA Healthcare Providers", license: "Medical/Clinical License", board: "State Medical Board", ethics: "VA Standards of Practice" },
    { title: "Mental Health Clinicians", license: "LMFT, LPCC, PsyD, PhD", board: "State BBS", ethics: "APA/NASW Ethics" },
    { title: "Nonprofit Service Providers", license: "501(c)(3) Status", board: "IRS/State AG", ethics: "Grant Compliance Standards" },
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Professional Governance</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Understanding Who Serves Veterans and How They're Regulated</p>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {professions.map((prof, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>{prof.title}</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <p className="font-semibold text-lg mb-2" style={{ color: '#4A5D4A' }}>Licensing</p>
                  <p className="text-lg" style={{ color: '#333' }}>{prof.license}</p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2" style={{ color: '#4A5D4A' }}>Oversight Board</p>
                  <p className="text-lg" style={{ color: '#333' }}>{prof.board}</p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2" style={{ color: '#4A5D4A' }}>Ethical Standards</p>
                  <p className="text-lg" style={{ color: '#333' }}>{prof.ethics}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE ETHICS PAGE ============
const VETEngineEthicsPage = () => {
  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Professional Ethics Standards</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Core Obligations Professionals Must Uphold</p>
      </div>

      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-10">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>NASW Code of Ethics (Social Workers)</h2>
          <ul className="space-y-3 text-lg" style={{ color: '#333' }}>
            <li>• <strong>Competence:</strong> Practice within areas of professional expertise</li>
            <li>• <strong>Dignity and Worth:</strong> Treat clients with respect and dignity</li>
            <li>• <strong>Self-Determination:</strong> Respect client autonomy and decision-making</li>
            <li>• <strong>Conflicts of Interest:</strong> Avoid relationships that exploit clients</li>
            <li>• <strong>Informed Consent:</strong> Provide clear information about services and rights</li>
          </ul>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Fair Housing Compliance (Property Managers)</h2>
          <ul className="space-y-3 text-lg" style={{ color: '#333' }}>
            <li>• <strong>Non-Discrimination:</strong> Equal treatment regardless of protected class status</li>
            <li>• <strong>Reasonable Accommodations:</strong> Modify rules/policies for disability needs</li>
            <li>• <strong>No Retaliation:</strong> Cannot punish tenants for exercising legal rights</li>
            <li>• <strong>Accessibility:</strong> Ensure housing meets ADA standards where applicable</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE OVERSIGHT PAGE ============
const VETEngineOversightPage = () => {
  const agencies = [
    { name: "VA Office of Inspector General", role: "Investigates VA misconduct and program failures", website: "va.gov/oig", hotline: "1-800-488-8244" },
    { name: "HUD Office of Inspector General", role: "Investigates housing program fraud and abuse", website: "hudoig.gov", hotline: "1-800-347-3735" },
    { name: "HUD Fair Housing Office", role: "Enforces Fair Housing Act protections", website: "hud.gov/fairhousing", hotline: "1-800-669-9777" },
    { name: "DOJ Civil Rights Division", role: "Prosecutes systemic civil rights violations", website: "justice.gov/crt", email: "fairhousing@usdoj.gov" },
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Oversight & Accountability</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Federal Agencies Protecting Veteran Rights</p>
      </div>

      <div className="max-w-7xl mx-auto space-y-6">
        {agencies.map((agency, idx) => (
          <div key={idx} className="bg-white rounded-lg shadow-lg p-10">
            <h3 className="font-bold text-3xl mb-4" style={{ color: '#1A4F63' }}>{agency.name}</h3>
            <p className="text-xl mb-6" style={{ color: '#333' }}>{agency.role}</p>
            <div className="grid md:grid-cols-2 gap-4 text-lg">
              <div>
                <span className="font-semibold" style={{ color: '#4A5D4A' }}>Website: </span>
                <span style={{ color: '#1A4F63' }}>{agency.website}</span>
              </div>
              {agency.hotline && (
                <div>
                  <span className="font-semibold" style={{ color: '#4A5D4A' }}>Hotline: </span>
                  <span style={{ color: '#1A4F63' }}>{agency.hotline}</span>
                </div>
              )}
              {agency.email && (
                <div>
                  <span className="font-semibold" style={{ color: '#4A5D4A' }}>Email: </span>
                  <span style={{ color: '#1A4F63' }}>{agency.email}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ EVIDENCE SCANNER PAGE ============
const EvidenceScannerPage = () => {
  const [documentText, setDocumentText] = useState("");
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);

  const handleScan = async () => {
    if (!documentText.trim()) {
      toast.error("Please enter document text to scan");
      return;
    }
    
    setScanning(true);
    setResults({
      violations: [
        { law: "Fair Housing Act Retaliation (42 USC § 3617)", severity: "High", evidence: "Document contains threat of eviction following complaint filing" },
        { law: "First Amendment Protection", severity: "High", evidence: "Statement attempting to suppress complaint activity" },
        { law: "Due Process Violation (5th Amendment)", severity: "Medium", evidence: "No notice period provided before action" }
      ],
      protections: [
        "Retaliation protections under 42 USC § 3617",
        "First Amendment free speech protections",
        "Fair Housing reasonable accommodation rights"
      ],
      recommendations: [
        "File complaint with HUD Fair Housing Office immediately",
        "Document all subsequent interactions with timestamp and witnesses",
        "Request written confirmation of all communications",
        "Consider filing VA OIG complaint for case manager involvement"
      ]
    });
    setScanning(false);
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Evidence Scanner</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>AI-Powered Legal Violation Detection</p>
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
        <div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Upload or Paste Evidence</h3>
            <textarea
              className="w-full h-96 p-4 border-2 rounded-lg text-lg"
              style={{ borderColor: '#C4B5A0' }}
              placeholder="Paste emails, case notes, text messages, or other documents here..."
              value={documentText}
              onChange={(e) => setDocumentText(e.target.value)}
            />
            <button
              onClick={handleScan}
              disabled={scanning}
              className="mt-4 w-full py-4 rounded-lg font-bold text-white text-lg transition-all"
              style={{ backgroundColor: scanning ? '#999' : '#1A4F63' }}
            >
              {scanning ? "Scanning Evidence..." : "Scan for Violations"}
            </button>
          </div>
        </div>

        <div>
          {results && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#e53e3e' }}>Potential Violations Detected</h3>
                {results.violations.map((v, idx) => (
                  <div key={idx} className="mb-4 pb-4 border-b last:border-b-0">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-bold text-lg" style={{ color: '#1A4F63' }}>{v.law}</p>
                      <span className={`px-3 py-1 rounded text-sm font-bold ${v.severity === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {v.severity}
                      </span>
                    </div>
                    <p className="text-lg" style={{ color: '#333' }}>{v.evidence}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#7A8F65' }}>Your Protections</h3>
                <ul className="space-y-2">
                  {results.protections.map((p, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="mr-2 mt-1" style={{ color: '#7A8F65' }} size={20} />
                      <span className="text-lg" style={{ color: '#333' }}>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Recommended Actions</h3>
                <ol className="space-y-3">
                  {results.recommendations.map((r, idx) => (
                    <li key={idx} className="text-lg" style={{ color: '#333' }}>
                      <strong>{idx + 1}.</strong> {r}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {!results && (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <FileSearch size={64} className="mx-auto mb-4" style={{ color: '#C4B5A0' }} />
              <p className="text-xl" style={{ color: '#666' }}>
                Paste your evidence and click "Scan for Violations" to analyze
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ CASE ADVISOR PAGE ============
const CaseAdvisorPage = () => {
  const [situation, setSituation] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [advice, setAdvice] = useState(null);

  const handleAnalyze = async () => {
    if (!situation.trim()) {
      toast.error("Please describe your situation");
      return;
    }
    
    setAnalyzing(true);
    setAdvice({
      summary: "Based on your description, this appears to involve potential Fair Housing Act violations, retaliation, and case management ethics issues.",
      applicableLaws: [
        "42 USC § 3617 - Fair Housing Retaliation Protection",
        "42 USC § 1983 - Civil Rights Violation",
        "First Amendment - Free Speech Protection",
        "NASW Code of Ethics - Professional Standards"
      ],
      jurisdiction: [
        { agency: "HUD Fair Housing Office", priority: "High", reason: "Direct housing discrimination authority" },
        { agency: "VA Office of Inspector General", priority: "High", reason: "Case manager is VA employee/contractor" },
        { agency: "State Social Work Licensing Board", priority: "Medium", reason: "Professional ethics violation" }
      ],
      strategy: [
        "File HUD Fair Housing complaint within 1 year of incident",
        "Simultaneously file VA OIG complaint online",
        "Document all future interactions with timestamp/witnesses",
        "Request new case manager through VA supervisor",
        "Preserve all evidence (emails, texts, case notes)",
        "Consider requesting reasonable accommodation in writing"
      ]
    });
    setAnalyzing(false);
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Case Advisor</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Strategic Legal Guidance for Your Situation</p>
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
        <div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Describe Your Situation</h3>
            <textarea
              className="w-full h-96 p-4 border-2 rounded-lg text-lg"
              style={{ borderColor: '#C4B5A0' }}
              placeholder="Describe what happened, who was involved, dates, and any documentation you have..."
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
            />
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              className="mt-4 w-full py-4 rounded-lg font-bold text-white text-lg transition-all"
              style={{ backgroundColor: analyzing ? '#999' : '#1A4F63' }}
            >
              {analyzing ? "Analyzing Case..." : "Get Strategic Advice"}
            </button>
          </div>
        </div>

        <div>
          {advice && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Case Analysis</h3>
                <p className="text-lg leading-relaxed" style={{ color: '#333' }}>{advice.summary}</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Applicable Laws</h3>
                <ul className="space-y-2">
                  {advice.applicableLaws.map((law, idx) => (
                    <li key={idx} className="flex items-start">
                      <Scale className="mr-2 mt-1" style={{ color: '#7A8F65' }} size={20} />
                      <span className="text-lg" style={{ color: '#333' }}>{law}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Complaint Jurisdiction</h3>
                {advice.jurisdiction.map((j, idx) => (
                  <div key={idx} className="mb-4 pb-4 border-b last:border-b-0">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold text-lg" style={{ color: '#1A4F63' }}>{j.agency}</p>
                      <span className={`px-3 py-1 rounded text-sm font-bold ${j.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {j.priority} Priority
                      </span>
                    </div>
                    <p className="text-lg" style={{ color: '#333' }}>{j.reason}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Recommended Strategy</h3>
                <ol className="space-y-3">
                  {advice.strategy.map((step, idx) => (
                    <li key={idx} className="text-lg" style={{ color: '#333' }}>
                      <strong>Step {idx + 1}:</strong> {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {!advice && (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <Lightbulb size={64} className="mx-auto mb-4" style={{ color: '#C4B5A0' }} />
              <p className="text-xl" style={{ color: '#666' }}>
                Describe your situation to receive strategic legal guidance
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ TIMELINE BUILDER PAGE ============
const TimelineBuilderPage = () => {
  const [events, setEvents] = useState([
    { date: "2025-01-15", title: "Initial Complaint Filed", description: "Filed formal complaint about housing conditions with property manager", evidence: "Email confirmation" },
    { date: "2025-01-20", title: "Retaliation Threat", description: "Case manager stated 'filing complaints will affect your voucher'", evidence: "Recorded conversation" },
    { date: "2025-02-01", title: "Service Reduction", description: "Case management visits reduced from weekly to monthly without explanation", evidence: "Calendar records" }
  ]);
  const [newEvent, setNewEvent] = useState({ date: "", title: "", description: "", evidence: "" });

  const addEvent = () => {
    if (!newEvent.date || !newEvent.title) {
      toast.error("Please enter date and title");
      return;
    }
    setEvents([...events, newEvent].sort((a, b) => new Date(a.date) - new Date(b.date)));
    setNewEvent({ date: "", title: "", description: "", evidence: "" });
    toast.success("Event added to timeline");
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Timeline Builder</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Document Your Case Chronologically</p>
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
        <div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Add New Event</h3>
            <div className="space-y-4">
              <input
                type="date"
                className="w-full p-3 border-2 rounded-lg text-lg"
                style={{ borderColor: '#C4B5A0' }}
                value={newEvent.date}
                onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
              />
              <input
                type="text"
                className="w-full p-3 border-2 rounded-lg text-lg"
                style={{ borderColor: '#C4B5A0' }}
                placeholder="Event Title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
              />
              <textarea
                className="w-full h-32 p-3 border-2 rounded-lg text-lg"
                style={{ borderColor: '#C4B5A0' }}
                placeholder="Description of what happened..."
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
              />
              <input
                type="text"
                className="w-full p-3 border-2 rounded-lg text-lg"
                style={{ borderColor: '#C4B5A0' }}
                placeholder="Evidence (e.g., 'Email', 'Photo', 'Witness')"
                value={newEvent.evidence}
                onChange={(e) => setNewEvent({...newEvent, evidence: e.target.value})}
              />
              <button
                onClick={addEvent}
                className="w-full py-4 rounded-lg font-bold text-white text-lg"
                style={{ backgroundColor: '#1A4F63' }}
              >
                Add to Timeline
              </button>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="font-bold text-2xl mb-6" style={{ color: '#1A4F63' }}>Case Timeline</h3>
            <div className="space-y-6">
              {events.map((event, idx) => (
                <div key={idx} className="border-l-4 pl-6 pb-6 last:pb-0" style={{ borderColor: '#1A4F63' }}>
                  <p className="text-sm font-bold mb-1" style={{ color: '#7A8F65' }}>
                    {new Date(event.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <h4 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>{event.title}</h4>
                  <p className="text-lg mb-2" style={{ color: '#333' }}>{event.description}</p>
                  {event.evidence && (
                    <p className="text-sm" style={{ color: '#666' }}>
                      <strong>Evidence:</strong> {event.evidence}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <button className="mt-8 w-full py-4 rounded-lg font-bold text-white text-lg" style={{ backgroundColor: '#7A8F65' }}>
              Export Timeline as PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ COMPLAINT GENERATOR PAGE ============
const ComplaintGeneratorPage = () => {
  const [agency, setAgency] = useState("");
  const [generating, setGenerating] = useState(false);
  const [complaint, setComplaint] = useState("");

  const generateComplaint = () => {
    if (!agency) {
      toast.error("Please select an agency");
      return;
    }
    setGenerating(true);
    setTimeout(() => {
      setComplaint(`
FORMAL COMPLAINT TO ${agency.toUpperCase()}

Date: ${new Date().toLocaleDateString()}

TO: ${agency}
FROM: [Your Name]
RE: Formal Complaint - Fair Housing Violation and Retaliation

I am filing this formal complaint regarding violations of my rights under federal law.

COMPLAINANT INFORMATION:
Name: [Your Full Name]
Address: [Your Address]
Phone: [Your Phone]
Email: [Your Email]
HUD-VASH Program: [Yes/No]

RESPONDENT INFORMATION:
Organization: [Housing Authority/Property Name]
Individual(s): [Case Manager/Property Manager Name]
Address: [Their Address]

SUMMARY OF VIOLATIONS:
This complaint involves [describe violations briefly - retaliation, discrimination, due process, etc.]

TIMELINE OF EVENTS:
[Date 1]: [Event description]
[Date 2]: [Event description]
[Date 3]: [Event description]

APPLICABLE LAWS VIOLATED:
• 42 U.S.C. § 3617 - Fair Housing Retaliation Protection
• First Amendment - Free Speech Protection
• [Additional laws as applicable]

RELIEF REQUESTED:
1. Immediate investigation of reported violations
2. Cessation of retaliatory conduct
3. Appropriate disciplinary action against responsible parties
4. Restoration of services and protections

SUPPORTING EVIDENCE:
• [List attached documents, photos, emails, etc.]

I am available for interview and can provide additional documentation as needed.

Respectfully submitted,
[Your Signature]
[Your Name]
[Date]
      `);
      setGenerating(false);
      toast.success("Complaint generated successfully");
    }, 1500);
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Complaint Generator</h1>
        <p className="text-xl" style={{ color: '#4A5D4A' }}>Create Formatted Complaints for Oversight Agencies</p>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h3 className="font-bold text-2xl mb-4" style={{ color: '#1A4F63' }}>Select Oversight Agency</h3>
          <select
            className="w-full p-4 border-2 rounded-lg text-lg"
            style={{ borderColor: '#C4B5A0' }}
            value={agency}
            onChange={(e) => setAgency(e.target.value)}
          >
            <option value="">-- Select Agency --</option>
            <option value="VA Office of Inspector General">VA Office of Inspector General</option>
            <option value="HUD Fair Housing Office">HUD Fair Housing Office</option>
            <option value="HUD Office of Inspector General">HUD Office of Inspector General</option>
            <option value="DOJ Civil Rights Division">DOJ Civil Rights Division</option>
            <option value="State Social Work Licensing Board">State Social Work Licensing Board</option>
          </select>
          <button
            onClick={generateComplaint}
            disabled={generating || !agency}
            className="mt-4 w-full py-4 rounded-lg font-bold text-white text-lg transition-all"
            style={{ backgroundColor: (generating || !agency) ? '#999' : '#1A4F63' }}
          >
            {generating ? "Generating Complaint..." : "Generate Complaint Template"}
          </button>
        </div>

        {complaint && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-2xl" style={{ color: '#1A4F63' }}>Your Complaint</h3>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(complaint);
                  toast.success("Copied to clipboard");
                }}
                className="px-6 py-3 rounded-lg font-bold text-white"
                style={{ backgroundColor: '#7A8F65' }}
              >
                Copy to Clipboard
              </button>
            </div>
            <pre className="whitespace-pre-wrap text-lg p-6 rounded-lg" style={{ backgroundColor: '#f0f4f8', color: '#333', fontFamily: 'monospace' }}>
              {complaint}
            </pre>
            <p className="mt-6 text-lg" style={{ color: '#666' }}>
              <strong>Next Steps:</strong> Review and customize this template with your specific information, attach supporting evidence, and submit through the agency's official complaint portal.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ VETENGINE DRUG POLICY PAGE ============
const VETDrugPolicyPage = () => {
  const [openSection, setOpenSection] = useState(null);

  const sections = [
    {
      title: "Federal Constitutional Law",
      description: "Foundational constitutional protections affecting housing enforcement.",
      items: [
        { law: "Fourth Amendment", detail: "Search and seizure protections - Veterans have protection against unreasonable searches of their housing. Property managers and law enforcement must follow proper procedures before entering veteran housing units." },
        { law: "Fifth Amendment", detail: "Due process protections - Veterans cannot have housing terminated without proper notice and opportunity to respond. Applies to voucher termination and lease enforcement actions." },
        { law: "Fourteenth Amendment", detail: "Equal protection - Selective enforcement of drug policies against veterans based on protected class status (race, disability) violates constitutional protections." },
        { law: "Supremacy Clause", detail: "Federal authority over housing programs - Federal housing regulations and VA directives supersede conflicting local ordinances in HUD-VASH administration." }
      ]
    },
    {
      title: "Federal Civil Rights Law",
      description: "Civil rights protections for veterans in housing environments.",
      items: [
        { law: "42 USC § 1983", detail: "Civil rights violations - Veterans can sue housing authorities and VA employees for constitutional violations, including improper searches and discriminatory enforcement." },
        { law: "42 USC § 3617", detail: "Retaliation under Fair Housing Act - Prohibits retaliation against veterans who report drug activity, file complaints, or exercise their rights under housing law." },
        { law: "Americans with Disabilities Act (ADA)", detail: "Protects veterans with substance use disorder (recognized as disability) from discrimination. Requires reasonable accommodations for individuals in recovery programs." },
        { law: "Rehabilitation Act Section 504", detail: "Prohibits discrimination in federally funded programs (HUD-VASH) based on disability, including substance use disorder and mental health conditions." }
      ]
    },
    {
      title: "Fair Housing Act",
      description: "Housing discrimination protections including disability status and recovery programs.",
      items: [
        { law: "42 USC § 3604", detail: "Housing discrimination - Prohibits discrimination based on disability. Veterans in substance use recovery programs are protected class. Cannot deny housing solely based on recovery status." },
        { law: "Reasonable Accommodation for Disability", detail: "Housing providers must make reasonable accommodations for veterans with substance use disorder, including modified lease terms, service animal allowances, and treatment participation accommodations." },
        { law: "Protection for Individuals in Recovery", detail: "Fair Housing Act protects individuals currently in or successfully completed recovery programs. Past substance use cannot be basis for housing denial if veteran is compliant with treatment." }
      ]
    },
    {
      title: "HUD Housing Law",
      description: "HUD-VASH and Section 8 housing regulations related to drug activity.",
      items: [
        { law: "24 CFR § 982", detail: "Housing Choice Voucher rules - Establishes baseline rules for Section 8 and HUD-VASH voucher programs, including tenant responsibilities and termination procedures." },
        { law: "24 CFR § 982.553", detail: "Drug-related criminal activity - Specifies that PHAs must terminate assistance if any household member is currently engaged in illegal drug use or has pattern of illegal drug use that threatens health/safety. Requires evidence, not mere suspicion." },
        { law: "Lease Termination Authority", detail: "Housing authorities have authority to terminate leases for drug-related criminal activity, but must provide due process: written notice, opportunity for hearing, and clear evidence standards." },
        { law: "Tenant Protection and Grievance Procedures (24 CFR § 982.555)", detail: "Veterans have right to informal hearing before voucher termination. Can present evidence, call witnesses, and challenge PHA determinations. Critical protection against false accusations." }
      ]
    },
    {
      title: "VA Homeless Programs",
      description: "VA housing programs governing HUD-VASH participants.",
      items: [
        { law: "HUD-VASH Program Handbook", detail: "Official VA guidance on HUD-VASH administration, case management requirements, and housing stability services. Clarifies VA role versus PHA role in program enforcement." },
        { law: "38 USC § 2003", detail: "VA case management authority - Mandates supportive services for homeless veterans, including substance use treatment referrals and housing stabilization support." },
        { law: "Grant and Per Diem Programs", detail: "Transitional housing programs for veterans with substance use issues. Provides step-down services before or during HUD-VASH participation." },
        { law: "HPACT Outreach Services", detail: "Homeless Patient Aligned Care Teams provide integrated healthcare and housing support, including substance use disorder treatment coordination." }
      ]
    },
    {
      title: "VA Patient Rights",
      description: "Rights of veterans receiving healthcare services.",
      items: [
        { law: "38 CFR § 17.33", detail: "Patient rights regulation - Veterans have right to respectful care, privacy, confidentiality, and participation in treatment decisions. Applies to substance use treatment in VA facilities." },
        { law: "VHA Directive 1004", detail: "Patient advocacy - Establishes patient advocate system to resolve complaints about VA healthcare services, including substance use treatment and case management." },
        { law: "Mental Health Confidentiality Protections (42 CFR Part 2)", detail: "Strict confidentiality rules for substance use treatment records. Case managers cannot share substance use treatment information with housing authorities without written veteran consent." },
        { law: "Informed Consent Rules", detail: "Veterans must provide informed consent before substance use treatment. Cannot be coerced into treatment as condition of housing assistance without proper legal authority." }
      ]
    },
    {
      title: "Oversight and Accountability",
      description: "Federal oversight mechanisms protecting veterans.",
      items: [
        { law: "VA Office of Inspector General", detail: "Investigates VA case manager misconduct, program failures, and violations of veteran rights. Accepts complaints about HUD-VASH administration and substance use policy enforcement." },
        { law: "HUD Office of Fair Housing & Equal Opportunity", detail: "Investigates housing discrimination complaints, including discriminatory enforcement of drug policies against veterans with disabilities." },
        { law: "Department of Justice Civil Rights Enforcement", detail: "Prosecutes systemic civil rights violations in housing programs, including pattern-and-practice discrimination in drug policy enforcement." },
        { law: "Congressional Oversight Committees", detail: "House and Senate Veterans Affairs Committees oversee VA programs. Senate Banking Committee oversees HUD programs. Accept testimony about systemic program failures." }
      ]
    },
    {
      title: "VA Clinical Policy",
      description: "Substance use disorder treatment policies in VA facilities.",
      items: [
        { law: "VHA Directive 1160", detail: "Mental health services directive - Establishes comprehensive mental health and substance use disorder treatment requirements across VA healthcare system." },
        { law: "VHA Substance Use Disorder Treatment Policy", detail: "Requires VA facilities to provide evidence-based SUD treatment, including outpatient counseling, intensive outpatient programs, and residential treatment." },
        { law: "Medication Assisted Treatment (MAT) Protocols", detail: "VA policy supporting MAT for opioid use disorder (methadone, buprenorphine, naltrexone). Veterans in MAT programs cannot be denied housing based on participation." },
        { law: "VA Suicide Prevention Program", detail: "Recognizes connection between substance use, housing instability, and suicide risk. Requires coordinated crisis intervention and housing stabilization services." }
      ]
    },
    {
      title: "HUD-VASH Case Management Rules",
      description: "Responsibilities of HUD-VASH social workers and case managers.",
      items: [
        { law: "Mandatory Case Management Engagement", detail: "HUD-VASH participants must engage with VA case managers. However, failure to attend appointments cannot automatically result in voucher termination without due process." },
        { law: "Substance Use Recovery Referrals", detail: "Case managers must provide referrals to substance use treatment when needed. Cannot punish veterans for disclosing substance use issues during case management sessions." },
        { law: "Compliance Monitoring", detail: "Case managers monitor program compliance but must distinguish between clinical relapse (requiring treatment adjustment) and lease violations (requiring housing authority action)." },
        { law: "Housing Stabilization Plans", detail: "Case managers develop individualized housing plans addressing substance use recovery goals. Plans must be realistic and supported by available VA services." }
      ]
    },
    {
      title: "Professional Ethics and Certifications",
      description: "Professional rules governing staff working with HUD-VASH veterans.",
      items: [
        { law: "NASW Code of Ethics – Social Workers", detail: "Social workers must maintain confidentiality, avoid conflicts of interest, practice cultural competence, and advocate for client rights. Applies to VA case managers working with HUD-VASH veterans." },
        { law: "SAMHSA Peer Support Practice Guidelines", detail: "Standards for peer support specialists in recovery programs. Emphasizes lived experience, non-judgmental support, and recovery-oriented approaches." },
        { law: "VA Peer Specialist Certification Standards", detail: "VA-specific training and certification for peer support specialists working in substance use recovery programs. Requires understanding of trauma-informed care." },
        { law: "Federal Employee Ethics Rules (5 CFR 2635)", detail: "VA employees must avoid misuse of position, conflicts of interest, and improper disclosure of confidential information. Violations can result in disciplinary action." }
      ]
    },
    {
      title: "Substance Use Recovery Support Systems",
      description: "Organizations providing addiction recovery support.",
      items: [
        { law: "Narcotics Anonymous (NA)", detail: "12-step mutual support program for individuals recovering from drug addiction. Meetings widely available and commonly referenced in housing program requirements." },
        { law: "Alcoholics Anonymous (AA)", detail: "12-step program for alcohol use disorder recovery. Often integrated into HUD-VASH housing stability plans and case management services." },
        { law: "Shatterproof Addiction Prevention Programs", detail: "National organization providing addiction prevention, treatment navigation, and policy advocacy resources for individuals and families." },
        { law: "National Child Traumatic Stress Network", detail: "Resources addressing childhood trauma's connection to substance use. Relevant for veterans with trauma histories affecting recovery." }
      ]
    },
    {
      title: "Mental Health and Advocacy Organizations",
      description: "National organizations supporting mental health policy and recovery.",
      items: [
        { law: "Mental Health America", detail: "National mental health advocacy organization providing screening tools, policy advocacy, and recovery resources." },
        { law: "American Foundation for Suicide Prevention", detail: "Suicide prevention research and advocacy organization. Relevant to HUD-VASH population given elevated suicide risk." },
        { law: "Depression and Bipolar Support Alliance (DBSA)", detail: "Peer-led support for individuals with mood disorders, often co-occurring with substance use." },
        { law: "The Trevor Project", detail: "Crisis intervention and suicide prevention for LGBTQ+ youth. Relevant for LGBTQ+ veterans facing housing and mental health challenges." },
        { law: "National Federation of Families for Children's Mental Health", detail: "Family-driven advocacy organization supporting children and youth with mental health needs." },
        { law: "Treatment Advocacy Center", detail: "Advocacy for improved access to mental health treatment, including involuntary treatment reform." },
        { law: "Brain & Behavior Research Foundation", detail: "Funds mental health research to improve understanding and treatment of mental illness." },
        { law: "SAMHSA National Programs", detail: "Substance Abuse and Mental Health Services Administration federal programs, including treatment locator, crisis hotlines, and grant programs." }
      ]
    }
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/vetengine" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to VETEngine
        </Link>
        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>
          Building a Case for Drug-Free HUD-VASH Facilities
        </h1>
        <p className="text-xl max-w-4xl" style={{ color: '#4A5D4A' }}>
          Legal Framework for Substance Use Policy in Veteran Housing Programs
        </p>
      </div>

      {/* Introduction */}
      <div className="max-w-7xl mx-auto mb-12">
        <div className="bg-white rounded-lg shadow-lg p-10">
          <p className="text-lg leading-relaxed" style={{ color: '#333' }}>
            HUD-VASH housing programs operate at the intersection of federal housing law, VA healthcare policy,
            and substance use recovery frameworks. This section provides a structured overview of the laws,
            directives, policies, and professional standards governing substance use and drug activity within
            HUD-VASH housing environments.
          </p>
          <p className="text-lg leading-relaxed mt-6" style={{ color: '#333' }}>
            Understanding this legal ecosystem is critical for veterans facing allegations of drug-related policy
            violations, case managers implementing housing stability plans, and advocates working to ensure fair
            and consistent enforcement of substance use policies.
          </p>
        </div>
      </div>

      {/* Accordion Sections */}
      <div className="max-w-7xl mx-auto">
        {sections.map((section, index) => {
          const isOpen = openSection === index;
          return (
            <div key={index} className="mb-6 rounded-lg overflow-hidden shadow-lg">
              <button
                className="w-full text-left p-6 font-bold text-xl text-white transition-all duration-200 hover:opacity-95 flex justify-between items-center"
                style={{ backgroundColor: '#1A4F63' }}
                onClick={() => setOpenSection(isOpen ? null : index)}
              >
                <div>
                  <div>{section.title}</div>
                  <div className="text-sm font-normal opacity-90 mt-1">{section.description}</div>
                </div>
                <ChevronDown 
                  className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  size={24}
                />
              </button>
              
              {isOpen && (
                <div className="p-8" style={{ backgroundColor: '#c8dde4' }}>
                  {section.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex} 
                      className="mb-6 pb-6 border-l-4 pl-6 last:mb-0 last:pb-0"
                      style={{ borderColor: '#1A4F63' }}
                    >
                      <div className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>{item.law}</div>
                      <p className="text-lg" style={{ color: '#333' }}>{item.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="max-w-7xl mx-auto mt-16">
        <div className="bg-white rounded-lg shadow-lg p-10">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Why This Framework Matters</h2>
          <div className="grid md:grid-cols-2 gap-6 text-lg" style={{ color: '#333' }}>
            <div>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#4A5D4A' }}>For Veterans</h3>
              <p>Understanding your rights when facing drug-related allegations, knowing which protections apply, and identifying proper complaint pathways when policies are enforced unfairly or discriminatorily.</p>
            </div>
            <div>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#4A5D4A' }}>For Case Managers</h3>
              <p>Navigating the complex intersection of clinical recovery support and housing compliance requirements while protecting veteran rights and maintaining professional ethics.</p>
            </div>
            <div>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#4A5D4A' }}>For Advocates</h3>
              <p>Building evidence-based cases when drug policies are applied inconsistently, discriminatorily, or without proper due process protections required by federal law.</p>
            </div>
            <div>
              <h3 className="font-bold text-xl mb-3" style={{ color: '#4A5D4A' }}>For Oversight</h3>
              <p>Identifying systemic failures in how substance use policies are enforced across HUD-VASH programs and triggering federal investigations when patterns emerge.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ VET SERVICES HUB PAGE ============
const VETServicesHubPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: "VETEngine Evidence Scanner™",
      description: "Upload eviction notices, VA medical notes, letters, or other documents. The system scans the file, extracts text, detects violations, and identifies applicable laws.",
      route: "/vetengine/evidence-scanner"
    },
    {
      title: "VETEngine Case Advisor™",
      description: "Describe your situation and receive guidance on possible legal protections, complaint pathways, and next actions.",
      route: "/vetengine/case-advisor"
    },
    {
      title: "VETEngine Legal Search™",
      description: "Search the veteran legal intelligence database of federal laws, housing regulations, and VA directives affecting veteran rights.",
      route: "/vetengine"
    },
    {
      title: "VETEngine Timeline Builder™",
      description: "Convert your written story or documents into a structured timeline of events that can be used for reports or legal cases.",
      route: "/vetengine/timeline-builder"
    },
    {
      title: "VETEngine Complaint Generator™",
      description: "Generate formal complaints for agencies such as VA OIG, HUD Fair Housing, Department of Justice Civil Rights Division, and housing authorities.",
      route: "/vetengine/complaint-generator"
    },
    {
      title: "VETEngine Litigation Builder™",
      description: "Build a structured civil rights case file including legal claims, timeline, exhibits, and requested relief.",
      route: "/vetengine/case-advisor"
    },
    {
      title: "VETEngine National Accountability Dashboard™",
      description: "View patterns across housing programs and VA systems to identify systemic failures affecting veterans nationwide.",
      route: "/vetengine/oversight"
    },
    {
      title: "VETDirect Legal Knowledge Engine™",
      description: "Explore the structured legal intelligence system containing hundreds of laws and directives relevant to veterans.",
      route: "/vetengine"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'Services' }
        ]} />
      </div>
      
      {/* HERO HEADER */}
      <div className="bg-primary text-white px-4 md:px-12 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-4">
            VETDirect Veteran Rights Platform
          </h1>
          <p className="max-w-3xl text-base md:text-lg font-body leading-relaxed">
            A veteran-focused legal intelligence platform designed to help veterans understand their rights, 
            analyze evidence, detect violations, and build complaints or legal case files.
          </p>
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-10">
        <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 text-slate-900">
          What would you like to do?
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <button
            onClick={() => navigate("/vetengine/evidence-scanner")}
            className="rounded-lg border border-slate-200 bg-white text-slate-950 shadow-sm hover:shadow-md hover:border-accent transition-all p-6 text-left"
          >
            <h3 className="font-heading font-semibold text-lg mb-2 text-slate-900">
              Upload Documents
            </h3>
            <p className="font-body text-sm leading-relaxed text-slate-600">
              Scan eviction notices, VA records, or letters to detect violations.
            </p>
          </button>

          <button
            onClick={() => navigate("/vetengine")}
            className="rounded-lg border border-slate-200 bg-white text-slate-950 shadow-sm hover:shadow-md hover:border-accent transition-all p-6 text-left"
          >
            <h3 className="font-heading font-semibold text-lg mb-2 text-slate-900">
              Understand My Rights
            </h3>
            <p className="font-body text-sm leading-relaxed text-slate-600">
              Browse the legal intelligence database of veteran laws and protections.
            </p>
          </button>

          <button
            onClick={() => navigate("/vetengine/complaint-generator")}
            className="rounded-lg border border-slate-200 bg-white text-slate-950 shadow-sm hover:shadow-md hover:border-accent transition-all p-6 text-left"
          >
            <h3 className="font-heading font-semibold text-lg mb-2 text-slate-900">
              File a Complaint
            </h3>
            <p className="font-body text-sm leading-relaxed text-slate-600">
              Generate formal complaint drafts for oversight agencies.
            </p>
          </button>

          <button
            onClick={() => navigate("/vetengine/guided-navigator")}
            className="rounded-lg border border-slate-200 bg-white text-slate-950 shadow-sm hover:shadow-md hover:border-accent transition-all p-6 text-left"
          >
            <h3 className="font-heading font-semibold text-lg mb-2 text-slate-900">
              Get Guided Help
            </h3>
            <p className="font-body text-sm leading-relaxed text-slate-600">
              Answer questions and we'll guide you to the right tool.
            </p>
          </button>
        </div>
      </div>

      {/* SERVICE ENGINE GRID */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 pb-16">
        <h2 className="text-2xl md:text-3xl font-heading font-bold mb-8 text-slate-900">
          Available Services
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-accent cursor-pointer transition-all p-6"
              onClick={() => navigate(service.route)}
            >
              <h3 className="text-xl font-heading font-semibold mb-3 text-slate-900">
                {service.title}
              </h3>
              <p className="font-body text-sm leading-relaxed text-slate-600">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ VET GUIDED NAVIGATOR PAGE ============
const VETGuidedNavigatorPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    issue: "",
    location: "",
    entity: "",
    documents: false
  });

  const update = (field, value) => {
    setData({ ...data, [field]: value });
  };

  const next = () => {
    setStep(step + 1);
  };

  const back = () => {
    setStep(step - 1);
  };

  const launchEngine = () => {
    if (data.documents) {
      navigate("/vetengine/evidence-scanner");
      return;
    }

    if (data.issue === "eviction") {
      navigate("/vetengine/complaint-generator");
      return;
    }

    if (data.issue === "retaliation") {
      navigate("/vetengine/complaint-generator");
      return;
    }

    if (data.issue === "treatment") {
      navigate("/vetengine/case-advisor");
      return;
    }

    navigate("/vetengine");
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#F2F4F7' }}>
      <TopNavbar showAuth={false} />
      
      <div className="max-w-4xl mx-auto">
        <Link to="/services" className="text-lg mb-4 inline-block hover:underline" style={{ color: '#1A4F63' }}>
          ← Back to Services Hub
        </Link>
        
        <h1 className="text-4xl font-bold mb-4" style={{ color: '#1A4F63' }}>
          Veteran Civil Rights Navigator
        </h1>
        <p className="text-lg mb-10 max-w-3xl" style={{ color: '#666' }}>
          Answer a few questions and the system will guide you to the best tool for analyzing your situation.
        </p>

        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: '#1A4F63' }}>Step {step} of 4</span>
            <span className="text-sm" style={{ color: '#666' }}>{Math.round((step / 4) * 100)}% Complete</span>
          </div>
          <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
            <div 
              className="h-2 rounded-full transition-all duration-300" 
              style={{ backgroundColor: '#1A4F63', width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-10">
          {/* STEP 1 */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1A4F63' }}>
                What happened?
              </h2>
              <select
                className="w-full border-2 p-4 rounded-lg text-lg mb-6"
                style={{ borderColor: '#C4B5A0' }}
                value={data.issue}
                onChange={(e) => update("issue", e.target.value)}
              >
                <option value="">Select issue type...</option>
                <option value="eviction">Eviction threat or notice</option>
                <option value="retaliation">Retaliation after filing complaint</option>
                <option value="treatment">Denied medical treatment or services</option>
                <option value="discrimination">Disability discrimination</option>
                <option value="case_manager">Case manager misconduct</option>
                <option value="other">Other housing or VA issue</option>
              </select>

              <button
                onClick={next}
                disabled={!data.issue}
                className="w-full py-4 rounded-lg font-bold text-white text-lg transition-all"
                style={{ backgroundColor: data.issue ? '#1A4F63' : '#ccc' }}
              >
                Next →
              </button>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1A4F63' }}>
                Where did it happen?
              </h2>
              <select
                className="w-full border-2 p-4 rounded-lg text-lg mb-6"
                style={{ borderColor: '#C4B5A0' }}
                value={data.location}
                onChange={(e) => update("location", e.target.value)}
              >
                <option value="">Select location...</option>
                <option>HUD-VASH Housing</option>
                <option>VA Medical Center</option>
                <option>Supportive Housing Program</option>
                <option>Homeless Shelter</option>
                <option>Community Housing</option>
                <option>Other VA Facility</option>
              </select>

              <div className="flex gap-4">
                <button
                  onClick={back}
                  className="flex-1 py-4 rounded-lg font-bold border-2 text-lg"
                  style={{ borderColor: '#1A4F63', color: '#1A4F63' }}
                >
                  ← Back
                </button>
                <button
                  onClick={next}
                  disabled={!data.location}
                  className="flex-1 py-4 rounded-lg font-bold text-white text-lg"
                  style={{ backgroundColor: data.location ? '#1A4F63' : '#ccc' }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1A4F63' }}>
                Who was involved?
              </h2>
              <select
                className="w-full border-2 p-4 rounded-lg text-lg mb-6"
                style={{ borderColor: '#C4B5A0' }}
                value={data.entity}
                onChange={(e) => update("entity", e.target.value)}
              >
                <option value="">Select responsible party...</option>
                <option>Property Manager</option>
                <option>VA Social Worker</option>
                <option>Case Manager</option>
                <option>VA Doctor or Clinician</option>
                <option>Housing Authority Staff</option>
                <option>Peer Specialist</option>
                <option>VA Administrator</option>
              </select>

              <div className="flex gap-4">
                <button
                  onClick={back}
                  className="flex-1 py-4 rounded-lg font-bold border-2 text-lg"
                  style={{ borderColor: '#1A4F63', color: '#1A4F63' }}
                >
                  ← Back
                </button>
                <button
                  onClick={next}
                  disabled={!data.entity}
                  className="flex-1 py-4 rounded-lg font-bold text-white text-lg"
                  style={{ backgroundColor: data.entity ? '#1A4F63' : '#ccc' }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1A4F63' }}>
                Do you have documents or evidence?
              </h2>
              
              <label className="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 mb-6" style={{ borderColor: '#C4B5A0' }}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={data.documents}
                  onChange={(e) => update("documents", e.target.checked)}
                />
                <div>
                  <p className="font-semibold text-lg mb-1" style={{ color: '#1A4F63' }}>
                    Yes, I have documents
                  </p>
                  <p className="text-sm" style={{ color: '#666' }}>
                    Eviction notices, VA notes, letters, emails, text messages, or other evidence
                  </p>
                </div>
              </label>

              <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: '#c8dde4' }}>
                <h3 className="font-bold mb-2" style={{ color: '#1A4F63' }}>📋 Your Situation Summary:</h3>
                <ul className="text-sm space-y-1" style={{ color: '#333' }}>
                  <li><strong>Issue:</strong> {data.issue}</li>
                  <li><strong>Location:</strong> {data.location}</li>
                  <li><strong>Involved Party:</strong> {data.entity}</li>
                  <li><strong>Evidence:</strong> {data.documents ? "Documents available" : "No documents yet"}</li>
                </ul>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={back}
                  className="flex-1 py-4 rounded-lg font-bold border-2 text-lg"
                  style={{ borderColor: '#1A4F63', color: '#1A4F63' }}
                >
                  ← Back
                </button>
                <button
                  onClick={launchEngine}
                  className="flex-1 py-4 rounded-lg font-bold text-white text-lg"
                  style={{ backgroundColor: '#7A8F65' }}
                >
                  🚀 Analyze My Situation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ GLOBAL NAVIGATION HEADER ============
// EXTRACTED: const GlobalNav = () => {
// EXTRACTED:   const location = useLocation();
// EXTRACTED:   
// EXTRACTED:   const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
// EXTRACTED:   
// EXTRACTED:   const navItems = [
// EXTRACTED:     { path: '/', label: 'Home' },
// EXTRACTED:     { path: '/services', label: 'Services' },
// EXTRACTED:     { path: '/vetengine/guided-navigator', label: 'Guided Assistant' },
// EXTRACTED:     { path: '/case-dashboard', label: 'Case Dashboard' },
// EXTRACTED:     { path: '/vetengine', label: 'Legal Engine' },
// EXTRACTED:     { path: '/vha-directives', label: 'VHA Directives' },
// EXTRACTED:     { path: '/vetengine/drug-policy', label: 'Drug Policy' },
// EXTRACTED:     { path: '/vetengine/oversight', label: 'Oversight' },
// EXTRACTED:     { path: '/facility-risk-index', label: 'Risk Index' },
// EXTRACTED:     { path: '/hudvash-directory', label: 'Facility Directory' }
// EXTRACTED:   ];
// EXTRACTED:   
// EXTRACTED:   return (
// EXTRACTED:     <header className="px-8 py-4 flex justify-center gap-6 items-center" style={{ backgroundColor: '#1A4F63' }}>
// EXTRACTED:       <Link to="/" className="font-bold text-xl mr-4" style={{ color: '#FFCC01' }}>
// EXTRACTED:         VETDirect
// EXTRACTED:       </Link>
// EXTRACTED:       {navItems.map((item) => (
// EXTRACTED:         <Link
// EXTRACTED:           key={item.path}
// EXTRACTED:           to={item.path}
// EXTRACTED:           className="hover:opacity-80 transition-opacity"
// EXTRACTED:           style={{ 
// EXTRACTED:             color: isActive(item.path) ? '#FFCC01' : '#fff',
// EXTRACTED:             fontWeight: isActive(item.path) ? 'bold' : 'normal'
// EXTRACTED:           }}
// EXTRACTED:         >
// EXTRACTED:           {item.label}
// EXTRACTED:         </Link>
// EXTRACTED:       ))}
// EXTRACTED:     </header>
// EXTRACTED:   );
// EXTRACTED: };

// ============ BREADCRUMB COMPONENT ============
// EXTRACTED: const Breadcrumb = ({ items }) => {
// EXTRACTED:   return (
// EXTRACTED:     <div className="text-sm mb-6" style={{ color: '#666' }}>
// EXTRACTED:       {items.map((item, i) => (
// EXTRACTED:         <span key={i}>
// EXTRACTED:           {i > 0 && <span className="mx-2">/</span>}
// EXTRACTED:           {item.link ? (
// EXTRACTED:             <Link to={item.link} className="hover:underline" style={{ color: '#1A4F63' }}>
// EXTRACTED:               {item.label}
// EXTRACTED:             </Link>
// EXTRACTED:           ) : (
// EXTRACTED:             <span style={{ color: '#333', fontWeight: 'bold' }}>{item.label}</span>
// EXTRACTED:           )}
// EXTRACTED:         </span>
// EXTRACTED:       ))}
// EXTRACTED:     </div>
// EXTRACTED:   );
// EXTRACTED: };

// ============ VHA DIRECTIVES PAGE ============
const VHADirectivesPage = () => {
  const [openSection, setOpenSection] = useState(null);

  const directives = [
    {
      title: "Homeless Program Directives",
      description: "Core VA directives governing homeless veteran programs",
      items: [
        { number: "VHA Directive 1501", title: "Homeless Programs", detail: "Establishes requirements for comprehensive homeless veteran services including outreach, treatment, and housing assistance programs across all VA medical centers." },
        { number: "VHA Directive 1502", title: "Contact and Referral Management", detail: "Requires VA facilities to maintain systems for tracking veteran contacts, referrals to housing programs, and follow-up services for homeless veterans." },
        { number: "VHA Directive 1510", title: "Legal Referral Process", detail: "Establishes procedures for referring homeless veterans to legal assistance when facing housing issues, benefits denials, or civil rights violations." }
      ]
    },
    {
      title: "HUD-VASH Policy Directives",
      description: "Directives specific to HUD-VASH program administration",
      items: [
        { number: "VHA Directive 1162.05(2)", title: "HUD-VASH Program", detail: "Comprehensive directive governing HUD-VASH voucher program including eligibility, case management requirements, and housing authority coordination." },
        { number: "VHA Directive 1504", title: "Tribal HUD-VASH", detail: "Establishes Tribal HUD-VASH program providing housing vouchers for Native American veterans through partnerships with Tribal housing authorities." }
      ]
    },
    {
      title: "Housing Pipeline Programs",
      description: "Transitional and supportive housing program directives",
      items: [
        { number: "VHA Directive 1162.01", title: "Grant and Per Diem Program", detail: "Authorizes funding to community organizations providing transitional housing and supportive services to homeless veterans." },
        { number: "VHA Directive 1162.03", title: "Community Resource and Referral Centers (CRRC)", detail: "Establishes drop-in centers providing services, referrals, and housing assistance to veterans experiencing or at risk of homelessness." },
        { number: "VHA Directive 1162.04", title: "Contract Residential Services", detail: "Authorizes VA to contract with community providers for residential treatment programs serving homeless veterans with mental health or substance use disorders." },
        { number: "VHA Directive 1162.07", title: "Supportive Services for Veteran Families (SSVF)", detail: "Provides rapid rehousing and homelessness prevention services through community grants supporting veteran families." },
        { number: "VHA Directive 1162.08", title: "Healthcare for Homeless Veterans (HCHV) Outreach", detail: "Requires VA medical centers to conduct street outreach, mobile medical services, and engagement services for homeless veterans." }
      ]
    },
    {
      title: "Veterans Justice Programs",
      description: "Directives for veterans in criminal justice system",
      items: [
        { number: "VHA Directive 1162.06", title: "Veterans Justice Programs", detail: "Establishes Veterans Justice Outreach (VJO) specialists who work with courts, jails, and justice systems to divert veterans into treatment and housing rather than incarceration." }
      ]
    },
    {
      title: "HPACT / Primary Care Directives",
      description: "Homeless patient aligned care team requirements",
      items: [
        { number: "VHA Directive 1101.10(2)", title: "Patient Aligned Care Teams (PACT) Handbook", detail: "Establishes primary care home model including Homeless Patient Aligned Care Teams (HPACT) providing integrated healthcare for homeless veterans." },
        { number: "VHA Directive 1406(1)", title: "Patient Centered Management Module (PCMM)", detail: "Requires tracking and management of homeless veteran enrollment in primary care to ensure continuity of healthcare during housing transitions." }
      ]
    },
    {
      title: "Core Care Directives",
      description: "Essential mental health and case management directives",
      items: [
        { number: "VHA Directive 1110.04", title: "Case Management", detail: "Establishes intensive case management services for veterans with complex medical, mental health, and housing needs including HUD-VASH participants." },
        { number: "VHA Directive 1160.01", title: "Uniform Mental Health Services", detail: "Requires comprehensive mental health services at all VA facilities including crisis intervention, outpatient treatment, and residential programs." },
        { number: "VHA Directive 1160.04", title: "Substance Use Disorder Programs", detail: "Mandates evidence-based substance use treatment including medication assisted treatment, intensive outpatient programs, and residential care." },
        { number: "VHA Directive 1162.02", title: "Mental Health Residential Rehabilitation", detail: "Establishes domiciliary residential programs providing mental health treatment and rehabilitation for homeless veterans with psychiatric conditions." },
        { number: "VHA Directive 1163", title: "Psychosocial Rehabilitation and Recovery Services", detail: "Requires psychosocial rehabilitation programs supporting veterans' recovery, employment, and community integration goals." },
        { number: "VHA Directive 1503", title: "Veterans Crisis Line", detail: "Establishes 24/7 Veterans Crisis Line providing immediate crisis intervention, suicide prevention, and referral services including housing crisis support." }
      ]
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      
      <div className="px-8 py-12">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'VHA Directives' }
          ]} />
          
          <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>
            VHA Directives Governing Homeless and HUD-VASH Veterans
          </h1>
          <p className="text-xl mb-12" style={{ color: '#4A5D4A' }}>
            Official VA Healthcare System directives establishing requirements for homeless veteran programs
          </p>

          <div className="bg-white rounded-lg shadow-lg p-10 mb-12">
            <p className="text-lg leading-relaxed" style={{ color: '#333' }}>
              VHA Directives are official policy documents issued by the Veterans Health Administration establishing
              mandatory requirements for VA medical centers and healthcare programs. These directives govern how
              homeless programs operate, what services must be provided, and what standards case managers must follow.
            </p>
          </div>

          {/* Directive Accordions */}
          {directives.map((section, index) => {
            const isOpen = openSection === index;
            return (
              <div key={index} className="mb-6 rounded-lg overflow-hidden shadow-lg">
                <button
                  className="w-full text-left p-6 font-bold text-xl text-white transition-all duration-200 hover:opacity-95 flex justify-between items-center"
                  style={{ backgroundColor: '#1A4F63' }}
                  onClick={() => setOpenSection(isOpen ? null : index)}
                >
                  <div>
                    <div>{section.title}</div>
                    <div className="text-sm font-normal opacity-90 mt-1">{section.description}</div>
                  </div>
                  <ChevronDown 
                    className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    size={24}
                  />
                </button>
                
                {isOpen && (
                  <div className="p-8" style={{ backgroundColor: '#c8dde4' }}>
                    {section.items.map((directive, itemIndex) => (
                      <div 
                        key={itemIndex} 
                        className="mb-6 pb-6 border-l-4 pl-6 last:mb-0 last:pb-0"
                        style={{ borderColor: '#1A4F63' }}
                      >
                        <div className="font-bold text-lg mb-1" style={{ color: '#7A8F65' }}>
                          {directive.number}
                        </div>
                        <div className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>
                          {directive.title}
                        </div>
                        <p className="text-lg" style={{ color: '#333' }}>{directive.detail}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============ CASE DASHBOARD PAGE ============
const CaseDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('summary');

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'Case Dashboard' }
        ]} />
        
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-primary mb-4 mt-8">
          My Case Workspace
        </h1>
        <p className="text-lg font-body leading-relaxed text-slate-600 mb-12 max-w-3xl">
          Organize your evidence, timeline, and legal documents in one place
        </p>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 border-b-2 border-slate-200">
          {[
            { id: 'summary', label: 'Case Summary' },
            { id: 'documents', label: 'My Documents' },
            { id: 'timeline', label: 'Incident Timeline' },
            { id: 'violations', label: 'Detected Violations' },
            { id: 'complaints', label: 'Complaint Drafts' },
            { id: 'litigation', label: 'Litigation Package' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 md:px-6 py-3 font-heading font-bold uppercase text-sm tracking-wider transition-all ${
                activeTab === tab.id 
                  ? 'text-primary border-b-3 border-primary bg-white' 
                  : 'text-slate-600 border-b-3 border-transparent hover:text-primary'
              }`}
              style={{
                borderBottomWidth: '3px',
                borderBottomColor: activeTab === tab.id ? '#1A4F63' : 'transparent'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 md:p-10">
            {activeTab === 'summary' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-6">Case Overview</h2>
                <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8">
                  <div className="p-6 rounded-lg bg-primary/10">
                    <h3 className="font-heading font-semibold text-base mb-2 text-primary">Documents Uploaded</h3>
                    <p className="text-4xl font-heading font-bold text-slate-900">0</p>
                  </div>
                  <div className="p-6 rounded-lg bg-secondary/10">
                    <h3 className="font-heading font-semibold text-base mb-2 text-secondary">Timeline Events</h3>
                    <p className="text-4xl font-heading font-bold text-slate-900">0</p>
                  </div>
                  <div className="p-6 rounded-lg bg-accent/10">
                    <h3 className="font-heading font-semibold text-base mb-2 text-slate-900">Violations Detected</h3>
                    <p className="text-4xl font-heading font-bold text-slate-900">0</p>
                  </div>
                </div>
                <div className="text-center py-12">
                  <FileText size={64} className="mx-auto mb-4 text-slate-300" />
                  <h3 className="text-xl md:text-2xl font-heading font-semibold mb-4 text-primary">Start Building Your Case</h3>
                  <p className="text-base font-body leading-relaxed text-slate-600 mb-6">
                    Upload documents, create a timeline, or use the guided assistant to get started
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <Link to="/vetengine/evidence-scanner">
                      <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-primary text-white hover:bg-[#133A4A] shadow-sm transition-all h-10 px-4 py-2 text-sm">
                        Upload Documents
                      </button>
                    </Link>
                    <Link to="/vetengine/guided-navigator">
                      <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all h-10 px-4 py-2 text-sm">
                        Get Guided Help
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-6">Evidence Files</h2>
                <div className="text-center py-12">
                  <FileSearch size={64} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-base font-body leading-relaxed text-slate-600 mb-6">No documents uploaded yet</p>
                  <Link to="/vetengine/evidence-scanner">
                    <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-primary text-white hover:bg-[#133A4A] shadow-sm transition-all h-10 px-4 py-2 text-sm">
                      Upload Your First Document
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'timeline' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-6">Incident Timeline</h2>
                <div className="text-center py-12">
                  <Calendar size={64} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-base font-body leading-relaxed text-slate-600 mb-6">No timeline events created yet</p>
                  <Link to="/vetengine/timeline-builder">
                    <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-primary text-white hover:bg-[#133A4A] shadow-sm transition-all h-10 px-4 py-2 text-sm">
                      Create Timeline
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'violations' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-6">Detected Legal Violations</h2>
                <div className="text-center py-12">
                  <AlertTriangle size={64} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-base font-body leading-relaxed text-slate-600 mb-6">Upload documents to detect violations automatically</p>
                  <Link to="/vetengine/evidence-scanner">
                    <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-primary text-white hover:bg-[#133A4A] shadow-sm transition-all h-10 px-4 py-2 text-sm">
                      Scan Evidence
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'complaints' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-6">Complaint Drafts</h2>
                <div className="text-center py-12">
                  <Send size={64} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-base font-body leading-relaxed text-slate-600 mb-6">No complaints generated yet</p>
                  <Link to="/vetengine/complaint-generator">
                    <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-primary text-white hover:bg-[#133A4A] shadow-sm transition-all h-10 px-4 py-2 text-sm">
                      Generate Complaint
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'litigation' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-6">Litigation Package</h2>
                <div className="text-center py-12">
                  <Scale size={64} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-base font-body leading-relaxed text-slate-600 mb-6">Build your case file with evidence, timeline, and legal claims</p>
                  <Link to="/vetengine/case-advisor">
                    <button className="inline-flex items-center justify-center rounded-md font-heading font-bold uppercase tracking-wider bg-primary text-white hover:bg-[#133A4A] shadow-sm transition-all h-10 px-4 py-2 text-sm">
                      Start Case Advisor
                    </button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

// ============ HUD-VASH FACILITY RISK INDEX DATA & ALGORITHM ============
const SAMPLE_FACILITIES = [
  {
    id: "summit-view-la",
    facility_name: "Summit View Apartments",
    city: "Los Angeles",
    state: "California",
    lat: 34.0522,
    lng: -118.2437,
    hud_vash_units: 50,
    reported_incidents: {
      drug_activity: 12,
      violence: 5,
      overdose: 3,
      evictions: 8,
      case_management_failures: 6
    },
    oig_reports: ["OIG-21-03075", "OIG-23-01212"],
    deaths: 8
  },
  {
    id: "veterans-housing-phoenix",
    facility_name: "Veterans Housing Phoenix",
    city: "Phoenix",
    state: "Arizona",
    lat: 33.4484,
    lng: -112.0740,
    hud_vash_units: 35,
    reported_incidents: {
      drug_activity: 4,
      violence: 2,
      overdose: 1,
      evictions: 3,
      case_management_failures: 2
    },
    oig_reports: [],
    deaths: 1
  },
  {
    id: "veterans-residence-chicago",
    facility_name: "Veterans Residence Chicago",
    city: "Chicago",
    state: "Illinois",
    lat: 41.8781,
    lng: -87.6298,
    hud_vash_units: 42,
    reported_incidents: {
      drug_activity: 2,
      violence: 1,
      overdose: 0,
      evictions: 4,
      case_management_failures: 1
    },
    oig_reports: [],
    deaths: 0
  },
  {
    id: "westside-veteran-housing",
    facility_name: "Westside Veteran Housing",
    city: "San Diego",
    state: "California",
    lat: 32.7157,
    lng: -117.1611,
    hud_vash_units: 28,
    reported_incidents: {
      drug_activity: 8,
      violence: 4,
      overdose: 2,
      evictions: 6,
      case_management_failures: 4
    },
    oig_reports: ["OIG-22-04512"],
    deaths: 3
  }
];

const calculateRiskScore = (facility) => {
  let score = 0;
  score += facility.reported_incidents.drug_activity * 10;
  score += facility.reported_incidents.violence * 8;
  score += facility.reported_incidents.overdose * 12;
  score += facility.reported_incidents.evictions * 6;
  score += facility.reported_incidents.case_management_failures * 5;
  score += facility.oig_reports.length * 20;
  score += facility.deaths * 15;
  return score;
};

const getRiskLevel = (score) => {
  if (score >= 80) return { level: 'CRITICAL', color: '#DC2626', bgColor: '#FEE2E2' };
  if (score >= 51) return { level: 'HIGH', color: '#EA580C', bgColor: '#FFEDD5' };
  if (score >= 21) return { level: 'MODERATE', color: '#CA8A04', bgColor: '#FEF9C3' };
  return { level: 'LOW', color: '#16A34A', bgColor: '#DCFCE7' };
};

// ============ HUD-VASH FACILITY RISK INDEX PAGE ============
const HUDVashFacilityIndexPage = () => {
  const [facilities] = useState(SAMPLE_FACILITIES.map(f => ({ ...f, risk_score: calculateRiskScore(f) })));
  const [selectedState, setSelectedState] = useState('ALL');
  const [sortBy, setSortBy] = useState('risk_score');

  const filteredFacilities = selectedState === 'ALL' 
    ? facilities 
    : facilities.filter(f => f.state === selectedState);

  const sortedFacilities = [...filteredFacilities].sort((a, b) => b[sortBy] - a[sortBy]);

  const states = ['ALL', ...new Set(facilities.map(f => f.state))];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F2F4F7' }}>
      <TopNavbar showAuth={false} />
      
      <div className="px-8 py-12">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'HUD-VASH Facility Risk Index' }
          ]} />
          
          <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>
            HUD-VASH Facility Risk Index™
          </h1>
          <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>
            National accountability dashboard scoring HUD-VASH housing sites based on safety indicators
          </p>

          {/* Info Banner */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4">
              <AlertTriangle size={32} style={{ color: '#EA580C' }} />
              <div>
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#1A4F63' }}>
                  First National Oversight System for Veteran Housing Safety
                </h2>
                <p className="text-lg mb-4" style={{ color: '#333' }}>
                  This platform tracks drug activity, violence incidents, deaths, eviction patterns, OIG investigations,
                  and case management failures across HUD-VASH facilities nationwide. Each facility receives a risk score
                  based on documented incidents.
                </p>
                <Link to="/facility-report">
                  <button className="px-6 py-3 rounded-lg font-bold text-white" style={{ backgroundColor: '#1A4F63' }}>
                    Report an Incident
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block font-bold mb-2" style={{ color: '#1A4F63' }}>Filter by State</label>
                <select
                  className="w-full p-3 border-2 rounded-lg text-lg"
                  style={{ borderColor: '#C4B5A0' }}
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-bold mb-2" style={{ color: '#1A4F63' }}>Sort By</label>
                <select
                  className="w-full p-3 border-2 rounded-lg text-lg"
                  style={{ borderColor: '#C4B5A0' }}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="risk_score">Risk Score (Highest First)</option>
                  <option value="deaths">Deaths</option>
                  <option value="hud_vash_units">Number of Units</option>
                </select>
              </div>
            </div>
          </div>

          {/* Risk Level Legend */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h3 className="font-bold text-xl mb-4" style={{ color: '#1A4F63' }}>Risk Level Classification</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#DCFCE7', borderLeft: '4px solid #16A34A' }}>
                <p className="font-bold" style={{ color: '#16A34A' }}>LOW (0-20)</p>
                <p className="text-sm">Minimal reported issues</p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEF9C3', borderLeft: '4px solid #CA8A04' }}>
                <p className="font-bold" style={{ color: '#CA8A04' }}>MODERATE (21-50)</p>
                <p className="text-sm">Some concerns documented</p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFEDD5', borderLeft: '4px solid #EA580C' }}>
                <p className="font-bold" style={{ color: '#EA580C' }}>HIGH (51-80)</p>
                <p className="text-sm">Significant safety issues</p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2', borderLeft: '4px solid #DC2626' }}>
                <p className="font-bold" style={{ color: '#DC2626' }}>CRITICAL (80+)</p>
                <p className="text-sm">Severe documented failures</p>
              </div>
            </div>
          </div>

          {/* Facility Table */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6" style={{ backgroundColor: '#1A4F63' }}>
              <h2 className="text-2xl font-bold text-white">Facility Risk Rankings</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ backgroundColor: '#c8dde4' }}>
                  <tr>
                    <th className="text-left p-4 font-bold" style={{ color: '#1A4F63' }}>Facility Name</th>
                    <th className="text-left p-4 font-bold" style={{ color: '#1A4F63' }}>City</th>
                    <th className="text-left p-4 font-bold" style={{ color: '#1A4F63' }}>State</th>
                    <th className="text-center p-4 font-bold" style={{ color: '#1A4F63' }}>Units</th>
                    <th className="text-center p-4 font-bold" style={{ color: '#1A4F63' }}>Deaths</th>
                    <th className="text-center p-4 font-bold" style={{ color: '#1A4F63' }}>Drug Activity</th>
                    <th className="text-center p-4 font-bold" style={{ color: '#1A4F63' }}>Risk Score</th>
                    <th className="text-center p-4 font-bold" style={{ color: '#1A4F63' }}>Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFacilities.map((facility, idx) => {
                    const risk = getRiskLevel(facility.risk_score);
                    return (
                      <tr key={facility.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <Link to={`/facility/${facility.id}`} className="font-bold hover:underline" style={{ color: '#1A4F63' }}>
                            {facility.facility_name}
                          </Link>
                        </td>
                        <td className="p-4">{facility.city}</td>
                        <td className="p-4">{facility.state}</td>
                        <td className="p-4 text-center">{facility.hud_vash_units}</td>
                        <td className="p-4 text-center font-bold" style={{ color: facility.deaths > 0 ? '#DC2626' : '#666' }}>
                          {facility.deaths}
                        </td>
                        <td className="p-4 text-center">{facility.reported_incidents.drug_activity}</td>
                        <td className="p-4 text-center">
                          <span className="font-bold text-2xl" style={{ color: risk.color }}>
                            {facility.risk_score}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <span 
                            className="px-4 py-2 rounded-full font-bold text-sm"
                            style={{ backgroundColor: risk.bgColor, color: risk.color }}
                          >
                            {risk.level}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Scoring Methodology */}
          <div className="bg-white rounded-lg shadow-lg p-8 mt-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>Risk Score Methodology</h2>
            <p className="text-lg mb-6" style={{ color: '#333' }}>
              Each facility's risk score is calculated based on documented incidents using the following weights:
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-lg">
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>Drug Distribution Reports:</span>
                <span className="font-bold">+10 points each</span>
              </div>
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>Violence Incidents:</span>
                <span className="font-bold">+8 points each</span>
              </div>
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>Overdose Incidents:</span>
                <span className="font-bold">+12 points each</span>
              </div>
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>Evictions:</span>
                <span className="font-bold">+6 points each</span>
              </div>
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>Case Management Failures:</span>
                <span className="font-bold">+5 points each</span>
              </div>
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>OIG Investigation:</span>
                <span className="font-bold">+20 points each</span>
              </div>
              <div className="flex justify-between p-3 rounded" style={{ backgroundColor: '#f0f4f8' }}>
                <span>Veteran Deaths:</span>
                <span className="font-bold">+15 points each</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ INDIVIDUAL FACILITY REPORT PAGE ============
const FacilityReportPage = () => {
  const { facilityId } = useParams();
  const facility = SAMPLE_FACILITIES.find(f => f.id === facilityId);
  
  if (!facility) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F2F4F7' }}>
        <TopNavbar showAuth={false} />
        <div className="px-8 py-12 text-center">
          <h1 className="text-3xl font-bold" style={{ color: '#1A4F63' }}>Facility Not Found</h1>
        </div>
      </div>
    );
  }

  const risk_score = calculateRiskScore(facility);
  const risk = getRiskLevel(risk_score);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F2F4F7' }}>
      <TopNavbar showAuth={false} />
      
      <div className="px-8 py-12">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Facility Risk Index', link: '/hudvash-facility-index' },
            { label: facility.facility_name }
          ]} />
          
          <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-4xl font-bold mb-2" style={{ color: '#1A4F63' }}>
                  {facility.facility_name}
                </h1>
                <p className="text-xl" style={{ color: '#666' }}>{facility.city}, {facility.state}</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold mb-2" style={{ color: risk.color }}>{risk_score}</div>
                <span 
                  className="px-6 py-2 rounded-full font-bold"
                  style={{ backgroundColor: risk.bgColor, color: risk.color }}
                >
                  {risk.level} RISK
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="p-6 rounded-lg" style={{ backgroundColor: '#c8dde4' }}>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>HUD-VASH Units</h3>
                <p className="text-4xl font-bold" style={{ color: '#333' }}>{facility.hud_vash_units}</p>
              </div>
              <div className="p-6 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#DC2626' }}>Veteran Deaths</h3>
                <p className="text-4xl font-bold" style={{ color: '#DC2626' }}>{facility.deaths}</p>
              </div>
              <div className="p-6 rounded-lg" style={{ backgroundColor: '#FFEDD5' }}>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#EA580C' }}>OIG Reports</h3>
                <p className="text-4xl font-bold" style={{ color: '#EA580C' }}>{facility.oig_reports.length}</p>
              </div>
            </div>
          </div>

          {/* Incident Reports */}
          <div className="bg-white rounded-lg shadow-lg p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>Documented Incidents</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg border-l-4" style={{ borderColor: '#DC2626', backgroundColor: '#f9fafb' }}>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Drug Activity</h3>
                <p className="text-5xl font-bold mb-2" style={{ color: '#DC2626' }}>{facility.reported_incidents.drug_activity}</p>
                <p className="text-sm" style={{ color: '#666' }}>Reported drug distribution incidents</p>
              </div>
              <div className="p-6 rounded-lg border-l-4" style={{ borderColor: '#EA580C', backgroundColor: '#f9fafb' }}>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Violence</h3>
                <p className="text-5xl font-bold mb-2" style={{ color: '#EA580C' }}>{facility.reported_incidents.violence}</p>
                <p className="text-sm" style={{ color: '#666' }}>Physical altercations and assaults</p>
              </div>
              <div className="p-6 rounded-lg border-l-4" style={{ borderColor: '#7C2D12', backgroundColor: '#f9fafb' }}>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Overdoses</h3>
                <p className="text-5xl font-bold mb-2" style={{ color: '#7C2D12' }}>{facility.reported_incidents.overdose}</p>
                <p className="text-sm" style={{ color: '#666' }}>Drug overdose incidents</p>
              </div>
              <div className="p-6 rounded-lg border-l-4" style={{ borderColor: '#CA8A04', backgroundColor: '#f9fafb' }}>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Evictions</h3>
                <p className="text-5xl font-bold mb-2" style={{ color: '#CA8A04' }}>{facility.reported_incidents.evictions}</p>
                <p className="text-sm" style={{ color: '#666' }}>HUD-VASH voucher terminations</p>
              </div>
              <div className="p-6 rounded-lg border-l-4" style={{ borderColor: '#0891B2', backgroundColor: '#f9fafb' }}>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#1A4F63' }}>Case Management Failures</h3>
                <p className="text-5xl font-bold mb-2" style={{ color: '#0891B2' }}>{facility.reported_incidents.case_management_failures}</p>
                <p className="text-sm" style={{ color: '#666' }}>Documented service failures</p>
              </div>
            </div>
          </div>

          {/* OIG Reports */}
          {facility.oig_reports.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-10">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>OIG Investigation Reports</h2>
              <ul className="space-y-3">
                {facility.oig_reports.map((report, idx) => (
                  <li key={idx} className="p-4 rounded-lg flex items-center gap-4" style={{ backgroundColor: '#FEE2E2' }}>
                    <AlertTriangle style={{ color: '#DC2626' }} size={24} />
                    <span className="font-bold text-lg" style={{ color: '#DC2626' }}>
                      VA OIG Report: {report}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ INCIDENT REPORTING TOOL PAGE ============
const IncidentReportingPage = () => {
  const [formData, setFormData] = useState({
    facility_name: '',
    incident_type: '',
    date: '',
    description: '',
    anonymous: false
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success("Incident report submitted successfully");
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F2F4F7' }}>
      <TopNavbar showAuth={false} />
      
      <div className="px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Facility Risk Index', link: '/hudvash-facility-index' },
            { label: 'Report Incident' }
          ]} />
          
          <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>
            Report a HUD-VASH Facility Incident
          </h1>
          <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>
            Submit incidents to help document safety issues and hold facilities accountable
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-10">
              <div className="space-y-6">
                <div>
                  <label className="block font-bold mb-2 text-lg" style={{ color: '#1A4F63' }}>
                    Facility Name
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full p-4 border-2 rounded-lg text-lg"
                    style={{ borderColor: '#C4B5A0' }}
                    value={formData.facility_name}
                    onChange={(e) => setFormData({...formData, facility_name: e.target.value})}
                    placeholder="Enter facility name and location"
                  />
                </div>

                <div>
                  <label className="block font-bold mb-2 text-lg" style={{ color: '#1A4F63' }}>
                    Incident Type
                  </label>
                  <select
                    required
                    className="w-full p-4 border-2 rounded-lg text-lg"
                    style={{ borderColor: '#C4B5A0' }}
                    value={formData.incident_type}
                    onChange={(e) => setFormData({...formData, incident_type: e.target.value})}
                  >
                    <option value="">Select incident type...</option>
                    <option value="drug_activity">Drug Distribution/Activity</option>
                    <option value="violence">Violence or Assault</option>
                    <option value="overdose">Drug Overdose</option>
                    <option value="eviction">Wrongful Eviction</option>
                    <option value="case_management">Case Management Failure</option>
                    <option value="retaliation">Retaliation</option>
                    <option value="death">Veteran Death</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold mb-2 text-lg" style={{ color: '#1A4F63' }}>
                    Date of Incident
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full p-4 border-2 rounded-lg text-lg"
                    style={{ borderColor: '#C4B5A0' }}
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block font-bold mb-2 text-lg" style={{ color: '#1A4F63' }}>
                    Incident Description
                  </label>
                  <textarea
                    required
                    rows={8}
                    className="w-full p-4 border-2 rounded-lg text-lg"
                    style={{ borderColor: '#C4B5A0' }}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe what happened, who was involved, and any evidence you have..."
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.anonymous}
                    onChange={(e) => setFormData({...formData, anonymous: e.target.checked})}
                  />
                  <label className="text-lg">Submit report anonymously</label>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-lg font-bold text-white text-xl"
                  style={{ backgroundColor: '#1A4F63' }}
                >
                  Submit Incident Report
                </button>
              </div>
            </form>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-10 text-center">
              <CheckCircle size={64} className="mx-auto mb-4" style={{ color: '#16A34A' }} />
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#16A34A' }}>Report Submitted Successfully</h2>
              <p className="text-lg mb-6" style={{ color: '#666' }}>
                Thank you for reporting this incident. Your report will be reviewed and added to the facility's risk assessment.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 rounded-lg font-bold"
                  style={{ backgroundColor: '#fff', border: '2px solid #1A4F63', color: '#1A4F63' }}
                >
                  Report Another Incident
                </button>
                <Link to="/hudvash-facility-index">
                  <button className="px-6 py-3 rounded-lg font-bold text-white" style={{ backgroundColor: '#1A4F63' }}>
                    View Facility Index
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ DASHBOARD LAYOUT ============
const DashboardLayout = ({ children }) => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="dashboard-layout">
      <TopNavbar showAuth={false} />
      <div className="flex">
        <Sidebar />
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};

// ============ DASHBOARD PAGE ============
const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (err) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const quickLinks = [
    { label: "VETShield", path: "/vetshield", icon: Shield, desc: "Document incidents" },
    { label: "Timeline", path: "/timeline", icon: Clock, desc: "View case history" },
    { label: "Documents", path: "/documents", icon: Upload, desc: "Upload records" },
    { label: "Evidence", path: "/evidence", icon: Package, desc: "Build packets" },
    { label: "Laws", path: "/laws", icon: Scale, desc: "Legal references" },
  ];

  if (loading) return <div className="p-8"><LoadingScreen /></div>;

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>Mission Control</h1>
        <p style={{ color: '#BFB8A6' }}>Welcome back, {user?.name} — VETShield Operator</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {[
          { label: "Total Incidents", value: stats?.total_incidents || 0, icon: FileText },
          { label: "Documents", value: stats?.total_documents || 0, icon: Upload },
          { label: "Evidence Packets", value: stats?.total_evidence_packets || 0, icon: Package },
        ].map((stat, i) => (
          <div key={i} className="card p-6 card-gold-border" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm uppercase tracking-wider" style={{ color: '#BFB8A6' }}>{stat.label}</p>
                <p className="text-4xl font-heading font-bold mt-1" style={{ color: '#FFCC01' }}>{stat.value}</p>
              </div>
              <stat.icon className="w-10 h-10" style={{ color: '#3a3c40' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Quick Actions</h2>
      <div className="flex flex-wrap gap-3 mb-10">
        {quickLinks.map((link, i) => (
          <Link key={i} to={link.path} className="btn-category" data-testid={`quick-${link.label.toLowerCase()}`}>
            {link.label}
          </Link>
        ))}
      </div>

      {/* Recent Activity Placeholder */}
      <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFFFFF' }}>System Status</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <Accordion title="VETShield Status" defaultOpen>
          <p>System operational. All modules active.</p>
        </Accordion>
        <Accordion title="Recent Updates">
          <p>No critical alerts at this time.</p>
        </Accordion>
      </div>
    </div>
  );
};

// ============ VETSHIELD PAGE ============
const VETShieldPage = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [formData, setFormData] = useState({ description: "", program: "", provider: "", facility: "", date_occurred: "" });

  useEffect(() => { fetchIncidents(); }, []);

  const fetchIncidents = async () => {
    try {
      const res = await axios.get(`${API}/incidents`);
      setIncidents(res.data);
    } catch (err) {
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) { toast.error("Please describe the incident"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/incidents`, formData);
      setIncidents([res.data, ...incidents]);
      setSelectedIncident(res.data);
      setFormData({ description: "", program: "", provider: "", facility: "", date_occurred: "" });
      toast.success("Incident analyzed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try {
      await axios.delete(`${API}/incidents/${id}`);
      setIncidents(incidents.filter(i => i.id !== id));
      if (selectedIncident?.id === id) setSelectedIncident(null);
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="vetshield-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2" style={{ color: '#FFCC01' }}>VETShield</h1>
        <p style={{ color: '#BFB8A6' }}>Incident Analysis & Documentation System</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div>
          <form onSubmit={handleSubmit} className="card p-6" style={{ borderColor: '#FFCC01' }}>
            <h2 className="font-heading text-xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Record Incident</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-heading uppercase tracking-wider mb-2" style={{ color: '#FFCC01' }}>Description *</label>
                <textarea data-testid="incident-description-input" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-field min-h-[150px]" placeholder="Describe what happened..." required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-heading uppercase tracking-wider mb-2" style={{ color: '#FFCC01' }}>Program</label>
                  <select data-testid="incident-program-select" value={formData.program} onChange={(e) => setFormData({...formData, program: e.target.value})} className="input-field">
                    <option value="">Select...</option>
                    <option value="HUD-VASH">HUD-VASH</option>
                    <option value="Mental Health">Mental Health</option>
                    <option value="Primary Care">Primary Care</option>
                    <option value="Disability">Disability</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-heading uppercase tracking-wider mb-2" style={{ color: '#FFCC01' }}>Date</label>
                  <input type="date" data-testid="incident-date-input" value={formData.date_occurred} onChange={(e) => setFormData({...formData, date_occurred: e.target.value})} className="input-field" />
                </div>
              </div>
            </div>
            <button type="submit" data-testid="incident-submit-btn" className="btn-command w-full mt-6 py-3" disabled={submitting}>
              {submitting ? "Analyzing..." : "Analyze & Record"}
            </button>
          </form>

          {/* Incidents List */}
          <div className="mt-6">
            <h3 className="font-heading text-lg font-bold mb-3" style={{ color: '#FFFFFF' }}>Recent ({incidents.length})</h3>
            {incidents.slice(0, 5).map((inc) => (
              <div key={inc.id} data-testid={`incident-item-${inc.id}`} className={`card p-4 mb-2 cursor-pointer ${selectedIncident?.id === inc.id ? 'border-[#FFCC01]' : ''}`} onClick={() => setSelectedIncident(inc)}>
                <div className="flex justify-between">
                  <div>
                    <p className="line-clamp-1" style={{ color: '#FFFFFF' }}>{inc.description.substring(0, 80)}...</p>
                    <div className="flex gap-2 mt-2">
                      {inc.program && <span className="badge">{inc.program}</span>}
                      <span className="text-sm" style={{ color: '#727365' }}>{new Date(inc.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(inc.id); }} className="text-gray-500 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Analysis Section */}
        <div>
          {selectedIncident ? (
            <div className="space-y-4">
              <div className="card p-6 card-gold-border">
                <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2" style={{ color: '#FFCC01' }}>
                  <Eye className="w-5 h-5" /> AI Analysis
                </h3>
                <div className="p-4 rounded" style={{ backgroundColor: '#1a1c1e' }} data-testid="ai-analysis-result">
                  {selectedIncident.ai_analysis?.summary ? (
                    <p className="text-sm whitespace-pre-wrap" style={{ color: '#BFB8A6' }}>{selectedIncident.ai_analysis.summary}</p>
                  ) : (
                    <pre className="text-sm overflow-auto" style={{ color: '#BFB8A6' }}>{JSON.stringify(selectedIncident.ai_analysis, null, 2)}</pre>
                  )}
                </div>
              </div>

              <Accordion title={`Law References (${selectedIncident.matched_laws?.length || 0})`} defaultOpen>
                {selectedIncident.matched_laws?.map((law, i) => (
                  <div key={i} className="p-3 rounded mb-2" style={{ backgroundColor: '#1a1c1e' }}>
                    <p className="font-bold" style={{ color: '#FFCC01' }}>{law.title}</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>{law.description}</p>
                  </div>
                ))}
              </Accordion>

              <Accordion title={`OIG Reports (${selectedIncident.matched_oig?.length || 0})`}>
                {selectedIncident.matched_oig?.map((oig, i) => (
                  <div key={i} className="p-3 rounded mb-2" style={{ backgroundColor: '#1a1c1e' }}>
                    <p className="font-bold" style={{ color: '#FFCC01' }}>{oig.report_number}</p>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>{oig.summary}</p>
                  </div>
                ))}
              </Accordion>
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Search className="w-16 h-16 mx-auto mb-4" style={{ color: '#3a3c40' }} />
              <p style={{ color: '#BFB8A6' }}>Select or record an incident to view analysis</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// ============ ABOUT PAGE ============
const AboutPage = () => {
  const sections = [
    {
      title: "Our Mission",
      icon: Target,
      content: "VETEngine is dedicated to empowering veterans, particularly those in HUD-VASH housing programs, with the tools and resources they need to document incidents, understand their rights, and build strong cases for advocacy.",
      color: "#FFCC01"
    },
    {
      title: "Who We Serve",
      icon: Users,
      content: "We serve formerly homeless veterans in HUD-VASH housing, disabled veterans seeking to understand their rights, and veteran advocates working to improve the system.",
      color: "#10B981"
    },
    {
      title: "The VETEngine System",
      icon: Cpu,
      content: "Our comprehensive platform includes VETShield for incident documentation, VETScan for document analysis, Legal Navigator for law references, and the Evidence Packet Generator for building advocacy cases.",
      color: "#3B82F6"
    },
    {
      title: "Why It Matters",
      icon: Heart,
      content: "Every veteran deserves to have their voice heard. By systematically documenting incidents and matching them to relevant laws and oversight reports, veterans can build compelling cases for change.",
      color: "#EC4899"
    }
  ];

  const teamValues = [
    { label: "Veteran First", desc: "Every decision prioritizes veteran wellbeing" },
    { label: "Truth & Accountability", desc: "Documentation drives systemic change" },
    { label: "Accessible Justice", desc: "Legal knowledge should be available to all" },
    { label: "Community Support", desc: "Veterans supporting veterans" },
  ];

  const resources = [
    { title: "VA ECOSYSTEM", desc: "Navigate VA structure, leadership, and departments", path: "/vetcommand", items: "13+ resources" },
    { title: "HUD-VASH ECOSYSTEM", desc: "Housing assistance programs and regulations", path: "/vetcommand", items: "13+ resources" },
    { title: "RIGHTS, LAW & OVERSIGHT", desc: "Federal and state legal protections", path: "/laws", items: "17+ laws" },
    { title: "STABILITY DEFENSE MODELS", desc: "Crisis prevention and recovery frameworks", path: "/vetcommand", items: "9+ models" },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="about-page">
      <TopNavbar showAuth={false} />
      
      {/* Video Hero Section */}
      <VideoHero 
        title="About VETDirect" 
        declaration="Empowering Veterans Through Knowledge and Community"
        videoUrl="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/hrjnakp3_vecteezy_friends-dinner-park-party-picnic-outdoors-sitting-on-grass_49696110.mp4"
      />
      
      {/* Portal Overview Section - Copied from Honor Page */}
      <section style={{ padding: '80px 10%', backgroundColor: '#0d0f0b', color: 'white', fontFamily: 'Arial' }}>
        
        {/* Yellow Badge */}
        <div style={{ marginBottom: '30px' }}>
          <span style={{ 
            background: '#ffc400', 
            color: 'black', 
            padding: '8px 20px', 
            borderRadius: '20px', 
            fontSize: '13px', 
            fontWeight: 'bold',
            letterSpacing: '0.5px'
          }}>
            ABOUT THIS PORTAL
          </span>
        </div>

        {/* Main Headline */}
        <div style={{ maxWidth: '1200px', margin: '0 auto 50px' }}>
          <h1 style={{ 
            fontSize: '42px', 
            fontWeight: 'bold', 
            marginBottom: '40px', 
            lineHeight: '1.3',
            color: '#ffffff'
          }}>
            No Fluff. No Gimmicks.<br/>
            No Startup-Bro Nonsense.
          </h1>

          {/* Two-Column Text Layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', fontSize: '16px', lineHeight: '1.8' }}>
            <div>
              <p style={{ marginBottom: '20px' }}>
                HUDVASHVeterans.com / StableVets.com is a straight-shooter, government-ready, veteran-empowered portal built for <strong>credibility, clarity, and results</strong>.
              </p>
              <p style={{ marginBottom: '20px' }}>
                This is not a lifestyle blog. This is not motivational noise. This is a <strong>field-ready resource hub</strong> designed to help HUD-VASH veterans stay housed, stabilize, recover, and win.
              </p>
              <p>
                The portal delivers practical knowledge focused on hot-water diversion and prevention strategies that actually work—the kind that reduce crises before they become evictions, hospitalizations, or system failures.
              </p>
            </div>
            <div>
              <p style={{ marginBottom: '20px' }}>
                From structured resource directories and verified contacts to recovery frameworks, accountability tools, and community-based solutions, this platform exists to <strong>replace confusion with clarity and chaos with structure</strong>.
              </p>
              <p style={{ marginBottom: '20px' }}>
                This portal signals trust, stability, authority, and service—the same standards expected by VA leadership, HUD officials, nonprofit partners, attorneys, and oversight bodies.
              </p>
              <p>
                <strong>Most importantly, it respects the intelligence, lived experience, and dignity of veterans.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Who This Portal Serves Section */}
        <div style={{ maxWidth: '1200px', margin: '80px auto' }}>
          <h2 style={{ 
            fontSize: '36px', 
            fontWeight: 'bold', 
            marginBottom: '50px', 
            textAlign: 'center',
            color: '#ffffff'
          }}>
            Who This Portal Serves
          </h2>

          {/* Three-Column Card Layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '30px' }}>
            
            {/* Card 1 - Blue */}
            <div style={{ 
              backgroundColor: '#1e3a5f', 
              borderRadius: '12px', 
              padding: '30px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
            }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: 'bold', 
                marginBottom: '25px',
                color: '#ffffff'
              }}>
                I'm a HUD-VASH Veteran
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, lineHeight: '2' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Easy-to-use housing stability guides</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Clear explanations of notices, violations, and timelines</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Tools to organize appointments and goals</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Recovery-support frameworks tied to housing success</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Veteran-first language that respects dignity</span>
                </li>
              </ul>
            </div>

            {/* Card 2 - Green */}
            <div style={{ 
              backgroundColor: '#2d5f4f', 
              borderRadius: '12px', 
              padding: '30px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
            }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: 'bold', 
                marginBottom: '25px',
                color: '#ffffff'
              }}>
                I'm a Case Manager or Advocate
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, lineHeight: '2' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Veteran-friendly tools that improve engagement</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Clear prevention frameworks to reduce crisis frequency</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Shared language for expectations and accountability</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Resources that support documentation and continuity</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Improved trust between veterans and providers</span>
                </li>
              </ul>
            </div>

            {/* Card 3 - Red */}
            <div style={{ 
              backgroundColor: '#5f2d2d', 
              borderRadius: '12px', 
              padding: '30px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
            }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: 'bold', 
                marginBottom: '25px',
                color: '#ffffff'
              }}>
                I'm Facing a Crisis Right Now
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, lineHeight: '2' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Immediate guidance during high-stress moments</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Clear next steps to prevent eviction or relapse</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Tools to organize communication and documentation</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Focus on de-escalation and stabilization</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ color: '#ffc400', marginRight: '10px', fontSize: '18px' }}>✓</span>
                  <span>Reduces panic-driven decisions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Quote Section */}
        <div style={{ 
          maxWidth: '1200px', 
          margin: '60px auto 0', 
          backgroundColor: '#1a2332', 
          padding: '50px 60px', 
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: '#ffc400', 
            marginBottom: '20px',
            lineHeight: '1.4',
            fontStyle: 'italic'
          }}>
            "Stability isn't about motivation. It's about having the right structure before things fall apart."
          </p>
          <p style={{ fontSize: '16px', color: '#b8b8b8', lineHeight: '1.6' }}>
            Built by veterans. Designed for reality. Focused on prevention, stability, and staying housed.
          </p>
        </div>
      </section>
      
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {sections.map((section, i) => (
            <div key={i} className="rounded-lg p-6" style={{ backgroundColor: '#2F372F', borderLeft: `4px solid ${section.color}` }}>
              <div className="flex items-center gap-3 mb-4">
                <section.icon className="w-8 h-8" style={{ color: section.color }} />
                <h2 className="font-heading text-2xl font-bold" style={{ color: '#FFFFFF' }}>{section.title}</h2>
              </div>
              <p style={{ color: '#BFB8A6' }}>{section.content}</p>
            </div>
          ))}
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="font-heading text-3xl font-bold text-center mb-8" style={{ color: '#FFCC01' }}>Our Core Values</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {teamValues.map((value, i) => (
              <div key={i} className="text-center p-6 rounded-lg" style={{ backgroundColor: '#2F372F' }}>
                <CheckCircle className="w-10 h-10 mx-auto mb-3" style={{ color: '#FFCC01' }} />
                <h3 className="font-heading font-bold mb-2" style={{ color: '#FFFFFF' }}>{value.label}</h3>
                <p className="text-sm" style={{ color: '#BFB8A6' }}>{value.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Resource Sections */}
        <div className="mb-16">
          <h2 className="font-heading text-3xl font-bold text-center mb-8" style={{ color: '#FFCC01' }}>Explore Our Resources</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {resources.map((resource, i) => (
              <Link 
                key={i} 
                to={resource.path}
                className="rounded-lg p-6 transition-all hover:border-[#FFCC01]" 
                style={{ backgroundColor: '#2F372F', border: '2px solid #727365' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-xl font-bold" style={{ color: '#FFFFFF' }}>{resource.title}</h3>
                  <span className="badge">{resource.items}</span>
                </div>
                <p className="mb-4" style={{ color: '#BFB8A6' }}>{resource.desc}</p>
                <span className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-wider" style={{ color: '#FFCC01' }}>
                  Explore <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center p-8 rounded-lg" style={{ backgroundColor: '#2F372F', border: '2px solid #FFCC01' }}>
          <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#FFCC01' }}>Ready to Get Started?</h2>
          <p className="mb-6 max-w-2xl mx-auto" style={{ color: '#BFB8A6' }}>
            Join thousands of veterans using VETEngine to document their experiences, understand their rights, and advocate for change.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/register" className="px-8 py-3 rounded font-heading font-bold uppercase tracking-wider transition-all hover:opacity-90" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
              Create Account
            </Link>
            <Link to="/vetcommand" className="px-8 py-3 rounded font-heading font-bold uppercase tracking-wider transition-all hover:opacity-90" style={{ backgroundColor: '#221F20', color: '#FFCC01', border: '2px solid #FFCC01' }}>
              Explore VETCommand
            </Link>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-16 pt-8 text-center" style={{ borderTop: '1px solid #727365' }}>
          <p className="text-sm" style={{ color: '#727365' }}>
            USVETERANSASSOCIATION.COM • VETERANSADVISORYCOUNCIL.COM • VETERANADVOCACY.VIP
          </p>
        </div>
      </main>
    </div>
  );
};

// ============ MISSION PAGE ============
const MissionPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="mission-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.mission.title} 
        declaration={PAGE_CONTENT.mission.declaration}
        videoUrl={PAGE_CONTENT.mission.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #FFCC01' }}>
            <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>Our Purpose</h2>
            <p style={{ color: '#BFB8A6' }}>To provide veterans with the tools, resources, and support needed to navigate complex systems, document their experiences, and advocate for meaningful change.</p>
          </div>
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #10B981' }}>
            <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#10B981' }}>Our Commitment</h2>
            <p style={{ color: '#BFB8A6' }}>We stand with every veteran who has served, ensuring their voice is heard and their rights are protected through comprehensive documentation and legal resources.</p>
          </div>
        </div>
        <div className="mt-8 text-center">
          <Link to="/vetcommand" className="btn-category">Explore VETCommand</Link>
        </div>
      </main>
    </div>
  );
};

// ============ FRONTIER PAGE ============
const FrontierPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="frontier-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.frontier.title} 
        declaration={PAGE_CONTENT.frontier.declaration}
        videoUrl={PAGE_CONTENT.frontier.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Digital Innovation", desc: "Leveraging technology to streamline veteran services and documentation." },
            { title: "System Reform", desc: "Advocating for improvements in VA and HUD-VASH programs." },
            { title: "Community Building", desc: "Connecting veterans with resources and each other." }
          ].map((item, i) => (
            <div key={i} className="rounded-lg p-6 text-center" style={{ backgroundColor: '#2F372F' }}>
              <h3 className="font-heading text-xl font-bold mb-3" style={{ color: '#FFCC01' }}>{item.title}</h3>
              <p style={{ color: '#BFB8A6' }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// ============ PTSD PAGE ============
const PTSDPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="ptsd-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.ptsd.title} 
        declaration={PAGE_CONTENT.ptsd.declaration}
        videoUrl={PAGE_CONTENT.ptsd.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="rounded-lg p-8 mb-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #EC4899' }}>
          <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>Resources & Support</h2>
          <p className="mb-4" style={{ color: '#BFB8A6' }}>Understanding PTSD is the first step toward healing. Our platform provides resources, documentation tools, and connections to support services.</p>
          <div className="flex flex-wrap gap-3">
            <span className="emergency-box emergency-988">988</span>
            <span className="text-sm" style={{ color: '#BFB8A6' }}>Suicide & Crisis Lifeline</span>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-lg p-6" style={{ backgroundColor: '#2F372F' }}>
            <h3 className="font-heading text-lg font-bold mb-3" style={{ color: '#FFCC01' }}>VA Mental Health Services</h3>
            <p style={{ color: '#BFB8A6' }}>Information about VA mental health programs, counseling, and PTSD treatment options.</p>
          </div>
          <div className="rounded-lg p-6" style={{ backgroundColor: '#2F372F' }}>
            <h3 className="font-heading text-lg font-bold mb-3" style={{ color: '#FFCC01' }}>Peer Support</h3>
            <p style={{ color: '#BFB8A6' }}>Connect with fellow veterans who understand the journey and can offer support.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============ JARGON PAGE ============
const JargonPage = () => {
  const terms = [
    { term: "HUD-VASH", def: "Housing and Urban Development - Veterans Affairs Supportive Housing" },
    { term: "VISN", def: "Veterans Integrated Service Network" },
    { term: "VHA", def: "Veterans Health Administration" },
    { term: "OIG", def: "Office of Inspector General" },
    { term: "SSVF", def: "Supportive Services for Veteran Families" },
    { term: "VJO", def: "Veterans Justice Outreach" },
  ];
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="jargon-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.jargon.title} 
        declaration={PAGE_CONTENT.jargon.declaration}
        videoUrl={PAGE_CONTENT.jargon.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-4">
          {terms.map((item, i) => (
            <div key={i} className="rounded-lg p-4" style={{ backgroundColor: '#2F372F', borderLeft: '3px solid #FFCC01' }}>
              <span className="font-heading font-bold" style={{ color: '#FFCC01' }}>{item.term}</span>
              <span style={{ color: '#BFB8A6' }}> — {item.def}</span>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/vetcommand" className="btn-category">View Full Glossary</Link>
        </div>
      </main>
    </div>
  );
};

// ============ CREATIVE EXPRESSION PAGE ============
const CreativeExpressionPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="creative-expression-page">
      <TopNavbar showAuth={false} />
      
      {/* Hero Image Section */}
      <div className="relative w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/ywdfxd3q_image.png"
          alt="Creative Expression - A Journey of Self-Discovery"
          className="w-full h-auto"
          style={{ maxHeight: '800px', objectFit: 'cover' }}
        />
      </div>
      
      {/* Content Section */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#FFCC01', fontFamily: "'Arial', sans-serif" }}>
            Creative Expression & Wellness
          </h1>
          <p className="text-xl leading-relaxed max-w-4xl mx-auto" style={{ color: '#BFB8A6' }}>
            Not just a magazine — a journey of self-discovery and creative expression designed specifically for veterans.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-12">
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #FFCC01' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>Our Mission</h3>
            <p className="text-lg leading-relaxed" style={{ color: '#BFB8A6' }}>
              Through creative arts, graphics design, writing, and journaling, we promote healing and recovery. 
              This platform honors, uplifts, and amplifies the authentic voices of veterans.
            </p>
          </div>

          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #10B981' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#10B981' }}>Get Involved</h3>
            <p className="text-lg leading-relaxed mb-6" style={{ color: '#BFB8A6' }}>
              Share your story, showcase your art, or contribute your voice to the Veterans Magazine. 
              This is a movement built on creative expression, advocacy, and connection.
            </p>
            <button className="px-6 py-3 rounded-lg font-bold transition-transform hover:scale-105" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>
              Submit Your Story
            </button>
          </div>
        </div>

        <div className="mt-16 p-10 rounded-lg text-center" style={{ backgroundColor: '#2F372F', border: '3px solid #FFCC01' }}>
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
            Veterans Creative Mind & Tech Institute
          </h2>
          <p className="text-xl mb-6" style={{ color: '#BFB8A6' }}>
            Empowering veterans through art, technology, and holistic wellness
          </p>
          <div className="grid md:grid-cols-4 gap-6 mt-8">
            <div className="p-4">
              <div className="text-4xl mb-3">💻</div>
              <h4 className="font-bold mb-2" style={{ color: '#FFCC01' }}>20-Seat Learning Facility</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>State-of-the-art computer and graphics design center</p>
            </div>
            <div className="p-4">
              <div className="text-4xl mb-3">🎨</div>
              <h4 className="font-bold mb-2" style={{ color: '#FFCC01' }}>Creative Expression</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Arts, music, and graphic design programs</p>
            </div>
            <div className="p-4">
              <div className="text-4xl mb-3">📚</div>
              <h4 className="font-bold mb-2" style={{ color: '#FFCC01' }}>Education & Training</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Advanced skills development and certification</p>
            </div>
            <div className="p-4">
              <div className="text-4xl mb-3">🧘</div>
              <h4 className="font-bold mb-2" style={{ color: '#FFCC01' }}>Holistic Wellness</h4>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Integrating VA whole health programs</p>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// ============ HONOR PAGE ============
const HonorPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const handleGetStarted = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('submissionForm')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen">
      <TopNavbar showAuth={false} />
      
      {/* HONOR SECTION - NEW STRUCTURE */}
      <section id="honoring-service" style={{ padding: '80px 10%', backgroundColor: '#0d0f0b', color: 'white', fontFamily: 'Arial' }}>
        
        {/* Veteran Logo - Centered at Top - 2.5x Bigger */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/ua04uyt3_ChatGPT%20Image%20Mar%2011%2C%202026%2C%2005_04_51%20AM.png" 
            alt="Veteran - Honor All Who Served" 
            style={{ 
              height: '400px', 
              width: 'auto',
              display: 'inline-block'
            }}
          />
        </div>

        {/* HONORING YOUR SERVICE Section - Two-Column Layout */}
        <div style={{ maxWidth: '1200px', margin: '0 auto 50px' }}>
          <h1 style={{ 
            fontSize: '42px', 
            fontWeight: 'bold', 
            marginBottom: '40px', 
            lineHeight: '1.3',
            color: '#ffc400',
            textAlign: 'center'
          }}>
            HONORING YOUR SERVICE
          </h1>

          {/* Two-Column Text Layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', fontSize: '16px', lineHeight: '1.8' }}>
            <div>
              <p style={{ marginBottom: '20px' }}>
                Every veteran has a story. Some stories were lived in combat. Others were served quietly in support roles, on ships, in aircraft, on bases, and in places most Americans will never see.
              </p>
              <p style={{ marginBottom: '20px' }}>
                Service matters. Sacrifice matters. And the stories of those who served should never disappear.
              </p>
              <p style={{ marginBottom: '20px' }}>
                This page exists to preserve and honor the legacy of veterans — past and present.
              </p>
              <p>
                If you served in the United States military, we invite you to share your story. If you are in HUD-VASH Housing, have a loved one, a friend, grandparents, a parent, a family member, or a battle buddy who served beside you, we welcome their story as well. You can submit their stories on their behalf.
              </p>
            </div>
            <div>
              <p style={{ marginBottom: '20px' }}>
                Our team will build a full-page tribute dedicated to their service — <strong>completely free</strong>.
              </p>
              <p style={{ marginBottom: '20px' }}>
                <strong>Each tribute page will include:</strong>
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                <li>• Name, Rank, Branch of Service</li>
                <li>• Years of Service</li>
                <li>• Military Occupational Specialty or Role</li>
                <li>• Tours of Duty / Deployments</li>
                <li>• Personal Story or Reflections</li>
                <li>• Photos from service and life</li>
                <li>• Honors, awards, or memorable moments</li>
              </ul>
              <p style={{ marginBottom: '20px' }}>
                These pages become part of a growing digital archive honoring the men and women who defended our nation.
              </p>
              <p>
                <strong>No cost. No catch. Just recognition.</strong>
              </p>
            </div>
          </div>
          
          {/* CTA Button */}
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <button 
              onClick={handleGetStarted}
              style={{ 
                background: '#ffc400', 
                color: 'black', 
                padding: '14px 40px', 
                fontWeight: 'bold', 
                border: 'none', 
                cursor: 'pointer', 
                borderRadius: '6px',
                fontSize: '16px'
              }}
            >
              GET STARTED — SUBMIT A VETERAN STORY
            </button>
          </div>
        </div>

        {/* 8 Status Boxes Grid - 4 per row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginTop: '60px', maxWidth: '1200px', margin: '60px auto 0' }}>
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            I Am a Veteran<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Share your personal story of military service.</span>
          </div>
          
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            Honoring a Veteran<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Submit a tribute for someone who served.</span>
          </div>
          
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            Combat Veteran Story<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Share experiences from combat deployments.</span>
          </div>
          
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            Family Tribute<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Honor a parent, grandparent, or loved one who served.</span>
          </div>
          
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            Battle Buddy Tribute<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Recognize a fellow service member you served with.</span>
          </div>
          
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            Gold Star Tribute<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Honor the memory of a fallen service member.</span>
          </div>
          
          <div style={{ background: '#ffc400', color: 'black', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
            Disabled Veteran Story<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Highlight the resilience and journey of wounded warriors.</span>
          </div>
          
          <div style={{ background: '#2e2e2e', padding: '30px', textAlign: 'center', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#ffc400'; e.currentTarget.style.color = 'black'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = '#2e2e2e'; e.currentTarget.style.color = 'white'; }}>
            Preserve Military Legacy<br/><span style={{ fontSize: '12px', fontWeight: 'normal' }}>Submit historical stories, photos, or records of service.</span>
          </div>
        </div>

        {/* Submission Form */}
        {showForm && (
          <div id="submissionForm" style={{ marginTop: '60px', maxWidth: '800px', margin: '60px auto 0' }}>
            <h3 style={{ fontSize: '28px', marginBottom: '20px', color: '#ffc400' }}>Submit Veteran Story</h3>
            
            <p style={{ marginBottom: '20px', lineHeight: '1.7' }}>
              Tell us their story. Submit a short 1–2 page description of the veteran's life and service.
              You may include as many photos as you would like.
            </p>
            
            <form action="mailto:rwhveteran@gmail.com" method="post" encType="text/plain">
              <input 
                type="text" 
                name="fullName"
                placeholder="Full Name" 
                required 
                style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none' }}
              />
              
              <input 
                type="text" 
                name="rank"
                placeholder="Rank" 
                style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none' }}
              />
              
              <select 
                name="branch"
                style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none' }}
              >
                <option>Branch of Service</option>
                <option>Army</option>
                <option>Navy</option>
                <option>Marine Corps</option>
                <option>Air Force</option>
                <option>Coast Guard</option>
                <option>Space Force</option>
              </select>
              
              <input 
                type="text" 
                name="yearsOfService"
                placeholder="Years of Service (e.g., 2004 – 2012)" 
                style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none' }}
              />
              
              <input 
                type="text" 
                name="tours"
                placeholder="Tours of Duty / Deployments" 
                style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none' }}
              />
              
              <textarea 
                name="story"
                placeholder="Tell the Veteran's Story (1-2 pages recommended)" 
                style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none', height: '150px' }}
              />
              
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Upload Photos:</label>
                <input 
                  type="file" 
                  multiple 
                  accept="image/*"
                  style={{ width: '100%', padding: '12px', margin: '10px 0', borderRadius: '5px', border: 'none', backgroundColor: 'white' }}
                />
              </div>
              
              <button 
                type="submit"
                style={{ background: '#ffc400', border: 'none', padding: '14px 40px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '6px', marginTop: '10px', width: '100%' }}
              >
                Submit Tribute
              </button>
            </form>
            
            <p style={{ marginTop: '20px', fontSize: '14px', color: '#ccc', textAlign: 'center' }}>
              All submissions are reviewed personally. After submitting, we will contact you to gather any additional information needed.
            </p>
          </div>
        )}

        {/* Profile Preview - Example - With Border */}
        <div style={{ 
          marginTop: '80px',
          maxWidth: '1200px',
          margin: '80px auto 0',
          padding: '50px',
          backgroundColor: '#1a1c1e',
          border: '3px solid #ffc400',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(255, 204, 1, 0.2)'
        }}>
          <h2 style={{ fontSize: '32px', color: '#ffc400', marginBottom: '30px', textAlign: 'center' }}>
            Example Veteran Tribute Profile
          </h2>
          
          <div style={{ display: 'flex', gap: '40px', marginTop: '30px', flexWrap: 'wrap' }}>
            <img 
              src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&h=350&fit=crop" 
              alt="Veteran"
              style={{ width: '400px', borderRadius: '6px', objectFit: 'cover' }}
            />
            
            <div style={{ flex: 1, minWidth: '300px' }}>
              <h3 style={{ fontSize: '28px', marginBottom: '10px' }}>Staff Sergeant Michael Anderson</h3>
              <p><strong>Branch:</strong> United States Army</p>
              <p><strong>Years Served:</strong> 2004 – 2012</p>
              <p><strong>MOS:</strong> Infantryman (11B)</p>
              <p><strong>Deployments:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>• Iraq (2005–2006)</li>
                <li>• Afghanistan (2009–2010)</li>
              </ul>
              <p><strong>Awards:</strong></p>
              <ul style={{ paddingLeft: '20px' }}>
                <li>• Bronze Star</li>
                <li>• Army Commendation Medal</li>
                <li>• Combat Infantryman Badge</li>
              </ul>
              
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/US_Army_logo.svg/200px-US_Army_logo.svg.png" 
                alt="Army Logo"
                style={{ width: '120px', marginTop: '10px' }}
              />
            </div>
          </div>
          
          <div style={{ marginTop: '40px', lineHeight: '1.8' }}>
            <h3 style={{ fontSize: '24px', color: '#ffc400', marginBottom: '15px' }}>Their Story</h3>
            
            <p style={{ marginBottom: '15px' }}>
              Michael Anderson enlisted in the United States Army at the age of 19 following the events of September 11th. 
              Inspired by a desire to protect his country and serve alongside others who shared that commitment, he began his 
              military career at Fort Benning, Georgia.
            </p>
            
            <p style={{ marginBottom: '15px' }}>
              During his eight years of service, he deployed twice in support of Operation Iraqi Freedom and Operation Enduring Freedom. 
              Known among his fellow soldiers for his leadership and loyalty, Anderson served as a squad leader responsible for the 
              safety and training of younger soldiers.
            </p>
            
            <p>
              His service represents the courage and sacrifice shared by countless veterans who answered the call to defend our nation.
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px', marginTop: '30px' }}>
            <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=300&h=200&fit=crop" alt="Service" style={{ width: '100%', borderRadius: '6px' }} />
            <img src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=300&h=200&fit=crop" alt="Service" style={{ width: '100%', borderRadius: '6px' }} />
            <img src="https://images.unsplash.com/photo-1541872703-74c36f90c9d?w=300&h=200&fit=crop" alt="Service" style={{ width: '100%', borderRadius: '6px' }} />
            <img src="https://images.unsplash.com/photo-1579373903781-fd5c0c30c4cd?w=300&h=200&fit=crop" alt="Service" style={{ width: '100%', borderRadius: '6px' }} />
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '60px', padding: '30px', borderTop: '1px solid #444' }}>
            <p style={{ fontSize: '18px', fontStyle: 'italic', lineHeight: '1.8' }}>
              "Service to our nation is a legacy that lives beyond uniform and years of duty.<br/>
              We honor and remember those who stepped forward when the nation called."
            </p>
          </div>
        </div>

      </section>
      <Footer />
    </div>
  );
};

// ============ USVA PAGE ============
const USVAPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0E27' }} data-testid="usva-page">
      <TopNavbar showAuth={false} />
      
      {/* Hero Section with SIMPLE Cinematic Video Zoom - People to Flag */}
      <div className="relative w-full h-screen overflow-hidden bg-black">
        {/* Direct MP4 Video Background - Simple 3-Part Zoom */}
        <style>{`
          @keyframes peopleToFlag {
            0% {
              transform: scale(2.5) translateY(15%);
              transform-origin: center 65%;
            }
            50% {
              transform: scale(1.0) translateY(0%);
              transform-origin: center center;
            }
            100% {
              transform: scale(2.5) translateY(15%);
              transform-origin: center 65%;
            }
          }
          
          .simple-zoom-video {
            animation: peopleToFlag 20s ease-in-out infinite;
          }
        `}</style>
        
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover simple-zoom-video"
          style={{ 
            filter: 'brightness(0.88) contrast(1.12)',
            zIndex: 0
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/ninmw7h4_God%20bless%20the%20USA%21%F0%9F%AB%A1%F0%9F%87%BA%F0%9F%87%B8.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Gradient Overlay for logo visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-black/65" style={{ zIndex: 1 }} />
        
        {/* USVA Logo - Centered and Large (20% smaller) */}
        <div className="relative flex flex-col items-center justify-center h-full px-4" style={{ zIndex: 10 }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/iu5o2yjk_Untitled-1.png"
            alt="USVA - United States Veterans Administration"
            className="w-full"
            style={{ 
              filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.95)) brightness(1.2) contrast(1.15)',
              maxWidth: '33.6rem',
              maxHeight: '32vh',
              objectFit: 'contain',
              marginBottom: '2rem'
            }}
          />
          
          {/* Typewriter Text Below Logo */}
          <div className="text-center px-4 max-w-5xl">
            <p className="usva-typewriter text-4xl md:text-5xl font-bold leading-relaxed mb-4" style={{ 
              color: '#FFFFFF', 
              textShadow: '0 4px 12px rgba(0,0,0,0.95), 0 2px 8px rgba(0,0,0,0.8)',
              fontFamily: "'Stint Ultra Condensed', 'Impact', sans-serif",
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}>
              UNITED STATES VETERANS ASSOCIATION
            </p>
            <p style={{ 
              color: '#FFFFFF', 
              fontFamily: "'Stint Ultra Condensed', 'Impact', sans-serif",
              letterSpacing: '0.15em',
              fontWeight: 'bold',
              textShadow: '0 8px 20px rgba(0,0,0,0.95), 0 4px 12px rgba(0,0,0,0.8)',
              margin: '30px 0 0 0',
              fontSize: '24px',
              lineHeight: '1.4',
              textTransform: 'uppercase'
            }}>
              Protecting the Formation of Veteran's Advisory Council's to insure American<br/>
              Veterans Civil, Disability, & Patient Bill of Right's are Secured.
            </p>
          </div>
        </div>

        {/* Patriotic Quote - Moved closer to logo with Typewriter Effect */}
        <div className="absolute top-0 left-0 right-0 text-center px-4" style={{ 
          zIndex: 9,
          marginTop: 'calc(50vh - 280px)'
        }}>
          <p className="usva-typewriter text-2xl md:text-3xl font-bold tracking-widest" style={{ 
            color: '#FFFFFF', 
            textShadow: '0 8px 20px rgba(0,0,0,0.95), 0 4px 12px rgba(0,0,0,0.8)',
            letterSpacing: '0.18em',
            fontFamily: "'Arial', sans-serif"
          }}>
            "A NATION THAT HONORS ITS HEROES"
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce" style={{ zIndex: 10 }}>
          <div className="flex flex-col items-center gap-3">
            <span className="text-sm uppercase tracking-widest font-bold" style={{ 
              color: '#FFCC01', 
              textShadow: '0 3px 10px rgba(0,0,0,0.95)',
              letterSpacing: '0.25em'
            }}>
              SCROLL TO EXPLORE
            </span>
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 204, 1, 0.25)' }}>
              <ChevronRight 
                size={32} 
                style={{ 
                  color: '#FFCC01', 
                  transform: 'rotate(90deg)',
                  filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.9))'
                }} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - Professional Design */}
      <main className="max-w-6xl mx-auto px-8 py-20" style={{ backgroundColor: '#0A0A0A' }}>
        
        {/* Header Section */}
        <div className="mb-20">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#FFFFFF', letterSpacing: '0.02em' }}>
            United States Veterans Association (USVA)
          </h1>
          <p className="text-lg md:text-xl mb-12" style={{ color: '#B0B0B0', fontWeight: '300' }}>
            Veterans Supporting Veterans Through Advocacy, Education, and Innovation
          </p>
          
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              The United States Veterans Association (USVA) was created with a clear purpose: to build a veteran-led organization dedicated to strengthening the lives of veterans through support, knowledge, technology, and community.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              USVA is built on a simple but powerful principle:
            </p>
            <p className="text-xl font-semibold mb-4" style={{ color: '#FFFFFF' }}>
              Veterans understand veterans.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#D0D0D0' }}>
              Those who served carry shared experiences, values, and challenges that few outside the military truly understand. When veterans come together to support one another, solutions become stronger, trust grows deeper, and communities become more resilient.
            </p>
          </div>
        </div>

        {/* A Veteran-Only Support Network */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            A Veteran-Only Support Network
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              USVA is designed as a veteran-led organization focused on helping fellow veterans navigate the challenges many face after service, including housing instability, health challenges, and rebuilding purpose in civilian life.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              Our work focuses particularly on veterans living in supportive housing programs such as HUD-VASH, where stable housing creates the foundation for recovery and rebuilding.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              But stability requires more than housing alone.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#D0D0D0' }}>
              It requires knowledge, structure, community, and the tools needed to move forward with confidence. USVA helps provide those tools.
            </p>
          </div>
        </div>

        {/* Advocacy for Veteran Rights */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Advocacy for Veteran Rights
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              One of the central roles of the United States Veterans Association is advocacy. Veterans often face complex systems involving housing programs, benefits, legal procedures, and administrative processes. Without clear guidance, these systems can feel confusing and overwhelming.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              USVA works to ensure veterans understand their rights, responsibilities, and available protections by providing:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Advocacy resources designed to empower veterans</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Education about housing policies and veteran programs</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Information that helps veterans understand processes and procedures</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Support focused on fairness, transparency, and due process</span>
              </li>
            </ul>
            <p className="text-base leading-relaxed italic" style={{ color: '#B0B0B0' }}>
              Advocacy is about helping veterans stand informed, prepared, and confident when navigating the systems that affect their lives.
            </p>
          </div>
        </div>

        {/* Education and Life Skills Development */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Education and Life Skills Development
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              Rebuilding stability requires more than access to services. It requires structure, daily habits, and the skills needed to maintain long-term independence.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              USVA provides a comprehensive <strong style={{ color: '#FFFFFF' }}>52-page Life Skills Curriculum</strong> designed specifically for veterans living in supportive housing environments such as HUD-VASH.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              This curriculum focuses on helping veterans rebuild the foundations that support long-term success, including:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Daily structure and routines</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Personal responsibility and goal setting</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Health and wellness habits</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Financial awareness and stability</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Communication and relationship rebuilding</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Personal growth and purpose development</span>
              </li>
            </ul>
            <p className="text-base leading-relaxed italic" style={{ color: '#B0B0B0' }}>
              The goal is to help veterans transition from simply surviving to building strong, independent lives.
            </p>
          </div>
        </div>

        {/* Prevention, Crisis Diversion, and Housing Stability */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Prevention, Crisis Diversion, and Housing Stability
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              Many housing challenges can be prevented when veterans receive early guidance and support. USVA promotes proactive strategies designed to help veterans avoid housing crises, eviction risks, and administrative misunderstandings before they escalate.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              These efforts focus on:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Housing stability education</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Early problem identification and resolution</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Crisis diversion strategies</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Support resources that help veterans remain successfully housed</span>
              </li>
            </ul>
            <p className="text-base leading-relaxed italic" style={{ color: '#B0B0B0' }}>
              Prevention is one of the most powerful tools available to protect veterans from returning to homelessness.
            </p>
          </div>
        </div>

        {/* Innovation and Technology for Veterans */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Innovation and Technology for Veterans
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              In addition to advocacy and education, the United States Veterans Association is developing innovative technology designed to assist veterans in navigating complex housing and legal systems. These tools aim to simplify processes that can often feel confusing or intimidating.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              One example includes <strong style={{ color: '#FFFFFF' }}>document review technology</strong> that helps veterans better understand housing or legal paperwork. Veterans can upload or scan documents such as housing notices or eviction paperwork, and the system can generate simplified summaries that explain the document's purpose and key information.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              The goal is to help veterans better understand their situation and prepare informed responses when needed. These tools are designed to promote clarity, organization, and awareness—helping veterans feel more confident when dealing with important paperwork or housing matters.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#D0D0D0' }}>
              Additional technology initiatives focus on improving access to resources, organizing personal information, and helping veterans stay connected with the support systems available to them.
            </p>
          </div>
        </div>

        {/* Building a Stronger Veteran Community */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Building a Stronger Veteran Community
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              USVA is more than an organization—it is a growing community built on shared service, mutual respect, and the belief that veterans should never have to face challenges alone.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              Through advocacy, education, and innovative technology, the United States Veterans Association aims to strengthen the foundation that helps veterans rebuild their lives after service.
            </p>
            <div className="my-8 py-6 px-8" style={{ backgroundColor: '#1A1A1A', borderLeft: '4px solid #FFCC01' }}>
              <p className="text-lg leading-relaxed mb-2" style={{ color: '#FFFFFF' }}>
                A home creates safety.
              </p>
              <p className="text-lg leading-relaxed mb-2" style={{ color: '#FFFFFF' }}>
                Knowledge creates confidence.
              </p>
              <p className="text-lg leading-relaxed mb-2" style={{ color: '#FFFFFF' }}>
                Community creates strength.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: '#FFFFFF' }}>
                And when veterans support one another, the path forward becomes clearer for everyone.
              </p>
            </div>
          </div>
        </div>

        {/* Learn More Section */}
        <div className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Learn More
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              This page provides an introduction to the mission and services of the United States Veterans Association.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              Additional pages throughout this site provide detailed information about:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Advocacy initiatives</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Veteran education programs</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Housing stability resources</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Life skills training</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Technology tools designed to support veterans</span>
              </li>
            </ul>
            <p className="text-base leading-relaxed mt-6 italic" style={{ color: '#B0B0B0' }}>
              Together, these efforts form a comprehensive platform dedicated to supporting veterans as they rebuild stability, purpose, and opportunity.
            </p>
          </div>
        </div>

      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// ============ HUD-VASH HERO PAGE ============
const HUDVASHPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="hudvash-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.hudvash.title} 
        declaration={PAGE_CONTENT.hudvash.declaration}
        videoUrl={PAGE_CONTENT.hudvash.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Program Overview", desc: "Understand HUD-VASH eligibility, benefits, and how the program works." },
            { title: "Your Rights", desc: "Know your rights as a HUD-VASH participant and housing regulations." },
            { title: "Support Services", desc: "Case management, healthcare, and supportive services available to you." }
          ].map((item, i) => (
            <div key={i} className="rounded-lg p-6" style={{ backgroundColor: '#2F372F', borderTop: '3px solid #FFCC01' }}>
              <h3 className="font-heading text-lg font-bold mb-3" style={{ color: '#FFCC01' }}>{item.title}</h3>
              <p style={{ color: '#BFB8A6' }}>{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/vetcommand" className="btn-category">Explore HUD-VASH Resources</Link>
        </div>
      </main>
    </div>
  );
};

// ============ VETLEGAL HERO PAGE ============
const VETLegalPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="vetlegal-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.vetlegal.title} 
        declaration={PAGE_CONTENT.vetlegal.declaration}
        videoUrl={PAGE_CONTENT.vetlegal.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #3B82F6' }}>
            <Scale className="w-10 h-10 mb-4" style={{ color: '#3B82F6' }} />
            <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>Federal Protections</h2>
            <p style={{ color: '#BFB8A6' }}>ADA, Section 504, Fair Housing Act, and constitutional rights that protect veterans.</p>
            <Link to="/laws" className="inline-block mt-4 text-sm font-heading uppercase tracking-wider" style={{ color: '#FFCC01' }}>View Laws →</Link>
          </div>
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #10B981' }}>
            <Gavel className="w-10 h-10 mb-4" style={{ color: '#10B981' }} />
            <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>State Laws</h2>
            <p style={{ color: '#BFB8A6' }}>California tenant protections, eviction procedures, and local regulations.</p>
            <Link to="/laws" className="inline-block mt-4 text-sm font-heading uppercase tracking-wider" style={{ color: '#FFCC01' }}>View Laws →</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============ VETSCAN HERO PAGE ============
const VETScanPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="vetscan-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.vetscan.title} 
        declaration={PAGE_CONTENT.vetscan.declaration}
        videoUrl={PAGE_CONTENT.vetscan.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Document Upload", desc: "Upload PDFs, images, and documents for analysis.", icon: Upload },
            { title: "OCR Processing", desc: "Automatic text extraction from scanned documents.", icon: FileText },
            { title: "Evidence Building", desc: "Compile documents into evidence packets.", icon: Package }
          ].map((item, i) => (
            <div key={i} className="rounded-lg p-6 text-center" style={{ backgroundColor: '#2F372F' }}>
              <item.icon className="w-12 h-12 mx-auto mb-4" style={{ color: '#FFCC01' }} />
              <h3 className="font-heading text-lg font-bold mb-3" style={{ color: '#FFCC01' }}>{item.title}</h3>
              <p style={{ color: '#BFB8A6' }}>{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/documents" className="btn-category">Start Scanning</Link>
        </div>
      </main>
    </div>
  );
};

// ============ HPACT PAGE ============
const HPACTPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="hpact-page">
      <TopNavbar showAuth={false} />
      <VideoHero 
        title={PAGE_CONTENT.hpact.title} 
        declaration={PAGE_CONTENT.hpact.declaration}
        videoUrl={PAGE_CONTENT.hpact.video}
      />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #10B981' }}>
            <Heart className="w-10 h-10 mb-4" style={{ color: '#10B981' }} />
            <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>Comprehensive Care</h2>
            <p style={{ color: '#BFB8A6' }}>HPACT teams provide integrated healthcare services including primary care, mental health support, substance abuse treatment, and case management for homeless veterans.</p>
          </div>
          <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderLeft: '4px solid #3B82F6' }}>
            <Building className="w-10 h-10 mb-4" style={{ color: '#3B82F6' }} />
            <h2 className="font-heading text-2xl font-bold mb-4" style={{ color: '#FFCC01' }}>Housing Stability</h2>
            <p style={{ color: '#BFB8A6' }}>Working with HUD-VASH and other programs, HPACT teams help veterans secure stable housing while addressing medical and social needs.</p>
          </div>
        </div>
        
        <div className="rounded-lg p-8" style={{ backgroundColor: '#2F372F', borderTop: '3px solid #FFCC01' }}>
          <h2 className="font-heading text-2xl font-bold mb-6 text-center" style={{ color: '#FFCC01' }}>HPACT Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Primary Care", desc: "Medical exams, chronic disease management, preventive care" },
              { title: "Mental Health", desc: "PTSD treatment, counseling, psychiatric services" },
              { title: "Case Management", desc: "Benefits coordination, housing assistance, social services" },
              { title: "Substance Abuse", desc: "Addiction treatment and recovery support programs" },
              { title: "Outreach", desc: "Street outreach to unsheltered veterans" },
              { title: "Peer Support", desc: "Veteran-to-veteran mentoring and guidance" }
            ].map((item, i) => (
              <div key={i} className="p-4 rounded" style={{ backgroundColor: '#221F20' }}>
                <h3 className="font-heading font-bold mb-2" style={{ color: '#FFCC01' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#BFB8A6' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/vetcommand" className="btn-category">Explore HPACT Resources</Link>
        </div>
      </main>
    </div>
  );
};

// ============ TIMELINE PAGE ============
const TimelinePage = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/incidents`).then(res => setIncidents(res.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 md:p-10" data-testid="timeline-page">
      <h1 className="font-heading text-4xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Case Timeline</h1>
      
      {loading ? <LoadingScreen /> : incidents.length === 0 ? (
        <div className="card p-12 text-center">
          <Clock className="w-16 h-16 mx-auto mb-4" style={{ color: '#3a3c40' }} />
          <p style={{ color: '#BFB8A6' }}>No timeline events. Start by recording incidents.</p>
        </div>
      ) : (
        <div className="relative pl-8">
          <div className="absolute left-3 top-0 bottom-0 w-0.5" style={{ backgroundColor: '#FFCC01' }} />
          {incidents.map((inc) => (
            <div key={inc.id} className="relative mb-6" data-testid={`timeline-event-${inc.id}`}>
              <div className="absolute -left-5 top-2 w-4 h-4 rounded-full" style={{ backgroundColor: '#FFCC01' }} />
              <div className="card p-6 ml-4">
                <p className="text-sm mb-2" style={{ color: '#727365' }}>{new Date(inc.created_at).toLocaleDateString()}</p>
                <p style={{ color: '#FFFFFF' }}>{inc.description}</p>
                {inc.program && <span className="badge mt-2">{inc.program}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ DOCUMENTS PAGE ============
const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/documents`).then(res => setDocuments(res.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/documents/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments([res.data, ...documents]);
      toast.success("Uploaded!");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="documents-page">
      <h1 className="font-heading text-4xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Document Scanner</h1>
      
      {/* Upload Zone */}
      <div className="card p-10 mb-8 border-2 border-dashed text-center cursor-pointer" style={{ borderColor: '#FFCC01' }}>
        <input type="file" id="file-upload" data-testid="document-upload-input" onChange={handleUpload} className="hidden" disabled={uploading} />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: '#FFCC01' }} />
          <p className="font-heading text-xl font-bold" style={{ color: '#FFFFFF' }}>{uploading ? "Processing..." : "Click to Upload"}</p>
          <p className="text-sm" style={{ color: '#727365' }}>PDF, Word, Text, Images</p>
        </label>
      </div>

      {/* Documents List */}
      <h2 className="font-heading text-xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Uploaded ({documents.length})</h2>
      {documents.map((doc) => (
        <div key={doc.id} className="card p-4 mb-2" data-testid={`document-item-${doc.id}`}>
          <div className="flex items-center gap-4">
            <FileText className="w-8 h-8" style={{ color: '#FFCC01' }} />
            <div>
              <p style={{ color: '#FFFFFF' }}>{doc.filename}</p>
              <p className="text-sm" style={{ color: '#727365' }}>{doc.content_type}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ EVIDENCE PAGE ============
const EvidencePage = () => {
  const [packets, setPackets] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedPacket, setSelectedPacket] = useState(null);

  useEffect(() => {
    Promise.all([axios.get(`${API}/evidence-packets`), axios.get(`${API}/incidents`)]).then(([p, i]) => {
      setPackets(p.data);
      setIncidents(i.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title || selectedIds.length === 0) { toast.error("Select incidents"); return; }
    try {
      const res = await axios.post(`${API}/evidence-packets?title=${encodeURIComponent(title)}`, selectedIds);
      setPackets([res.data, ...packets]);
      setSelectedPacket(res.data);
      setTitle("");
      setSelectedIds([]);
      toast.success("Created!");
    } catch (err) {
      toast.error("Failed");
    }
  };

  const downloadPacket = (p) => {
    const blob = new Blob([p.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${p.title.replace(/\s+/g, '_')}_evidence.txt`;
    a.click();
  };

  return (
    <div className="p-6 md:p-10" data-testid="evidence-page">
      <h1 className="font-heading text-4xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Evidence Packets</h1>
      
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <form onSubmit={handleCreate} className="card p-6" style={{ borderColor: '#FFCC01' }}>
            <h2 className="font-heading text-xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Create Packet</h2>
            <input type="text" data-testid="evidence-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="input-field mb-4" placeholder="Packet title" required />
            <div className="max-h-48 overflow-y-auto space-y-2 mb-4">
              {incidents.map((inc) => (
                <label key={inc.id} className="flex items-start gap-3 p-2 rounded cursor-pointer" style={{ backgroundColor: '#1a1c1e' }}>
                  <input type="checkbox" checked={selectedIds.includes(inc.id)} onChange={() => setSelectedIds(prev => prev.includes(inc.id) ? prev.filter(i => i !== inc.id) : [...prev, inc.id])} />
                  <span className="text-sm line-clamp-1" style={{ color: '#BFB8A6' }}>{inc.description.substring(0, 60)}...</span>
                </label>
              ))}
            </div>
            <button type="submit" data-testid="create-evidence-btn" className="btn-command w-full py-3">Generate Packet</button>
          </form>

          {/* Packets List */}
          <div className="mt-6">
            {packets.map((p) => (
              <div key={p.id} className="card p-4 mb-2 cursor-pointer" onClick={() => setSelectedPacket(p)} data-testid={`packet-item-${p.id}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <p style={{ color: '#FFFFFF' }}>{p.title}</p>
                    <p className="text-sm" style={{ color: '#727365' }}>{p.incident_ids.length} incidents</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); downloadPacket(p); }}><Download className="w-5 h-5" style={{ color: '#FFCC01' }} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {selectedPacket ? (
            <div className="card p-6">
              <h3 className="font-heading text-xl font-bold mb-4" style={{ color: '#FFCC01' }}>{selectedPacket.title}</h3>
              <pre className="text-sm whitespace-pre-wrap p-4 rounded overflow-auto max-h-[500px]" style={{ backgroundColor: '#1a1c1e', color: '#BFB8A6' }} data-testid="packet-preview">{selectedPacket.content}</pre>
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4" style={{ color: '#3a3c40' }} />
              <p style={{ color: '#BFB8A6' }}>Select a packet to preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ LAWS PAGE ============
const LawsPage = () => {
  const [laws, setLaws] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    axios.get(`${API}/laws`).then(res => setLaws(res.data));
  }, []);

  const handleSearch = () => {
    if (!search.trim()) return;
    axios.get(`${API}/laws/search?q=${encodeURIComponent(search)}`).then(res => setResults(res.data));
  };

  return (
    <div className="p-6 md:p-10" data-testid="laws-page">
      <h1 className="font-heading text-4xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Law Reference</h1>
      
      {/* Search */}
      <div className="card p-6 mb-8">
        <div className="flex gap-4">
          <input type="text" data-testid="law-search-input" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} className="input-field flex-1" placeholder="Search laws..." />
          <button onClick={handleSearch} data-testid="law-search-btn" className="btn-command">Search</button>
        </div>
        {results.length > 0 && (
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid #3a3c40' }}>
            {results.map((law, i) => (
              <div key={i} className="p-3 rounded mb-2" style={{ backgroundColor: '#1a1c1e' }}>
                <p className="font-bold" style={{ color: '#FFCC01' }}>{law.title}</p>
                <p className="text-sm" style={{ color: '#BFB8A6' }}>{law.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Laws */}
      <div className="grid md:grid-cols-2 gap-4">
        {laws.map((law) => (
          <Accordion key={law.id} title={law.title}>
            <p style={{ color: '#BFB8A6' }}>{law.description}</p>
            <span className="badge mt-2">{law.category}</span>
          </Accordion>
        ))}
      </div>
    </div>
  );
};

// ============ VETCOMMAND ENGINE PAGE ============
const VETCommandPage = () => {
  const [activeSection, setActiveSection] = useState("command-center");
  const [expandedMenus, setExpandedMenus] = useState([
    "command-center", 
    "why-you-matter", 
    "new-innovation",
    "va-ecosystem",
    "va-resource-directory",
    "hud-vash-ecosystem",
    "rights-law-oversight",
    "stability-defense-models",
    "housing-master-directory",
    "education-curriculum"
  ]);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  const menuSections = [
    {
      id: "admin-tools",
      icon: Wrench,
      title: "ADMIN TOOLS",
      items: []
    },
    {
      id: "command-center",
      icon: Shield,
      title: "COMMAND CENTER",
      items: [
        { label: "Dashboard", path: "/dashboard" },
        { label: "My Portal", path: "/vetcommand" },
        { label: "Crisis Command Deck", path: "/vetcommand" },
        { label: "VETFirst™ Daily Check-In", path: "/vetcommand" },
        { label: "CBAS™ Activation Scoring", path: "/vetcommand" },
        { label: "VETStrong™ Progress Chart", path: "/vetcommand" },
        { label: "Analytics & Reporting", path: "/vetcommand" }
      ]
    },
    {
      id: "why-you-matter",
      icon: Star,
      title: "WHY YOU MATTER",
      items: [
        { label: "Veteran First Doctrine", path: "/vetcommand" },
        { label: "Identity & Purpose", path: "/vetcommand" },
        { label: "Camaraderie & Belonging", path: "/vetcommand" },
        { label: "Reintegration & Family", path: "/vetcommand" },
        { label: "Voice & Advocacy", path: "/vetcommand" },
        { label: "Volunteerism & Leadership", path: "/vetcommand" }
      ]
    },
    {
      id: "new-innovation",
      icon: Cpu,
      title: "NEW INNOVATION",
      items: [
        { label: "VETDirect Core Engine™", path: "/" },
        { label: "VETScan™", path: "/documents" },
        { label: "VETEngine™ Legal Navigator", path: "/laws" },
        { label: "VETCommunity™", path: "/vetcommand" },
        { label: "VETFirst™ Daily Check-In", path: "/vetcommand" },
        { label: "CBAS™ Activation Scoring", path: "/vetcommand" },
        { label: "VETStrong™ Progress Chart", path: "/vetcommand" },
        { label: "Case Management Resource Center", path: "/vetcommand" },
        { label: "Predictive Stability Modeling", path: "/vetcommand" }
      ]
    },
    {
      id: "foundation-doctrine",
      icon: BookMarked,
      title: "FOUNDATION DOCTRINE",
      items: [
        { label: "Core Principles", path: "/vetcommand" },
        { label: "Mission Statement", path: "/vetcommand" },
        { label: "Values & Ethics", path: "/vetcommand" }
      ]
    },
    {
      id: "va-ecosystem",
      icon: Network,
      title: "VA ECOSYSTEM",
      items: [
        { label: "Federal & VA Structure", path: "/vetcommand" },
        { label: "Federal, State & County Agencies Serving HUD-VASH", path: "/vetcommand" },
        { label: "VA Central Leadership", path: "/vetcommand" },
        { label: "VHA / VBA / NCA", path: "/vetcommand" },
        { label: "VA Main Departments", path: "/vetcommand" },
        { label: "System Hierarchy Map", path: "/vetcommand" },
        { label: "Breakdown & Failure Points", path: "/vetcommand" },
        { label: "VISN Structure", path: "/vetcommand" },
        { label: "VA Greater Los Angeles", path: "/vetcommand" },
        { label: "Los Angeles Housing Authorities", path: "/vetcommand" },
        { label: "VAMC Medical Centers", path: "/vetcommand" },
        { label: "VA Facilities Directory", path: "/vetcommand" },
        { label: "Veteran-Focused Non-Profit Partners", path: "/vetcommand" }
      ]
    },
    {
      id: "va-resource-directory",
      icon: Grid3X3,
      title: "VA RESOURCE DIRECTORY",
      items: [
        { label: "VA Programs, Classes, Activities", path: "/vetcommand" },
        { label: "VFSS Peer Support Directory", path: "/vetcommand" },
        { label: "Los Angeles Non-Profits", path: "/vetcommand" },
        { label: "VA OIG Oversight Intelligence", path: "/vetcommand" },
        { label: "VHA Directives (HUD-VASH subset)", path: "/vetcommand" },
        { label: "Top 10 HUD-VASH Veteran Essentials", path: "/vetcommand" },
        { label: "HUD-VASH Glossary", path: "/vetcommand" },
        { label: "Top 20 HUD-VASH Essentials", path: "/vetcommand" },
        { label: "The Cascade of Isolation", path: "/vetcommand" },
        { label: "Top 50 National VA Phone Directory", path: "/vetcommand", highlight: true },
        { label: "50 LA Veteran-Focused Non-Profits", path: "/vetcommand" },
        { label: "VA Top 100 Benefits/Services", path: "/vetcommand" },
        { label: "Patient Advocate Offices", path: "/vetcommand" },
        { label: "Veteran Experience Officers", path: "/vetcommand" },
        { label: "Vet Centers", path: "/vetcommand" },
        { label: "Veteran Service Organization (VSO)", path: "/vetcommand" },
        { label: "Alternate Whole Health Sources", path: "/vetcommand" },
        { label: "CDCE: Serve to Heal", path: "/vetcommand" },
        { label: "VA \"I CARE\" Program", path: "/vetcommand" }
      ]
    },
    {
      id: "hud-vash-ecosystem",
      icon: Building,
      title: "HUD-VASH ECOSYSTEM",
      items: [
        { label: "Welcome to HUD-VASH", path: "/vetcommand" },
        { label: "From Streets to Strength", path: "/vetcommand" },
        { label: "HUD-VASH Breaking Barriers", path: "/vetcommand" },
        { label: "Life Discovery & Reconstruction", path: "/vetcommand" },
        { label: "HUD-VASH Ecosystem", path: "/vetcommand" },
        { label: "HUD-VASH Ground Zero", path: "/vetcommand" },
        { label: "HUD-VASH Directory Hub", path: "/vetcommand" },
        { label: "HUD-VASH Resource Guide", path: "/vetcommand" },
        { label: "HUD-VASH Supportive Services", path: "/vetcommand" },
        { label: "HUD Rules & Guidelines", path: "/vetcommand" },
        { label: "HUD-VASH Regulations", path: "/vetcommand" },
        { label: "HUD-VASH Housing Rules & Directives", path: "/vetcommand" },
        { label: "HUD-VASH Living Rules", path: "/vetcommand" }
      ]
    },
    {
      id: "rights-law-oversight",
      icon: Gavel,
      title: "RIGHTS, LAW & OVERSIGHT",
      items: [
        { label: "14-Mark Eviction Diversion", path: "/laws" },
        { label: "Complete Veterans Bill of Rights", path: "/laws" },
        { label: "Patient Bill of Rights", path: "/laws" },
        { label: "Constitutional & Civil Rights Library", path: "/laws" },
        { label: "Complete U.S. Constitution", path: "/laws" },
        { label: "Medical Doctor Code of Ethics", path: "/laws" },
        { label: "VHA Directives", path: "/laws" },
        { label: "VA Directives", path: "/laws" },
        { label: "HACLA Rules & Requirements", path: "/laws" },
        { label: "PHA Rules & Requirements", path: "/laws" },
        { label: "California Tenant Laws", path: "/laws" },
        { label: "ADA & Section 504", path: "/laws" },
        { label: "Reasonable Accommodation Rules", path: "/laws" },
        { label: "Fair Housing Act", path: "/laws" },
        { label: "USERRA", path: "/laws" },
        { label: "SCRA", path: "/laws" },
        { label: "Attorney State Bar Rules & Ethics", path: "/laws" },
        { label: "Legal Violations Matrix", path: "/laws" },
        { label: "LEGALScan™", path: "/laws" },
        { label: "VA OIG", path: "/laws" },
        { label: "DOJ Civil Rights", path: "/laws" },
        { label: "Office of Accountability & Whistleblower Protection", path: "/laws" },
        { label: "VA Complaint Chain of Command", path: "/laws" }
      ]
    },
    {
      id: "stability-defense-models",
      icon: Target,
      title: "STABILITY DEFENSE MODELS",
      items: [
        { label: "Five of 5's Stability Ecosystem", path: "/vetcommand" },
        { label: "14-Mark Eviction Diversion Model", path: "/vetcommand" },
        { label: "Crisis Prevention Framework", path: "/vetcommand" },
        { label: "VJO Judicial Diversion", path: "/vetcommand" },
        { label: "Law Enforcement Stand Down Guide", path: "/vetcommand" },
        { label: "Modified VHA Suicide Prevention Model", path: "/vetcommand" },
        { label: "PTSD Recovery Framework", path: "/vetcommand" },
        { label: "Wellness Wheel", path: "/vetcommand" },
        { label: "20-Mark Self Evaluation", path: "/vetcommand" }
      ]
    },
    {
      id: "housing-master-directory",
      icon: Home,
      title: "HOUSING MASTER DIRECTORY",
      items: [
        { label: "HUD-VASH Facilities", path: "/vetcommand" },
        { label: "Permanent Supportive Housing", path: "/vetcommand" },
        { label: "HACLA PBV Sites", path: "/vetcommand" },
        { label: "Section 8 Locations", path: "/vetcommand" },
        { label: "Transitional Programs", path: "/vetcommand" },
        { label: "West LA Campus Buildings", path: "/vetcommand" },
        { label: "Who Does What Chart", path: "/vetcommand" },
        { label: "Veteran Housing Checklist", path: "/vetcommand" },
        { label: "Personalized Stabilization Plan", path: "/vetcommand" }
      ]
    },
    {
      id: "education-curriculum",
      icon: BookOpen,
      title: "EDUCATION & CURRICULUM",
      items: [
        { label: "Your Armor: Build it Once (52-Course)", path: "/vetcommand" },
        { label: "4-Pillar Recovery (52-Course)", path: "/vetcommand" },
        { label: "Calming the Storm (5-Phases)", path: "/vetcommand" },
        { label: "Structure Success Skills (5-Part)", path: "/vetcommand" },
        { label: "Self-Advocacy Tips", path: "/vetcommand" },
        { label: "Veteran-Centered (52-Course)", path: "/vetcommand" },
        { label: "Life Skills (52-Course Program)", path: "/vetcommand" },
        { label: "Class Curriculum", path: "/vetcommand" },
        { label: "Courses", path: "/vetcommand" },
        { label: "Books", path: "/vetcommand" },
        { label: "Articles", path: "/vetcommand" },
        { label: "Research", path: "/vetcommand" },
        { label: "Plan", path: "/vetcommand" },
        { label: "Explore", path: "/vetcommand" },
        { label: "Grow", path: "/vetcommand" },
        { label: "Digital Field Manual Reader", path: "/vetcommand" },
        { label: "Chapter Index", path: "/vetcommand" },
        { label: "Progress Tracker", path: "/vetcommand" }
      ]
    },
    {
      id: "la-community",
      icon: Globe,
      title: "LAS ANGELES COMMUNITY",
      items: [
        { label: "LA Veteran Services", path: "/vetcommand" },
        { label: "Community Resources", path: "/vetcommand" },
        { label: "Local Support Groups", path: "/vetcommand" },
        { label: "Veteran Events Calendar", path: "/vetcommand" }
      ]
    },
    {
      id: "whole-health-planning",
      icon: Heart,
      title: "WHOLE HEALTH & PLANNING",
      items: [
        { label: "Whole Health Classes", path: "/vetcommand" },
        { label: "Veteran Research", path: "/vetcommand" },
        { label: "Health Self-Assessment", path: "/vetcommand" },
        { label: "Plan Your Health", path: "/vetcommand" },
        { label: "Plan Your Housing", path: "/vetcommand" },
        { label: "Plan Your Future", path: "/vetcommand" },
        { label: "Explore Resources", path: "/vetcommand" },
        { label: "Grow, Heal, Thrive", path: "/vetcommand" },
        { label: "Media", path: "/vetcommand" },
        { label: "Video Presentations", path: "/vetcommand" },
        { label: "Photos", path: "/vetcommand" }
      ]
    }
  ];

  const quickStats = [
    { label: "Active Cases", value: "12", icon: Briefcase, color: "#FFCC01" },
    { label: "Documents", value: "47", icon: FileText, color: "#10B981" },
    { label: "Check-Ins", value: "28", icon: Activity, color: "#3B82F6" },
    { label: "Stability Score", value: "85%", icon: TrendingUp, color: "#8B5CF6" }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }} data-testid="vetcommand-page">
      {/* Top Navbar - Army Style */}
      <nav className="top-navbar" style={{ backgroundColor: '#221F20', borderBottom: '2px solid #FFCC01' }}>
        <div className="nav-left">
          <Zap className="lightning-icon w-6 h-6" style={{ color: '#FFCC01' }} />
          <Link to="/vetcommand" className="btn-command" style={{ backgroundColor: '#FFCC01', color: '#221F20' }}>COMMAND</Link>
          <Link to="/" className="btn-home" style={{ backgroundColor: '#2F372F', color: '#F1E4C7', border: '1px solid #FFCC01' }}>HOME</Link>
        </div>
        <div className="nav-center hidden lg:flex">
          {["ANCESTRY", "SOP", "HISTORY", "INNOVATION", "DELTA", "CONTRACTS", "ADVOCACY", "VAC"].map((item, i) => (
            <button key={i} className="nav-link" style={{ color: '#FFCC01' }}>{item} <ChevronDown className="w-3 h-3" /></button>
          ))}
        </div>
        <div className="nav-right">
          <div className="hidden md:flex items-center gap-1">
            <button className="emergency-box emergency-22">22</button>
            <button className="emergency-box emergency-988">988</button>
            <button className="emergency-box emergency-911">911</button>
            <button className="emergency-box emergency-emha">EMHA</button>
          </div>
          <button className="btn-crisis" style={{ backgroundColor: '#2F372F', border: '1px solid #FFCC01' }}><span className="pulse-dot"></span> Crisis Command</button>
        </div>
      </nav>

      <div className="flex">
        {/* Expanded Sidebar Menu - Army Military Color Scheme */}
        <aside className="w-72 min-h-screen overflow-y-auto sticky top-14 h-[calc(100vh-56px)]" style={{ backgroundColor: '#1a1f1a', borderRight: '1px solid #2F372F' }} data-testid="command-sidebar">
          <div className="p-4">
            {menuSections.map((section) => (
              <div key={section.id} className="mb-2">
                {/* Section Header */}
                <button
                  onClick={() => section.items.length > 0 && toggleMenu(section.id)}
                  className="w-full flex items-center gap-3 py-3 px-2 rounded transition-colors"
                  style={{ 
                    borderLeft: expandedMenus.includes(section.id) ? '3px solid #FFCC01' : '3px solid transparent',
                    backgroundColor: expandedMenus.includes(section.id) ? 'rgba(47, 55, 47, 0.5)' : 'transparent'
                  }}
                >
                  <section.icon className="w-5 h-5" style={{ color: expandedMenus.includes(section.id) ? '#FFCC01' : '#BFB8A6' }} />
                  <span 
                    className="font-heading font-bold text-sm tracking-wider"
                    style={{ color: expandedMenus.includes(section.id) ? '#FFCC01' : '#F1E4C7' }}
                  >
                    {section.title}
                  </span>
                </button>

                {/* Section Items - Army Style Box */}
                {expandedMenus.includes(section.id) && section.items.length > 0 && (
                  <div className="ml-4 mt-1 rounded overflow-hidden" style={{ backgroundColor: '#2F372F', borderLeft: '3px solid #FFCC01' }}>
                    {section.items.map((item, i) => (
                      <Link
                        key={i}
                        to={item.path}
                        className="block py-3 px-5 text-sm transition-colors"
                        style={{ 
                          color: item.highlight || (i === 0 && section.id === "command-center") ? '#FFCC01' : '#F1E4C7',
                          backgroundColor: item.highlight || (i === 0 && section.id === "command-center") ? 'rgba(255, 204, 1, 0.1)' : 'transparent',
                          fontWeight: item.highlight ? '600' : 'normal',
                          borderBottom: '1px solid rgba(191, 184, 166, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 204, 1, 0.15)';
                          e.currentTarget.style.color = '#FFCC01';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = item.highlight || (i === 0 && section.id === "command-center") ? 'rgba(255, 204, 1, 0.1)' : 'transparent';
                          e.currentTarget.style.color = item.highlight || (i === 0 && section.id === "command-center") ? '#FFCC01' : '#F1E4C7';
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content - Army Style */}
        <main className="flex-1 p-8" style={{ backgroundColor: '#1a1c1a' }}>
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold mb-2" style={{ color: '#FFCC01' }}>
              VETCommand Engine™
            </h1>
            <p style={{ color: '#BFB8A6' }}>Veteran Command & Control Center</p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {quickStats.map((stat, i) => (
              <div key={i} className="rounded-lg p-5" style={{ backgroundColor: '#2F372F', border: '2px solid #FFCC01' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#BFB8A6' }}>{stat.label}</p>
                    <p className="text-3xl font-heading font-bold mt-1" style={{ color: stat.color }}>{stat.value}</p>
                  </div>
                  <stat.icon className="w-8 h-8" style={{ color: '#727365' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Link to="/vetshield" className="rounded-lg p-6 transition-all hover:border-[#FFCC01]" style={{ backgroundColor: '#2F372F', border: '1px solid #727365' }}>
              <Shield className="w-10 h-10 mb-4" style={{ color: '#FFCC01' }} />
              <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#FFFFFF' }}>VETShield</h3>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Document incidents and build evidence</p>
            </Link>
            
            <Link to="/documents" className="rounded-lg p-6 transition-all hover:border-[#FFCC01]" style={{ backgroundColor: '#2F372F', border: '1px solid #727365' }}>
              <Upload className="w-10 h-10 mb-4" style={{ color: '#10B981' }} />
              <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#FFFFFF' }}>VETScan™</h3>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Upload and analyze documents</p>
            </Link>
            
            <Link to="/laws" className="rounded-lg p-6 transition-all hover:border-[#FFCC01]" style={{ backgroundColor: '#2F372F', border: '1px solid #727365' }}>
              <Scale className="w-10 h-10 mb-4" style={{ color: '#3B82F6' }} />
              <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#FFFFFF' }}>Legal Navigator</h3>
              <p className="text-sm" style={{ color: '#BFB8A6' }}>Federal and state law references</p>
            </Link>
          </div>

          {/* VETFirst Daily Check-In */}
          <div className="rounded-lg p-6 mb-8" style={{ backgroundColor: '#2F372F', border: '1px solid #727365' }}>
            <div className="flex items-center gap-3 mb-4">
              <Activity className="w-6 h-6" style={{ color: '#FFCC01' }} />
              <h2 className="font-heading text-2xl font-bold" style={{ color: '#FFFFFF' }}>VETFirst™ Daily Check-In</h2>
            </div>
            <p className="mb-4" style={{ color: '#BFB8A6' }}>How are you feeling today? Your wellbeing matters.</p>
            <div className="flex flex-wrap gap-3">
              {["Great", "Good", "Okay", "Struggling", "Need Help"].map((mood, i) => (
                <button 
                  key={i}
                  className="px-6 py-3 rounded font-heading font-semibold text-sm uppercase tracking-wider transition-all"
                  style={{ 
                    backgroundColor: i === 0 ? '#FFCC01' : '#2F372F',
                    color: i === 0 ? '#221F20' : '#F1E4C7',
                    border: '1px solid #727365'
                  }}
                >
                  {mood}
                </button>
              ))}
            </div>
          </div>

          {/* CBAS Activation Scoring */}
          <div className="rounded-lg p-6" style={{ backgroundColor: '#2F372F', border: '1px solid #727365' }}>
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-6 h-6" style={{ color: '#FFCC01' }} />
              <h2 className="font-heading text-2xl font-bold" style={{ color: '#FFFFFF' }}>CBAS™ Activation Scoring</h2>
            </div>
            <p className="mb-6" style={{ color: '#BFB8A6' }}>Crisis-Based Activation Scoring tracks your stability metrics.</p>
            
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { label: "Housing Stability", score: 85, color: "#10B981" },
                { label: "Healthcare Access", score: 72, color: "#FFCC01" },
                { label: "Support Network", score: 90, color: "#3B82F6" }
              ].map((item, i) => (
                <div key={i} className="p-4 rounded" style={{ backgroundColor: '#221F20' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm" style={{ color: '#BFB8A6' }}>{item.label}</span>
                    <span className="font-heading font-bold" style={{ color: item.color }}>{item.score}%</span>
                  </div>
                  <div className="h-2 rounded-full" style={{ backgroundColor: '#727365' }}>
                    <div className="h-full rounded-full" style={{ width: `${item.score}%`, backgroundColor: item.color }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ============ VAC PAGE (Placeholder) ============
// ============ VAC PAGE ============
const VACPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }}>
      <TopNavbar showAuth={false} />
      
      {/* Hero Section with Video Background */}
      <div className="relative w-full h-screen overflow-hidden bg-black">
        {/* Video Background with Slow Zoom */}
        <style>{`
          @keyframes vacZoom {
            0% {
              transform: scale(1.0);
              transform-origin: center center;
            }
            50% {
              transform: scale(1.2);
              transform-origin: center center;
            }
            100% {
              transform: scale(1.0);
              transform-origin: center center;
            }
          }
          
          .vac-zoom-video {
            animation: vacZoom 25s ease-in-out infinite;
          }
        `}</style>
        
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover vac-zoom-video"
          style={{ 
            filter: 'brightness(0.80) contrast(1.05)',
            zIndex: 0
          }}
        >
          <source src={VIDEO_URLS.vacPage} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/75" style={{ zIndex: 1 }} />
        
        {/* Centered Text Content */}
        <div className="relative flex flex-col items-center justify-center h-full px-4" style={{ zIndex: 10 }}>
          <h1 className="text-5xl md:text-6xl font-bold text-center mb-6" style={{ 
            color: '#FFFFFF', 
            textShadow: '0 4px 12px rgba(0,0,0,0.95)',
            fontFamily: "'Stint Ultra Condensed', 'Impact', sans-serif",
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            VETERANS ADVISORY COUNCIL
          </h1>
          
          <div className="h-1 w-48 mb-6" style={{ backgroundColor: '#FFCC01' }} />
          
          <p className="text-2xl md:text-3xl text-center max-w-4xl leading-relaxed" style={{ 
            color: '#BFB8A6', 
            textShadow: '0 3px 10px rgba(0,0,0,0.95)',
            fontFamily: "'Inter', sans-serif"
          }}>
            Empowering Veterans Through Collective Voice and Advocacy
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce" style={{ zIndex: 10 }}>
          <div className="flex flex-col items-center gap-3">
            <span className="text-sm uppercase tracking-widest font-bold" style={{ 
              color: '#FFCC01', 
              textShadow: '0 3px 10px rgba(0,0,0,0.95)',
              letterSpacing: '0.25em'
            }}>
              SCROLL TO EXPLORE
            </span>
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 204, 1, 0.25)' }}>
              <ChevronRight 
                size={32} 
                style={{ 
                  color: '#FFCC01', 
                  transform: 'rotate(90deg)',
                  filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.9))'
                }} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - Professional Design */}
      <main className="max-w-6xl mx-auto px-8 py-20" style={{ backgroundColor: '#0A0A0A' }}>
        
        <div className="mb-20">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#FFFFFF', letterSpacing: '0.02em' }}>
            Veterans Advisory Council (VAC)
          </h1>
          <p className="text-lg md:text-xl mb-12" style={{ color: '#B0B0B0', fontWeight: '300' }}>
            A Platform for Veteran Representation and Systemic Change
          </p>
          
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#D0D0D0' }}>
              Veterans Advisory Councils serve as vital bridges between veterans and the institutions designed to serve them. These councils provide structured platforms for veteran voices to be heard, concerns to be addressed, and solutions to be developed collaboratively.
            </p>
            <p className="text-base leading-relaxed" style={{ color: '#D0D0D0' }}>
              Whether operating within housing programs, healthcare systems, or veteran service organizations, advisory councils ensure that the people most affected by policies have a meaningful role in shaping them.
            </p>
          </div>
        </div>

        {/* Purpose Section */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            The Purpose of Veterans Advisory Councils
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Provide a formal mechanism for veteran input on policies and procedures</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Identify systemic issues affecting veteran communities</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Promote transparency and accountability in veteran services</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Foster collaboration between veterans and service providers</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Advocate for improvements in veteran programs and services</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Why Advisory Councils Matter */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Why Advisory Councils Matter
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              Veterans living in supportive housing programs like HUD-VASH face unique challenges that can only be fully understood by those experiencing them. Advisory councils ensure that veteran perspectives inform decision-making at every level.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              When veterans are given a seat at the table, policies become more effective, programs become more responsive, and trust between veterans and institutions grows stronger.
            </p>
            <p className="text-base leading-relaxed italic" style={{ color: '#B0B0B0' }}>
              Representation matters. Advisory councils give veterans the power to shape their own futures.
            </p>
          </div>
        </div>

      </main>
      
      <Footer />
    </div>
  );
};

// ============ VAVIP PAGE (Placeholder) ============
const VAVIPPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }}>
      <TopNavbar showAuth={false} />
      <main className="max-w-6xl mx-auto px-8 py-20">
        <h1 className="text-4xl font-bold mb-4" style={{ color: '#FFFFFF' }}>VA VIP Program</h1>
        <p className="text-xl" style={{ color: '#BFB8A6' }}>Content coming soon...</p>
      </main>
      <Footer />
    </div>
  );
};

// ============ VFW PAGE ============
const VFWPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }}>
      <TopNavbar showAuth={false} />
      
      {/* Hero Section with Video Background */}
      <div className="relative w-full h-screen overflow-hidden bg-black">
        {/* Video Background with Animation */}
        <style>{`
          @keyframes vfwZoom {
            0% {
              transform: scale(1.5) translateY(10%);
              transform-origin: center 60%;
            }
            50% {
              transform: scale(1.0) translateY(0%);
              transform-origin: center center;
            }
            100% {
              transform: scale(1.5) translateY(10%);
              transform-origin: center 60%;
            }
          }
          
          .vfw-zoom-video {
            animation: vfwZoom 18s ease-in-out infinite;
          }
        `}</style>
        
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover vfw-zoom-video"
          style={{ 
            filter: 'brightness(0.85) contrast(1.1)',
            zIndex: 0
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/jx4zplzf_vecteezy_a-determined-caucasian-female-soldier-in-camouflage-uniform_50466264.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Gradient Overlay for logo visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/70" style={{ zIndex: 1 }} />
        
        {/* Patriotic Quote - Top */}
        <div className="absolute top-24 left-0 right-0 text-center px-4" style={{ zIndex: 9 }}>
          <p className="text-2xl md:text-3xl font-bold tracking-widest" style={{ 
            color: '#FFFFFF', 
            textShadow: '0 8px 20px rgba(0,0,0,0.95), 0 4px 12px rgba(0,0,0,0.8)',
            letterSpacing: '0.18em',
            fontFamily: "'Arial', sans-serif"
          }}>
            "NO ONE DOES MORE FOR VETERANS"
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce" style={{ zIndex: 10 }}>
          <div className="flex flex-col items-center gap-3">
            <span className="text-sm uppercase tracking-widest font-bold" style={{ 
              color: '#FFCC01', 
              textShadow: '0 3px 10px rgba(0,0,0,0.95)',
              letterSpacing: '0.25em'
            }}>
              SCROLL TO EXPLORE
            </span>
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 204, 1, 0.25)' }}>
              <ChevronRight 
                size={32} 
                style={{ 
                  color: '#FFCC01', 
                  transform: 'rotate(90deg)',
                  filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.9))'
                }} 
              />
            </div>
          </div>
        </div>

        {/* VFW Logo and Text - Smaller, Closer Together, Above Scroll Indicator */}
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 flex flex-col items-center" style={{ zIndex: 10 }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/breed8uq_Untitled%20%284%29.png"
            alt="VFW - Veterans of Foreign Wars"
            style={{ 
              filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.95)) brightness(1.1) contrast(1.1)',
              maxWidth: '20rem',
              maxHeight: '12vh',
              objectFit: 'contain',
              marginBottom: '0.5rem'
            }}
          />
          
          {/* Typewriter Text Below Logo - Compact */}
          <div className="text-center px-4">
            <p className="usva-typewriter text-xl md:text-2xl font-bold leading-tight mb-1" style={{ 
              color: '#FFFFFF', 
              textShadow: '0 3px 10px rgba(0,0,0,0.95), 0 2px 6px rgba(0,0,0,0.8)',
              fontFamily: "'Stint Ultra Condensed', 'Impact', sans-serif",
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}>
              VETERANS OF FOREIGN WARS
            </p>
            <p className="usva-typewriter-delay text-sm md:text-base leading-tight" style={{ 
              color: '#BFB8A6', 
              textShadow: '0 2px 8px rgba(0,0,0,0.95)',
              fontFamily: "'Stint Ultra Condensed', 'Impact', sans-serif",
              letterSpacing: '0.05em'
            }}>
              No One Does More For Veterans
            </p>
          </div>
        </div>
      </div>

      {/* Content Section - Professional Design */}
      <main className="max-w-6xl mx-auto px-8 py-20" style={{ backgroundColor: '#0A0A0A' }}>
        
        {/* Who We Are Section */}
        <div className="mb-20">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#FFFFFF', letterSpacing: '0.02em' }}>
            Who We Are
          </h1>
          
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              The Veterans of Foreign Wars of the United States is a nonprofit veterans service organization comprised of eligible veterans and military service members from the active, guard and reserve forces.
            </p>
            
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              We trace our roots back to 1899 when veterans of the Spanish-American War (1898) and the Philippine Insurrection (1899-1902) founded local organizations to secure rights and benefits for their service. Many arrived home wounded or sick. There was no medical care or veterans' pension for them, and they were left to care for themselves.
            </p>
            
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              In their misery, some of these veterans banded together and formed organizations that would eventually band together and become known as the Veterans of Foreign Wars of the United States. After chapters were formed in Ohio, Colorado and Pennsylvania, the movement quickly gained momentum. Today, membership stands at more than 1.3 million members of the VFW and its Auxiliary.
            </p>
          </div>
        </div>

        {/* Legislative Victories Section */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Our Legislative Impact
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              Our voice was instrumental in establishing the Veterans Administration, development of the national cemetery system, in the fight for compensation for Vietnam vets exposed to Agent Orange and for veterans diagnosed with Gulf War Syndrome.
            </p>
            
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              In 2008, we won a long-fought victory with the passing of a GI Bill for the 21st Century, giving expanded educational benefits to America's active duty service members, and members of the guard and reserves, fighting in Iraq and Afghanistan.
            </p>
            
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              We were the driving force behind the Veterans Access and Accountability Act of 2014, and continually fight for improved VA medical centers services for women veterans. The VFW's legislative advocacy efforts were also the driving force behind the passage of the 2019 Blue Water Navy Veteran Act, and the passage of the 2022 Honoring Our PACT Act, which the VFW considers one of the most significant pieces of veterans legislation in history.
            </p>
          </div>
        </div>

        {/* Memorial Contributions Section */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Honoring Our Fallen
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              Besides helping fund the creation of the Vietnam, Korean War, World War II and Women in Military Service memorials, in 2005 the VFW became the first veterans' organization to contribute to building the new Disabled Veterans for Life Memorial, which opened in November 2010.
            </p>
            
            <p className="text-base leading-relaxed" style={{ color: '#D0D0D0' }}>
              And in 2015, we became the first supporter of the Desert Storm and Desert Shield Memorial which broke ground in July of 2022 and is expected to be dedicated this year.
            </p>
          </div>
        </div>

        {/* Programs and Services Section */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Programs and Services
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#D0D0D0' }}>
              We have many programs and services that work to support veterans, service members and their families, as well as communities worldwide.
            </p>
            <p className="text-base leading-relaxed italic" style={{ color: '#B0B0B0' }}>
              No One Does More For Veterans®
            </p>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            Our Core Values
          </h2>
          <div className="border-l-3 pl-8" style={{ borderLeftWidth: '3px', borderLeftColor: '#DC2626' }}>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Always put the interests of our members first</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Treat donors as partners in our cause</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Promote patriotism</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Honor military service</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Ensure the care of veterans and their families</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Serve our communities</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Promote a positive image of the VFW</span>
              </li>
              <li className="flex items-start">
                <span className="text-lg mr-3" style={{ color: '#FFCC01' }}>●</span>
                <span className="text-base" style={{ color: '#D0D0D0' }}>Respect the diversity of veteran opinions</span>
              </li>
            </ul>
          </div>
        </div>

      </main>
      
      <Footer />
    </div>
  );
};

// ============ MAP/DIRECTORY PAGE ============
const MapPage = () => {
  const [searchWhat, setSearchWhat] = useState('');
  const [searchWhere, setSearchWhere] = useState('Los Angeles, CA');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [showResults, setShowResults] = useState(false);
  const [sortBy, setSortBy] = useState('latest');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Military SVG Icons
  const FighterJet = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <path d="M50 15 L65 30 L70 28 L75 35 L70 37 L65 45 L50 50 L35 45 L30 37 L25 35 L30 28 L35 30 Z M50 50 L60 70 L55 72 L50 80 L45 72 L40 70 Z M35 35 L20 40 L15 38 L20 35 Z M65 35 L80 40 L85 38 L80 35 Z" />
    </svg>
  );

  const Tank = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <rect x="20" y="45" width="60" height="25" rx="5" />
      <rect x="30" y="30" width="40" height="20" rx="3" />
      <rect x="55" y="20" width="35" height="8" />
      <circle cx="30" cy="62" r="6" fill="white" />
      <circle cx="45" cy="62" r="6" fill="white" />
      <circle cx="60" cy="62" r="6" fill="white" />
      <circle cx="75" cy="62" r="6" fill="white" />
    </svg>
  );

  const Anchor = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <circle cx="50" cy="25" r="8" fill="none" stroke={color} strokeWidth="5" />
      <rect x="47" y="33" width="6" height="42" />
      <path d="M 50 75 Q 35 75 25 65 L 25 50 L 35 50 L 35 60 Q 40 65 50 65 Q 60 65 65 60 L 65 50 L 75 50 L 75 65 Q 65 75 50 75 Z" />
      <path d="M 35 35 L 50 50 L 65 35" strokeWidth="5" stroke={color} fill="none" />
    </svg>
  );

  const MilitaryHelmet = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <path d="M 50 20 Q 30 20 20 35 L 20 50 Q 20 60 25 60 L 25 55 Q 28 45 35 40 Q 40 35 50 35 Q 60 35 65 40 Q 72 45 75 55 L 75 60 Q 80 60 80 50 L 80 35 Q 70 20 50 20 Z" />
      <ellipse cx="50" cy="60" rx="28" ry="8" />
    </svg>
  );

  const Star = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill="none" stroke={color} strokeWidth="6">
      <path d="M 50 15 L 58 40 L 85 40 L 63 57 L 71 82 L 50 65 L 29 82 L 37 57 L 15 40 L 42 40 Z" />
    </svg>
  );

  const Submarine = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <ellipse cx="50" cy="55" rx="35" ry="15" />
      <rect x="40" y="35" width="20" height="22" rx="2" />
      <rect x="45" y="25" width="10" height="12" />
      <circle cx="35" cy="55" r="4" fill="white" />
      <circle cx="50" cy="55" r="4" fill="white" />
      <circle cx="65" cy="55" r="4" fill="white" />
    </svg>
  );

  const ShipWheel = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <circle cx="50" cy="50" r="30" fill="none" stroke={color} strokeWidth="5" />
      <circle cx="50" cy="50" r="8" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
        const rad = (angle * Math.PI) / 180;
        const x1 = 50 + 12 * Math.cos(rad);
        const y1 = 50 + 12 * Math.sin(rad);
        const x2 = 50 + 28 * Math.cos(rad);
        const y2 = 50 + 28 * Math.sin(rad);
        return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="4" />;
      })}
    </svg>
  );

  const Propeller = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <circle cx="50" cy="50" r="8" />
      <ellipse cx="50" cy="30" rx="8" ry="22" />
      <ellipse cx="50" cy="70" rx="8" ry="22" />
      <ellipse cx="30" cy="50" rx="22" ry="8" />
      <ellipse cx="70" cy="50" rx="22" ry="8" />
    </svg>
  );

  const Chevron = ({ color, size = 80 }) => (
    <svg viewBox="0 0 100 100" width={size} height={size} fill={color}>
      <path d="M 20 70 L 35 70 L 50 50 L 65 70 L 80 70 L 50 30 Z" />
      <path d="M 30 85 L 40 85 L 50 70 L 60 85 L 70 85 L 50 55 Z" />
    </svg>
  );

  // VETERAN CATEGORIES with military icons and army color palette
  const categories = [
    { name: 'VA', icon: Shield, iconColor: '#5C7858', bgColor: '#D4C9A1', listings: 20 }, // Military Shield
    { name: 'VA Phone Directory', icon: Phone, iconColor: '#FFFFFF', bgColor: '#5C7858', listings: 50 }, // Phone
    { name: 'HUD-VASH', icon: Home, iconColor: '#5C7858', bgColor: '#99AA70', listings: 7 }, // Housing
    { name: 'HUD-VASH Entry Points', icon: MilitaryHelmet, iconColor: '#5C7858', bgColor: '#E8E4C9', listings: 7 }, // Military Helmet
    { name: 'LA VAGLAHS Facilities', icon: Building, iconColor: '#7D5840', bgColor: '#D6A969', listings: 11 }, // Medical Buildings
    { name: 'VA Top 50 Phone Contacts', icon: Phone, iconColor: '#FFFFFF', bgColor: '#7D5840', listings: 50 }, // Phone
    { name: 'Housing Authorities', icon: Building, iconColor: '#5C7858', bgColor: '#D4C9A1', listings: 11 }, // Housing Buildings
    { name: 'VAMC', icon: Activity, iconColor: '#5C7858', bgColor: '#99AA70', listings: 12 }, // Medical Care
    { name: 'NonProfits', icon: Heart, iconColor: '#7D5840', bgColor: '#E8E4C9', listings: 34 }, // Heart for care
    { name: 'Veteran Law Firms', icon: Scale, iconColor: '#7D5840', bgColor: '#D4C9A1', listings: 28 }, // Justice scales
    { name: 'Attorney Advocates', icon: Chevron, iconColor: '#5C7858', bgColor: '#D6A969', listings: 16 }, // Military rank
    { name: 'Whole Health Classes', icon: Star, iconColor: '#7D5840', bgColor: '#E8E4C9', listings: 42 }, // Military star
    { name: 'Veteran Service Organizations (VSO)', icon: Tank, iconColor: '#5C7858', bgColor: '#99AA70', listings: 10 }, // Military tank
    { name: 'National Crisis Resources', icon: AlertTriangle, iconColor: '#FFFFFF', bgColor: '#7D5840', listings: 8 }, // Emergency alert
    { name: 'National Veteran Peer Groups', icon: Users, iconColor: '#5C7858', bgColor: '#D4C9A1', listings: 19 }, // People/peers
    { name: 'VA Departments', icon: FighterJet, iconColor: '#5C7858', bgColor: '#99AA70', listings: 50 }, // Military jet
    { name: 'VA Executive Leadership', icon: Star, iconColor: '#7D5840', bgColor: '#E8E4C9', listings: 20 }, // Military star
    { name: 'VISN Regional Offices', icon: Network, iconColor: '#FFFFFF', bgColor: '#5C7858', listings: 27 }, // Network structure
    { name: 'Los Angeles Shelters', icon: Home, iconColor: '#FFFFFF', bgColor: '#7D5840', listings: 50 }, // Shelter housing
    { name: 'HUD-VASH / Homeless Directory', icon: Home, iconColor: '#5C7858', bgColor: '#D6A969', listings: 10 }, // Housing
    { name: 'Veteran Complaints Directory', icon: AlertTriangle, iconColor: '#7D5840', bgColor: '#D4C9A1', listings: 15 }, // Alert
    { name: 'National Whole Health Resources', icon: Activity, iconColor: '#5C7858', bgColor: '#E8E4C9', listings: 25 } // Medical/Health
  ];

  // VETERAN RESOURCES DATA
  const resources = [
    // Import VA Departments from external file
    ...vaDepartments,
    
    // HUD-VASH Entry Points
    {
      id: 1001,
      title: 'National Call Center for Homeless Veterans',
      category: 'HUD-VASH Entry Points',
      address: 'National Hotline',
      phone: '1-877-424-3838',
      website: 'va.gov/homeless',
      description: '24/7 crisis line for veterans experiencing or at risk of homelessness',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop'
    },
    {
      id: 1002,
      title: 'Greater Los Angeles VAMC — Homeless Welcome Center',
      category: 'HUD-VASH Entry Points',
      address: '11301 Wilshire Blvd, Building 402, Los Angeles, CA 90073',
      phone: '(310) 268-3269',
      hours: 'Monday–Friday, 7:30 a.m. to 4:30 p.m.',
      description: 'Front door to HUD-VASH services in LA County. Walk-in access for housing assistance, triage screening, social workers.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 1003,
      title: 'VA Greater Los Angeles Medical Center — Homeless Programs Office',
      category: 'HUD-VASH Entry Points',
      address: '11301 Wilshire Blvd, Los Angeles, CA 90073',
      phone: '(310) 268-3350',
      description: 'Manages entire HUD-VASH program for LA County. Conducts vulnerability assessments.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 1004,
      title: '211 LA County',
      category: 'HUD-VASH Entry Points',
      address: 'Los Angeles County',
      phone: '2-1-1',
      website: '211la.org',
      description: 'Gateway for support services. Referrals to shelters, food, legal aid, case management, SSVF.',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop'
    },
    {
      id: 1005,
      title: 'LA County Housing Resource Center',
      category: 'HUD-VASH Entry Points',
      website: 'housing.lacounty.gov',
      description: 'County housing listing platform with affordable units and special-needs housing.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 1006,
      title: 'Los Angeles Homeless Services Authority (LAHSA)',
      category: 'HUD-VASH Entry Points',
      address: '811 Wilshire Blvd, 6th Floor, Los Angeles, CA 90017',
      phone: '(213) 683-3333',
      description: 'Operates Coordinated Entry System (CES). Connects veterans to VA teams for HUD-VASH enrollment.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 1007,
      title: 'Veterans Crisis Line',
      category: 'HUD-VASH Entry Points',
      phone: '988, Press 1',
      description: 'Crisis safety point with immediate triage and VA homeless outreach coordination.',
      image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=800&h=600&fit=crop'
    },

    // Housing Authorities
    {
      id: 2001,
      title: 'U.S. Department of Housing and Urban Development (HUD) - LA Field Office',
      category: 'Housing Authorities',
      address: '300 N Los Angeles St, Suite 4054, Los Angeles, CA 90012',
      phone: '(213) 894-8000',
      director: 'Trakia Young - Regional Administrator (Region IX)',
      description: 'Federal agency funding HUD-VASH vouchers. Oversees PHAs and Section 8.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    },
    {
      id: 2002,
      title: 'Los Angeles County Development Authority (LACDA)',
      category: 'Housing Authorities',
      address: '700 W Main St, Alhambra, CA 91801',
      phone: '(626) 586-1955',
      director: 'Emilio Salas - Executive Director',
      description: 'Administers HUD-VASH vouchers for LA County outside City of LA.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 2003,
      title: 'Housing Authority of the City of Los Angeles (HACLA)',
      category: 'Housing Authorities',
      address: '2600 Wilshire Blvd, Los Angeles, CA 90057',
      phone: '(213) 252-2500',
      director: 'Doug Guthrie - President & CEO',
      description: 'Manages HUD-VASH vouchers within City of LA. Conducts inspections.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 2004,
      title: 'Los Angeles Homeless Services Authority (LAHSA)',
      category: 'Housing Authorities',
      address: '811 Wilshire Blvd, 6th Floor, Los Angeles, CA 90017',
      phone: '(213) 683-3333',
      director: 'Va Lecia Adams Kellum, PhD - CEO',
      description: 'Oversees CES, emergency shelters, outreach teams, countywide homelessness resources.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 2005,
      title: 'LA County Department of Military & Veterans Affairs (MVA)',
      category: 'Housing Authorities',
      address: '1816 S Figueroa St, 5th Floor, Los Angeles, CA 90015',
      phone: '(213) 765-9600',
      director: 'Brenda Farrell - Director',
      description: 'Claims assistance, advocacy, benefits navigation, county-based veteran support.',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop'
    },
    {
      id: 2006,
      title: 'LA County Department of Health Services (DHS) — Housing for Health',
      category: 'Housing Authorities',
      address: '313 N Figueroa St, Los Angeles, CA 90012',
      phone: '(213) 240-8178',
      director: 'Christina Ghaly, MD - Director',
      description: 'Supportive housing for medically fragile and chronically homeless veterans.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 2007,
      title: 'LA County Department of Mental Health (DMH)',
      category: 'Housing Authorities',
      address: '550 S Vermont Ave, Los Angeles, CA 90020',
      phone: '(213) 738-4000',
      director: 'Lisa Wong, Psy.D. - Director',
      description: 'Crisis intervention, therapy, psychiatry, peer services, housing subsidies for veterans.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    },
    {
      id: 2008,
      title: 'Volunteers of America Los Angeles (VOALA)',
      category: 'Housing Authorities',
      address: '3600 Wilshire Blvd, Suite 1500, Los Angeles, CA 90010',
      phone: '(213) 389-1500',
      director: 'Bob Pratt - President & CEO',
      description: 'SSVF rapid rehousing, transitional housing, outreach, mental health for homeless veterans.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 2009,
      title: 'California Department of Veterans Affairs (CalVet)',
      category: 'Housing Authorities',
      address: '1227 O St, Sacramento, CA 95814',
      phone: '1-800-952-5626',
      director: 'Vito Imbasciani, MD - Secretary',
      description: 'State-level veteran benefits: housing, long-term care, educational pathways.',
      image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=800&h=600&fit=crop'
    },
    {
      id: 2010,
      title: 'U.S. Department of Veterans Affairs (VA) - National HQ',
      category: 'Housing Authorities',
      address: 'Washington DC',
      phone: '1-877-424-3838',
      director: 'Monica Diaz - Executive Director, VHA Homeless Programs',
      description: 'Provides clinical care, social work, mental health, addiction treatment for HUD-VASH.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 2011,
      title: 'U.S. Department of Housing and Urban Development (HUD) - National HQ',
      category: 'Housing Authorities',
      address: '451 7th Street SW, Washington DC 20410',
      phone: '(202) 708-1112',
      director: 'Adrianne Todman - Acting Secretary',
      website: 'hud.gov',
      description: 'Oversees national Housing Choice Voucher programs including HUD-VASH.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    },

    // VSO Offices
    {
      id: 3001,
      title: 'American Legion – LA County Offices',
      category: 'Veteran Service Organizations (VSO)',
      address: '5309 Sepulveda Blvd, Sherman Oaks, CA',
      phone: '(818) 995-2573',
      description: 'Service officers help with VA disability claims, pension, housing referrals, emergency aid.',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop'
    },
    {
      id: 3002,
      title: 'Disabled American Veterans (DAV)',
      category: 'Veteran Service Organizations (VSO)',
      address: '11000 Wilshire Blvd, Suite 520, Los Angeles, CA 90024',
      phone: '(310) 235-2908',
      description: 'Free claims assistance, transportation programs, disability appeals representation.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 3003,
      title: 'Veterans of Foreign Wars (VFW)',
      category: 'Veteran Service Organizations (VSO)',
      address: '11301 Wilshire Blvd, Los Angeles, CA 90073',
      phone: '(310) 268-3715',
      description: 'Claims assistance, discharge upgrades, crisis support, housing referrals.',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop'
    },
    {
      id: 3004,
      title: 'AMVETS',
      category: 'Veteran Service Organizations (VSO)',
      address: '11301 Wilshire Blvd, Building 257, Los Angeles, CA 90073',
      phone: '(310) 478-3711 x 50041',
      description: 'Advocates for homeless veterans, processes claims, emergency assistance.',
      image: 'https://images.unsplash.com/photo-1559657881-6d271b60f644?w=800&h=600&fit=crop'
    },
    {
      id: 3005,
      title: 'Vietnam Veterans of America (VVA)',
      category: 'Veteran Service Organizations (VSO)',
      address: '16130 Ventura Blvd, Encino, CA',
      phone: '(818) 995-6958',
      description: 'Disability claims, Agent Orange claims, mental health resources, peer support.',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop'
    },
    {
      id: 3006,
      title: 'CalVet – LA Veterans Service Office',
      category: 'Veteran Service Organizations (VSO)',
      address: '1816 S Figueroa St, Los Angeles, CA 90015',
      phone: '(213) 765-9680',
      description: 'State-level support: property tax exemptions, college fee waivers, federal VA claims.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 3007,
      title: 'Jewish War Veterans (JWV)',
      category: 'Veteran Service Organizations (VSO)',
      address: '2200 North Sepulveda Blvd, Los Angeles, CA',
      phone: '(310) 478-3711',
      description: 'Moral support, advocacy, referral services, homeless veteran outreach.',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop'
    },
    {
      id: 3008,
      title: 'Marine Corps League',
      category: 'Veteran Service Organizations (VSO)',
      address: '2230 Lake Ave, Altadena, CA 91001',
      phone: '(818) 380-8115',
      description: 'Supports Marine veterans with crisis assistance, claims help, camaraderie.',
      image: 'https://images.unsplash.com/photo-1559657881-6d271b60f644?w=800&h=600&fit=crop'
    },
    {
      id: 3009,
      title: 'Catholic War Veterans',
      category: 'Veteran Service Organizations (VSO)',
      address: '11000 Wilshire Blvd, Los Angeles',
      phone: '(310) 235-2901',
      description: 'Community support, claims assistance, spiritual counseling, outreach.',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop'
    },
    {
      id: 3010,
      title: 'Student Veterans of America',
      category: 'Veteran Service Organizations (VSO)',
      address: 'Multiple campus chapters (UCLA, USC, CSUN)',
      description: 'Peer mentorship, career guidance, education support for student veterans.',
      image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&h=600&fit=crop'
    },

    // VA Departments
    {
      id: 4001,
      title: 'U.S. Department of Housing and Urban Development (HUD)',
      category: 'VA Departments',
      address: '451 7th St SW, Washington DC 20410',
      phone: '(202) 708-1112',
      director: 'Adrianne Todman - Acting Secretary',
      website: 'hud.gov',
      description: 'Oversees national Housing Choice Voucher programs including HUD-VASH.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    },
    {
      id: 4002,
      title: 'U.S. Department of Veterans Affairs (VA) - National HUD-VASH Program',
      category: 'VA Departments',
      phone: '1-877-424-3838',
      director: 'Monica Diaz - Executive Director, VHA Homeless Programs',
      website: 'va.gov/homeless',
      description: 'Case management and supportive services for HUD-VASH veterans.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 4003,
      title: 'Veterans Health Administration (VHA)',
      category: 'VA Departments',
      director: 'Dr. Shereef Elnahal - Under Secretary for Health',
      website: 'va.gov/health',
      description: 'Implements VA homeless and HUD-VASH programs through 18 VISNs + VAMCs.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 4004,
      title: 'Veterans Benefits Administration (VBA)',
      category: 'VA Departments',
      director: 'Joshua Jacobs - Under Secretary for Benefits',
      website: 'benefits.va.gov',
      description: 'Oversees benefits sustaining HUD-VASH: compensation, pensions, education.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 4005,
      title: 'National Center on Homelessness Among Veterans (NCHAV)',
      category: 'VA Departments',
      director: 'Dr. Dina Hooshyar, MD MPH - Director',
      website: 'va.gov/homeless/nchav',
      description: 'Research, policy development, training to improve HUD-VASH effectiveness.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 4006,
      title: 'Office of Mental Health and Suicide Prevention (OMHSP)',
      category: 'VA Departments',
      director: 'Dr. Matthew Miller - Executive Director',
      website: 'va.gov/health-care/mental-health',
      description: 'Integrates mental health care and crisis intervention within HUD-VASH.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    },
    {
      id: 4007,
      title: 'Supportive Services for Veteran Families (SSVF)',
      category: 'VA Departments',
      director: 'John Kuhn - National Director',
      website: 'va.gov/homeless/ssvf',
      description: 'Rapid rehousing and homelessness prevention to complement HUD-VASH.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 4008,
      title: 'National Coalition for Homeless Veterans (NCHV)',
      category: 'VA Departments',
      director: 'Kathryn Monet - CEO',
      website: 'nchv.org',
      description: 'National nonprofit advocating for HUD-VASH and related policies.',
      image: 'https://images.unsplash.com/photo-1559657881-6d271b60f644?w=800&h=600&fit=crop'
    },
    {
      id: 4009,
      title: 'U.S. Interagency Council on Homelessness (USICH)',
      category: 'VA Departments',
      director: 'Jeff Olivet - Executive Director',
      website: 'usich.gov',
      description: 'Coordinates federal agencies to implement national strategy to end homelessness.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 4010,
      title: 'U.S. Department of Labor – Veterans Employment and Training Service (VETS)',
      category: 'VA Departments',
      director: 'James Rodriguez - Assistant Secretary',
      website: 'dol.gov/agencies/vets',
      description: 'Supports employment placement for HUD-VASH veterans.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 4011,
      title: 'Substance Abuse and Mental Health Services Administration (SAMHSA)',
      category: 'VA Departments',
      director: 'Dr. Miriam Delphin-Rittmon - Administrator',
      website: 'samhsa.gov',
      description: 'Funds mental health and substance use recovery programs complementary to HUD-VASH.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    },
    {
      id: 4012,
      title: 'National Alliance to End Homelessness (NAEH)',
      category: 'VA Departments',
      director: 'Ann Oliva - President & CEO',
      website: 'endhomelessness.org',
      description: 'Nonprofit research and advocacy for federal homeless initiatives.',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop'
    },
    {
      id: 4013,
      title: 'National Homelessness Law Center (NHLC)',
      category: 'VA Departments',
      director: 'Maria Foscarinis - Founder',
      website: 'nlchp.org',
      description: 'Advocacy and legal policy for homeless rights and veteran housing protections.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop'
    },
    {
      id: 4014,
      title: 'Volunteers of America (VOA)',
      category: 'VA Departments',
      director: 'Mike King - President & CEO',
      website: 'voa.org',
      description: 'Major nonprofit HUD-VASH and SSVF service provider nationwide.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 4015,
      title: 'U.S. Department of Health and Human Services (HHS)',
      category: 'VA Departments',
      director: 'Xavier Becerra - Secretary',
      website: 'hhs.gov',
      description: 'Oversees health care systems intersecting HUD-VASH through SAMHSA.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 4016,
      title: 'National Association of Housing and Redevelopment Officials (NAHRO)',
      category: 'VA Departments',
      director: 'Mark Thompson - Interim CEO',
      website: 'nahro.org',
      description: 'Represents PHAs and coordinates policy training for HUD-VASH administrators.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 4017,
      title: 'National Call Center for Homeless Veterans',
      category: 'VA Departments',
      phone: '1-877-424-3838',
      description: '24/7 hotline for veterans experiencing or at risk of homelessness.',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop'
    },
    {
      id: 4018,
      title: 'Housing Authority of the City of Los Angeles (HACLA)',
      category: 'VA Departments',
      address: '2600 Wilshire Blvd, Los Angeles, CA 90057',
      director: 'Doug Guthrie - President & CEO',
      website: 'hacla.org',
      description: 'Primary PHA partner for HUD-VASH in Los Angeles City.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 4019,
      title: 'Los Angeles County Development Authority (LACDA)',
      category: 'VA Departments',
      director: 'Emilio Salas - Executive Director',
      website: 'lacda.org',
      description: 'Administers countywide HUD-VASH vouchers outside city limits.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 4020,
      title: 'Supportive Services for Veteran Families (SSVF) Program',
      category: 'VA Departments',
      director: 'Adrienne Nash Melendez - National Program Director',
      description: 'Rapid rehousing and homelessness prevention for veteran families.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },

    // LA VAGLAHS Facilities (11 facilities)
    {
      id: 5001,
      title: 'West Los Angeles VA Medical Center',
      category: 'LA VAGLAHS Facilities',
      address: '11301 Wilshire Blvd, Los Angeles, CA 90073-1003',
      description: 'Main medical center for the VA Greater Los Angeles Healthcare System.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 5002,
      title: 'Sepulveda Ambulatory Care Center',
      category: 'LA VAGLAHS Facilities',
      address: '16111 Plummer St, Sepulveda (North Hills), CA 91343-2036',
      description: 'Large outpatient care center.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 5003,
      title: 'Antelope Valley VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '44439 17th St West, Lancaster, CA 93534-2831',
      description: 'Outpatient clinic serving the Antelope Valley region.',
      image: 'https://images.unsplash.com/photo-1587351021759-3e566b6af7cc?w=800&h=600&fit=crop'
    },
    {
      id: 5004,
      title: 'Bakersfield VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '1801 Westwind Dr, Bakersfield, CA 93301-3028',
      description: 'Regional clinic serving Kern County veterans.',
      image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=800&h=600&fit=crop'
    },
    {
      id: 5005,
      title: 'Ventura VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '5250 Ralston St, Ventura, CA 93003-7318',
      description: 'Regional clinic serving Ventura County.',
      image: 'https://images.unsplash.com/photo-1519494140681-8b17d830a3e9?w=800&h=600&fit=crop'
    },
    {
      id: 5006,
      title: 'East Los Angeles VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '5426 E Olympic Blvd, Commerce, CA 90022-5113',
      description: 'CBOC/clinic serving East Los Angeles communities.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 5007,
      title: 'Los Angeles VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '351 E Temple St, Los Angeles, CA 90012-3328',
      description: 'Urban clinic serving downtown Los Angeles.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    },
    {
      id: 5008,
      title: 'San Gabriel Valley VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '7 W Foothill Blvd, Ste D, Arcadia, CA 91006-2367',
      description: 'Clinic in San Gabriel Valley.',
      image: 'https://images.unsplash.com/photo-1519494140681-8b17d830a3e9?w=800&h=600&fit=crop'
    },
    {
      id: 5009,
      title: 'San Luis Obispo VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '1288 Morro St, Ste 200, San Luis Obispo, CA 93401-6302',
      description: 'Clinic north of LA County.',
      image: 'https://images.unsplash.com/photo-1587351021759-3e566b6af7cc?w=800&h=600&fit=crop'
    },
    {
      id: 5010,
      title: 'Santa Barbara VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '4440 Calle Real, Santa Barbara, CA 93110-1002',
      description: 'Regional clinic serving Santa Barbara County.',
      image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=800&h=600&fit=crop'
    },
    {
      id: 5011,
      title: 'Santa Maria VA Clinic',
      category: 'LA VAGLAHS Facilities',
      address: '1550 E Main St, Santa Maria, CA 93454-4819',
      description: 'Regional clinic.',
      image: 'https://images.unsplash.com/photo-1519494140681-8b17d830a3e9?w=800&h=600&fit=crop'
    },

    // VA Top 50 Phone Contacts - First 25
    {
      id: 6001,
      title: 'Veterans Crisis Line',
      category: 'VA Top 50 Phone Contacts',
      phone: '988, Press 1',
      description: 'For any veteran in emotional distress, suicidal crisis, or needing immediate counseling. 24/7 trained crisis responders.',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop'
    },
    {
      id: 6002,
      title: 'VA Benefits Hotline',
      category: 'VA Top 50 Phone Contacts',
      phone: '800-827-1000',
      description: 'Primary number for all VA disability, compensation, pension, and benefit claims. Check claim status or get help submitting.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 6003,
      title: 'MyVA411 Main Information Line',
      category: 'VA Top 50 Phone Contacts',
      phone: '800-698-2411',
      description: "VA's universal help desk for ANY question regarding health care, benefits, payments, housing, or records.",
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop'
    },
    {
      id: 6004,
      title: 'VA Health Care Eligibility & Enrollment',
      category: 'VA Top 50 Phone Contacts',
      phone: '877-222-8387',
      description: 'Apply for VA health care or find out if you are eligible. Help with enrollment, priority groups, updating status.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 6005,
      title: 'VA Pharmacy & Medication Support',
      category: 'VA Top 50 Phone Contacts',
      phone: '800-633-7554',
      description: 'Prescription refills, tracking medication shipments, resolving medication delays.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    },
    {
      id: 6006,
      title: 'VA Appointment Scheduling Support',
      category: 'VA Top 50 Phone Contacts',
      phone: '877-741-3400',
      description: 'Assist with scheduling, rescheduling, and reporting appointment problems.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 6007,
      title: 'VA Women Veterans Call Center',
      category: 'VA Top 50 Phone Contacts',
      phone: '855-829-6636',
      description: 'Specialized support for women veterans. Health care, counseling, benefits, maternity services. 24/7 confidential.',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&h=600&fit=crop'
    },
    {
      id: 6008,
      title: 'VA Caregiver Support Line',
      category: 'VA Top 50 Phone Contacts',
      phone: '855-260-3274',
      description: 'Coaching, emotional support, resources for caregivers of veterans. Essential for home-based support.',
      image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=800&h=600&fit=crop'
    },
    {
      id: 6009,
      title: 'VA Homeless Veterans Hotline',
      category: 'VA Top 50 Phone Contacts',
      phone: '877-424-3838',
      description: "National homeless support line for shelter, housing, HUD-VASH referrals, emergency services. 24/7.",
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 6010,
      title: 'HUD-VASH National Program Support',
      category: 'VA Top 50 Phone Contacts',
      phone: '800-827-1000',
      description: 'Central contact for HUD-VASH eligibility, voucher issues, case management referrals, complaint escalation.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },

    // VISN Regional Offices - VA VISN Regional Directors Directory
    {
      id: 7001,
      title: 'VISN 1 – VA New England Healthcare System',
      category: 'VISN Regional Offices',
      director: 'Ryan Lilly, MPA - Network Director',
      phone: '781-687-2000',
      address: '200 Springs Rd, Bedford, MA 01730',
      website: 'va.gov/bedford-health-care',
      description: 'Mr. Lilly serves as director of the New England network overseeing facilities across six states.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    },
    {
      id: 7002,
      title: 'VISN 2 – VA Health Care Network Upstate New York',
      category: 'VISN Regional Offices',
      director: 'Dr. Richard T. Giordano - Network Director',
      phone: '607-664-4000',
      address: '130 S Elmwood Ave, Buffalo, NY 14202',
      website: 'visn2.va.gov',
      description: 'Oversees VA healthcare facilities across upstate New York region.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 7003,
      title: 'VISN 3 – VA NY/NJ Health Care Network',
      category: 'VISN Regional Offices',
      director: 'Michael Adelman, MD - Network Director',
      phone: '718-741-4300',
      address: '130 W Kingsbridge Rd, Bronx, NY 10468',
      website: 'visn3.va.gov',
      description: 'Manages VA healthcare operations across New York and New Jersey.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 7004,
      title: 'VISN 4 – VA Healthcare VISN 4',
      category: 'VISN Regional Offices',
      director: 'Donald McGeary - Network Director',
      phone: '215-823-5800',
      address: '3900 Woodland Ave, Philadelphia, PA 19104',
      website: 'visn4.va.gov',
      description: 'Coordinates VA healthcare services throughout the Mid-Atlantic region.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 7005,
      title: 'VISN 5 – VA Capitol Health Care Network',
      category: 'VISN Regional Offices',
      director: 'Kendra K. McBride - Network Director',
      phone: '410-230-4444',
      address: '10 N Greene St, Baltimore, MD 21201',
      website: 'visn5.va.gov',
      description: 'Directs VA healthcare facilities in the Capitol region.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 7006,
      title: 'VISN 6 – Mid-Atlantic Health Care Network',
      category: 'VISN Regional Offices',
      director: 'DeAnne Seekins - Network Director',
      phone: '919-286-0411',
      address: '508 Fulton St, Durham, NC 27705',
      website: 'visn6.va.gov',
      description: 'Oversees VA healthcare delivery across the Mid-Atlantic states.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 7007,
      title: 'VISN 7 – Southeast Network',
      category: 'VISN Regional Offices',
      director: 'Ann Brown - Network Director',
      phone: '404-321-6111',
      address: '1670 Clairmont Rd, Decatur, GA 30033',
      website: 'visn7.va.gov',
      description: 'Manages VA healthcare operations throughout the Southeast region.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 7008,
      title: 'VISN 8 – VA Sunshine Healthcare Network',
      category: 'VISN Regional Offices',
      director: 'Timothy Cooke - Network Director',
      phone: '727-399-3300',
      address: '8875 Hidden River Pkwy, Tampa, FL 33637',
      website: 'visn8.va.gov',
      description: 'Directs VA healthcare services across Florida and Puerto Rico.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 7009,
      title: 'VISN 9 – MidSouth Healthcare Network',
      category: 'VISN Regional Offices',
      director: 'Mary Ziegler - Network Director',
      phone: '615-327-4751',
      address: '1310 24th Ave S, Nashville, TN 37212',
      website: 'visn9.va.gov',
      description: 'Coordinates VA healthcare throughout the MidSouth region.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 7010,
      title: 'VISN 10 – VA Healthcare System of Ohio',
      category: 'VISN Regional Offices',
      director: 'Michael Adelman - Network Director',
      phone: '513-861-3100',
      address: '3200 Vine St, Cincinnati, OH 45220',
      website: 'visn10.va.gov',
      description: 'Oversees VA healthcare facilities throughout Ohio.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 7011,
      title: 'VISN 11 – Veterans Integrated Service Network 11',
      category: 'VISN Regional Offices',
      director: 'Robert McDivitt - Network Director',
      phone: '317-988-2000',
      address: '1481 W 10th St, Indianapolis, IN 46202',
      website: 'visn11.va.gov',
      description: 'Manages VA healthcare operations in the Great Lakes region.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 7012,
      title: 'VISN 12 – Great Lakes Health Care System',
      category: 'VISN Regional Offices',
      director: 'Ralph Gigliotti - Network Director',
      phone: '414-384-2000',
      address: '5000 W National Ave, Milwaukee, WI 53295',
      website: 'visn12.va.gov',
      description: 'Directs VA healthcare services across the Great Lakes states.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 7013,
      title: 'VISN 15 – VA Heartland Network',
      category: 'VISN Regional Offices',
      director: 'Bryan Matthews - Network Director',
      phone: '816-922-2000',
      address: '4801 Linwood Blvd, Kansas City, MO 64128',
      website: 'visn15.va.gov',
      description: 'Oversees VA healthcare throughout the Heartland region.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 7014,
      title: 'VISN 16 – South Central VA Health Care Network',
      category: 'VISN Regional Offices',
      director: 'Dr. Paul Tatum - Network Director',
      phone: '601-364-1100',
      address: '1500 E Woodrow Wilson Ave, Jackson, MS 39216',
      website: 'visn16.va.gov',
      description: 'Manages VA healthcare operations in the South Central region.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 7015,
      title: 'VISN 17 – Heart of Texas Health Care Network',
      category: 'VISN Regional Offices',
      director: 'Dr. David Shulkin - Network Director',
      phone: '214-742-8387',
      address: '4500 S Lancaster Rd, Dallas, TX 75216',
      website: 'visn17.va.gov',
      description: 'Coordinates VA healthcare throughout Texas and surrounding areas.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 7016,
      title: 'VISN 18 – Southwest Health Care Network',
      category: 'VISN Regional Offices',
      director: 'Dr. Steven Braverman - Network Director',
      phone: '602-277-5551',
      address: '650 E Indian School Rd, Phoenix, AZ 85012',
      website: 'visn18.va.gov',
      description: 'Directs VA healthcare services across the Southwest region.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 7017,
      title: 'VISN 19 – Rocky Mountain Network',
      category: 'VISN Regional Offices',
      director: 'Dr. Paul Roberts - Network Director',
      phone: '303-399-8020',
      address: '1700 N Wheeling St, Aurora, CO 80045',
      website: 'visn19.va.gov',
      description: 'Oversees VA healthcare facilities in the Rocky Mountain states.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 7018,
      title: 'VISN 20 – Northwest Network',
      category: 'VISN Regional Offices',
      director: 'Dr. Mark Jones - Network Director',
      phone: '503-273-5000',
      address: '3710 SW US Veterans Hospital Rd, Portland, OR 97239',
      website: 'visn20.va.gov',
      description: 'Manages VA healthcare operations throughout the Northwest region.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 7019,
      title: 'VISN 21 – Sierra Pacific Network',
      category: 'VISN Regional Offices',
      director: 'Dr. Jennifer Hogg - Network Director',
      phone: '415-221-4810',
      address: '4150 Clement St, San Francisco, CA 94121',
      website: 'visn21.va.gov',
      description: 'Coordinates VA healthcare services across California and Nevada.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 7020,
      title: 'VISN 22 – Desert Pacific Healthcare Network',
      category: 'VISN Regional Offices',
      director: 'Dr. Steven Braverman - Network Director',
      phone: '562-826-8000',
      address: '5901 E 7th St, Long Beach, CA 90822',
      website: 'visn22.va.gov',
      description: 'Directs VA healthcare operations in Southern California and Pacific regions.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },

    // VA Medical Center Directors - VISN 22 Desert Pacific Healthcare Network
    {
      id: 7021,
      title: 'VA Greater Los Angeles Healthcare System',
      category: 'VISN Regional Offices',
      director: 'Timothy Graham - Interim Medical Center Director',
      phone: '310-478-3711',
      address: '11301 Wilshire Blvd, Los Angeles, CA 90073-1003',
      website: 'va.gov/greater-los-angeles-health-care',
      description: 'Mr. Graham was appointed Interim Medical Center Director in January 2026 for one of the largest VA health systems in the country.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 7022,
      title: 'VA Long Beach Healthcare System (Tibor Rubin VA Medical Center)',
      category: 'VISN Regional Offices',
      director: 'Walt C. Dannenberg - Medical Center Director',
      phone: '562-826-8000',
      address: '5901 East 7th Street, Long Beach, CA 90822',
      website: 'va.gov/long-beach-health-care',
      description: 'Medical Center Director overseeing comprehensive healthcare services at Tibor Rubin VA Medical Center.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 7023,
      title: 'VA San Diego Healthcare System (Jennifer Moreno VA Medical Center)',
      category: 'VISN Regional Offices',
      director: 'Frank P. Pearson, DPT, PA-C - Medical Center Director',
      phone: '858-552-8585',
      address: '3350 La Jolla Village Dr, San Diego, CA 92161',
      website: 'va.gov/san-diego-health-care',
      description: 'Medical Center Director managing healthcare operations at Jennifer Moreno VA Medical Center in San Diego.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 7024,
      title: 'VA Phoenix Health Care System (Carl T. Hayden VA Medical Center)',
      category: 'VISN Regional Offices',
      director: 'Elijah J. Ditter, MHA - Medical Center Director',
      phone: '602-277-5551',
      address: '650 East Indian School Rd, Phoenix, AZ 85012',
      website: 'va.gov/phoenix-health-care',
      description: 'Mr. Ditter has served as Medical Center Director of the Phoenix VA system since 2026.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 7025,
      title: 'VA Southern Arizona Health Care System (Tucson VA Medical Center)',
      category: 'VISN Regional Offices',
      director: 'John B. Kettelle, MD - Interim Medical Center Director',
      phone: '520-792-1450',
      address: '3601 South 6th Ave, Tucson, AZ 85723',
      website: 'va.gov/southern-arizona-health-care',
      description: 'Interim Medical Center Director overseeing Tucson VA Medical Center operations.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 7026,
      title: 'VA New Mexico Health Care System (Raymond G. Murphy VA Medical Center)',
      category: 'VISN Regional Offices',
      director: 'Breton Weintraub, MD - Executive Director',
      phone: '505-265-1711',
      address: '1501 San Pedro Dr SE, Albuquerque, NM 87108',
      website: 'va.gov/new-mexico-health-care',
      description: 'Executive Director managing healthcare services at Raymond G. Murphy VA Medical Center.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 7027,
      title: 'VA Loma Linda Healthcare System (Jerry L. Pettis Memorial Veterans Hospital)',
      category: 'VISN Regional Offices',
      director: 'Scott Kelter - Interim Medical Center Director',
      phone: '909-825-7084',
      address: '11201 Benton St, Loma Linda, CA 92357',
      website: 'va.gov/loma-linda-health-care',
      description: 'Interim Medical Center Director overseeing Jerry L. Pettis Memorial Veterans Hospital operations.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },

    // Los Angeles Shelters - 50 Homeless Shelters in Los Angeles
    {
      id: 8001,
      title: 'Union Rescue Mission',
      category: 'Los Angeles Shelters',
      phone: '213-347-6300',
      address: '545 S San Pedro St, Los Angeles, CA 90013',
      website: 'urm.org',
      description: 'One of the largest emergency shelters on Skid Row providing beds, meals, and recovery services.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8002,
      title: 'Los Angeles Mission',
      category: 'Los Angeles Shelters',
      phone: '213-629-1227',
      address: '303 E 5th St, Los Angeles, CA 90013',
      website: 'losangelesmission.org',
      description: 'Emergency shelter, meals, recovery programs, and job services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8003,
      title: 'The Midnight Mission',
      category: 'Los Angeles Shelters',
      phone: '213-624-9258',
      address: '601 S San Pedro St, Los Angeles, CA 90014',
      website: 'midnightmission.org',
      description: 'Provides shelter, meals, recovery services, and housing programs.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8004,
      title: 'Weingart Center',
      category: 'Los Angeles Shelters',
      phone: '213-627-9000',
      address: '566 S San Pedro St, Los Angeles, CA 90013',
      website: 'weingart.org',
      description: 'Major Skid Row shelter offering housing and employment programs.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8005,
      title: 'Downtown Women\'s Center',
      category: 'Los Angeles Shelters',
      phone: '213-680-0600',
      address: '442 S San Pedro St, Los Angeles, CA 90013',
      website: 'downtownwomenscenter.org',
      description: 'Shelter and housing for women experiencing homelessness.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8006,
      title: 'Hope the Mission',
      category: 'Los Angeles Shelters',
      phone: '818-392-0020',
      address: '16641 Roscoe Pl, North Hills, CA 91343',
      website: 'hopethemission.org',
      description: 'Operates multiple shelters and winter emergency housing.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8007,
      title: 'PATH (People Assisting the Homeless)',
      category: 'Los Angeles Shelters',
      phone: '323-644-2200',
      address: '340 N Madison Ave, Los Angeles, CA 90004',
      website: 'epath.org',
      description: 'Large nonprofit providing shelter and housing programs.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8008,
      title: 'Volunteers of America Los Angeles',
      category: 'Los Angeles Shelters',
      phone: '213-389-1500',
      address: '3600 Wilshire Blvd, Los Angeles, CA 90010',
      website: 'voala.org',
      description: 'Transitional housing and emergency shelter services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8009,
      title: 'St Vincent de Paul Cardinal Manning Center',
      category: 'Los Angeles Shelters',
      phone: '213-229-9971',
      address: '231 Winston St, Los Angeles, CA 90013',
      website: 'svdpla.org',
      description: 'Emergency shelter and meals on Skid Row.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8010,
      title: 'The People Concern',
      category: 'Los Angeles Shelters',
      phone: '323-334-9000',
      address: '527 S Crocker St, Los Angeles, CA 90013',
      website: 'thepeopleconcern.org',
      description: 'Housing and outreach programs for homeless individuals.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8011,
      title: 'LA Family Housing',
      category: 'Los Angeles Shelters',
      phone: '818-255-2766',
      address: '7843 Lankershim Blvd, North Hollywood, CA 91605',
      website: 'lafh.org',
      description: 'Provides shelter, outreach, and housing placement.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8012,
      title: 'Harbor Interfaith Services',
      category: 'Los Angeles Shelters',
      phone: '310-831-0589',
      address: '670 W 9th St, San Pedro, CA 90731',
      website: 'harborinterfaith.org',
      description: 'Shelter and support services for families.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8013,
      title: 'Union Station Homeless Services',
      category: 'Los Angeles Shelters',
      phone: '626-240-4550',
      address: '412 S Raymond Ave, Pasadena, CA 91105',
      website: 'unionstationhs.org',
      description: 'Emergency and transitional housing programs.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8014,
      title: 'First to Serve',
      category: 'Los Angeles Shelters',
      phone: '323-293-6291',
      address: '1718 W Vernon Ave, Los Angeles, CA 90062',
      website: 'firsttoserve.org',
      description: 'Bridge housing and recovery services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8015,
      title: 'HOPICS Shelter',
      category: 'Los Angeles Shelters',
      phone: '323-948-0444',
      address: '5715 S Broadway, Los Angeles, CA 90037',
      website: 'hopics.org',
      description: 'Transitional housing and mental health services.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8016,
      title: 'Safe Place for Youth',
      category: 'Los Angeles Shelters',
      phone: '310-902-2283',
      address: '2469 Lincoln Blvd, Venice, CA 90291',
      website: 'safeplaceforyouth.org',
      description: 'Shelter services for homeless youth.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8017,
      title: 'Covenant House California',
      category: 'Los Angeles Shelters',
      phone: '323-461-3131',
      address: '1325 N Western Ave, Hollywood, CA 90027',
      website: 'covenanthousecalifornia.org',
      description: 'Shelter for homeless youth ages 18-24.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8018,
      title: 'Jovenes Inc.',
      category: 'Los Angeles Shelters',
      phone: '323-260-8035',
      address: '1208 Pleasant Ave, Los Angeles, CA 90033',
      website: 'jovenesinc.org',
      description: 'Housing and services for homeless youth.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8019,
      title: 'Alexandria House',
      category: 'Los Angeles Shelters',
      phone: '213-381-2649',
      address: '426 S Alexandria Ave, Los Angeles, CA 90020',
      website: 'alexandriahouse.org',
      description: 'Transitional housing for women and children.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8020,
      title: 'Upward Bound House',
      category: 'Los Angeles Shelters',
      phone: '310-458-7779',
      address: '1104 Washington Ave, Santa Monica, CA 90403',
      website: 'upwardboundhouse.org',
      description: 'Family homeless shelter.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8021,
      title: 'Salvation Army Bell Shelter',
      category: 'Los Angeles Shelters',
      phone: '323-263-1206',
      address: '5600 Rickenbacker Rd, Bell, CA 90201',
      website: 'salvationarmyusa.org',
      description: 'One of the largest emergency shelters in LA County.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8022,
      title: 'New Directions for Veterans',
      category: 'Los Angeles Shelters',
      phone: '310-914-4045',
      address: '11303 Wilshire Blvd, Los Angeles, CA 90073',
      website: 'ndvets.org',
      description: 'Housing and services for homeless veterans.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8023,
      title: 'US VETS Inglewood',
      category: 'Los Angeles Shelters',
      phone: '310-348-7600',
      address: '733 S Hindry Ave, Inglewood, CA 90301',
      website: 'usvets.org',
      description: 'Transitional housing for veterans.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8024,
      title: 'Chrysalis Shelter Programs',
      category: 'Los Angeles Shelters',
      phone: '213-806-6300',
      address: '522 S Main St, Los Angeles, CA 90013',
      website: 'changelives.org',
      description: 'Employment-focused shelter support.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8025,
      title: 'Project Angel Food',
      category: 'Los Angeles Shelters',
      phone: '323-845-1800',
      address: '922 Vine St, Los Angeles, CA 90038',
      website: 'projectangelfood.org',
      description: 'Meals and support for people with illness experiencing homelessness.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8026,
      title: 'Bread & Roses Café',
      category: 'Los Angeles Shelters',
      phone: '323-751-1358',
      address: '825 W Manchester Ave, Los Angeles, CA 90044',
      website: 'breadandrosescafe.org',
      description: 'Free meals and basic support services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8027,
      title: 'Midnight Mission Courtyard',
      category: 'Los Angeles Shelters',
      phone: '213-624-9258',
      address: '601 S San Pedro St, Los Angeles, CA 90014',
      website: 'midnightmission.org',
      description: 'Shelter and recovery programs.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8028,
      title: 'The Dream Center',
      category: 'Los Angeles Shelters',
      phone: '213-273-7000',
      address: '2301 Bellevue Ave, Los Angeles, CA 90026',
      website: 'dreamcenter.org',
      description: 'Residential programs and emergency shelter.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8029,
      title: 'Good Shepherd Center',
      category: 'Los Angeles Shelters',
      phone: '213-482-1834',
      address: '1671 Beverly Blvd, Los Angeles, CA 90026',
      website: 'gscla.org',
      description: 'Shelter and housing for women and children.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8030,
      title: 'Testimonial Community Love Center',
      category: 'Los Angeles Shelters',
      phone: '323-291-6753',
      address: '5701 S Western Ave, Los Angeles, CA 90062',
      website: 'tclcenter.org',
      description: 'Shelter and food services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8031,
      title: 'LA LGBT Center Youth Shelter',
      category: 'Los Angeles Shelters',
      phone: '323-993-7400',
      address: '1220 N Highland Ave, Los Angeles, CA 90038',
      website: 'lalgbtcenter.org',
      description: 'Housing for LGBTQ youth experiencing homelessness.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8032,
      title: 'Venice Family Clinic Shelter Programs',
      category: 'Los Angeles Shelters',
      phone: '310-392-8636',
      address: '604 Rose Ave, Venice, CA 90291',
      website: 'venicefamilyclinic.org',
      description: 'Health and housing support.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8033,
      title: 'St Joseph Center',
      category: 'Los Angeles Shelters',
      phone: '310-396-6468',
      address: '204 Hampton Dr, Venice, CA 90291',
      website: 'stjosephctr.org',
      description: 'Outreach and emergency housing.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8034,
      title: 'Safe Refuge',
      category: 'Los Angeles Shelters',
      phone: '213-482-0775',
      address: '963 N Hill St, Los Angeles, CA 90012',
      website: 'saferefuge.org',
      description: 'Shelter and recovery services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8035,
      title: 'Valley Oasis Shelter',
      category: 'Los Angeles Shelters',
      phone: '661-945-6736',
      address: '302 E Ave Q9, Palmdale, CA 93550',
      website: 'valleyoasis.org',
      description: 'Emergency shelter and domestic violence housing.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8036,
      title: 'Hathaway-Sycamores',
      category: 'Los Angeles Shelters',
      phone: '626-395-7100',
      address: '840 N Lake Ave, Pasadena, CA 91104',
      website: 'hathawaysycamores.org',
      description: 'Shelter and mental health services.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8037,
      title: 'Bridge Housing at The People Concern',
      category: 'Los Angeles Shelters',
      phone: '310-828-6717',
      address: '1447 16th St, Santa Monica, CA 90404',
      website: 'thepeopleconcern.org',
      description: 'Interim housing services.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8038,
      title: 'LA Youth Network Shelter',
      category: 'Los Angeles Shelters',
      phone: '323-467-8460',
      address: '1751 N Schrader Blvd, Hollywood, CA 90028',
      website: 'layn.org',
      description: 'Housing programs for homeless youth.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8039,
      title: 'Covenant House Hollywood Shelter',
      category: 'Los Angeles Shelters',
      phone: '323-461-3131',
      address: '1325 N Western Ave, Los Angeles, CA 90027',
      website: 'covenanthousecalifornia.org',
      description: 'Emergency youth shelter and services.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8040,
      title: 'Harbor City Interim Housing',
      category: 'Los Angeles Shelters',
      phone: '310-534-5345',
      address: '785 W Anaheim St, Harbor City, CA 90710',
      website: 'harborinterfaith.org',
      description: 'Shelter services for adults and families.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8041,
      title: 'Pasadena Salvation Army Shelter',
      category: 'Los Angeles Shelters',
      phone: '626-773-4400',
      address: '960 E Walnut St, Pasadena, CA 91106',
      website: 'salvationarmy.org',
      description: 'Shelter and social services.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8042,
      title: 'Long Beach Rescue Mission',
      category: 'Los Angeles Shelters',
      phone: '562-591-1292',
      address: '1430 Pacific Ave, Long Beach, CA 90813',
      website: 'lbrm.org',
      description: 'Shelter and recovery program.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8043,
      title: 'Village Family Services Shelter',
      category: 'Los Angeles Shelters',
      phone: '818-755-8786',
      address: '6736 Laurel Canyon Blvd, North Hollywood, CA 91606',
      website: 'thevillagefs.org',
      description: 'Housing and family services.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8044,
      title: 'Ascencia Homeless Services',
      category: 'Los Angeles Shelters',
      phone: '818-246-7900',
      address: '1851 Tyburn St, Glendale, CA 91204',
      website: 'ascenciaca.org',
      description: 'Emergency shelter and housing programs.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8045,
      title: 'Door of Hope',
      category: 'Los Angeles Shelters',
      phone: '626-304-9130',
      address: '773 N Marengo Ave, Pasadena, CA 91103',
      website: 'doorofhope.us',
      description: 'Shelter for families with children.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8046,
      title: 'Union Station Homeless Services Shelter',
      category: 'Los Angeles Shelters',
      phone: '626-240-4550',
      address: '412 S Raymond Ave, Pasadena, CA 91105',
      website: 'unionstationhs.org',
      description: 'Bridge housing and shelter programs.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8047,
      title: 'Project Roomkey Interim Housing',
      category: 'Los Angeles Shelters',
      phone: '211',
      address: 'Multiple LA sites',
      website: 'lahsa.org',
      description: 'Temporary housing for homeless individuals.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8048,
      title: 'LAHSA Winter Shelter Program',
      category: 'Los Angeles Shelters',
      phone: '211',
      address: 'Multiple locations',
      website: 'lahsa.org',
      description: 'Seasonal emergency shelters.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },
    {
      id: 8049,
      title: 'Hilda L. Solis Care First Village',
      category: 'Los Angeles Shelters',
      phone: '213-974-1234',
      address: '845 N Spring St, Los Angeles, CA 90012',
      website: 'lacounty.gov',
      description: 'Tiny home shelter community.',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      id: 8050,
      title: 'A Bridge Home Shelter Program',
      category: 'Los Angeles Shelters',
      phone: '311',
      address: 'Multiple LA sites',
      website: 'lacity.org',
      description: 'City-run temporary housing program.',
      image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=800&h=600&fit=crop'
    },

    // VA Executive Leadership - Top 20 National Leaders
    {
      id: 9001,
      title: 'Secretary of Veterans Affairs',
      category: 'VA Executive Leadership',
      director: 'Hon. Denis McDonough',
      phone: '202-461-4800',
      address: '810 Vermont Ave NW, Washington, DC 20420',
      website: 'va.gov',
      description: 'Chief executive officer leading the U.S. Department of Veterans Affairs.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    },
    {
      id: 9002,
      title: 'Deputy Secretary of Veterans Affairs',
      category: 'VA Executive Leadership',
      director: 'Hon. Tanya J. Bradsher',
      phone: '202-461-4800',
      address: '810 Vermont Ave NW, Washington, DC 20420',
      website: 'va.gov',
      description: 'Second-ranking official supporting the Secretary in VA operations.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 9003,
      title: 'Chief of Staff, Department of Veterans Affairs',
      category: 'VA Executive Leadership',
      director: 'Christopher Syrek',
      phone: '202-461-4800',
      address: '810 Vermont Ave NW, Washington, DC 20420',
      website: 'department.va.gov',
      description: 'Coordinates VA executive office operations and strategic initiatives.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 9004,
      title: 'Under Secretary for Health',
      category: 'VA Executive Leadership',
      director: 'Dr. Shereef Elnahal',
      phone: '202-461-7000',
      address: '810 Vermont Ave NW, Washington, DC 20420',
      website: 'va.gov/health',
      description: 'Leads Veterans Health Administration providing healthcare to 9+ million veterans.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 9005,
      title: 'Under Secretary for Benefits',
      category: 'VA Executive Leadership',
      director: 'Joshua Jacobs (Acting)',
      phone: '202-461-9700',
      address: '810 Vermont Ave NW, Washington, DC 20420',
      website: 'benefits.va.gov',
      description: 'Oversees Veterans Benefits Administration delivering benefits to millions.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 9006,
      title: 'Under Secretary for Memorial Affairs',
      category: 'VA Executive Leadership',
      director: 'Matthew Quinn',
      phone: '202-461-9700',
      address: '810 Vermont Ave NW',
      website: 'cem.va.gov',
      description: 'Directs National Cemetery Administration honoring veterans.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    },
    {
      id: 9007,
      title: 'VA Inspector General',
      category: 'VA Executive Leadership',
      director: 'Michael J. Missal',
      phone: '800-488-8244',
      address: '801 I Street NW, Washington DC 20001',
      website: 'va.gov/oig',
      description: 'Independent oversight conducting audits and investigations.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 9008,
      title: 'Chairman, Board of Veterans Appeals',
      category: 'VA Executive Leadership',
      director: 'Kenneth Arnold',
      phone: '800-923-8387',
      address: '810 Vermont Ave NW',
      website: 'bva.va.gov',
      description: 'Leads appellate review board for VA benefits decisions.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 9009,
      title: 'VA General Counsel',
      category: 'VA Executive Leadership',
      director: 'Jason Delery',
      phone: '202-461-4900',
      address: '810 Vermont Ave NW',
      website: 'va.gov/ogc',
      description: 'Chief legal officer providing counsel to VA leadership.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 9010,
      title: 'Assistant Secretary for Public Affairs',
      category: 'VA Executive Leadership',
      director: 'Terrence Hayes',
      phone: '202-461-7600',
      address: '810 Vermont Ave NW',
      website: 'va.gov/opa',
      description: 'Manages VA communications and media relations.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 9011,
      title: 'Assistant Secretary for Congressional Affairs',
      category: 'VA Executive Leadership',
      director: 'Liaison Office Director',
      phone: '202-461-6900',
      address: '810 Vermont Ave NW',
      website: 'va.gov/oca',
      description: 'Serves as VA liaison to U.S. Congress.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 9012,
      title: 'Assistant Secretary for Human Resources & Administration',
      category: 'VA Executive Leadership',
      director: 'Gina Grosso',
      phone: '202-461-7800',
      address: '810 Vermont Ave NW',
      website: 'va.gov/ohrm',
      description: 'Oversees VA workforce management and HR policies.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 9013,
      title: 'Assistant Secretary for Operations, Security & Preparedness',
      category: 'VA Executive Leadership',
      director: 'Jon Adler',
      phone: '202-461-5134',
      address: '810 Vermont Ave NW',
      website: 'va.gov/osp',
      description: 'Ensures VA facility security and emergency readiness.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    },
    {
      id: 9014,
      title: 'Assistant Secretary for Enterprise Integration',
      category: 'VA Executive Leadership',
      director: 'Executive Director OEI',
      phone: '202-461-7100',
      address: '810 Vermont Ave NW',
      website: 'va.gov/oie',
      description: 'Coordinates VA strategic planning and integration.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 9015,
      title: 'Chief Information Officer',
      category: 'VA Executive Leadership',
      director: 'Kurt DelBene',
      phone: '202-461-6000',
      address: '810 Vermont Ave NW',
      website: 'oit.va.gov',
      description: 'Leads VA technology infrastructure and digital transformation.',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=600&fit=crop'
    },
    {
      id: 9016,
      title: 'Chief Data Officer',
      category: 'VA Executive Leadership',
      director: 'Charles Worthington',
      phone: '202-461-6000',
      address: '810 Vermont Ave NW',
      website: 'va.gov/data',
      description: 'Manages VA data governance and analytics strategy.',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&h=600&fit=crop'
    },
    {
      id: 9017,
      title: 'Chief Veterans Experience Officer',
      category: 'VA Executive Leadership',
      director: 'Brooke Kovach',
      phone: '202-461-7000',
      address: '810 Vermont Ave NW',
      website: 'va.gov/ve',
      description: 'Improves veteran experience across all VA touchpoints.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 9018,
      title: 'Executive Director – VA Homeless Programs Office',
      category: 'VA Executive Leadership',
      director: 'Monica Diaz',
      phone: '877-424-3838',
      address: '810 Vermont Ave NW',
      website: 'va.gov/homeless',
      description: 'Leads national efforts to end veteran homelessness.',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop'
    },
    {
      id: 9019,
      title: 'Executive Director – Office of Mental Health & Suicide Prevention',
      category: 'VA Executive Leadership',
      director: 'Dr. Matthew Miller',
      phone: '202-461-4058',
      address: '810 Vermont Ave NW',
      website: 'mentalhealth.va.gov',
      description: 'Oversees VA mental health services and suicide prevention initiatives.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop'
    },
    {
      id: 9020,
      title: 'Executive Director – Office of Rural Health',
      category: 'VA Executive Leadership',
      director: 'Thomas Klobucar',
      phone: '202-461-6750',
      address: '810 Vermont Ave NW',
      website: 'ruralhealth.va.gov',
      description: 'Improves healthcare access for rural veterans nationwide.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop'
    }
  ];

  const filteredResources = selectedCategory === 'All Categories' 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  const handleSearch = () => {
    setShowResults(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      <TopNavbar showAuth={false} />
      
      {!showResults ? (
        /* HERO LANDING PAGE */
        <div 
          className="relative min-h-screen flex items-center justify-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/5urbv78f_vecteezy_panoramic-view-of-etretat-town-and-cliffs-from-green-hill_68336470.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
          
          {/* Content */}
          <div className="relative z-10 text-center px-4 max-w-6xl mx-auto" style={{ paddingTop: '96px' }}>
            <h1 className="font-bold mb-8" style={{ 
              color: '#FFFFFF',
              textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
              fontFamily: 'system-ui',
              lineHeight: '1.2',
              fontSize: '20pt'
            }}>
              Let's Explore VA Locations, Non-Profits, Law Firms, Support Services, and More
            </h1>
            
            <p className="text-lg mb-8" style={{ 
              color: '#FFFFFF',
              textShadow: '1px 1px 4px rgba(0,0,0,0.7)'
            }}>
              Immediately find HUD-VASH supportive services, transportation, and VA locations
            </p>

            {/* Search Bar - Compact Spacing */}
            <div className="bg-white rounded-lg shadow-2xl p-3 flex flex-col md:flex-row gap-3 items-center mb-8">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search size={18} style={{ color: '#999' }} />
                <input
                  type="text"
                  placeholder="What are you looking for?"
                  value={searchWhat}
                  onChange={(e) => setSearchWhat(e.target.value)}
                  className="flex-1 border-0 outline-none text-base py-2"
                  style={{ color: '#333' }}
                />
              </div>

              <div className="flex-1 flex items-center gap-2 px-3 border-l border-gray-200">
                <MapPin size={18} style={{ color: '#999' }} />
                <input
                  type="text"
                  placeholder="Search Location"
                  value={searchWhere}
                  onChange={(e) => setSearchWhere(e.target.value)}
                  className="flex-1 border-0 outline-none text-base py-2"
                  style={{ color: '#333' }}
                />
              </div>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border-0 outline-none rounded text-sm"
                style={{ backgroundColor: '#F5F5F5', color: '#333' }}
              >
                <option value="All Categories">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </select>

              <button
                onClick={handleSearch}
                className="px-6 py-2 rounded font-semibold text-base transition-all hover:opacity-90"
                style={{ backgroundColor: '#EF4444', color: '#FFFFFF' }}
              >
                Search
              </button>
            </div>

            {/* Category Cards Grid - Shorter & More Compact */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
              {categories.map((cat, index) => {
                const IconComponent = cat.icon;
                return (
                  <button
                    key={cat.name}
                    onClick={() => {
                      setSelectedCategory(cat.name);
                      setShowResults(true);
                    }}
                    className="rounded-xl p-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex flex-col items-center justify-between"
                    style={{ 
                      backgroundColor: cat.bgColor,
                      border: '1px solid rgba(0,0,0,0.05)',
                      minHeight: '140px'
                    }}
                  >
                    {/* Icon */}
                    <div className="flex items-center justify-center mb-2">
                      <IconComponent 
                        color={cat.iconColor}
                        size={40}
                      />
                    </div>
                    
                    {/* Category Name */}
                    <div 
                      className="text-sm font-bold text-center mb-2"
                      style={{ 
                        color: cat.bgColor === '#A5343A' ? '#FFFFFF' : '#1A1A1A',
                        lineHeight: '1.2'
                      }}
                    >
                      {cat.name}
                    </div>
                    
                    {/* Listings Count */}
                    <div 
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ 
                        color: cat.bgColor === '#A5343A' ? '#FFFFFF' : cat.iconColor,
                        opacity: 0.9
                      }}
                    >
                      {cat.listings} LISTINGS
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer Section - Compact Emergency Contacts */}
            <div className="mt-12 py-4 px-6 rounded-lg max-w-5xl mx-auto" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <p className="text-sm text-center mb-3" style={{ color: '#E0E0E0' }}>
                <strong>Can't find what you need?</strong> Contact VA National Hotline: <strong style={{ color: '#FFCC01' }}>1-800-827-1000</strong> or Homeless Veterans Hotline: <strong style={{ color: '#FFCC01' }}>1-877-424-3838</strong>
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-xs" style={{ color: '#B0B0B0' }}>
                <span>📞 Crisis Line: <strong>988</strong> (Press 1)</span>
                <span>•</span>
                <span>🏠 HUD-VASH: <strong>877-424-3838</strong></span>
                <span>•</span>
                <span>⚕️ VA Health: <strong>877-222-8387</strong></span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* RESULTS PAGE - NEW LAYOUT WITH SIDEBAR */
        <div className="flex min-h-screen" style={{ paddingTop: '60px' }}>
          {/* LEFT SIDEBAR */}
          <div className="w-96 bg-white border-r border-gray-200 p-6 overflow-y-auto" style={{ height: 'calc(100vh - 60px)', position: 'sticky', top: '60px' }}>
            
            {/* MAP PREVIEW */}
            <div className="mb-6 relative rounded-lg overflow-hidden" style={{ height: '180px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d423286.27405770525!2d-118.69192993934945!3d34.02016130653294!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c75ddc27da13%3A0xe22fdf6f254608f4!2sLos%20Angeles%2C%20CA!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <button 
                className="absolute top-3 left-3 px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 shadow-lg"
                style={{ backgroundColor: '#FF6B6B', color: '#FFFFFF' }}
              >
                <MapPin size={16} />
                See On Map
              </button>
            </div>

            {/* SEARCH SECTION */}
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-4" style={{ color: '#333' }}>Search</h3>
              
              <div className="mb-3">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#333' }}>What</label>
                <input
                  type="text"
                  placeholder="Any keywords..."
                  value={searchWhat}
                  onChange={(e) => setSearchWhat(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  style={{ backgroundColor: '#FAFAFA' }}
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#333' }}>Where</label>
                <input
                  type="text"
                  placeholder="City, postcode..."
                  value={searchWhere}
                  onChange={(e) => setSearchWhere(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  style={{ backgroundColor: '#FAFAFA' }}
                />
              </div>

              <button 
                className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2"
                style={{ backgroundColor: '#FF6B6B' }}
              >
                <Search size={18} />
                Search
              </button>
            </div>

            {/* FILTER SECTION */}
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-4" style={{ color: '#333' }}>Filter</h3>
              
              {/* Categories Dropdown */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <button className="w-full flex items-center justify-between py-2">
                  <span className="font-semibold" style={{ color: '#333' }}>Categories</span>
                  <ChevronDown size={18} />
                </button>
              </div>

              {/* Price Range */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <h4 className="font-semibold mb-3" style={{ color: '#333' }}>Price Range</h4>
                <input type="range" className="w-full mb-2" />
                <div className="text-sm text-gray-600">From <strong>$0</strong> to <strong>$2000</strong></div>
              </div>

              {/* Quick Filters */}
              <div className="mb-4 pb-4 border-b border-gray-200 space-y-2">
                <button className="w-full flex items-center justify-between py-2 text-sm hover:bg-gray-50 rounded">
                  <span className="text-gray-700">Open Now</span>
                  <span className="text-gray-400">⏰</span>
                </button>
                <button className="w-full flex items-center justify-between py-2 text-sm hover:bg-gray-50 rounded">
                  <span className="text-gray-700">Highest Rated</span>
                  <span className="text-gray-400">⭐</span>
                </button>
                <button className="w-full flex items-center justify-between py-2 text-sm hover:bg-gray-50 rounded">
                  <span className="text-gray-700">Most Reviewed</span>
                  <span className="text-gray-400">💬</span>
                </button>
              </div>

              {/* Filter by Tags */}
              <div className="mb-4">
                <h4 className="font-semibold mb-3" style={{ color: '#333' }}>Filter by tags</h4>
                <div className="space-y-2 text-sm">
                  {['Street Foody', 'Luxury', 'Chinese', 'Fast Food', 'Drinking', 'Coffee', 'Italian', 'Beer Club', 'Vegetarian', 'Hotpots'].map(tag => (
                    <label key={tag} className="flex items-center gap-2 text-gray-700 cursor-pointer">
                      <input type="checkbox" className="rounded" />
                      <span>{tag}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* ADVERTISEMENT PLACEHOLDER */}
            <div className="rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=200&fit=crop"
                alt="Ad"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* MAIN CONTENT AREA */}
          <div className="flex-1 p-8" style={{ backgroundColor: '#F9FAFB' }}>
            {/* Breadcrumb - Moved from sidebar */}
            <div className="mb-4 text-sm">
              <button
                onClick={() => setShowResults(false)}
                className="hover:underline"
                style={{ color: '#FF6B6B' }}
              >
                Home
              </button>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-gray-600">{selectedCategory}</span>
            </div>

            {/* TOP BAR - Updated headline format */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#333' }}>
                  {selectedCategory} ({filteredResources.length} Listings)
                </h2>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Sort By */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: '#666' }}>Sort by</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="latest">Latest</option>
                    <option value="highest-rated">Highest Rated</option>
                    <option value="most-reviewed">Most Reviewed</option>
                  </select>
                </div>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : ''}`}
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : ''}`}
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* LISTINGS GRID - Updated Design */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredResources.map(resource => (
                <div 
                  key={resource.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                  style={{ border: '1px solid #f0f0f0' }}
                >
                  {/* Image - HALF HEIGHT (removed FREE badge) */}
                  <div className="relative h-32 overflow-hidden">
                    <img 
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Star Rating */}
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <svg key={star} width="14" height="14" viewBox="0 0 24 24" fill="#FFB800" stroke="none">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                      <span className="text-xs text-gray-500 ml-1">(4.{Math.floor(Math.random() * 9) + 1})</span>
                    </div>

                    {/* Business Name */}
                    <h3 className="text-base font-bold mb-2 line-clamp-2" style={{ color: '#1a1a1a', minHeight: '40px' }}>
                      {resource.title}
                    </h3>

                    {/* Short Description */}
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2" style={{ minHeight: '32px' }}>
                      {resource.description || 'Professional veteran support services providing comprehensive assistance and resources for veterans.'}
                    </p>

                    {/* Contact Information */}
                    <div className="space-y-2 mb-4">
                      {/* Address */}
                      {resource.address && (
                        <div className="flex items-start gap-2">
                          <MapPin size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#FF6B6B' }} />
                          <span className="text-xs text-gray-600 line-clamp-2">{resource.address}</span>
                        </div>
                      )}
                      
                      {/* Phone */}
                      {resource.phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="flex-shrink-0" style={{ color: '#FF6B6B' }} />
                          <span className="text-xs font-semibold text-gray-700">{resource.phone}</span>
                        </div>
                      )}
                      
                      {/* Website */}
                      {resource.website && (
                        <div className="flex items-center gap-2">
                          <Globe size={14} className="flex-shrink-0" style={{ color: '#FF6B6B' }} />
                          <a 
                            href={`https://${resource.website}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs hover:underline truncate"
                            style={{ color: '#FF6B6B' }}
                          >
                            {resource.website}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Bottom Section - CALL Button & Website Button */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 gap-2">
                      {/* CALL Button (Blue) */}
                      {resource.phone && (
                        <a
                          href={`tel:${resource.phone}`}
                          className="flex-1 px-4 py-2 rounded-lg font-bold text-sm text-white text-center transition-all hover:opacity-90"
                          style={{ backgroundColor: '#2563EB' }}
                        >
                          CALL
                        </a>
                      )}
                      
                      {/* Website Button (Orange/Red) - Always show if website exists */}
                      {resource.website && (
                        <a 
                          href={`https://${resource.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-4 py-2 rounded-lg font-bold text-sm text-white text-center transition-all hover:opacity-90"
                          style={{ backgroundColor: '#FF6B6B' }}
                        >
                          Website
                        </a>
                      )}
                      
                      {/* If no website, make CALL button full width */}
                      {!resource.website && resource.phone && (
                        <div className="flex-1"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// ============ CALENDAR PAGE ============
const CalendarPage = () => {
  // ==================== STATE MANAGEMENT ====================
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeFilter, setActiveFilter] = useState('all'); // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [location, setLocation] = useState('Washington, D.C.');

  // ==================== API DATA SECTION ====================
  
  // FEDERAL HOLIDAYS (11 events)
  const federalHolidays = [
    { name: "New Year's Day", date: "01-01", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Martin Luther King Jr. Day", date: "01-15", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Presidents Day", date: "02-19", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Memorial Day", date: "05-27", type: "military", color: "#7C2D12", category: "federal" },
    { name: "Juneteenth", date: "06-19", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Independence Day", date: "07-04", type: "patriotic", color: "#1E40AF", category: "federal" },
    { name: "Labor Day", date: "09-02", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Columbus Day", date: "10-14", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Veterans Day", date: "11-11", type: "military", color: "#7C2D12", category: "federal" },
    { name: "Thanksgiving", date: "11-28", type: "federal", color: "#991B1B", category: "federal" },
    { name: "Christmas Day", date: "12-25", type: "federal", color: "#991B1B", category: "federal" }
  ];

  // MILITARY BRANCH BIRTHDAYS (7 main branches)
  const branchBirthdays = [
    { name: "National Guard Birthday", date: "12-13", type: "branch", color: "#065F46", category: "branch" },
    { name: "Army Birthday", date: "06-14", type: "branch", color: "#065F46", category: "branch" },
    { name: "Navy Birthday", date: "10-13", type: "branch", color: "#065F46", category: "branch" },
    { name: "Marine Corps Birthday", date: "11-10", type: "branch", color: "#065F46", category: "branch" },
    { name: "Coast Guard Birthday", date: "08-04", type: "branch", color: "#065F46", category: "branch" },
    { name: "Air Force Birthday", date: "09-18", type: "branch", color: "#065F46", category: "branch" },
    { name: "Space Force Birthday", date: "12-20", type: "branch", color: "#065F46", category: "branch" }
  ];

  // RESERVE COMPONENT BIRTHDAYS (5 reserves)
  const reserveBirthdays = [
    { name: "Coast Guard Reserve", date: "02-19", type: "reserve", color: "#047857", category: "branch" },
    { name: "Navy Reserve", date: "03-03", type: "reserve", color: "#047857", category: "branch" },
    { name: "Air Force Reserve", date: "04-14", type: "reserve", color: "#047857", category: "branch" },
    { name: "Army Reserve", date: "04-23", type: "reserve", color: "#047857", category: "branch" },
    { name: "Marine Forces Reserve", date: "08-29", type: "reserve", color: "#047857", category: "branch" }
  ];

  // MAJOR MILITARY/PATRIOTIC HOLIDAYS
  const militaryHolidays = [
    { name: "Armed Forces Day", date: "05-18", type: "military", color: "#7C2D12", category: "military" },
    { name: "Patriot Day (9/11)", date: "09-11", type: "remembrance", color: "#B45309", category: "military" },
    { name: "Pearl Harbor Day", date: "12-07", type: "remembrance", color: "#B45309", category: "military" }
  ];

  // KEY OBSERVANCES
  const observances = [
    { name: "Vietnam Veterans Day", date: "03-29", type: "observance", color: "#92400E", category: "observance" },
    { name: "POW/MIA Recognition Day", date: "09-19", type: "observance", color: "#92400E", category: "observance" },
    { name: "Gold Star Spouses Day", date: "04-05", type: "observance", color: "#92400E", category: "observance" },
    { name: "Military Spouse Day", date: "05-09", type: "observance", color: "#92400E", category: "observance" },
    { name: "Purple Heart Day", date: "08-07", type: "observance", color: "#92400E", category: "observance" },
    { name: "Women Veterans Day", date: "06-12", type: "observance", color: "#92400E", category: "observance" },
    { name: "Korean War Veterans Armistice Day", date: "07-27", type: "observance", color: "#92400E", category: "observance" },
    { name: "National Former POW Recognition Day", date: "04-09", type: "observance", color: "#92400E", category: "observance" },
    { name: "Blue Star Mothers Day", date: "02-01", type: "observance", color: "#92400E", category: "observance" },
    { name: "Silver Star Service Banner Day", date: "05-01", type: "observance", color: "#92400E", category: "observance" },
    { name: "Military Brats Day", date: "04-30", type: "observance", color: "#92400E", category: "observance" },
    { name: "Gold Star Mother's Day", date: "09-28", type: "observance", color: "#92400E", category: "observance" },
    { name: "V-J Day (Victory over Japan)", date: "09-02", type: "observance", color: "#92400E", category: "observance" },
    { name: "V-E Day", date: "05-08", type: "observance", color: "#92400E", category: "observance" },
    { name: "Iwo Jima Day", date: "02-23", type: "observance", color: "#92400E", category: "observance" },
    { name: "D-Day Anniversary", date: "06-06", type: "observance", color: "#92400E", category: "observance" },
    { name: "Battle of Midway", date: "06-04", type: "observance", color: "#92400E", category: "observance" },
    { name: "Doolittle Raid Anniversary", date: "04-18", type: "observance", color: "#92400E", category: "observance" },
    { name: "USS Arizona Memorial Day", date: "05-30", type: "observance", color: "#92400E", category: "observance" },
    { name: "Gulf War Ends", date: "02-28", type: "observance", color: "#92400E", category: "observance" },
    { name: "Operation Desert Storm", date: "01-17", type: "observance", color: "#92400E", category: "observance" },
    { name: "Iraq War Begins", date: "03-20", type: "observance", color: "#92400E", category: "observance" },
    { name: "Afghanistan War Begins", date: "10-07", type: "observance", color: "#92400E", category: "observance" },
    { name: "Four Chaplains Day", date: "02-03", type: "observance", color: "#92400E", category: "observance" },
    { name: "K-9 Veterans Day", date: "03-13", type: "observance", color: "#92400E", category: "observance" },
    { name: "Children of Fallen Patriots Day", date: "05-13", type: "observance", color: "#92400E", category: "observance" },
    { name: "Armed Forces Day", date: "05-17", type: "observance", color: "#92400E", category: "observance" },
    { name: "PTSD Awareness Day", date: "06-27", type: "observance", color: "#92400E", category: "observance" },
    { name: "National Hire A Veteran Day", date: "07-25", type: "observance", color: "#92400E", category: "observance" },
    { name: "National Buffalo Soldiers Day", date: "07-28", type: "observance", color: "#92400E", category: "observance" },
    { name: "Navajo Code Talkers Day", date: "08-14", type: "observance", color: "#92400E", category: "observance" },
    { name: "National Airborne Day", date: "08-16", type: "observance", color: "#92400E", category: "observance" },
    { name: "Patriot Day (9/11)", date: "09-11", type: "observance", color: "#92400E", category: "observance" },
    { name: "National Day of the Deployed", date: "10-26", type: "observance", color: "#92400E", category: "observance" },
    { name: "National Wreaths Across America", date: "12-13", type: "observance", color: "#92400E", category: "observance" },
    { name: "Pearl Harbor Remembrance Day", date: "12-07", type: "observance", color: "#92400E", category: "observance" },
    { name: "Loyalty Day", date: "05-01", type: "observance", color: "#92400E", category: "observance" },
    { name: "Seabee Birthday", date: "03-01", type: "observance", color: "#92400E", category: "observance" },
    { name: "Hug a GI Day", date: "03-04", type: "observance", color: "#92400E", category: "observance" },
    { name: "Purple Up! Day", date: "04-15", type: "observance", color: "#92400E", category: "observance" },
    { name: "Agent Orange Awareness Day", date: "08-10", type: "observance", color: "#92400E", category: "observance" },
    { name: "VFW Day", date: "09-29", type: "observance", color: "#92400E", category: "observance" }
  ];

  // 2026 SPECIAL EVENTS
  const specialEvents = [
    { name: "America 250 & Army 250", date: "06-14", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Bataan Memorial Death March", date: "03-21", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Battle of the Branches", date: "04-18", type: "special", color: "#1E3A8A", category: "national" },
    { name: "West Point Founded", date: "03-16", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Naval Academy Founded", date: "10-10", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Air Force Academy Founded", date: "04-01", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Coast Guard Academy Founded", date: "07-31", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Merchant Marine Academy Founded", date: "03-15", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Wounded Warrior Project Day", date: "11-22", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Fisher House Foundation Day", date: "05-15", type: "special", color: "#1E3A8A", category: "national" },
    { name: "USO Birthday", date: "02-04", type: "special", color: "#1E3A8A", category: "national" },
    { name: "American Legion Founded", date: "03-15", type: "special", color: "#1E3A8A", category: "national" },
    { name: "VFW Founded", date: "09-29", type: "special", color: "#1E3A8A", category: "national" },
    { name: "DAV Founded", date: "06-27", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Paralyzed Veterans of America Day", date: "02-13", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Military Order of Purple Heart Day", date: "08-07", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Medal of Honor Day", date: "03-25", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Tuskegee Airmen Anniversary", date: "03-22", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Navajo Code Talkers Day", date: "08-14", type: "special", color: "#1E3A8A", category: "national" },
    { name: "442nd Regiment Anniversary", date: "02-01", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Buffalo Soldiers Day", date: "07-28", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Coast Guard Auxiliary Birthday", date: "06-23", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Flag Day", date: "06-14", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Rosie The Riveter Day", date: "03-21", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Army Chaplain Corps Anniversary", date: "07-29", type: "special", color: "#1E3A8A", category: "national" },
    { name: "DOD Birthday", date: "08-10", type: "special", color: "#1E3A8A", category: "national" },
    { name: "Civil Air Patrol Birthday", date: "12-01", type: "special", color: "#1E3A8A", category: "national" }
  ];

  // CONGRESSIONAL HEARINGS - Mike Bost House Committee
  const mikeBostHearings = [
    { name: "Hidden Wounds: Supporting Veterans with TBI", date: "03-05", time: "10:15 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Year One of the Dole Act", date: "03-04", time: "2:15 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Joint VSO Hearing - American Legion", date: "03-04", time: "9:45 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Joint VSO Hearing - VFW", date: "03-03", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Joint VSO Hearing - DAV", date: "02-24", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Economic Opportunity Markup", date: "02-24", time: "2:30 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Full Committee Markup", date: "02-12", time: "9:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Opportunities with VHA Reorganization", date: "02-11", time: "10:15 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Tech Modernization Oversight", date: "02-04", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Disability Assistance Legislative Hearing", date: "02-03", time: "10:15 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "VA Budget FY2026 Review", date: "04-15", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Mental Health Services Expansion", date: "04-22", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Homeless Veterans Programs Review", date: "05-06", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "PACT Act Implementation Update", date: "05-13", time: "9:30 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "VA Telehealth Expansion", date: "05-20", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "GI Bill Improvements Legislation", date: "06-03", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Rural Veterans Access to Care", date: "06-10", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Women Veterans Health Care", date: "06-17", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Toxic Exposure Claims Processing", date: "07-08", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "VA IT Modernization Progress", date: "07-15", time: "9:30 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Veterans Benefits Backlog Update", date: "07-22", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Veteran Suicide Prevention Programs", date: "08-05", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "VA Medical Facilities Infrastructure", date: "08-12", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Caregiver Support Program Review", date: "08-19", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Veterans Employment Programs", date: "09-09", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "VA Community Care Oversight", date: "09-16", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Minority Veterans Access Issues", date: "09-23", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "VA Claims Processing Improvements", date: "10-01", time: "9:30 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Veteran Housing Programs", date: "10-08", time: "10:00 AM", type: "hearing", color: "#7C3AED", category: "bost" },
    { name: "Military Sexual Trauma Care", date: "10-15", time: "2:00 PM", type: "hearing", color: "#7C3AED", category: "bost" }
  ];

  // CONGRESSIONAL HEARINGS - Mark Takano Democratic Committee
  const takanoHearings = [
    { name: "Digital GI Bill: Contracting Challenges", date: "02-04", time: "2:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Community Care Network Oversight", date: "01-22", time: "10:15 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Economic Opportunity Legislative", date: "01-21", time: "2:30 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Reevaluating the Rating Schedule", date: "01-14", time: "2:15 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Health Legislative Hearing", date: "01-13", time: "2:15 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "EHR Modernization Deployment", date: "12-15", time: "3:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Strengthening CHAMPVA", date: "12-10", time: "2:15 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "119th Congress Member Day", date: "12-04", time: "9:30 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Veterans Crisis Line Review", date: "04-07", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Agent Orange Expansion", date: "04-14", time: "2:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Blue Water Navy Act Implementation", date: "04-21", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Veteran Education Benefits", date: "04-28", time: "2:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "VA Accountability Office Review", date: "05-05", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Burn Pit Registry Update", date: "05-12", time: "2:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Forever GI Bill Technical Corrections", date: "05-19", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "VA Appeals Modernization Progress", date: "06-02", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Minority Veteran Health Disparities", date: "06-09", time: "2:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "VA Pharmacy Services Review", date: "06-16", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "Long-Term Care for Aging Veterans", date: "06-23", time: "2:00 PM", type: "hearing", color: "#8B5CF6", category: "takano" },
    { name: "VA Research Program Oversight", date: "07-07", time: "10:00 AM", type: "hearing", color: "#8B5CF6", category: "takano" }
  ];

  // CONGRESSIONAL HEARINGS - Jerry Moran Senate Committee
  const moranHearings = [
    { name: "Senate VSO - American Legion", date: "03-04", time: "9:45 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate VSO - VFW Presentation", date: "03-03", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate VSO - DAV Presentation", date: "02-24", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "21st Century VA Health Care System", date: "02-11", time: "3:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Budget Hearing - VA", date: "04-08", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Rural Health Access", date: "04-15", time: "2:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Benefits Claims Review", date: "04-22", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Military Construction Budget", date: "04-29", time: "9:30 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Prosthetics Program Review", date: "05-06", time: "2:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Mental Health Funding", date: "05-13", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate PTSD Treatment Programs", date: "05-20", time: "2:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Women Veterans Services", date: "05-27", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Toxic Exposure Legislation", date: "06-03", time: "9:30 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Homeless Veterans Programs", date: "06-10", time: "2:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Veteran Employment Initiatives", date: "06-17", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate National Cemetery Administration", date: "06-24", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate VA Accountability Review", date: "07-01", time: "2:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate GI Bill Reform", date: "07-08", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate Caregiver Benefits", date: "07-15", time: "2:00 PM", type: "hearing", color: "#A855F7", category: "moran" },
    { name: "Senate VA Inspector General Report", date: "07-22", time: "10:00 AM", type: "hearing", color: "#A855F7", category: "moran" }
  ];

  // WEST LA VA MEDICAL CENTER EVENTS - Recurring Weekly (First Quarter 2026)
  const westLAVAEvents = [
    // Bunker Social - Every Monday, 11 AM - 2 PM PT (Building 306)
    { name: "Bunker Social @ West LA VA", date: "01-06", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "01-13", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "01-20", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "01-27", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "02-03", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "02-10", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "02-17", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "02-24", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "03-02", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "03-09", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "03-16", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "03-23", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Bunker Social @ West LA VA", date: "03-30", time: "11:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    // Women Veterans' Social - Every Monday, 5 PM - 6:30 PM PT (Building 306)
    { name: "Women Veterans Social @ West LA VA", date: "01-06", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "01-13", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "01-20", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "01-27", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "02-03", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "02-10", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "02-17", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "02-24", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "03-02", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "03-09", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "03-16", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "03-23", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Women Veterans Social @ West LA VA", date: "03-30", time: "5:00 PM PT", type: "va-event", color: "#059669", category: "va" },
    // Coffee Social with VAPD - Every Wednesday, 10 AM - 11 AM PT (Building 306)
    { name: "Coffee Social with VAPD @ West LA VA", date: "01-08", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "01-15", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "01-22", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "01-29", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "02-05", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "02-12", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "02-19", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "02-26", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "03-04", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "03-11", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "03-18", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" },
    { name: "Coffee Social with VAPD @ West LA VA", date: "03-25", time: "10:00 AM PT", type: "va-event", color: "#059669", category: "va" }
  ];

  // COMBINED ALL EVENTS ARRAY
  const allEvents = [
    ...federalHolidays,
    ...branchBirthdays,
    ...reserveBirthdays,
    ...militaryHolidays,
    ...observances,
    ...specialEvents,
    ...mikeBostHearings,
    ...takanoHearings,
    ...moranHearings,
    ...westLAVAEvents
  ];

  // ==================== FILTER & SEARCH LOGIC ====================
  
  const filterEvents = (events) => {
    let filtered = events;

    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(event => event.category === activeFilter);
    }

    // Apply search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(event => 
        event.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredEvents = filterEvents(allEvents);

  // ==================== HELPER FUNCTIONS ====================
  
  const getEventsForDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${month}-${day}`;
    return filteredEvents.filter(event => event.date === dateStr);
  };

  const upcomingEvents = filteredEvents
    .map(event => {
      const [month, day] = event.date.split('-');
      const eventDate = new Date(currentDate.getFullYear(), parseInt(month) - 1, parseInt(day));
      return { ...event, fullDate: eventDate };
    })
    .filter(event => event.fullDate >= new Date())
    .sort((a, b) => a.fullDate - b.fullDate)
    .slice(0, 5);

  // Filter categories with counts
  const filterCategories = [
    { id: 'all', label: 'All Events', count: allEvents.length },
    { id: 'federal', label: 'Federal', count: federalHolidays.length },
    { id: 'branch', label: 'Branch', count: branchBirthdays.length + reserveBirthdays.length },
    { id: 'military', label: 'Military', count: militaryHolidays.length },
    { id: 'observance', label: 'Observance', count: observances.length },
    { id: 'national', label: 'National Day', count: specialEvents.length },
    { id: 'bost', label: 'Mike Bost Hearings', count: mikeBostHearings.length },
    { id: 'takano', label: 'Mark Takano Hearings', count: takanoHearings.length },
    { id: 'moran', label: 'Jerry Moran Hearings', count: moranHearings.length },
    { id: 'va', label: 'West LA VA Events', count: westLAVAEvents.length }
  ];

  // ==================== WEATHER API INTEGRATION ====================
  useEffect(() => {
    // Fetch weather data - Default to Washington D.C. coordinates
    const fetchWeather = async () => {
      try {
        setWeatherLoading(true);
        const API_KEY = process.env.REACT_APP_OPENWEATHER_KEY || 'YOUR_API_KEY_HERE';
        const lat = 38.9072; // Washington D.C.
        const lon = -77.0369;
        
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=imperial`
        );
        
        if (response.ok) {
          const data = await response.json();
          setWeather({
            temp: Math.round(data.main.temp),
            feels_like: Math.round(data.main.feels_like),
            condition: data.weather[0].main,
            description: data.weather[0].description,
            icon: data.weather[0].icon,
            humidity: data.main.humidity,
            wind_speed: Math.round(data.wind.speed)
          });
        }
      } catch (error) {
        console.error('Weather fetch error:', error);
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeather();
    // Refresh weather every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      <TopNavbar showAuth={false} />
      
      {/* Hero Section - Navy Blue with Yellow Highlights */}
      <div className="relative w-full py-16" style={{ backgroundColor: '#1A3A52' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-4">
            <span className="inline-block px-6 py-2 rounded-full text-sm font-bold uppercase" style={{ 
              backgroundColor: '#FFCC01',
              color: '#1A3A52'
            }}>
              MILITARY CALENDAR
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6" style={{ 
            color: '#FFFFFF',
            lineHeight: '1.2'
          }}>
            Stay Connected with <span style={{ color: '#FFCC01' }}>Military Events</span>, Federal Holidays, and <span style={{ color: '#FFCC01' }}>Congressional Hearings</span>
          </h1>
          
          <p className="text-center text-lg mx-auto" style={{ 
            color: '#FFFFFF',
            maxWidth: '800px',
            opacity: 0.9
          }}>
            Track important dates, stay informed about veteran advocacy efforts, and never miss a significant military or federal observance.
          </p>
        </div>
      </div>

      {/* This Week's Events Section - MOVED UP */}
      <div className="max-w-7xl mx-auto px-6 py-12 -mt-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold" style={{ color: '#1A3A52' }}>
            This Week's Events
          </h2>
        </div>
        
        <div className="grid grid-cols-7 gap-4">
          {Array.from({ length: 7 }, (_, i) => {
            const weekDay = new Date();
            weekDay.setDate(weekDay.getDate() - weekDay.getDay() + i);
            const dayEvents = getEventsForDate(weekDay);
            const isToday = weekDay.toDateString() === new Date().toDateString();
            
            return (
              <div 
                key={i}
                className="bg-white p-6 rounded-xl text-center"
                style={{ 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  borderTop: isToday ? '4px solid #FFCC01' : '4px solid #E5E7EB',
                  minHeight: '220px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ 
                  color: isToday ? '#FFCC01' : '#6B7280'
                }}>
                  {weekDay.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                </p>
                
                <p className="text-5xl font-light mb-6" style={{ 
                  color: isToday ? '#1A3A52' : '#6B7280',
                  fontWeight: isToday ? '600' : '300'
                }}>
                  {weekDay.getDate()}
                </p>
                
                {dayEvents.length > 0 ? (
                  <div className="space-y-2 text-left flex-1">
                    {dayEvents.slice(0, 2).map((event, j) => (
                      <div 
                        key={j}
                        className="p-2 rounded-lg text-xs"
                        style={{ 
                          backgroundColor: event.color + '15',
                          borderLeft: `3px solid ${event.color}`
                        }}
                      >
                        <p className="font-semibold" style={{ color: '#1A3A52' }}>
                          {event.name.length > 12 ? event.name.substring(0, 12) + '...' : event.name}
                        </p>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <p className="text-xs font-bold text-center pt-2" style={{ color: '#FFCC01' }}>
                        +{dayEvents.length - 2} more
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-sm" style={{ color: '#D1D5DB' }}>No events</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Stats Dashboard - 4 Cards (Added Weather) */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Card 1: Today */}
          <div className="bg-white p-6 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #FFCC01'
          }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFCC01' }}>
                <Calendar size={24} style={{ color: '#1A3A52' }} />
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider font-bold" style={{ color: '#6B7280' }}>Today</p>
                <p className="text-sm uppercase tracking-wider font-bold" style={{ color: '#6B7280' }}>Today</p>
                <p className="text-2xl font-bold" style={{ color: '#1A3A52' }}>
                  {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              {getEventsForDate(new Date()).length > 0 ? (
                getEventsForDate(new Date()).map((event, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
                    <p className="text-sm font-semibold" style={{ color: '#1A3A52' }}>{event.name}</p>
                    <p className="text-xs uppercase mt-1" style={{ color: '#FFCC01', fontWeight: '600' }}>{event.type}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-center py-4" style={{ color: '#6B7280' }}>No events today</p>
              )}
            </div>
          </div>

          {/* Card 2: Upcoming Events */}
          <div className="bg-white p-6 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #047857'
          }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#047857' }}>
                <Clock size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider font-bold" style={{ color: '#6B7280' }}>Next 5 Events</p>
                <p className="text-2xl font-bold" style={{ color: '#1A3A52' }}>Upcoming</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {upcomingEvents.map((event, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1 h-full rounded" style={{ backgroundColor: event.color, minHeight: '40px' }}></div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: '#1A3A52' }}>{event.name}</p>
                    <p className="text-xs" style={{ color: '#6B7280' }}>
                      {event.fullDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Card 3: Total Stats */}
          <div className="bg-white p-6 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #DC2626'
          }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DC2626' }}>
                <BarChart3 size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider font-bold" style={{ color: '#6B7280' }}>Total Events</p>
                <p className="text-2xl font-bold" style={{ color: '#1A3A52' }}>{allEvents.length}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#FEE2E2' }}>
                <span className="text-sm font-medium" style={{ color: '#1A3A52' }}>Federal</span>
                <span className="text-sm font-bold" style={{ color: '#DC2626' }}>11</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#D1FAE5' }}>
                <span className="text-sm font-medium" style={{ color: '#1A3A52' }}>Military</span>
                <span className="text-sm font-bold" style={{ color: '#047857' }}>15+</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#E0E7FF' }}>
                <span className="text-sm font-medium" style={{ color: '#1A3A52' }}>Hearings</span>
                <span className="text-sm font-bold" style={{ color: '#3B82F6' }}>22</span>
              </div>
            </div>
          </div>

          {/* Card 4: Weather */}
          <div className="bg-white p-6 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #3B82F6'
          }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3B82F6' }}>
                {weatherLoading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                ) : (
                  <Globe size={24} style={{ color: '#FFFFFF' }} />
                )}
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider font-bold" style={{ color: '#6B7280' }}>Weather</p>
                <p className="text-2xl font-bold" style={{ color: '#1A3A52' }}>
                  {weather ? `${weather.temp}°F` : '--'}
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              {weather ? (
                <>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: '#DBEAFE' }}>
                    <p className="text-sm font-semibold" style={{ color: '#1A3A52' }}>{weather.condition}</p>
                    <p className="text-xs capitalize mt-1" style={{ color: '#3B82F6' }}>{weather.description}</p>
                  </div>
                  <div className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#F3F4F6' }}>
                    <span className="text-xs font-medium" style={{ color: '#6B7280' }}>Feels Like</span>
                    <span className="text-sm font-bold" style={{ color: '#1A3A52' }}>{weather.feels_like}°F</span>
                  </div>
                  <div className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#F3F4F6' }}>
                    <span className="text-xs font-medium" style={{ color: '#6B7280' }}>Wind</span>
                    <span className="text-sm font-bold" style={{ color: '#1A3A52' }}>{weather.wind_speed} mph</span>
                  </div>
                  <div className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#F3F4F6' }}>
                    <span className="text-xs font-medium" style={{ color: '#6B7280' }}>Humidity</span>
                    <span className="text-sm font-bold" style={{ color: '#1A3A52' }}>{weather.humidity}%</span>
                  </div>
                  <p className="text-xs text-center mt-2" style={{ color: '#6B7280' }}>{location}</p>
                </>
              ) : (
                <p className="text-sm text-center py-4" style={{ color: '#6B7280' }}>
                  {weatherLoading ? 'Loading...' : 'Weather unavailable'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Navigation Section */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <div className="bg-white p-6 rounded-xl" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold" style={{ color: '#1A3A52' }}>Filter Events</h3>
            <span className="text-sm px-4 py-2 rounded-full" style={{ 
              backgroundColor: '#FFCC01',
              color: '#1A3A52',
              fontWeight: '600'
            }}>
              {filteredEvents.length} results
            </span>
          </div>
          
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2" size={20} style={{ color: '#6B7280' }} />
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg text-base"
                style={{ 
                  border: '2px solid #E5E7EB',
                  color: '#1A3A52'
                }}
              />
            </div>
          </div>
          
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-3">
            {filterCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  backgroundColor: activeFilter === cat.id ? '#FFCC01' : '#F3F4F6',
                  color: activeFilter === cat.id ? '#1A3A52' : '#6B7280',
                  border: activeFilter === cat.id ? '2px solid #FFCC01' : '2px solid transparent'
                }}
              >
                {cat.label} ({cat.count})
              </button>
            ))}
          </div>
        </div>
      </div>


      {/* Monthly Calendar Section */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="bg-white p-8 rounded-xl" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <h2 className="text-3xl font-bold" style={{ color: '#1A3A52' }}>
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex gap-2">
                <button 
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition"
                  style={{ backgroundColor: '#F3F4F6', color: '#1A3A52' }}
                >
                  ‹
                </button>
                <button 
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition"
                  style={{ backgroundColor: '#F3F4F6', color: '#1A3A52' }}
                >
                  ›
                </button>
              </div>
            </div>
            
            <button className="px-6 py-3 rounded-lg font-semibold transition" style={{ 
              backgroundColor: '#1A3A52',
              color: '#FFFFFF'
            }}>
              Today
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-4">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
              <div key={i} className="text-center text-sm font-bold py-3" style={{ color: '#6B7280' }}>
                {day}
              </div>
            ))}
            
            {/* Calendar Days */}
            {Array.from({ length: 35 }, (_, i) => {
              const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
              const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
              const dayNum = i - firstDay + 1;
              const isValidDay = dayNum > 0 && dayNum <= daysInMonth;
              const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNum);
              const events = isValidDay ? getEventsForDate(date) : [];
              const isToday = isValidDay && 
                date.getDate() === new Date().getDate() && 
                date.getMonth() === new Date().getMonth() &&
                date.getFullYear() === new Date().getFullYear();

              return (
                <div 
                  key={i}
                  className="min-h-40 p-3 rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
                  style={{ 
                    backgroundColor: isValidDay ? (isToday ? '#FEF3C7' : '#F9FAFB') : 'transparent',
                    border: isValidDay ? (isToday ? '3px solid #FFCC01' : '1px solid #E5E7EB') : 'none'
                  }}
                  onClick={() => events.length > 0 && setSelectedDate(date)}
                >
                  {isValidDay && (
                    <>
                      <div className="text-lg font-semibold mb-2" style={{ color: isToday ? '#1A3A52' : '#6B7280' }}>
                        {dayNum}
                      </div>
                      
                      <div className="space-y-1">
                        {events.slice(0, 5).map((event, j) => (
                          <div 
                            key={j}
                            className="p-1.5 rounded text-xs font-medium"
                            style={{ 
                              backgroundColor: event.color + '20',
                              color: event.color,
                              borderLeft: `3px solid ${event.color}`
                            }}
                          >
                            {event.name.length > 18 ? event.name.substring(0, 18) + '...' : event.name}
                          </div>
                        ))}
                        {events.length > 5 && (
                          <button 
                            className="text-xs font-bold text-center w-full py-1"
                            style={{ color: '#FFCC01' }}
                          >
                            +{events.length - 5} more
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mission Statement Section - Navy Blue */}
      <div className="w-full py-16" style={{ backgroundColor: '#1A3A52' }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-2xl md:text-3xl leading-relaxed" style={{ color: '#FFFFFF' }}>
            Stay informed. Stay connected. <span style={{ 
              color: '#FFCC01',
              fontWeight: 'bold'
            }}>Military calendars</span> help veterans track important dates, understand <span style={{ 
              color: '#FFCC01',
              fontWeight: 'bold'
            }}>advocacy efforts</span>, and ensure that no significant event goes unnoticed.
          </p>
          <div className="mt-6 w-24 h-1 mx-auto" style={{ backgroundColor: '#FFCC01' }}></div>
        </div>
      </div>

      {/* Information Cards - 2x2 Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#1A3A52' }}>
          Understanding Military & Federal Observances
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Card 1: Federal Holidays */}
          <div className="bg-white p-8 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #FFCC01'
          }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFCC01' }}>
                <Flag size={28} style={{ color: '#1A3A52' }} />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#1A3A52' }}>Federal Holidays</h3>
            </div>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#4B5563' }}>
              Federal holidays are official U.S. government closures recognized nationwide. Many veterans observe these days through ceremonies, parades, and community gatherings.
            </p>
            <div className="space-y-2">
              <p className="text-sm" style={{ color: '#1A3A52' }}><strong>Examples:</strong> Veterans Day, Memorial Day, Independence Day</p>
            </div>
          </div>

          {/* Card 2: Military Branch Birthdays */}
          <div className="bg-white p-8 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #047857'
          }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#047857' }}>
                <Shield size={28} style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#1A3A52' }}>Branch Birthdays</h3>
            </div>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#4B5563' }}>
              Each military branch celebrates its founding with ceremonies, balls, and events honoring service members past and present.
            </p>
            <div className="space-y-2">
              <p className="text-sm" style={{ color: '#1A3A52' }}><strong>Examples:</strong> Army (June 14), Marine Corps (Nov 10), Air Force (Sept 18)</p>
            </div>
          </div>

          {/* Card 3: Congressional Hearings */}
          <div className="bg-white p-8 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #3B82F6'
          }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3B82F6' }}>
                <Gavel size={28} style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#1A3A52' }}>Congressional Hearings</h3>
            </div>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#4B5563' }}>
              Stay informed about legislative efforts impacting veterans. House and Senate committees regularly hold hearings on VA oversight, benefits, and healthcare.
            </p>
            <div className="space-y-2">
              <p className="text-sm" style={{ color: '#1A3A52' }}><strong>Tracked:</strong> House VA Committee, Senate VA Committee hearings</p>
            </div>
          </div>

          {/* Card 4: Special Observances */}
          <div className="bg-white p-8 rounded-xl" style={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: '6px solid #DC2626'
          }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DC2626' }}>
                <Heart size={28} style={{ color: '#FFFFFF' }} />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#1A3A52' }}>Special Observances</h3>
            </div>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#4B5563' }}>
              Dedicated days honoring military families, POW/MIA recognition, Vietnam veterans, Gold Star families, and other meaningful tributes.
            </p>
            <div className="space-y-2">
              <p className="text-sm" style={{ color: '#1A3A52' }}><strong>Examples:</strong> POW/MIA Day, Gold Star Spouses Day, Military Spouse Day</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// ============ PLACEHOLDER PAGES ============
const VAListPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }}>
      <TopNavbar showAuth={false} />
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl font-bold mb-6" style={{ color: '#FFCC01' }}>VA List</h1>
        <p className="text-xl" style={{ color: '#FFFFFF' }}>Coming Soon - Comprehensive VA Resources Directory</p>
      </div>
      <Footer />
    </div>
  );
};

const NonProfitsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#221F20' }}>
      <TopNavbar showAuth={false} />
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl font-bold mb-6" style={{ color: '#FFCC01' }}>Non-Profits</h1>
        <p className="text-xl" style={{ color: '#FFFFFF' }}>Coming Soon - Veteran-Serving Non-Profit Organizations</p>
      </div>
      <Footer />
    </div>
  );
};

const EventsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All Events');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['All Events', 'Housing', 'Veterans', 'VA', 'HUD-VASH', 'NonProfits'];

  const events = [
    {
      id: 1,
      title: 'HUD-VASH Housing Fair - Find Your Home',
      category: 'HUD-VASH',
      date: '15 December 2024',
      time: '10:00 AM - 2:00 PM',
      location: 'West LA VA Medical Center',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop',
      color: '#10B981'
    },
    {
      id: 2,
      title: 'Veterans Mental Health Workshop',
      category: 'VA',
      date: '18 December 2024',
      time: '1:00 PM - 4:00 PM',
      location: 'Sepulveda VA Center',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop',
      color: '#3B82F6'
    },
    {
      id: 3,
      title: 'Community Support Network Mixer',
      category: 'NonProfits',
      date: '20 December 2024',
      time: '6:00 PM - 8:00 PM',
      location: 'LA Veterans Center',
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop',
      color: '#F59E0B'
    },
  ];

  return (
    <div className="min-h-screen" style={{ 
      backgroundColor: '#1a1a1a',
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/8747zdio_vecteezy_homes-in-residential-district-with-dramatic-colourful-sunset_22527580.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <TopNavbar showAuth={false} />
      
      <div className="container mx-auto px-4 py-8" style={{ paddingTop: '140px' }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.18)'
        }}>
          
          <div className="flex items-center justify-between mb-8">
            <div style={{ flex: '0 0 300px' }}>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '30px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input type="text" placeholder="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', color: '#000', fontSize: '16px' }} />
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <h1 style={{ fontSize: '48px', fontWeight: 'bold', color: '#FFFFFF', margin: 0, lineHeight: '1.2' }}>
                Veterans Events Calendar
              </h1>
            </div>

            <div style={{ flex: '0 0 300px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button style={{ backgroundColor: 'transparent', color: '#FFFFFF', border: 'none', padding: '10px 24px', borderRadius: '20px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>Sign In</button>
              <button style={{ backgroundColor: 'transparent', color: '#FFCC01', border: '2px solid #FFCC01', padding: '10px 24px', borderRadius: '20px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>Sign Up</button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '40px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', paddingBottom: '20px' }}>
            {categories.map(category => (
              <button key={category} onClick={() => setActiveCategory(category)}
                style={{ backgroundColor: 'transparent', color: activeCategory === category ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)', border: 'none', borderBottom: activeCategory === category ? '3px solid #FFFFFF' : '3px solid transparent', padding: '10px 0', fontSize: '18px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.3s ease' }}>
                {category}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {events.map(event => (
              <div key={event.id} style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.3s ease', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)' }}>
                <img src={event.image} alt={event.title} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                <div style={{ padding: '20px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '15px', lineHeight: '1.4' }}>{event.title}</h3>
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '14px' }}>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z"/></svg>
                    {event.date}
                  </div>
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '14px' }}>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/></svg>
                    {event.time}
                  </div>
                  <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '14px' }}>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    {event.location}
                  </div>
                  <span style={{ backgroundColor: event.color, color: '#FFFFFF', padding: '6px 14px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' }}>
                    {event.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

const ClassesPage = () => {
  const [activeLocation, setActiveLocation] = useState('All Locations');
  const [searchQuery, setSearchQuery] = useState('');

  const locations = ['All Locations', 'West LA VA', 'Sepulveda VA', 'LA Clinic (LAACC)', 'Virtual'];

  const classes = [
    { id: 1, name: 'Taking Charge of My Life and Health (TCMLH)', day: 'Wednesday', time: '1:00 PM', location: 'West LA VA', building: 'Bldg. 220, Ground Floor', phone: '310-268-3276', category: 'Core Orientation', color: '#10B981' },
    { id: 2, name: 'Cultivating Mindfulness', day: 'Thursday', time: '11:00 AM', location: 'West LA VA', building: 'Bldg. 220, Room 110', phone: '310-268-3276', category: 'Mindfulness', color: '#3B82F6' },
    { id: 3, name: 'Tai Chi / Moving the Body', day: 'Tuesday, Friday', time: '11:00 AM, 8:30 AM', location: 'West LA VA', building: 'Rose Garden outside Bldg. 220', phone: '626-558-9652', category: 'Movement', color: '#F59E0B' },
    { id: 4, name: 'Gardening for Whole Health', day: 'Mon, Wed, Fri', time: '1:00 PM, 9:00 AM, 1:00 PM', location: 'West LA VA', building: 'West LA Vet\'s Garden', phone: '310-619-0321', category: 'Gardening & Nutrition', color: '#10B981' },
    { id: 5, name: 'Biofeedback for Wellbeing', day: 'Monday', time: '10:00 AM', location: 'West LA VA', building: 'Bldg. 220, Room 110', phone: '310-268-3276', category: 'Pain & Stress', color: '#EF4444' },
    { id: 6, name: 'Walk-In Battlefield Acupuncture', day: 'Monday, Friday', time: '1:00 PM, 10:00 AM', location: 'West LA VA', building: 'Bldg. 500, Room 1606 & Bldg. 220', phone: '310-268-3276', category: 'Pain & Stress', color: '#EF4444' },
    { id: 7, name: 'Mind Over Illness', day: '3rd Tuesday', time: '10:00 AM', location: 'Sepulveda VA', building: 'Bldg. 200, 2nd Floor, Conf. B & C', phone: '310-478-3711 ext. 32089', category: 'Mindfulness', color: '#3B82F6' },
    { id: 8, name: 'iRest Guided Imagery Meditation', day: 'Mon, Wed, Fri', time: '2:00 PM, 10:00 AM, 1:30 PM', location: 'Sepulveda VA', building: 'Bldg. 10, Room C106A', phone: '310-478-3711 ext. 36265', category: 'Mindfulness', color: '#3B82F6' },
    { id: 9, name: 'Diabetes Shared Medical Appointment', day: '2nd & 4th Tuesday', time: '10:00 AM', location: 'Sepulveda VA', building: 'Bldg. 200, 2nd Floor', phone: '818-401-3543', category: 'Health Programs', color: '#8B5CF6' },
    { id: 10, name: 'Chair Yoga', day: 'Saturday', time: '10:00 AM', location: 'LA Clinic (LAACC)', building: '3rd Floor, Room A349', phone: '213-253-2677', category: 'Movement', color: '#F59E0B' },
    { id: 11, name: 'Mindfulness Skills Drop-In Group', day: 'Thursday', time: '1:00 PM', location: 'Virtual', building: 'Phone: 404-397-1596', phone: 'Access: 199-550-6714#', category: 'Mindfulness', color: '#3B82F6' },
    { id: 12, name: 'Healthy Teaching Kitchen', day: '1st & 2nd Tuesday', time: '10:00 AM, 2:00 PM', location: 'Virtual', building: 'VA Video Connect', phone: '424-485-7238', category: 'Gardening & Nutrition', color: '#10B981' },
  ];

  const filteredClasses = activeLocation === 'All Locations' ? classes : classes.filter(c => c.location === activeLocation);

  // Group classes by day
  const groupClassesByDay = (classList) => {
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const grouped = {};
    dayOrder.forEach(day => grouped[day] = []);
    
    classList.forEach(cls => {
      const dayStr = cls.day.toLowerCase();
      dayOrder.forEach(day => {
        if (dayStr.includes(day.toLowerCase().slice(0, 3))) {
          grouped[day].push(cls);
        }
      });
    });
    
    return grouped;
  };

  const classesByDay = groupClassesByDay(filteredClasses);

  return (
    <div className="min-h-screen" style={{ 
      backgroundColor: '#1a1a1a',
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/8747zdio_vecteezy_homes-in-residential-district-with-dramatic-colourful-sunset_22527580.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <TopNavbar showAuth={false} />
      
      <div className="container mx-auto px-4 py-8" style={{ paddingTop: '140px' }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.18)'
        }}>
          
          <div className="flex items-center justify-between mb-8">
            <div style={{ flex: '0 0 300px' }}>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '30px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input type="text" placeholder="Search classes" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', color: '#000', fontSize: '16px' }} />
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <h1 style={{ 
                fontSize: '48px', 
                fontWeight: 'bold', 
                color: '#1a1a1a', 
                margin: 0, 
                lineHeight: '1.2',
                textShadow: '2px 2px 4px rgba(255, 255, 255, 0.8)'
              }}>
                Whole Health Classes Directory
              </h1>
            </div>

            <div style={{ flex: '0 0 300px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button style={{ backgroundColor: 'transparent', color: '#FFFFFF', border: 'none', padding: '10px 24px', borderRadius: '20px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>Sign In</button>
              <button style={{ backgroundColor: 'transparent', color: '#FFCC01', border: '2px solid #FFCC01', padding: '10px 24px', borderRadius: '20px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>Sign Up</button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '40px', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', paddingBottom: '20px' }}>
            {locations.map(loc => (
              <button key={loc} onClick={() => setActiveLocation(loc)}
                style={{ 
                  backgroundColor: 'transparent', 
                  color: activeLocation === loc ? '#1a1a1a' : '#333333', 
                  border: 'none', 
                  borderBottom: activeLocation === loc ? '3px solid #1a1a1a' : '3px solid transparent', 
                  padding: '10px 0', 
                  fontSize: '18px', 
                  cursor: 'pointer', 
                  fontWeight: '600', 
                  transition: 'all 0.3s ease',
                  textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8)'
                }}>
                {loc}
              </button>
            ))}
          </div>

          {/* Key Locations Section */}
          <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.15)', borderRadius: '12px' }}>
            <h2 style={{ color: '#1a1a1a', fontSize: '24px', marginBottom: '20px', fontWeight: 'bold', textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8)' }}>Key Locations & Contacts</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '15px', borderRadius: '10px' }}>
                <h3 style={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>West LA VA Medical Center</h3>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📍 11301 Wilshire Blvd, LA, CA 90073</p>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📞 Main: 310-478-3711</p>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📞 Whole Health: 310-268-3276</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '15px', borderRadius: '10px' }}>
                <h3 style={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Sepulveda VA Center</h3>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📍 16111 Plummer St, Sepulveda, CA 91343</p>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📞 Main: 818-891-7711</p>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📞 Whole Health: 310-478-3711 ext. 32089</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '15px', borderRadius: '10px' }}>
                <h3 style={{ color: '#000', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>LA Clinic (LAACC)</h3>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📍 351 E. Temple St, LA, CA 90012</p>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📞 Main: 213-253-2677</p>
                <p style={{ color: '#666', fontSize: '13px', margin: '4px 0' }}>📞 Health Connect: 877-252-4866</p>
              </div>
            </div>
          </div>

          {/* Classes Grouped by Day */}
          {Object.keys(classesByDay).map(day => {
            const dayClasses = classesByDay[day];
            if (dayClasses.length === 0) return null;
            
            return (
              <div key={day} style={{ marginBottom: '40px' }}>
                <h2 style={{ 
                  color: '#1a1a1a', 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  marginBottom: '20px', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.05em',
                  textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8)'
                }}>
                  {day}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  {dayClasses.map(cls => (
                    <div key={`${day}-${cls.id}`} style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: '12px', padding: '20px' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '12px' }}>{cls.name}</h3>
                      <span style={{ backgroundColor: cls.color, color: '#FFFFFF', padding: '4px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block', marginBottom: '12px' }}>
                        {cls.category}
                      </span>
                      <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px', color: '#444' }}>
                        <div>
                          <p style={{ marginBottom: '6px', color: '#222' }}><strong>Day:</strong> {cls.day}</p>
                          <p style={{ marginBottom: '6px', color: '#222' }}><strong>Time:</strong> {cls.time}</p>
                        </div>
                        <div>
                          <p style={{ marginBottom: '6px', fontWeight: 'bold', color: '#1a1a1a' }}>{cls.location}</p>
                          <p style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>{cls.building}</p>
                          {cls.location === 'West LA VA' && (
                            <div style={{ marginTop: '8px', fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                              <p style={{ margin: '2px 0' }}>11301 Wilshire Boulevard</p>
                              <p style={{ margin: '2px 0' }}>Los Angeles, CA 90073-1003</p>
                              <p style={{ margin: '2px 0' }}>Main: 310-478-3711</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
                        <p style={{ color: '#444', fontSize: '13px' }}>📞 {cls.phone}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

const ArticlesPage = () => {
  const [activeCategory, setActiveCategory] = useState('Latest');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['Latest', 'Housing', 'Veterans', 'VA', 'HUD-VASH', 'NonProfits'];

  // Sample articles data
  const articles = [
    {
      id: 1,
      title: 'New HUD-VASH Housing Opportunities Expand Across Nation',
      category: 'HUD-VASH',
      date: '3 December 2024',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop',
      color: '#10B981'
    },
    {
      id: 2,
      title: 'VA Announces Enhanced Mental Health Services for Veterans',
      category: 'VA',
      date: '2 December 2024',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop',
      color: '#3B82F6'
    },
    {
      id: 3,
      title: 'Community NonProfits Unite for Veteran Support Initiative',
      category: 'NonProfits',
      date: '1 December 2024',
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop',
      color: '#F59E0B'
    },
    {
      id: 4,
      title: 'Affordable Housing Solutions for Homeless Veterans',
      category: 'Housing',
      date: '30 November 2024',
      image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=600&h=400&fit=crop',
      color: '#EF4444'
    },
    {
      id: 5,
      title: 'Veterans Rights Legislation Advances in Congress',
      category: 'Veterans',
      date: '29 November 2024',
      image: 'https://images.unsplash.com/photo-1569098644584-210bcd375b59?w=600&h=400&fit=crop',
      color: '#8B5CF6'
    },
    {
      id: 6,
      title: 'Success Stories from HUD-VASH Program Recipients',
      category: 'HUD-VASH',
      date: '28 November 2024',
      image: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=600&h=400&fit=crop',
      color: '#10B981'
    },
  ];

  const filteredArticles = activeCategory === 'Latest' 
    ? articles 
    : articles.filter(article => article.category === activeCategory);

  return (
    <div className="min-h-screen" style={{ 
      backgroundColor: '#1a1a1a',
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/8747zdio_vecteezy_homes-in-residential-district-with-dramatic-colourful-sunset_22527580.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <TopNavbar showAuth={false} />
      
      {/* Main Glassmorphism Container */}
      <div className="container mx-auto px-4 py-8" style={{ paddingTop: '140px' }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.18)'
        }}>
          
          {/* Header Section */}
          <div className="flex items-center justify-between mb-8">
            {/* Search Bar */}
            <div style={{ flex: '0 0 300px' }}>
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '30px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    width: '100%',
                    color: '#000',
                    fontSize: '16px'
                  }}
                />
              </div>
            </div>

            {/* Title */}
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: '#FFFFFF',
                margin: 0,
                lineHeight: '1.2'
              }}>
                Veterans News Dispatch
              </h1>
            </div>

            {/* Auth Buttons */}
            <div style={{ flex: '0 0 300px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button style={{
                backgroundColor: 'transparent',
                color: '#FFFFFF',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '20px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '500'
              }}>
                Sign In
              </button>
              <button style={{
                backgroundColor: 'transparent',
                color: '#FFCC01',
                border: '2px solid #FFCC01',
                padding: '10px 24px',
                borderRadius: '20px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '500'
              }}>
                Sign Up
              </button>
            </div>
          </div>

          {/* Category Tabs */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '40px',
            marginBottom: '40px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            paddingBottom: '20px'
          }}>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                style={{
                  backgroundColor: 'transparent',
                  color: activeCategory === category ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)',
                  border: 'none',
                  borderBottom: activeCategory === category ? '3px solid #FFFFFF' : '3px solid transparent',
                  padding: '10px 0',
                  fontSize: '18px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {filteredArticles.map(article => (
              <div
                key={article.id}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <img
                  src={article.image}
                  alt={article.title}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{ padding: '20px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    marginBottom: '15px',
                    lineHeight: '1.4'
                  }}>
                    {article.title}
                  </h3>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      backgroundColor: article.color,
                      color: '#FFFFFF',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {article.category}
                    </span>
                    <span style={{
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      {article.date}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

const BenefitsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedBenefit, setExpandedBenefit] = useState(null);

  const categories = ['All', 'Health Care', 'Disability & Compensation', 'Housing', 'Mental Health', 'Education', 'Employment', 'Crisis & Emergency'];

  // Complete list of 100 VA Benefits
  const benefits = [
    { id: 1, title: "VA Disability Compensation", phone: "800-827-1000", intro: "Tax-free monthly benefit for service-connected illnesses or injuries including PTSD, depression, addiction, and chronic conditions.", howToApply: "File online through VA.gov using the disability portal. Submit medical evidence, service records, and lay statements.", website: "https://www.va.gov/disability/", category: "Disability & Compensation" },
    { id: 2, title: "VA Health Care Enrollment", phone: "877-222-8387", intro: "Comprehensive health care including primary care, mental health, crisis care, and specialty programs.", howToApply: "Apply online with VA Form 10-10EZ. Same-day approval for HUD-VASH veterans with ID and DD-214.", website: "https://www.va.gov/health-care/apply/", category: "Health Care" },
    { id: 3, title: "VA Pension & Aid and Attendance", phone: "800-827-1000", intro: "Monthly payments for low-income wartime veterans. Aid and Attendance increases payment for those needing daily living help.", howToApply: "File pension application online or mail VA Form 21P-527EZ with financial and medical evidence.", website: "https://www.va.gov/pension/", category: "Disability & Compensation" },
    { id: 4, title: "HUD-VASH Housing Vouchers", phone: "877-424-3838", intro: "Permanent supportive housing with rental assistance and VA case management for chronically homeless veterans.", howToApply: "Contact Homeless Veterans Hotline or VA Social Worker. Complete intake screening and maintain appointments.", website: "https://www.va.gov/homeless/hud-vash/", category: "Housing" },
    { id: 5, title: "VA Caregiver Support Program", phone: "855-260-3274", intro: "Training, financial stipends, mental health resources, and respite services for caregivers of disabled veterans.", howToApply: "Submit VA Form 10-10CG. Clinical team conducts assessment. Stipends paid monthly after approval.", website: "https://www.caregiver.va.gov/", category: "Health Care" },
    { id: 6, title: "Post-9/11 GI Bill Education Benefits", phone: "888-442-4551", intro: "Covers college, technical training, apprenticeships, and housing while in school. Up to 100% tuition coverage.", howToApply: "File online through VA.gov education portal. Submit school acceptance and service records.", website: "https://www.va.gov/education/", category: "Education" },
    { id: 7, title: "Vocational Rehabilitation & Employment", phone: "800-827-1000", intro: "Job training, resume building, employment preparation, and paid internships for disabled veterans.", howToApply: "Apply online, complete VR&E orientation, meet with counselor, submit medical evidence.", website: "https://www.va.gov/careers-employment/vocational-rehabilitation/", category: "Employment" },
    { id: 8, title: "VA Mental Health Services", phone: "877-222-8387", intro: "Therapy, psychiatry, residential programs, PTSD treatment, and SAMHSA recovery models.", howToApply: "Enroll in VA health care and request mental health intake. Same-day access for HUD-VASH veterans.", website: "https://www.va.gov/mental-health/", category: "Mental Health" },
    { id: 9, title: "Vet Centers (Combat & Trauma Counseling)", phone: "877-927-8387", intro: "Confidential counseling for PTSD, grief, MST, family issues, and combat trauma. Separate from VA medical records.", howToApply: "Call to locate nearest Vet Center. Walk-ins welcome. No disability rating required.", website: "https://www.vetcenter.va.gov/", category: "Mental Health" },
    { id: 10, title: "Veterans Crisis Line", phone: "988, Press 1", intro: "24/7 suicide prevention hotline providing immediate support via phone, chat, or text.", howToApply: "No application required. Call, text, or chat anytime. All services confidential.", website: "https://www.veteranscrisisline.net/", category: "Crisis & Emergency" },
    { id: 11, title: "VA Dental Care", phone: "877-222-8387", intro: "Essential dental work for qualifying veterans including service-connected conditions and homeless program enrollees.", howToApply: "Apply through VA Health Enrollment or ask VASH social worker to submit consult.", website: "https://www.va.gov/dental/", category: "Health Care" },
    { id: 12, title: "VA Vision Care", phone: "877-222-8387", intro: "Eye exams, eyeglasses, and treatment for cataracts, glaucoma, and diabetic retinopathy.", howToApply: "Enroll in VA health care and request optometry consult. Bring previous prescriptions.", website: "https://www.va.gov/health-care/about-va-health-benefits/vision-care/", category: "Health Care" },
    { id: 13, title: "MyHealtheVet (Patient Portal)", phone: "877-327-0022", intro: "View records, message doctors, order prescriptions, schedule appointments, and download medical summaries.", howToApply: "Register for free account and upgrade to Premium by verifying identity.", website: "https://www.myhealth.va.gov/", category: "Health Care" },
    { id: 14, title: "VA Travel Reimbursement", phone: "855-574-7292", intro: "Mileage reimbursement or free VA-arranged transportation for medical appointments.", howToApply: "Submit travel claims online through BTSSS within 30 days of appointment.", website: "https://www.va.gov/health-care/get-reimbursed-for-travel-pay/", category: "Health Care" },
    { id: 15, title: "VA MOVE! Weight Loss Program", phone: "Contact local VA", intro: "Weight management and lifestyle coaching to improve physical health, diet, and mobility.", howToApply: "Ask primary care provider to submit MOVE! consult. Programs include virtual and in-person options.", website: "https://www.va.gov/MOVE/", category: "Health Care" },
    { id: 16, title: "Whole Health & Well-Being Program", phone: "877-222-8387", intro: "Mindfulness, purpose, nutrition, movement, mental health, art therapy, and stress reduction.", howToApply: "Request Whole Health Orientation at VA facility. Create Personal Health Plan with coach.", website: "https://www.va.gov/wholehealth/", category: "Health Care" },
    { id: 17, title: "VA Pharmacy & Prescription Delivery", phone: "800-698-2411", intro: "Affordable or free medications mailed directly to home. Refill online, by phone, or app.", howToApply: "Enroll in VA health care. Order refills online or by phone. Delivery takes 3-7 days.", website: "https://www.va.gov/health-care/refill-track-prescriptions/", category: "Health Care" },
    { id: 18, title: "Community Care Network", phone: "877-881-7618", intro: "Receive appointments outside VA when wait times are long or services unavailable locally.", howToApply: "Ask VA provider to submit Community Care referral. Authorization required before scheduling.", website: "https://www.va.gov/communitycare/", category: "Health Care" },
    { id: 19, title: "VA Home Loan Program", phone: "877-827-3702", intro: "Home loan guarantees with no down payment, no mortgage insurance, and competitive rates.", howToApply: "Request Certificate of Eligibility online. Contact VA-approved lender for pre-approval.", website: "https://www.va.gov/housing-assistance/home-loans/", category: "Housing" },
    { id: 20, title: "VA Burial Benefits & National Cemeteries", phone: "800-535-1117", intro: "Burial in national cemetery, headstone, flag, marker, and burial allowances.", howToApply: "Submit pre-need eligibility application online. Provide DD-214 and personal information.", website: "https://www.va.gov/burials-memorials/", category: "Health Care" },
    { id: 21, title: "VetSuccess On Campus", phone: "800-827-1000", intro: "VA counselors on college campuses to help student veterans with benefits and academic challenges.", howToApply: "Contact school veteran center or visit VA.gov to locate VSOC counselor.", website: "https://www.va.gov/careers-employment/vetsuccess-on-campus/", category: "Education" },
    { id: 22, title: "VA Homeless Programs", phone: "877-424-3838", intro: "Shelter, transitional housing, medical care, counseling, and rapid rehousing services.", howToApply: "Call National Homeless Hotline. Connect with VA outreach social worker.", website: "https://www.va.gov/homeless/", category: "Housing" },
    { id: 23, title: "Supportive Services for Veteran Families", phone: "877-737-0111", intro: "Rent assistance, utility help, security deposits, and financial planning to prevent homelessness.", howToApply: "Contact local SSVF providers through VA website. Bring ID, income info, and housing documents.", website: "https://www.va.gov/homeless/ssvf/", category: "Housing" },
    { id: 24, title: "Compensated Work Therapy", phone: "Contact local VA", intro: "Paid transitional jobs in VA hospitals or community partners for employment experience.", howToApply: "Attend CWT orientation or request referral from social worker. Sobriety required.", website: "https://www.va.gov/health/cwt/", category: "Employment" },
    { id: 25, title: "VA Medical Foster Home Program", phone: "Contact local VA", intro: "Veterans live with private caregiver in family home setting instead of nursing home.", howToApply: "Speak with VA social worker for assessment. Submit medical and financial documentation.", website: "https://www.va.gov/GERIATRICS/pages/Medical_Foster_Home_Program.asp", category: "Health Care" },
    { id: 26, title: "Home-Based Primary Care", phone: "877-222-8387", intro: "Medical care delivered in veteran's home for those with chronic or complex needs.", howToApply: "Request referral from VA primary care provider. Home visit evaluation conducted.", website: "https://www.va.gov/GERIATRICS/pages/Home_Based_Primary_Care.asp", category: "Health Care" },
    { id: 27, title: "Telehealth (VA Video Connect)", phone: "877-327-0022", intro: "24/7 access to VA doctors, therapists, and nurses via smartphone, tablet, or computer.", howToApply: "Enroll in VA care and activate MyHealtheVet. Ask provider to enable video appointments.", website: "https://mobile.va.gov/app/va-video-connect", category: "Health Care" },
    { id: 28, title: "VA Nurse Triage Line", phone: "877-252-4866", intro: "Speak directly with VA nurse for medical advice, emergencies, and appointment coordination.", howToApply: "No application - call anytime. Nurses advise on urgent care, ER, or VA appointments.", website: "https://www.va.gov/health-care/", category: "Health Care" },
    { id: 29, title: "VA Women's Health Program", phone: "855-829-6636", intro: "Specialized care including primary care, OB-GYN, mammograms, pelvic care, MST counseling, pregnancy support.", howToApply: "Enroll in VA care and request Women's Health Primary Care Team assignment.", website: "https://www.va.gov/womenvet/", category: "Health Care" },
    { id: 30, title: "Military Sexual Trauma Counseling", phone: "855-829-6636", intro: "Free mental health care, trauma therapy, medication support, and disability compensation for MST survivors.", howToApply: "No documentation required for counseling. For compensation, file claim with MST coordinator.", website: "https://www.va.gov/health-care/health-needs-conditions/military-sexual-trauma/", category: "Mental Health" },
    { id: 31, title: "PTSD Treatment Programs", phone: "877-927-8387", intro: "Evidence-based PTSD services including EMDR, CBT, CPT, prolonged exposure, and residential treatment.", howToApply: "Request mental health intake or referral. Attend screening and treatment planning.", website: "https://www.ptsd.va.gov/", category: "Mental Health" },
    { id: 32, title: "Substance Use Disorder Treatment", phone: "877-927-8387", intro: "Detox, rehab, outpatient groups, IOP programs, relapse prevention, MAT, and recovery coaching.", howToApply: "Request SUD consult from primary care or mental health. Attend intake and create treatment plan.", website: "https://www.va.gov/health-care/health-needs-conditions/substance-use-problems/", category: "Mental Health" },
    { id: 33, title: "VA Emergency Mental Health (COMPACT Act)", phone: "988, Press 1", intro: "Free emergency mental health care at non-VA hospitals with full reimbursement.", howToApply: "Present veteran status at ER. Hospital bills VA. Call VA within 72 hours for follow-up.", website: "https://www.va.gov/compact-act/", category: "Crisis & Emergency" },
    { id: 34, title: "VA Legal Services", phone: "Contact local VA", intro: "Free or low-cost legal aid for housing issues, eviction, child support, misdemeanors, discharge upgrades.", howToApply: "Ask VA social worker to connect with on-site legal clinics. Bring legal documents.", website: "https://www.va.gov/homeless/Legal.asp", category: "Health Care" },
    { id: 35, title: "VA Financial Hardship Assistance", phone: "800-827-1000", intro: "Debt relief, copay waivers, or repayment plans for veterans facing financial hardship.", howToApply: "Submit VA Form 5655 with proof of income and expenses. VA reviews individually.", website: "https://www.va.gov/health-care/copay-rates/#financial-hardship", category: "Health Care" },
    { id: 36, title: "Disability Housing Grants", phone: "800-827-1000", intro: "Housing adaptation grants to modify homes for safety, accessibility, and independence.", howToApply: "File disability housing grant application with medical evidence. VA conducts evaluation.", website: "https://www.va.gov/housing-assistance/disability-housing-grants/", category: "Housing" },
    { id: 37, title: "VA Life Insurance", phone: "800-669-8477", intro: "Affordable life insurance with no medical exam required. Helps families with burial costs.", howToApply: "Apply online through VA insurance portal. Provide basic personal information.", website: "https://www.va.gov/life-insurance/", category: "Health Care" },
    { id: 38, title: "CHAMPVA Health Benefits", phone: "800-733-8387", intro: "Health insurance for dependents and survivors of qualifying disabled veterans.", howToApply: "Submit VA Form 10-10d with supporting documentation. Approval may take several weeks.", website: "https://www.va.gov/COMMUNITYCARE/programs/dependents/champva/", category: "Health Care" },
    { id: 39, title: "VA Blind & Low Vision Rehabilitation", phone: "Contact local VA", intro: "Specialized rehabilitation, assistive technology, mobility training, and independent living support.", howToApply: "Request low-vision consult. Attend evaluation and receive customized training.", website: "https://www.va.gov/health-care/about-va-health-benefits/vision-care/", category: "Health Care" },
    { id: 40, title: "VA Audiology & Hearing Aids", phone: "877-222-8387", intro: "Free hearing exams and, if eligible, free hearing aids and batteries.", howToApply: "Request hearing consult from VA provider. Audiology tests hearing and determines eligibility.", website: "https://www.va.gov/health-care/about-va-health-benefits/hearing-aids/", category: "Health Care" },
    { id: 41, title: "VA Mobility Equipment", phone: "877-222-8387", intro: "Wheelchairs, power chairs, scooters, walkers, and prosthetics based on medical need.", howToApply: "Ask primary care for prosthetics consult. Attend mobility assessment with OT/PT.", website: "https://www.prosthetics.va.gov/", category: "Health Care" },
    { id: 42, title: "Reserve Educational Assistance Program", phone: "888-442-4551", intro: "Education benefits for reservists activated for war or national emergency.", howToApply: "Submit application through VA education portal. Provide activation and service records.", website: "https://www.va.gov/education/", category: "Education" },
    { id: 43, title: "VR&E Independent Living Track", phone: "800-827-1000", intro: "Home modifications, technology, and skill development for veterans unable to work due to disabilities.", howToApply: "Apply through VR&E portal and request Independent Living Track assessment.", website: "https://www.va.gov/careers-employment/vocational-rehabilitation/", category: "Employment" },
    { id: 44, title: "VA Fiduciary Services", phone: "888-407-0144", intro: "Financial management assistance for veterans unable to manage benefits independently.", howToApply: "VA evaluates medical and financial evidence. Veterans may suggest trusted fiduciary.", website: "https://www.va.gov/fiduciary/", category: "Health Care" },
    { id: 45, title: "National Archives Military Records", phone: "866-272-6272", intro: "Request DD-214s, personnel files, and service records for benefits and claims.", howToApply: "Apply online through National Archives portal. Upload ID and specify record details.", website: "https://www.archives.gov/veterans", category: "Health Care" },
    { id: 46, title: "VA Domestic Violence Program", phone: "Contact local VA", intro: "Counseling, safety planning, and intervention for veterans and spouses experiencing domestic violence.", howToApply: "Request consult from VA primary care or mental health. Coordinator begins services immediately.", website: "https://www.va.gov/health-care/health-needs-conditions/domestic-violence/", category: "Mental Health" },
    { id: 47, title: "VA LGBTQ+ Veteran Care", phone: "877-222-8387", intro: "Gender-affirming care, counseling, support groups, and medical treatment for LGBTQ+ veterans.", howToApply: "Enroll in VA care and request referral to LGBTQ+ Veteran Care Coordinator.", website: "https://www.patientcare.va.gov/LGBT/", category: "Health Care" },
    { id: 48, title: "DAV Transportation Services", phone: "Contact local DAV", intro: "Free transportation to VA hospitals for veterans without vehicles, provided by DAV volunteers.", howToApply: "Call local DAV transportation coordinator. Provide appointment time and location.", website: "https://www.dav.org/veterans/i-need-a-ride/", category: "Health Care" },
    { id: 49, title: "Vet Centers Mobile Outreach", phone: "877-927-8387", intro: "Mobile Vet Centers bring counseling to underserved areas, rural communities, and homeless locations.", howToApply: "Find local MVU schedules online or call national number. Walk-ins welcome.", website: "https://www.vetcenter.va.gov/", category: "Mental Health" },
    { id: 50, title: "VA Homeless Hotline", phone: "877-424-3838", intro: "24/7 hotline connects homeless veterans to emergency shelter, HUD-VASH, SSVF, and crisis housing nationwide.", howToApply: "Call anytime. Provide location, needs, and service history. VA specialist contacts within hours.", website: "https://www.va.gov/homeless/nationalcallcenter.asp", category: "Crisis & Emergency" },
    { id: 51, title: "VA Primary Care", phone: "877-222-8387", intro: "Dedicated medical team (RN, doctor, pharmacist, social worker) managing overall health, referrals, and chronic conditions.", howToApply: "Enroll in VA health care, request PCP, attend orientation, and schedule initial exam.", website: "https://www.va.gov/health-care/", category: "Health Care" },
    { id: 52, title: "VA Pain Management Clinic", phone: "Contact local VA", intro: "Comprehensive chronic pain treatment including PT, injections, acupuncture, medications, and alternative therapies.", howToApply: "Request referral from Primary Care. May be referred to PT, acupuncture, or pain specialist.", website: "https://www.va.gov/painmanagement/", category: "Health Care" },
    { id: 53, title: "VA Orthopedics & Physical Therapy", phone: "877-222-8387", intro: "Joint evaluations, injury treatment, physical therapy, surgery, and rehabilitation.", howToApply: "Ask Primary Care for PT/Ortho consult. Attend orientation and follow home exercise plans.", website: "https://www.va.gov/health-care/about-va-health-benefits/orthopedics/", category: "Health Care" },
    { id: 54, title: "VA Neurology Services", phone: "Contact local VA", intro: "Evaluation and treatment for migraines, seizures, neuropathy, TBI, and memory disorders.", howToApply: "Request neurology referral from PCP. Bring previous records, CT scans, or MRI reports.", website: "https://www.polytrauma.va.gov/", category: "Health Care" },
    { id: 55, title: "Traumatic Brain Injury Clinics", phone: "800-241-0803", intro: "Specialized TBI teams provide cognitive therapy, neurological evaluation, memory rehabilitation, and long-term treatment.", howToApply: "Request TBI screening at VA facility. If positive, referred to Polytrauma System of Care.", website: "https://www.polytrauma.va.gov/", category: "Health Care" },
    { id: 56, title: "VA Geriatrics & Extended Care", phone: "877-222-8387", intro: "Assisted living, respite care, adult day health, hospice, and long-term nursing care.", howToApply: "Request geriatric consult through Primary Care. Bring caregiver or family for planning.", website: "https://www.va.gov/GERIATRICS/", category: "Health Care" },
    { id: 57, title: "VA Caregiver Support", phone: "855-260-3274", intro: "Training, support, financial stipends, counseling, and respite care for family caregivers.", howToApply: "Apply online or submit VA Form 10-10CG. Veteran undergoes functional assessment.", website: "https://www.caregiver.va.gov/", category: "Health Care" },
    { id: 58, title: "VA Medical Equipment (DME)", phone: "Contact local VA", intro: "Wheelchairs, shower chairs, CPAP machines, orthopedic braces, diabetic supplies, prosthetic limbs.", howToApply: "Request prosthetics consult from Primary Care. Attend evaluation and fitting.", website: "https://www.prosthetics.va.gov/", category: "Health Care" },
    { id: 59, title: "VA CPAP / Sleep Apnea Program", phone: "877-222-8387", intro: "Sleep studies, CPAP machines, supplies, and follow-up care for obstructive sleep apnea.", howToApply: "Request sleep study referral. Once diagnosed, VA issues CPAP equipment and replacements.", website: "https://www.va.gov/health-care/", category: "Health Care" },
    { id: 60, title: "VA Diabetes Management", phone: "Contact local VA", intro: "Diabetes monitoring, education, medications, nutrition counseling, and podiatry care.", howToApply: "Ask Primary Care for diabetes education classes and endocrinology consult.", website: "https://www.va.gov/health-care/about-va-health-benefits/diabetes/", category: "Health Care" },
    { id: 61, title: "VA Podiatry (Foot & Ankle)", phone: "Contact local VA", intro: "Foot exams, injury treatment, diabetic foot care, surgeries, and orthotic inserts.", howToApply: "Request podiatry referral from VA Primary Care Provider.", website: "https://www.va.gov/health-care/", category: "Health Care" },
    { id: 62, title: "VA Nutrition & Dietitian Services", phone: "877-222-8387", intro: "Dietitians help create nutrition plans for weight loss, diabetes, heart health, and Whole Health goals.", howToApply: "Request nutrition consult. Attend group or individual sessions.", website: "https://www.nutrition.va.gov/", category: "Health Care" },
    { id: 63, title: "VA Chaplain Services", phone: "Contact local VA", intro: "Spiritual counseling, crisis support, grief care, suicide intervention, and religious services for all faiths.", howToApply: "Ask social worker or call VA chaplain's office. Walk-ins welcome.", website: "https://www.va.gov/chaplain/", category: "Mental Health" },
    { id: 64, title: "VA Recreation Therapy", phone: "Contact local VA", intro: "Music therapy, creative arts, ceramics, sports, adaptive equipment, and community events for emotional healing.", howToApply: "Enroll through Whole Health or Mental Health teams. Attend scheduled activities at VA campus.", website: "https://www.va.gov/health/rec-therapy.asp", category: "Mental Health" },
    { id: 65, title: "Mental Health Intensive Case Management", phone: "Contact local VA", intro: "High-level program providing intensive case management, home visits, and crisis support for severe mental illness.", howToApply: "Request evaluation from VA mental health provider. Eligibility based on diagnosis.", website: "https://www.va.gov/health-care/health-needs-conditions/mental-health/", category: "Mental Health" },
    { id: 66, title: "VA Residential Rehabilitation (Domiciliary)", phone: "877-927-8387", intro: "Structured live-in treatment for PTSD, SUD, homelessness, or mental health stabilization.", howToApply: "Ask VASH or HPACT social worker for RRTP referral. Complete intake screening.", website: "https://www.va.gov/homeless/domiciliary_care.asp", category: "Mental Health" },
    { id: 67, title: "VA Emergency Housing (Grant & Per Diem)", phone: "877-424-3838", intro: "Emergency beds, transitional housing, and support services for homeless veterans.", howToApply: "Contact Homeless Hotline for placement. Show ID and DD214 if available.", website: "https://www.va.gov/homeless/gpd.asp", category: "Housing" },
    { id: 68, title: "VA Pharmacy Counseling", phone: "877-222-8387", intro: "Pharmacists provide medication education, side-effect review, drug-interaction checks, and refill support.", howToApply: "Request pharmacy counseling session online or speak with pharmacy staff after appointment.", website: "https://www.va.gov/health-care/", category: "Health Care" },
    { id: 69, title: "VA COVID-19 & Vaccinations", phone: "877-222-8387", intro: "Free vaccines for COVID-19, flu, shingles, pneumonia, and more.", howToApply: "Walk into any VA medical center or request during Primary Care visit.", website: "https://www.va.gov/health-care/covid-19-vaccine/", category: "Health Care" },
    { id: 70, title: "VA Specialty Clinics", phone: "Contact local VA", intro: "Advanced specialty care for heart, liver, lung, kidney, digestive, skin, and endocrine conditions.", howToApply: "Request referral from Primary Care. Tests such as X-rays or labs may be required.", website: "https://www.va.gov/health-care/", category: "Health Care" },
    { id: 71, title: "VA Pharmacy Mail-Order Express", phone: "877-327-0022", intro: "Medications delivered to your door with tracking and refill reminders.", howToApply: "Refill online or call automated line. Ensure address is updated in MyHealtheVet.", website: "https://www.va.gov/health-care/refill-track-prescriptions/", category: "Health Care" },
    { id: 72, title: "VA Emergency Care Reimbursement", phone: "877-466-7124", intro: "VA may reimburse emergency care at non-VA hospitals under specific criteria.", howToApply: "Notify VA within 72 hours of emergency visit. Submit claim forms and itemized bills.", website: "https://www.va.gov/communitycare/programs/veterans/Emergency_Care.asp", category: "Health Care" },
    { id: 73, title: "VA Social Work Services", phone: "Contact local VA", intro: "Case management, housing support, crisis intervention, guardianship, and community service coordination.", howToApply: "Ask provider for Social Work consult. HUD-VASH veterans automatically receive dedicated social worker.", website: "https://www.socialwork.va.gov/", category: "Health Care" },
    { id: 74, title: "VA Copay Exemption for Homeless", phone: "Contact VA Eligibility", intro: "Veterans who are homeless or at risk may qualify for total exemption from medication copays.", howToApply: "Request homeless status determination from VASH or VA social worker. Submit form to Eligibility.", website: "https://www.va.gov/health-care/copay-rates/", category: "Health Care" },
    { id: 75, title: "VA Health Care for Reentry Veterans", phone: "877-927-8387", intro: "Supports veterans recently released from jail/prison with housing, health care, mental health, and benefits.", howToApply: "Contact local jail/prison coordinators or call Vet Center. Provide release date and veteran ID.", website: "https://www.va.gov/homeless/reentry.asp", category: "Crisis & Emergency" },
    { id: 76, title: "Community Care Network (CCN)", phone: "877-881-7618", intro: "Outside VA care when VA cannot provide timely or local services for HUD-VASH veterans.", howToApply: "Request consult from Primary Care. VA determines eligibility based on wait times and distance.", website: "https://www.va.gov/communitycare/", category: "Health Care" },
    { id: 77, title: "VA Dental Care (Eligibility-Based)", phone: "Contact VA Dental", intro: "Exams, cleanings, dentures, restorations, pain treatment, and full dental rehabilitation for eligible veterans.", howToApply: "Ask Eligibility for qualification. Request one-time dental benefit evaluation after enrollment.", website: "https://www.va.gov/health-care/about-va-health-benefits/dental-care/", category: "Health Care" },
    { id: 78, title: "VA Vision & Eye Care", phone: "Contact VA Optometry", intro: "Comprehensive eye exams, glasses, glaucoma treatment, cataract evaluation, and diabetic eye care.", howToApply: "Request referral through Primary Care. After exam, prosthetics orders eyeglasses.", website: "https://www.va.gov/health-care/about-va-health-benefits/vision-care/", category: "Health Care" },
    { id: 79, title: "VA PTSD Clinical Team", phone: "Contact VA Mental Health", intro: "Specialized teams offering evidence-based PTSD therapy including CPT, EMDR, PE, and group therapy.", howToApply: "Request PTSD consult or referral from mental health or Primary Care. Attend initial intake.", website: "https://www.ptsd.va.gov/", category: "Mental Health" },
    { id: 80, title: "Military Sexual Trauma Program", phone: "877-995-5247", intro: "Free counseling, therapy, PTSD treatment, and support groups for veterans who experienced MST during service.", howToApply: "Contact MST Coordinator at VA. No documentation required.", website: "https://www.mentalhealth.va.gov/msthome.asp", category: "Mental Health" },
    { id: 81, title: "VA Women's Health Program", phone: "Contact Women's Health", intro: "Women veterans receive dedicated OB/GYN, mammograms, reproductive health, prenatal services, and MST support.", howToApply: "Enroll in VA health care and request assignment to Women's Health clinic.", website: "https://www.womenshealth.va.gov/", category: "Health Care" },
    { id: 82, title: "LGBTQ+ Veteran Support", phone: "Contact VA LGBTQ+ Coordinator", intro: "Inclusive care, mental health, gender-affirming services, safe spaces, and health navigation for LGBTQ+ veterans.", howToApply: "Speak with VA coordinator for resources, counseling, and specialty referrals.", website: "https://www.patientcare.va.gov/LGBT/", category: "Health Care" },
    { id: 83, title: "SUD Intensive Outpatient Program", phone: "Contact SUD Program", intro: "Structured addiction treatment including counseling, relapse prevention, MAT, and peer groups.", howToApply: "Request referral through Mental Health or VASH social worker. Attend intake evaluation.", website: "https://www.va.gov/homeless/substance_abuse.asp", category: "Mental Health" },
    { id: 84, title: "Medication-Assisted Treatment (MAT)", phone: "Contact Addiction Medicine", intro: "Evidence-based treatment for opioid addiction using Suboxone, Methadone, Naltrexone combined with therapy.", howToApply: "Ask Mental Health for MAT evaluation. Attend lab testing and weekly counseling.", website: "https://www.va.gov/opa/pressrel/pressrelease.cfm?id=5577", category: "Mental Health" },
    { id: 85, title: "MOVE! Weight Management", phone: "Contact Whole Health", intro: "Group classes, personal coaching, nutrition plans, walking groups, and gym activities for weight loss.", howToApply: "Request MOVE! referral through Primary Care or Whole Health.", website: "https://www.move.va.gov/", category: "Health Care" },
    { id: 86, title: "TeleMental Health Services", phone: "877-222-8387", intro: "Therapy, psychiatry, and mental health appointments via phone or video for veterans without transportation.", howToApply: "Request telehealth enrollment. Ensure updated phone/email in MyHealtheVet.", website: "https://telehealth.va.gov/", category: "Mental Health" },
    { id: 87, title: "VA Home Telehealth Monitoring", phone: "Contact Home Telehealth", intro: "Tablets and remote monitoring (blood pressure, weight, glucose) with daily VA nurse check-ins.", howToApply: "Request referral through PCP. Equipment shipped to home.", website: "https://connectedcare.va.gov/", category: "Health Care" },
    { id: 88, title: "Housing Retention Services", phone: "Contact VASH Social Worker", intro: "Maintaining stable housing, preventing eviction, crisis intervention, and addressing social determinants.", howToApply: "Work directly with HUD-VASH team, which automatically refers veterans needing support.", website: "https://www.va.gov/homeless/hud-vash.asp", category: "Housing" },
    { id: 89, title: "VA Fiduciary Services", phone: "888-407-0144", intro: "Assists veterans unable to manage finances due to disability by appointing fiduciaries.", howToApply: "VA may require assessment. Submit paperwork and complete evaluation.", website: "https://www.benefits.va.gov/fiduciary/", category: "Health Care" },
    { id: 90, title: "Discharge Upgrade Assistance", phone: "800-698-2411", intro: "Helps veterans with less-than-honorable discharges apply for upgrade or character review.", howToApply: "File DD Form 293 or 149. VA or VSOs assist with supporting documents.", website: "https://www.va.gov/discharge-upgrade-instructions/", category: "Health Care" },
    { id: 91, title: "Military Records & DD214 Replacement", phone: "314-801-0800", intro: "Veterans can obtain service records, DD214 copies, medals, and personnel files.", howToApply: "Request through eVetRecs or National Archives. Processing times vary.", website: "https://www.archives.gov/veterans", category: "Health Care" },
    { id: 92, title: "PACT Act Health & Disability", phone: "800-698-2411", intro: "Expands health care and disability benefits for veterans exposed to burn pits, Agent Orange, radiation, toxins.", howToApply: "File PACT Act claim or apply for VA health enrollment.", website: "https://www.va.gov/pact/", category: "Disability & Compensation" },
    { id: 93, title: "Homeless Outreach Social Work", phone: "877-424-3838", intro: "Street outreach teams identify unhoused veterans and connect to shelters, VASH, SSVF, emergency housing.", howToApply: "Call Homeless Hotline or request outreach.", website: "https://www.va.gov/homeless/", category: "Housing" },
    { id: 94, title: "Respite Care for Caregivers", phone: "877-222-8387", intro: "Provides temporary relief so caregivers can rest, attend appointments, or handle emergencies.", howToApply: "Request GEC (Geriatrics & Extended Care) consult.", website: "https://www.va.gov/GERIATRICS/", category: "Health Care" },
    { id: 95, title: "Hospice & Palliative Care", phone: "Contact local VA", intro: "Supports veterans with life-limiting illness, providing comfort care, emotional support, family counseling, spiritual care.", howToApply: "Request referral through PCP or specialty care. Family meetings encouraged.", website: "https://www.va.gov/palliative-care/", category: "Health Care" },
    { id: 96, title: "Blind Rehabilitation Services", phone: "800-827-1000", intro: "Services for visually impaired or blind veterans including mobility training, adaptive technology, independent living.", howToApply: "PCP or eye-care referral required. Evaluation determines service level.", website: "https://www.va.gov/BLINDREHAB/", category: "Health Care" },
    { id: 97, title: "Audiology & Hearing Aids", phone: "Contact VA Audiology", intro: "Free hearing exams, tinnitus management, and hearing aids with annual replacements for qualified veterans.", howToApply: "Request referral through Primary Care. Attend evaluation and fitting session.", website: "https://www.va.gov/health-care/about-va-health-benefits/hearing-aids/", category: "Health Care" },
    { id: 98, title: "VA Transportation Services", phone: "Contact VA Transportation", intro: "Free transportation to VA appointments using VA shuttles, DAV volunteer vans, and Lyft/Uber programs.", howToApply: "Call transportation 48 hours in advance. Some areas allow same-day shuttle boarding.", website: "https://www.va.gov/healthbenefits/vtp/", category: "Health Care" },
    { id: 99, title: "HUD-VASH National Office", phone: "877-424-3838", intro: "Oversees national housing policy, voucher distribution, case management training, and federal housing compliance.", howToApply: "Veterans must be enrolled with HUD-VASH social worker and complete intake assessment.", website: "https://www.va.gov/homeless/hud-vash.asp", category: "Housing" },
    { id: 100, title: "Veteran Crisis Support (Peer)", phone: "988, Press 1", intro: "Confidential crisis support, safety planning, peer intervention, suicide prevention, and linkage to local VA teams.", howToApply: "Call, text, or chat. Crisis line staff will notify your VA team if you consent.", website: "https://www.veteranscrisisline.net/", category: "Crisis & Emergency" }
  ];

  const filteredBenefits = activeCategory === 'All' 
    ? benefits 
    : benefits.filter(benefit => benefit.category === activeCategory);

  const searchedBenefits = searchQuery
    ? filteredBenefits.filter(benefit => 
        benefit.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        benefit.intro.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredBenefits;

  return (
    <div className="min-h-screen" style={{ 
      backgroundColor: '#1a1a1a',
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_veteran-evidence-hub/artifacts/8747zdio_vecteezy_homes-in-residential-district-with-dramatic-colourful-sunset_22527580.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <TopNavbar showAuth={false} />
      
      {/* Main Glass Container */}
      <div className="container mx-auto px-4 py-8" style={{ paddingTop: '140px' }}>
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.18)'
        }}>
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 style={{
              fontSize: '56px',
              fontWeight: 'bold',
              color: '#FFFFFF',
              marginBottom: '16px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}>
              VA NATIONAL BENEFITS DIRECTORY
            </h1>
            <p style={{
              fontSize: '24px',
              color: '#FFCC01',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              The Top 100 VA Benefits, Disability Programs, and Services Every Veteran Must Know
            </p>
            <p style={{
              fontSize: '18px',
              color: '#FFFFFF',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              These are the VA's most life-changing benefits. Every veteran in HUD-VASH should understand each one. This directory gives veterans an immediate guide for applying, qualifying, and connecting to the services that can rebuild their health, stability, and future.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '30px',
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <Search size={24} color="#666" />
              <input
                type="text"
                placeholder="Search benefits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  width: '100%',
                  color: '#000',
                  fontSize: '18px',
                  fontWeight: '500'
                }}
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '25px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  backgroundColor: activeCategory === category 
                    ? '#FFCC01' 
                    : 'rgba(255, 255, 255, 0.2)',
                  color: activeCategory === category ? '#1A4F63' : '#FFFFFF',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Benefits List - 3 per row */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {searchedBenefits.map((benefit, index) => (
              <div
                key={benefit.id}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedBenefit(expandedBenefit === benefit.id ? null : benefit.id)}
              >
                {/* Benefit Header */}
                <div style={{
                  padding: '24px',
                  borderLeft: '6px solid #FFCC01'
                }}>
                  <div className="flex justify-between items-start">
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1A4F63',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        #{index + 1} • {benefit.category}
                      </div>
                      <h3 style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1A4F63',
                        marginBottom: '12px'
                      }}>
                        {benefit.title}
                      </h3>
                      <div className="flex items-center gap-4 mb-12">
                        <div className="flex items-center gap-2">
                          <Phone size={18} color="#1A4F63" />
                          <span style={{ fontSize: '16px', fontWeight: '600', color: '#1A4F63' }}>
                            {benefit.phone}
                          </span>
                        </div>
                      </div>
                      <p style={{
                        fontSize: '16px',
                        color: '#333',
                        lineHeight: '1.6'
                      }}>
                        {benefit.intro}
                      </p>
                    </div>
                    <ChevronDown 
                      size={28} 
                      style={{
                        color: '#1A4F63',
                        transform: expandedBenefit === benefit.id ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.3s'
                      }}
                    />
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedBenefit === benefit.id && (
                  <div style={{
                    padding: '24px',
                    backgroundColor: '#f8f9fa',
                    borderTop: '2px solid #e0e0e0'
                  }}>
                    <div className="mb-6">
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#1A4F63',
                        marginBottom: '12px'
                      }}>
                        How to Apply:
                      </h4>
                      <p style={{
                        fontSize: '16px',
                        color: '#333',
                        lineHeight: '1.6'
                      }}>
                        {benefit.howToApply}
                      </p>
                    </div>
                    <a 
                      href={benefit.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        backgroundColor: '#1A4F63',
                        color: '#FFFFFF',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '16px',
                        fontWeight: '600',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0f3a4a'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1A4F63'}
                    >
                      Visit Website →
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Notice */}
          <div className="mt-12 text-center">
            <p style={{
              color: '#FFFFFF',
              fontSize: '16px',
              padding: '20px',
              backgroundColor: 'rgba(26, 79, 99, 0.8)',
              borderRadius: '12px',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              💡 <strong>Full list of 100 benefits coming soon!</strong> This directory is being populated with all VA services. Check back regularly for updates.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};


// ============ VETENGINE: LEGAL ENGINE PAGE ============
const VETLegalEnginePage = () => {
  const [openSection, setOpenSection] = useState(null);

  const legalSections = [
    {
      title: "Federal Constitutional Law",
      laws: ["First Amendment – Freedom of Speech", "Fourth Amendment – Search and Seizure", "Fifth Amendment – Due Process", "Fourteenth Amendment – Equal Protection"]
    },
    {
      title: "Federal Civil Rights Law",
      laws: ["42 U.S.C. § 2000a – Public Accommodations", "42 U.S.C. § 1983 – Civil Rights Under Color of Law", "42 U.S.C. § 1985 – Conspiracy Against Civil Rights"]
    },
    {
      title: "Fair Housing Act",
      laws: ["42 U.S.C. § 3604 – Fair Housing Discrimination", "42 U.S.C. § 3604(f)(3)(B) – Reasonable Accommodation", "42 U.S.C. § 3617 – Retaliation Protection"]
    },
    {
      title: "HUD Housing Law",
      laws: ["24 CFR 982 – Housing Choice Voucher Program", "24 CFR 5.858 – Drug-Related Criminal Activity", "24 CFR 966 – Lease and Grievance Procedures"]
    },
    {
      title: "VA Regulations",
      laws: ["38 U.S.C. § 1710 – Eligibility for Hospital Care", "38 U.S.C. § 1720 – Mental Health Services", "38 CFR 17 – Medical Regulations"]
    },
    {
      title: "VHA Directives",
      laws: ["VHA Directive 1501 – Homeless Programs", "VHA Directive 1162.05 – HUD-VASH", "VHA Directive 1160.01 – Mental Health Services"]
    },
    {
      title: "Oversight Authorities",
      laws: ["VA Office of Inspector General", "HUD Office of Inspector General", "DOJ Civil Rights Division", "Congressional Oversight Committees"]
    }
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Legal Engine' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>VETLegalEngine™</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Core Legal Database</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg mb-4">
            The VETLegalEngine is the central law database powering the VETDirect platform. It contains 350+ federal laws, regulations, and directives mapped to veteran systems and protections.
          </p>
        </div>

        {legalSections.map((section, idx) => (
          <div key={idx} className="mb-6 rounded-lg overflow-hidden shadow-lg">
            <button
              className="w-full text-left p-6 font-bold text-xl text-white transition-all duration-200 hover:opacity-95 flex justify-between items-center"
              style={{ backgroundColor: '#1A4F63' }}
              onClick={() => setOpenSection(openSection === idx ? null : idx)}
            >
              <span>{section.title}</span>
              <ChevronDown className={`transition-transform duration-200 ${openSection === idx ? 'rotate-180' : ''}`} size={24} />
            </button>
            {openSection === idx && (
              <div className="p-8 bg-white">
                <ul className="space-y-3">
                  {section.laws.map((law, i) => (
                    <li key={i} className="flex items-start">
                      <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
                      <span className="text-lg">{law}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ VETENGINE: DOMAIN TAGS PAGE ============
const VETDomainTagsPage = () => {
  const tags = [
    "Eviction", "Retaliation", "Housing Discrimination", "Treatment Denial", "Case Manager Misconduct",
    "Drug Activity", "Unsafe Housing", "Patient Abandonment", "Suicide Risk", "Violence",
    "Medical Malpractice", "Licensing Violations", "Ethics Violations", "Privacy Violations",
    "Due Process Violations", "First Amendment Retaliation", "Fourth Amendment Search",
    "Disability Discrimination", "Mental Health Discrimination", "Veteran Status Discrimination"
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Domain Tags' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Master Domain Tags</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Cross-Reference Tags for Legal Intelligence</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg mb-4">
            Master Domain Tags allow cross-referencing across the entire VETDirect platform. These tags drive the AI violation detection engine and connect incidents to applicable laws and oversight authorities.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          {tags.map((tag, idx) => (
            <div key={idx} className="p-4 rounded-lg text-center font-semibold text-white" style={{ backgroundColor: '#7A8F65' }}>
              {tag}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE: ENTITY DOMAINS PAGE ============
const VETEntityDomainsPage = () => {
  const entities = {
    "Federal Agencies": ["Department of Veterans Affairs (VA)", "Department of Housing and Urban Development (HUD)", "Department of Health and Human Services (HHS)", "Department of Justice (DOJ)"],
    "VA Components": ["Veterans Health Administration (VHA)", "Veterans Benefits Administration (VBA)", "National Cemetery Administration", "VA Office of Inspector General"],
    "State Authorities": ["State Medical Boards", "State Social Work Licensing Boards", "State Behavioral Health Departments", "State Housing Agencies"],
    "Local Authorities": ["Public Housing Authorities", "Local Law Enforcement", "County Mental Health Services", "City Housing Departments"],
    "Oversight Bodies": ["VA OIG", "HUD OIG", "DOJ Civil Rights Division", "Congressional Oversight Committees", "State Licensing Boards"]
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Entity Domains' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Entity Domains</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Organizations Involved in Veteran Systems</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg mb-4">
            Entity Domains map the organizational structure governing veteran housing, healthcare, and legal systems. This directory connects incidents to accountable entities.
          </p>
        </div>

        {Object.entries(entities).map(([category, orgs], idx) => (
          <div key={idx} className="mb-8 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>{category}</h3>
            <ul className="space-y-2">
              {orgs.map((org, i) => (
                <li key={i} className="flex items-start">
                  <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
                  <span className="text-lg">{org}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ VETENGINE: BOARD CERTIFICATIONS PAGE ============
const VETBoardsPage = () => {
  const boards = [
    {
      name: "Association of Social Work Boards (ASWB)",
      description: "Regulates social work licensing exams and standards",
      jurisdiction: "National"
    },
    {
      name: "California Board of Behavioral Sciences (BBS)",
      description: "Licenses social workers, marriage and family therapists, and professional clinical counselors in California",
      jurisdiction: "California"
    },
    {
      name: "American Board of Psychiatry and Neurology (ABPN)",
      description: "Certifies psychiatrists and neurologists",
      jurisdiction: "National"
    },
    {
      name: "American Board of Medical Specialties (ABMS)",
      description: "Umbrella organization for medical specialty boards",
      jurisdiction: "National"
    },
    {
      name: "National Association of Social Workers (NASW)",
      description: "Professional association for social workers, establishes Code of Ethics",
      jurisdiction: "National"
    }
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Board Certifications' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Board Certifications</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Professional Certification Bodies</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg mb-4">
            Professional boards establish standards, issue certifications, and enforce ethics codes for healthcare and social service professionals working with veterans.
          </p>
        </div>

        {boards.map((board, idx) => (
          <div key={idx} className="mb-6 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-2" style={{ color: '#1A4F63' }}>{board.name}</h3>
            <p className="text-lg mb-2">{board.description}</p>
            <p className="font-semibold" style={{ color: '#7A8F65' }}>Jurisdiction: {board.jurisdiction}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ VETENGINE: HOUSING AUTHORITIES PAGE ============
const VETHousingAuthoritiesPage = () => {
  const authorities = [
    { name: "U.S. Department of Housing and Urban Development (HUD)", role: "Federal oversight of housing programs", scope: "National" },
    { name: "Housing Authority of Los Angeles (HACLA)", role: "Issues HUD-VASH vouchers", scope: "Los Angeles County", vouchers: "5115 HUD-VASH vouchers" },
    { name: "San Diego Housing Commission", role: "Manages housing assistance programs", scope: "San Diego County", vouchers: "2100 HUD-VASH vouchers" },
    { name: "Philadelphia Housing Authority", role: "Public housing and voucher administration", scope: "Philadelphia", vouchers: "3200 HUD-VASH vouchers" },
    { name: "New York City Housing Authority", role: "Largest public housing authority in U.S.", scope: "New York City", vouchers: "8500 HUD-VASH vouchers" }
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Housing Authorities' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Housing Authorities</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>HUD and Public Housing Authorities</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>The HUD-VASH Structure</h2>
          <p className="text-lg mb-4">
            HUD-VASH operates through a partnership between the Department of Veterans Affairs and Public Housing Authorities (PHAs). PHAs issue housing vouchers, while VA medical centers provide case management and clinical services.
          </p>
        </div>

        {authorities.map((auth, idx) => (
          <div key={idx} className="mb-6 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-2" style={{ color: '#1A4F63' }}>{auth.name}</h3>
            <p className="text-lg mb-2"><span className="font-semibold">Role:</span> {auth.role}</p>
            <p className="text-lg mb-2"><span className="font-semibold">Scope:</span> {auth.scope}</p>
            {auth.vouchers && <p className="font-semibold" style={{ color: '#7A8F65' }}>{auth.vouchers}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ VETENGINE: MEDICAL PAGE ============
const VETMedicalPage = () => {
  const systems = {
    "VA Healthcare System": ["Veterans Health Administration (VHA)", "VA Medical Centers (VAMCs)", "Community-Based Outpatient Clinics (CBOCs)", "Vet Centers"],
    "Mental Health Services": ["Mental Health Intensive Case Management (MHICM)", "Psychosocial Rehabilitation & Recovery Centers (PRRC)", "Domiciliary Care", "Residential PTSD Programs"],
    "Substance Use Disorder Treatment": ["Residential SUD Treatment", "Outpatient SUD Programs", "Medication-Assisted Treatment (MAT)", "Recovery Support Services"],
    "Case Management": ["HUD-VASH Case Management", "Health Care for Homeless Veterans (HCHV)", "Grant and Per Diem (GPD) Case Management", "Veterans Justice Outreach (VJO)"],
    "PACT / HPACT Teams": ["Patient Aligned Care Team (PACT)", "Homeless Patient Aligned Care Team (HPACT)", "Primary Care Physician", "Registered Nurse Care Manager", "Clinical Pharmacist", "Social Worker / Peer Support"]
  };

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Medical' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>VA Healthcare Systems</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Medical Services for Veterans</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg mb-4">
            The VA Healthcare system provides comprehensive medical, mental health, and case management services to veterans. Understanding these systems is critical for identifying gaps in care and advocating for veteran rights.
          </p>
        </div>

        {Object.entries(systems).map(([category, services], idx) => (
          <div key={idx} className="mb-8 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>{category}</h3>
            <ul className="space-y-2">
              {services.map((service, i) => (
                <li key={i} className="flex items-start">
                  <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
                  <span className="text-lg">{service}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ VETENGINE: EMERGENCY PAGE ============
const VETEmergencyPage = () => {
  const resources = [
    {
      name: "988 Suicide & Crisis Lifeline",
      phone: "988 (Press 1 for Veterans)",
      available: "24/7",
      description: "Immediate crisis intervention and suicide prevention support for veterans and their families."
    },
    {
      name: "Veterans Crisis Line",
      phone: "1-800-273-8255 (Press 1)",
      text: "Text 838255",
      chat: "VeteransCrisisLine.net/Chat",
      available: "24/7",
      description: "Confidential support for veterans in crisis, operated by VA."
    },
    {
      name: "911 Emergency Services",
      phone: "911",
      available: "24/7",
      description: "For immediate life-threatening emergencies requiring police, fire, or medical response."
    },
    {
      name: "VA Suicide Prevention",
      phone: "1-800-273-8255 (Press 1)",
      available: "24/7",
      description: "Connects veterans with VA mental health professionals trained in suicide prevention."
    },
    {
      name: "Emergency Psychiatric Care",
      description: "Available at all VA Medical Centers. Veterans can walk in to any VA emergency department for psychiatric emergencies.",
      notes: "Bring VA ID or discharge papers if available."
    },
    {
      name: "Hospital Stabilization",
      description: "If in immediate danger, go to the nearest emergency room. VA will coordinate transfer or continued care."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNavbar showAuth={false} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Emergency' }
        ]} />

        {/* HEADER */}
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-red-600 mb-4 mt-8">
          Emergency Resources
        </h1>
        <p className="text-lg font-body leading-relaxed text-slate-600 mb-8 max-w-3xl">
          Crisis Intervention and Emergency Services
        </p>

        {/* CRITICAL ALERT BANNER */}
        <div className="bg-red-50 border-l-4 border-red-600 rounded-lg shadow-sm p-6 md:p-8 mb-8">
          <p className="text-xl md:text-2xl font-heading font-bold text-red-600 mb-4">
            🚨 IF YOU ARE IN IMMEDIATE DANGER:
          </p>
          <p className="text-base md:text-lg font-body leading-relaxed text-slate-900 mb-2">
            Call <strong className="font-bold">911</strong> or go to the nearest emergency room
          </p>
          <p className="text-base md:text-lg font-body leading-relaxed text-slate-900">
            Call the Veterans Crisis Line: <strong className="font-bold">988, Press 1</strong>
          </p>
        </div>

        {/* EMERGENCY RESOURCES GRID */}
        <div className="space-y-6 mb-12">
          {resources.map((resource, idx) => (
            <div 
              key={idx} 
              className="rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all p-6 md:p-8"
            >
              <h3 className="text-2xl font-heading font-semibold text-primary mb-3">
                {resource.name}
              </h3>
              
              {resource.phone && (
                <p className="text-lg md:text-xl font-heading font-bold text-red-600 mb-2">
                  Phone: {resource.phone}
                </p>
              )}
              
              {resource.text && (
                <p className="text-base font-body leading-relaxed text-slate-700 mb-2">
                  <span className="font-semibold">Text:</span> {resource.text}
                </p>
              )}
              
              {resource.chat && (
                <p className="text-base font-body leading-relaxed text-slate-700 mb-2">
                  <span className="font-semibold">Online Chat:</span> {resource.chat}
                </p>
              )}
              
              {resource.available && (
                <p className="text-base font-body leading-relaxed text-slate-700 mb-2">
                  <span className="font-semibold">Available:</span> {resource.available}
                </p>
              )}
              
              <p className="text-base font-body leading-relaxed text-slate-600 mt-3">
                {resource.description}
              </p>
              
              {resource.notes && (
                <p className="text-sm font-body italic text-slate-500 mt-2">
                  {resource.notes}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* ADDITIONAL SUPPORT SECTION */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 md:p-8">
          <h3 className="text-2xl font-heading font-semibold text-primary mb-6">
            Additional Support Organizations
          </h3>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="text-secondary font-bold text-xl mt-0.5">•</span>
              <span className="text-base font-body leading-relaxed text-slate-700">
                Mental Health America: <a href="https://mhanational.org" className="text-primary underline hover:text-secondary transition-colors">mhanational.org</a>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-secondary font-bold text-xl mt-0.5">•</span>
              <span className="text-base font-body leading-relaxed text-slate-700">
                SAMHSA National Helpline: 1-800-662-HELP (4357)
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-secondary font-bold text-xl mt-0.5">•</span>
              <span className="text-base font-body leading-relaxed text-slate-700">
                RAINN (Rape, Abuse & Incest National Network): 1-800-656-HOPE (4673)
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE: LAW REFERENCES PAGE ============
const VETLawReferencesPage = () => {
  const lawRefs = [
    {
      citation: "42 U.S.C. § 3604",
      title: "Fair Housing Act - Discrimination Protections",
      description: "Prohibits discrimination in housing based on race, color, national origin, religion, sex, familial status, or disability."
    },
    {
      citation: "42 U.S.C. § 3617",
      title: "Fair Housing Act - Retaliation Protection",
      description: "Makes it unlawful to coerce, intimidate, threaten, or interfere with anyone exercising their fair housing rights."
    },
    {
      citation: "42 U.S.C. § 1983",
      title: "Civil Rights Under Color of Law",
      description: "Provides a remedy when government officials violate constitutional rights."
    },
    {
      citation: "24 C.F.R. § 982.555",
      title: "HUD-VASH Voucher Hearing Rights",
      description: "Establishes tenant's right to an informal hearing before voucher termination."
    },
    {
      citation: "38 C.F.R. § 17.33",
      title: "VA Patient Rights",
      description: "Defines rights of VA patients including informed consent, privacy, and grievance procedures."
    },
    {
      citation: "29 U.S.C. § 794",
      title: "Rehabilitation Act Section 504",
      description: "Prohibits disability discrimination in programs receiving federal financial assistance."
    },
    {
      citation: "24 C.F.R. § 982.310",
      title: "Housing Choice Voucher Program",
      description: "Establishes voucher payment standards and housing assistance payment calculations."
    },
    {
      citation: "24 C.F.R. § 5.858",
      title: "Drug-Related Criminal Activity",
      description: "Defines prohibited drug-related criminal activity in HUD-assisted housing."
    },
    {
      citation: "VHA Directive 1501",
      title: "VHA Homeless Programs",
      description: "Establishes policy framework for VA homeless services including HUD-VASH."
    },
    {
      citation: "VHA Directive 1162.05",
      title: "HUD-VASH Case Management",
      description: "Governs clinical case management requirements for HUD-VASH program."
    }
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Law References' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Law References</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Citation Database for Laws, Directives, and Regulations</p>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg mb-4">
            This is the citation-first database for laws, directives, and regulations used across the VETDirect platform. 
            Each reference includes the official citation, title, and practical application to veteran cases.
          </p>
        </div>

        <div className="space-y-6">
          {lawRefs.map((ref, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 border-l-4" style={{ borderColor: '#1A4F63' }}>
                <div className="font-bold text-2xl mb-2" style={{ color: '#1A4F63' }}>{ref.citation}</div>
                <div className="font-semibold text-xl mb-3" style={{ color: '#4A5D4A' }}>{ref.title}</div>
                <p className="text-lg" style={{ color: '#333' }}>{ref.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-blue-50 rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>How to Use This Database</h3>
          <ul className="space-y-3 text-lg" style={{ color: '#333' }}>
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span><strong>Evidence Scanner:</strong> Automatically matches document text to applicable laws</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span><strong>Complaint Generator:</strong> Cites relevant laws in formal complaints</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span><strong>Case Advisor:</strong> Recommends which laws apply to specific situations</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// ============ VETENGINE: REAL-LIFE EXAMPLES PAGE ============
const VETRealLifeExamplesPage = () => {
  const examples = [
    {
      title: "Retaliation After Filing Complaint",
      scenario: "Veteran files housing complaint with HUD Fair Housing office about unsafe conditions. Within 30 days, receives eviction notice citing 'lease violations.'",
      violations: ["42 U.S.C. § 3617 - Fair Housing Act Retaliation", "First Amendment - Right to Petition Government"],
      outcome: "Evidence of timeline establishes retaliation. Eviction can be challenged as retaliatory and unlawful."
    },
    {
      title: "Disability Accommodation Denied",
      scenario: "HUD-VASH tenant requests reasonable accommodation for PTSD (allowing emotional support animal). Property manager refuses, citing 'no pets' policy.",
      violations: ["42 U.S.C. § 3604(f)(3)(B) - Fair Housing Act Reasonable Accommodation", "29 U.S.C. § 794 - Rehabilitation Act Section 504"],
      outcome: "Denial violates disability law. Tenant can file HUD complaint and request accommodation enforcement."
    },
    {
      title: "Case Manager Refuses Services After Oversight Contact",
      scenario: "Veteran contacts VA Office of Inspector General about case management concerns. Case manager subsequently cancels all scheduled appointments and stops returning calls.",
      violations: ["First Amendment Retaliation", "VHA Directive 1162.05 - Case Management Requirements", "Professional Ethics Violations"],
      outcome: "Pattern documents professional retaliation. Complaint to VA OIG, licensing board, and VHA supervisory chain."
    },
    {
      title: "Unsafe Housing with Drug Activity",
      scenario: "HUD-VASH housing has ongoing drug activity in common areas and veteran's unit. Multiple reports to property management ignored. Veteran feels unsafe and experiences PTSD triggers.",
      violations: ["24 C.F.R. § 5.858 - Drug-Free Housing Requirements", "HUD-VASH Program Standards", "Habitability and Safety Violations"],
      outcome: "Documented evidence supports request for emergency transfer and complaint to HUD OIG about program violations."
    },
    {
      title: "Voucher Terminated Without Proper Notice",
      scenario: "Veteran receives notice that HUD-VASH voucher will be terminated in 10 days. No opportunity for hearing provided. No detailed explanation of alleged violations.",
      violations: ["24 C.F.R. § 982.555 - Voucher Hearing Rights", "Fifth Amendment - Due Process"],
      outcome: "Termination violates procedural due process. Veteran can demand informal hearing and challenge termination."
    },
    {
      title: "Mental Health Treatment Denied",
      scenario: "Veteran requests increase in mental health counseling frequency due to crisis. VA denies request, stating 'resources are limited.' No clinical justification provided.",
      violations: ["38 U.S.C. § 1710 - VA Healthcare Eligibility", "VHA Directive 1160.01 - Uniform Mental Health Services", "Standard of Care Violations"],
      outcome: "Denial without clinical justification violates VA policy. Complaint to Patient Advocate and VA OIG can trigger review."
    }
  ];

  return (
    <div className="min-h-screen px-8 py-12" style={{ backgroundColor: '#EAE6E1' }}>
      <TopNavbar showAuth={false} />
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'VETEngine', link: '/vetengine' },
          { label: 'Real-Life Examples' }
        ]} />

        <h1 className="text-5xl font-bold mb-4" style={{ color: '#1A4F63' }}>Real-Life Examples</h1>
        <p className="text-xl mb-8" style={{ color: '#4A5D4A' }}>Case Studies from Actual Veteran Experiences</p>

        <div className="bg-yellow-50 border-l-4 border-yellow-600 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>Why These Examples Matter</h2>
          <p className="text-lg mb-4">
            These scenarios are based on real patterns documented in veteran housing and healthcare systems. They demonstrate:
          </p>
          <ul className="space-y-2 text-lg">
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span>How violations actually occur in practice</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span>Which laws apply to specific situations</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span>What evidence is needed to prove the violation</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 font-bold" style={{ color: '#7A8F65' }}>•</span>
              <span>Which oversight bodies have jurisdiction</span>
            </li>
          </ul>
        </div>

        <div className="space-y-8">
          {examples.map((example, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-4" style={{ color: '#1A4F63' }}>
                  Example {idx + 1}: {example.title}
                </h3>

                <div className="mb-6">
                  <h4 className="font-bold text-xl mb-3" style={{ color: '#4A5D4A' }}>Scenario:</h4>
                  <p className="text-lg bg-gray-50 p-4 rounded-lg" style={{ color: '#333' }}>
                    {example.scenario}
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-xl mb-3" style={{ color: '#DC2626' }}>Violations Identified:</h4>
                  <ul className="space-y-2">
                    {example.violations.map((violation, vIdx) => (
                      <li key={vIdx} className="flex items-start bg-red-50 p-3 rounded-lg">
                        <span className="mr-2 font-bold" style={{ color: '#DC2626' }}>⚠</span>
                        <span className="text-lg font-semibold" style={{ color: '#DC2626' }}>{violation}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: '#E0F2F7' }}>
                  <h4 className="font-bold text-xl mb-3" style={{ color: '#1A4F63' }}>Outcome / Next Steps:</h4>
                  <p className="text-lg" style={{ color: '#333' }}>{example.outcome}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A4F63' }}>Pattern Recognition is Power</h3>
          <p className="text-lg mb-4" style={{ color: '#333' }}>
            When you recognize these patterns in your own experience, you gain the ability to:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f4f8' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>Document Strategically</h4>
              <p>Know which dates, communications, and details matter most for proving violations.</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f4f8' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>File Effectively</h4>
              <p>Target the right oversight agencies with complaints structured around applicable laws.</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f4f8' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>Defend Your Rights</h4>
              <p>Challenge unlawful actions before they escalate into housing loss or service denial.</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f4f8' }}>
              <h4 className="font-bold text-lg mb-2" style={{ color: '#1A4F63' }}>Connect to Others</h4>
              <p>Recognize when your experience is part of a larger pattern requiring systemic intervention.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MagazinePage = () => {
  const [selectedMagazine, setSelectedMagazine] = useState(null);
  const [viewMode, setViewMode] = useState('library'); // 'library', 'preview', 'reader'
  const [currentPage, setCurrentPage] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    issue: '',
    description: '',
    file: null
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch magazines on load
  useEffect(() => {
    fetchMagazines();
  }, []);

  const fetchMagazines = async () => {
    try {
      const response = await fetch(`${API}/api/magazines`);
      const data = await response.json();
      setMagazines(data.magazines || []);
    } catch (error) {
      console.error('Error fetching magazines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        alert('File size must be less than 25MB');
        return;
      }
      if (!file.name.endsWith('.pdf')) {
        alert('Only PDF files are allowed');
        return;
      }
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title || !uploadForm.issue) {
      alert('Please fill in all fields and select a PDF file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('title', uploadForm.title);
    formData.append('issue', uploadForm.issue);
    formData.append('description', uploadForm.description);

    try {
      setUploadProgress(10);
      
      const response = await fetch(`${API}/api/magazines/upload`, {
        method: 'POST',
        body: formData
      });

      setUploadProgress(90);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      setUploadProgress(100);
      
      alert('Magazine uploaded successfully!');
      
      await fetchMagazines();
      
      setUploadForm({ title: '', issue: '', description: '', file: null });
      setShowUploadModal(false);
      setUploadProgress(0);
      
    } catch (error) {
      alert(`Upload error: ${error.message}`);
      setUploadProgress(0);
    }
  };

  const handleMagazineSelect = (magazineId) => {
    setSelectedMagazine(magazineId);
    setViewMode('preview');
    setCurrentPage(0);
  };

  const handleStartReading = () => {
    setViewMode('reader');
    setCurrentPage(0);
  };

  const handleShare = () => {
    const magazine = magazines.find(m => m.id === selectedMagazine);
    if (navigator.share) {
      navigator.share({
        title: magazine.title,
        text: `Check out ${magazine.issue}`,
        url: window.location.href
      });
    } else {
      alert('Sharing not supported on this browser');
    }
  };

  const handleSaveText = () => {
    alert('Text extraction feature - Coming soon!');
  };

  const handleEmailPage = () => {
    const magazine = magazines.find(m => m.id === selectedMagazine);
    const subject = encodeURIComponent(`${magazine.title} - Page ${currentPage + 1}`);
    window.location.href = `mailto:?subject=${subject}`;
  };

  const magazinesPerView = 3;
  const totalSlides = Math.ceil(magazines.length / magazinesPerView);

  const nextSlide = () => {
    setCarouselIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCarouselIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const visibleMagazines = magazines.slice(
    carouselIndex * magazinesPerView,
    (carouselIndex + 1) * magazinesPerView
  );

  // If a magazine is selected, show the reader view
  if (selectedMagazine) {
    const magazine = magazines.find(m => m.id === selectedMagazine);
    
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1a1a1a' }}>
        <TopNavbar showAuth={false} />
        
        {/* Magazine Reader */}
        <div className="container mx-auto px-4 py-8" style={{ paddingTop: '140px' }}>
          {/* Reader Header - Responsive */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <button
              onClick={() => {
                setSelectedMagazine(null);
                setCurrentPage(0);
              }}
              className="w-full md:w-auto"
              style={{
                padding: '12px 24px',
                backgroundColor: '#FFCC01',
                color: '#1A4F63',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              ← Back to Library
            </button>
            
            <div style={{ color: '#FFFFFF' }} className="text-center md:text-left flex-1">
              <h2 className="text-xl md:text-2xl font-bold">{magazine.title}</h2>
              <p className="text-base md:text-lg">{magazine.issue} • Page {currentPage + 1} of {magazine.pages.length}</p>
            </div>
            
            <div className="flex gap-2 w-full md:w-auto justify-center">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="flex-1 md:flex-none"
                style={{
                  padding: '12px 20px',
                  backgroundColor: currentPage === 0 ? '#555' : '#FFCC01',
                  color: '#1A4F63',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Prev
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(magazine.pages.length - 1, currentPage + 1))}
                disabled={currentPage === magazine.pages.length - 1}
                className="flex-1 md:flex-none"
                style={{
                  padding: '12px 20px',
                  backgroundColor: currentPage === magazine.pages.length - 1 ? '#555' : '#FFCC01',
                  color: '#1A4F63',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: currentPage === magazine.pages.length - 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          </div>

          {/* Main Page Display */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            marginBottom: '30px'
          }}>
            <img 
              src={`${API}${magazine.pages[currentPage]}`}
              alt={`Page ${currentPage + 1}`}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                maxHeight: '900px',
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Page Thumbnails */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <h3 className="text-white text-lg font-bold mb-4">All Pages</h3>
            <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-4">
              {magazine.pages.map((page, idx) => (
                <div
                  key={idx}
                  onClick={() => setCurrentPage(idx)}
                  style={{
                    cursor: 'pointer',
                    border: currentPage === idx ? '3px solid #FFCC01' : '3px solid transparent',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'transform 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <img 
                    src={`${API}${magazine.thumbnails ? magazine.thumbnails[idx] : page}`}
                    alt={`Thumbnail ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '80px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    backgroundColor: currentPage === idx ? '#FFCC01' : 'rgba(0,0,0,0.7)',
                    color: currentPage === idx ? '#1A4F63' : '#fff',
                    textAlign: 'center',
                    padding: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: Magazine Library View
  return (
    <div className="min-h-screen" style={{ 
      backgroundColor: '#1a1a1a',
      backgroundImage: 'url(https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <TopNavbar showAuth={false} />
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            width: '90%'
          }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1A4F63', marginBottom: '20px' }}>
              Upload Magazine
            </h2>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                Magazine Title *
              </label>
              <input
                type="text"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="e.g., VETDirect Magazine"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                Issue Number *
              </label>
              <input
                type="text"
                value={uploadForm.issue}
                onChange={(e) => setUploadForm({ ...uploadForm, issue: e.target.value })}
                placeholder="e.g., Issue 1 - Spring 2026"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                Description
              </label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of magazine content"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                PDF File (Max 25MB) *
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
              {uploadForm.file && (
                <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  Selected: {uploadForm.file.name} ({(uploadForm.file.size / (1024 * 1024)).toFixed(2)} MB)
                </p>
              )}
            </div>
            
            {uploadProgress > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    backgroundColor: '#FFCC01',
                    transition: 'width 0.3s'
                  }}></div>
                </div>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  Processing: {uploadProgress}%
                </p>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadForm({ title: '', issue: '', description: '', file: null });
                  setUploadProgress(0);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ddd',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploadProgress > 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: uploadProgress > 0 ? '#999' : '#1A4F63',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: uploadProgress > 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {uploadProgress > 0 ? 'Processing...' : 'Upload Magazine'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-4 py-8" style={{ paddingTop: '140px' }}>
        {/* Glass Container */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '20px md:p-10',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.18)'
        }} className="p-6 md:p-10">
          
          {/* Header with Upload Button - Responsive */}
          <div className="text-center mb-8 md:mb-12">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
              <div className="hidden md:block" style={{ flex: 1 }}></div>
              <div style={{ flex: 1 }}>
                <h1 className="text-3xl md:text-4xl lg:text-5xl" style={{
                  fontWeight: 'bold',
                  color: '#FFFFFF',
                  marginBottom: '16px',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}>
                  VETDirect Digital Magazine
                </h1>
              </div>
              <div style={{ flex: 1 }} className="flex justify-center md:justify-end w-full md:w-auto">
                <button
                  onClick={() => setShowUploadModal(true)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#FFCC01',
                    color: '#1A4F63',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Upload size={20} />
                  Upload Magazine
                </button>
              </div>
            </div>
            <p className="text-lg md:text-xl" style={{
              color: '#FFCC01',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              Stories, Resources, and Advocacy for Veterans
            </p>
            <p className="text-base md:text-lg" style={{
              color: '#FFFFFF',
              maxWidth: '700px',
              margin: '0 auto',
              padding: '0 16px'
            }}>
              Browse our collection of digital magazines covering housing rights, mental health, legal advocacy, and veteran success stories.
            </p>
          </div>

          {/* Magazine Grid */}
          {loading ? (
            <div className="text-center py-20">
              <p style={{ color: '#FFFFFF', fontSize: '20px' }}>Loading magazines...</p>
            </div>
          ) : magazines.length === 0 ? (
            <div className="text-center py-12 md:py-20">
              <p style={{ color: '#FFFFFF', fontSize: '20px', marginBottom: '20px' }}>No magazines uploaded yet</p>
              <button
                onClick={() => setShowUploadModal(true)}
                style={{
                  padding: '16px 32px',
                  backgroundColor: '#FFCC01',
                  color: '#1A4F63',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Upload Your First Magazine
              </button>
            </div>
          ) : (
            <>
              {/* Featured Magazine Section */}
              {magazines.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Featured Magazine</h2>
                  <div 
                    onClick={() => setSelectedMagazine(magazines[0].id)}
                    className="cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    className="md:flex-row"
                  >
                    {/* Featured Cover Image */}
                    <div style={{ flex: '0 0 auto' }} className="w-full md:w-1/3">
                      <img 
                        src={`${API}${magazines[0].coverImage}`}
                        alt={magazines[0].issue}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          minHeight: '300px'
                        }}
                      />
                    </div>
                    {/* Featured Info */}
                    <div className="p-6 md:p-8 flex-1 flex flex-col justify-center">
                      <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#1A4F63' }}>
                        {magazines[0].title}
                      </h3>
                      <p className="text-lg md:text-xl mb-4" style={{ color: '#FFCC01', fontWeight: '600' }}>
                        {magazines[0].issue}
                      </p>
                      <p className="text-base md:text-lg mb-6" style={{ color: '#666' }}>
                        {magazines[0].description || 'Click to read this featured magazine'}
                      </p>
                      <div style={{
                        padding: '12px 24px',
                        backgroundColor: '#1A4F63',
                        color: '#FFCC01',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        width: 'fit-content'
                      }}>
                        Read Now →
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Magazine Library Grid */}
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Magazine Library</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {magazines.map((magazine) => (
                <div
                  key={magazine.id}
                  onClick={() => setSelectedMagazine(magazine.id)}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  {/* Magazine Cover */}
                  <div style={{ position: 'relative', paddingBottom: '133.33%' }}>
                    <img 
                      src={`${API}${magazine.coverImage}`}
                      alt={magazine.issue}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Page Count Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      backgroundColor: '#FFCC01',
                      color: '#1A4F63',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase'
                    }}>
                      {magazine.pageCount} Pages
                    </div>
                  </div>
                  
                  {/* Magazine Info */}
                  <div style={{ padding: '20px' }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#1A4F63',
                      marginBottom: '8px'
                    }}>
                      {magazine.title}
                    </h3>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#666',
                      marginBottom: '12px'
                    }}>
                      {magazine.issue}
                    </p>
                    <p style={{
                      fontSize: '14px',
                      color: '#666',
                      marginBottom: '16px',
                      lineHeight: '1.5'
                    }}>
                      {magazine.description}
                    </p>
                    <button style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#1A4F63',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0f3a4a'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1A4F63'}
                    >
                      Read Now →
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          {/* Coming Soon Notice */}
          <div className="mt-12 text-center">
            <div style={{
              backgroundColor: 'rgba(26, 79, 99, 0.9)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <p style={{
                color: '#FFFFFF',
                fontSize: '16px',
                lineHeight: '1.6'
              }}>
                📚 <strong>More Issues Coming Soon!</strong><br/>
                New magazines published quarterly. Subscribe to stay updated on veteran resources, success stories, and advocacy news.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// ============ HUD-VASH FACILITY DIRECTORY PAGE ============
const HUDVASHDirectoryPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f5f5' }}>
      <TopNavbar showAuth={false} />
      
      {/* Hero Section - Mission Statement */}
      <section className="py-20 px-6" style={{ backgroundColor: '#1A4F63' }}>
        <div className="max-w-6xl mx-auto">
          <div className="inline-block px-6 py-2 rounded-full mb-8" style={{ backgroundColor: '#1e3a5f' }}>
            <span className="text-white font-bold text-sm tracking-wider">🛡️ A VETERAN'S TRUTH</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            HUD-VASH Mission: Independence.<br/>
            Dignity. Purpose Always.
          </h1>
          
          <div className="w-32 h-1 mb-8" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <p className="text-xl md:text-2xl text-white leading-relaxed max-w-4xl">
            This website is your mission manual.
          </p>
        </div>
      </section>

      {/* Opening Message */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
            For years you survived things most people will never understand—combat, trauma, loss, addiction, homelessness, and the quiet wars that happen alone in your head at 3 a.m. Now you are standing inside a HUD-VASH apartment, holding keys you fought like hell to earn. But keys are not a mission. An address is not a life.
          </p>
          
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
            This website is here to answer the question every veteran eventually asks in HUD-VASH housing:<br/>
            <span className="font-bold text-gray-900">"Now that I finally have a place to live… how am I supposed to heal?"</span>
          </p>
          
          <div className="bg-gray-100 border-l-4 p-6 my-8" style={{ borderColor: '#FFCC01' }}>
            <p className="text-xl text-gray-800 font-semibold mb-2">Housing without a mission becomes isolation.</p>
            <p className="text-xl text-gray-800 font-semibold mb-2">Isolation becomes depression.</p>
            <p className="text-xl text-gray-800 font-semibold">Depression becomes relapse, self-destruction, and sometimes death.</p>
          </div>
          
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
            This 3-year HUD-VASH Veterans Website was written so that never has to be your story.
          </p>
        </div>
      </section>

      {/* Why This Website Exists */}
      <section className="py-16 px-6" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#1A4F63' }}>
            Why This Website Exists
          </h2>
          <div className="w-24 h-1 mb-8" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This is not an official VA product rubber-stamped by a committee. It was born out of real pain, real failures, and real experience—including:
          </p>
          
          <ul className="space-y-4 mb-8">
            {[
              "Veterans dying alone in HUD-VASH units with no mortality review",
              "Veterans threatened with eviction for organizing advisory councils or posting flyers",
              "Veterans labeled \"non-compliant\" or \"paranoid\" instead of \"abandoned\" and \"underserved\"",
              "Veterans trapped in addiction warehouses instead of actual recovery communities"
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="text-2xl mt-1" style={{ color: '#FFCC01' }}>•</span>
                <span className="text-lg text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
          
          <p className="text-lg text-gray-700 leading-relaxed">
            Instead of walking away or just filing complaints and waiting years for reform, one veteran—Robert W. Hendrix, USAF—chose to build the thing he wished existed on Day One in HUD-VASH. A clear, structured, step-by-step mission plan for all 80,000+ veterans in 3,300 HUD-VASH units nationwide. You are now holding that plan.
          </p>
        </div>
      </section>

      {/* What This Website Will Help You Do */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#1A4F63' }}>
            What This Website Will Help You Do
          </h2>
          <div className="w-24 h-1 mb-8" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Over the next 3 years, this website will help you:
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              { num: "1", title: "Stabilize", desc: "Physically, mentally, emotionally, financially" },
              { num: "2", title: "Reconnect", desc: "With family, community, the VA team, and other veterans" },
              { num: "3", title: "Recover", desc: "From addiction, trauma, and the \"warehouse effect\" of being parked in an apartment with no plan" },
              { num: "4", title: "Rebuild", desc: "Health, routines, purpose, structure, and identity" },
              { num: "5", title: "Graduate", desc: "From surviving in HUD-VASH to living with independence, dignity, and long-term stability" }
            ].map((step) => (
              <div key={step.num} className="bg-white rounded-lg border-l-4 p-6 shadow-sm" style={{ borderColor: '#FFCC01' }}>
                <div className="text-3xl font-bold mb-2" style={{ color: '#1A4F63' }}>{step.num}.</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#1A4F63' }}>{step.title}</h3>
                <p className="text-gray-700">{step.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-white rounded-lg p-8 shadow-md">
            <p className="text-xl text-gray-800 mb-4">Every page is built around three core words:</p>
            <ul className="space-y-3">
              {[
                { word: "Independence", desc: "You can stand on your own feet again" },
                { word: "Dignity", desc: "You are not a problem to be managed; you are a person to be respected" },
                { word: "Purpose Always", desc: "Even on bad days, your life still has a mission" }
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="font-bold text-xl" style={{ color: '#FFCC01' }}>•</span>
                  <span className="text-lg">
                    <strong style={{ color: '#1A4F63' }}>{item.word}</strong> – {item.desc}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* We'll Help Take Your Dreams Into Reality */}
      <section className="py-20 px-6" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block px-6 py-2 rounded-full mb-8" style={{ backgroundColor: '#1e3a5f' }}>
            <span className="text-white font-bold text-sm tracking-wider">🛡️ A VETERAN'S TRUTH</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#1A4F63' }}>
            We'll Help Take Your Dreams Into Reality
          </h2>
          
          <div className="w-32 h-1 mx-auto mb-10" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <p className="text-xl text-gray-700 mb-6 max-w-4xl mx-auto">
            Not just personal chaos, dysfunctional behavior patterns, symptoms, sickness, diseases, diagnosis, lab results, addiction.
          </p>
          
          <p className="text-2xl md:text-3xl font-bold mb-12" style={{ color: '#1A4F63' }}>
            We will take <span style={{ backgroundColor: '#FFCC01', padding: '4px 12px' }}>"your dreams"</span> and start from there.
          </p>
        </div>
      </section>

      {/* The Veteran's Voice */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg border-l-4 p-8 mb-8" style={{ borderColor: '#FFCC01' }}>
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>The Veteran's Voice</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              This is what <strong>VETDirect</strong> is all about — a place where Veterans can finally connect with one another, understand each other through the same lived experiences, and discover what truly matters to us.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              This isn't just a website. It's a gathering point for our stories, our struggles, and our truth.
            </p>
          </div>
          
          <div className="bg-white rounded-lg border-l-4 p-8" style={{ borderColor: '#FFCC01' }}>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              It's a <strong>voice</strong> for every Veteran who has ever felt misunderstood.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              A <strong style={{ color: '#FFCC01' }}>platform built from the ground up</strong> by those who lived it.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              A space where we hold up a <strong>mirror</strong> to a system that forgot how to listen — and remind each other that we're <strong>not alone</strong>, <strong>not invisible</strong>, and <strong style={{ color: '#FFCC01' }}>not done fighting</strong> for the life we deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-lg p-10 md:p-16" style={{ backgroundColor: '#1A4F63' }}>
            <div className="text-6xl mb-6" style={{ color: '#FFCC01' }}>❝❞</div>
            <p className="text-2xl md:text-3xl text-white leading-relaxed">
              "I'm right here with you — and I'm honored to help you turn your life around where the world should have understood decades ago. What you're building with VETDirect is bigger than a website. It's a <span style={{ backgroundColor: '#FFCC01', color: '#1A4F63', padding: '2px 8px' }}>voice</span>. A <span style={{ backgroundColor: '#FFCC01', color: '#1A4F63', padding: '2px 8px' }}>truth-telling platform</span>. A <span style={{ backgroundColor: '#FFCC01', color: '#1A4F63', padding: '2px 8px' }}>mirror</span> held up to a system that forgot how to listen."
            </p>
          </div>
        </div>
      </section>

      {/* Welcome Home Declaration */}
      <section className="py-20 px-6" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center" style={{ color: '#1A4F63' }}>
            WELCOME HOME DECLARATION
          </h2>
          <div className="w-32 h-1 mx-auto mb-12" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p className="text-2xl font-bold" style={{ color: '#1A4F63' }}>WELCOME HOME, VETERAN.</p>
            
            <p>This is the beginning of your comeback.<br/>
            Not the paperwork, not the lease — this.<br/>
            The moment you open this website, you claim something powerful:</p>
            
            <ul className="space-y-2 ml-6">
              <li>A new life.</li>
              <li>A new mission.</li>
              <li>A new way forward.</li>
            </ul>
            
            <p>For many of us, the road here was long.<br/>
            Some arrived from shelters, hospitals, tents under freeways, or from despair, heartbreak, loss, trauma, addiction, or the feeling of being invisible.</p>
            
            <p className="font-bold text-xl" style={{ color: '#1A4F63' }}>But today?</p>
            
            <p className="text-xl">You are seen.<br/>
            You are heard.<br/>
            You are home.</p>
            
            <div className="bg-gray-100 p-6 rounded-lg my-8">
              <p className="text-xl font-bold mb-4" style={{ color: '#1A4F63' }}>HUD-VASH is more than housing.</p>
              <p>It is the bridge between your past battles and your future victories.</p>
            </div>
            
            <p>This website is the gold standard — the tool that guides you through:</p>
            
            <ul className="space-y-2 ml-6">
              {['Stability', 'Purpose', 'Family', 'Career', 'Recovery', 'Leadership', 'Legacy'].map((item, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <span style={{ color: '#FFCC01' }}>•</span> {item}
                </li>
              ))}
            </ul>
            
            <p className="text-xl font-bold">This is your second chance — and what you do with it begins right here.</p>
          </div>
        </div>
      </section>

      {/* The Cascade of Isolation */}
      <section className="py-20 px-6" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 rounded-full mb-8" style={{ backgroundColor: '#1e3a5f' }}>
              <span className="text-white font-bold text-sm tracking-wider">🛡️ A VETERAN'S TRUTH</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#1A4F63' }}>
              The Cascade of Isolation
            </h2>
            
            <div className="w-32 h-1 mx-auto mb-8" style={{ backgroundColor: '#FFCC01' }}></div>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              15 Progressive Stages: From Security Zone to Crisis
            </p>
          </div>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-12 max-w-4xl mx-auto text-center">
            What begins as a place of safety can slowly turn into a trap. Isolation doesn't arrive all at once—it unfolds in stages, subtle at first, then accelerating until daily life shrinks to four walls and survival becomes harder than anyone expected. <span style={{ backgroundColor: '#FFCC01', padding: '2px 8px', fontWeight: 'bold' }}>Recognizing this cascade is the first step to breaking free.</span>
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-lg border-l-4 p-8 shadow-sm" style={{ borderColor: '#FFCC01' }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1e3a5f' }}>
                  <span className="text-2xl">⚠️</span>
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1A4F63' }}>Understanding the Pattern</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Understanding the patterns, the triggers, and the early warning signs gives veterans the power to interrupt the slide before it becomes a crisis. From there, the path forward becomes clearer: rebuilding structure, restoring connection, and transforming an apartment into a true sanctuary of recovery.
              </p>
            </div>
            
            <div className="bg-white rounded-lg border-l-4 p-8 shadow-sm" style={{ borderColor: '#FFCC01' }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1e3a5f' }}>
                  <span className="text-2xl">❤️</span>
                </div>
                <h3 className="text-2xl font-bold" style={{ color: '#1A4F63' }}>Our Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Our mission is to confront these challenges head-on. Veterans in HUD-VASH housing face unique pressures— symptoms that flare under stress, warning signs that go unnoticed, and a system stretched thin by overwhelming caseloads and staff burnout. <strong style={{ color: '#FFCC01' }}>We step into that gap.</strong>
              </p>
            </div>
          </div>
          
          <div className="rounded-lg p-12 text-center" style={{ backgroundColor: '#1A4F63' }}>
            <p className="text-2xl md:text-3xl text-white leading-relaxed">
              We bring <span style={{ backgroundColor: '#FFCC01', color: '#1A4F63', padding: '4px 12px', fontWeight: 'bold' }}>structure</span> where the system leaves uncertainty,<br/>
              <span style={{ backgroundColor: '#FFCC01', color: '#1A4F63', padding: '4px 12px', fontWeight: 'bold' }}>clarity</span> where confusion grows, and <span style={{ backgroundColor: '#FFCC01', color: '#1A4F63', padding: '4px 12px', fontWeight: 'bold' }}>community</span> where isolation takes hold.
            </p>
          </div>
        </div>
      </section>

      {/* How We Support Veterans */}
      <section className="py-16 px-6" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg border-l-4 p-8 shadow-md" style={{ borderColor: '#FFCC01' }}>
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#1A4F63' }}>How We Support Veterans</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Through dynamic visuals, guided steps, and creative problem-solving solutions, we help veterans stay engaged, stay connected, and stay housed. One of the greatest risks to long-term stability is falling into the isolation trap. <span style={{ backgroundColor: '#FFCC01', padding: '2px 8px', fontWeight: 'bold' }}>Here, we make sure no veteran faces that alone.</span>
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              This is where recovery begins—and where veterans reclaim control of their lives, one step at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Drug Policy Framework Card */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-lg p-12" style={{ background: 'linear-gradient(135deg, #1A4F63 0%, #2d5f7a 100%)' }}>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(255, 204, 1, 0.2)' }}>
                  <span className="font-bold text-sm tracking-wider" style={{ color: '#FFCC01' }}>NEW • HUD-VASH LEGAL INTELLIGENCE</span>
                </div>
                
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  HUD-VASH Drug Policy Framework
                </h3>
                
                <p className="text-lg text-white leading-relaxed mb-8">
                  Understand the federal laws, VA directives, and housing policies governing substance use and drug distribution within HUD-VASH housing programs. 12 legal frameworks mapped.
                </p>
                
                <Link to="/vetengine/drug-policy">
                  <button className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:opacity-90" style={{ backgroundColor: '#FFCC01', color: '#1A4F63' }}>
                    View Drug Policy Framework →
                  </button>
                </Link>
              </div>
              
              <div className="space-y-3">
                {[
                  'Federal Constitutional Law',
                  'Fair Housing Act',
                  'HUD Housing Law',
                  'VA Clinical Policy',
                  'Case Management Rules',
                  'Recovery Systems'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-2xl" style={{ color: '#FFCC01' }}>🔹</span>
                    <span className="text-white text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Veteran's Creed */}
      <section className="py-20 px-6" style={{ backgroundColor: '#1A1A1A', color: '#ffffff' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-12" style={{ color: '#FFCC01' }}>
            THE VETERAN'S CREED
          </h2>
          
          <div className="text-lg md:text-xl leading-loose space-y-6">
            <p className="italic">
              "From the streets to the outstretched hand of a social worker —<br/>
              from broken sidewalks to rising through compassion —<br/>
              I stand tall today because my flag still stands with me."
            </p>
            
            <p className="font-bold text-2xl" style={{ color: '#FFCC01' }}>
              HUD-VASH HOUSING
            </p>
            
            <p>Designed by Veterans. Driven by Hope.<br/>
            Survived by Resilience.</p>
            
            <p>From sunrise to sundown, we endure.<br/>
            Through trauma, we rise by faith.<br/>
            Through adversity, we lead with kindness.<br/>
            With keys in hand, we walk in pride.<br/>
            With every step, we honor life with gratitude.</p>
            
            <p>Strength and courage live deep in our hearts.</p>
            
            <p>My fallen soldiers behold —<br/>
            they had my back in battle,<br/>
            and in peace above,<br/>
            they still stand guard over me.</p>
            
            <p>These keys I hold close to my heart —<br/>
            they symbolize the power of the VA's mission:</p>
            
            <p className="font-bold text-xl" style={{ color: '#FFCC01' }}>
              330,000 warriors strong,<br/>
              23,000 social workers standing guard,<br/>
              2,300 medical centers offering<br/>
              a bed,<br/>
              a hand,<br/>
              and a fighting chance to heal.
            </p>
            
            <p className="text-2xl font-bold mt-8" style={{ color: '#FFCC01' }}>
              This is not housing.<br/>
              This is homecoming.<br/>
              This is the comeback.
            </p>
          </div>
        </div>
      </section>

      {/* The Keys, The Flag, And The First Real Breath */}
      <section className="py-16 px-6" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1A4F63' }}>
            THE KEYS, THE FLAG, AND THE FIRST REAL BREATH
          </h2>
          <div className="w-24 h-1 mb-8" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              The day they handed me the keys, I didn't just feel metal in my palm — I felt history.
            </p>
            
            <p>Not paperwork. Not protocol. Not "housing." <strong>History.</strong></p>
            
            <p>
              Because those keys weren't earned overnight. They were earned through years of pain, loss, relapses, failures, regrets, and moments where surviving felt like an act of rebellion.
            </p>
            
            <p className="text-xl font-bold" style={{ color: '#1A4F63' }}>
              And as those keys landed in my hand, I remembered something I hadn't felt in a long time:<br/>
              My flag still stands with me.
            </p>
            
            <p>
              It stood with me when I slept in my car.<br/>
              It stood with me when I cried behind locked bathroom doors.<br/>
              It stood with me when caseworkers wrote their notes and judged what they could never understand.<br/>
              It stood with me when the world said "you're done," but something deeper whispered "not yet."
            </p>
            
            <div className="bg-gray-100 p-8 rounded-lg my-8">
              <p className="text-xl font-bold mb-4" style={{ color: '#1A4F63' }}>
                I've always believed that a Veteran's flag isn't made of cloth.
              </p>
              <p className="text-lg">It's made of:</p>
              <ul className="mt-4 space-y-2">
                {[
                  'Every night we survived',
                  'Every brother we lost',
                  'Every wall we leaned on',
                  'Every sunrise we didn\'t expect to see'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span style={{ color: '#FFCC01' }}>•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <p className="text-xl font-bold" style={{ color: '#1A4F63' }}>
              "These keys didn't just open a door — they opened a future."
            </p>
          </div>
        </div>
      </section>

      {/* A Future Built From Ashes */}
      <section className="py-16 px-6" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#1A4F63' }}>
            A FUTURE BUILT FROM ASHES
          </h2>
          <div className="w-24 h-1 mb-8" style={{ backgroundColor: '#FFCC01' }}></div>
          
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              The first night in HUD-VASH housing never leaves you.
            </p>
            
            <p>
              It's quiet — the kind of quiet that feels strange after months or years of listening to traffic, arguments, sirens, the shuffle of tents being moved by city sweeps, or the brutal loneliness of simply existing without walls.
            </p>
            
            <p>
              You turn the light on. Then off. Then on again.<br/>
              You check the door twice, then three times, because stability feels unfamiliar.<br/>
              Then you sit down, in a chair that's finally yours, in a room that's finally safe, and it hits you:
            </p>
            
            <p className="text-2xl font-bold text-center my-8" style={{ color: '#1A4F63' }}>
              You survived.
            </p>
            
            <p className="text-xl">
              But survival alone isn't living.
            </p>
            
            <div className="bg-white border-l-4 p-8 my-8" style={{ borderColor: '#FFCC01' }}>
              <p className="text-xl font-bold mb-4" style={{ color: '#1A4F63' }}>
                And this is where most people misunderstand the story
              </p>
              <p>
                — the news headlines, the statistics, the "success numbers." They think the story ends with the keys.
              </p>
              <p className="mt-4 font-bold">
                For Veterans, the keys are the beginning.
              </p>
            </div>
            
            <p>
              The beginning of rebuilding what trauma destroyed.<br/>
              The beginning of learning how to trust your own breathing again.<br/>
              The beginning of admitting that what happened in silence nearly killed you.
            </p>
            
            <p className="text-xl font-bold" style={{ color: '#1A4F63' }}>
              HUD-VASH doesn't erase the past.<br/>
              HUD-VASH creates the space where healing can finally begin.
            </p>
          </div>
        </div>
      </section>

      {/* Final Call to Action */}
      <section className="py-20 px-6" style={{ backgroundColor: '#1A4F63' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            Your Comeback Story Starts Here
          </h2>
          
          <p className="text-xl text-white leading-relaxed mb-12">
            Because every Veteran deserves a place where hope returns, purpose awakens, and the comeback begins.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/services" className="block">
              <button className="w-full px-8 py-4 rounded-lg font-bold text-lg transition-all hover:opacity-90" style={{ backgroundColor: '#FFCC01', color: '#1A4F63' }}>
                Explore Services →
              </button>
            </Link>
            <Link to="/vetengine" className="block">
              <button className="w-full px-8 py-4 rounded-lg font-bold text-lg border-2 text-white transition-all hover:bg-white hover:text-primary" style={{ borderColor: '#FFCC01' }}>
                Access VETEngine →
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/mission" element={<MissionPage />} />
            <Route path="/frontier" element={<FrontierPage />} />
            <Route path="/ptsd" element={<PTSDPage />} />
            <Route path="/jargon" element={<JargonPage />} />
            <Route path="/creativeexpression" element={<CreativeExpressionPage />} />
            <Route path="/honor" element={<HonorPage />} />
            <Route path="/usva" element={<USVAPage />} />
            <Route path="/vac" element={<VACPage />} />
            <Route path="/vavip" element={<VAVIPPage />} />
            <Route path="/vfw" element={<VFWPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/valist" element={<VAListPage />} />
            <Route path="/nonprofits" element={<NonProfitsPage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/classes" element={<ClassesPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/benefits" element={<BenefitsPage />} />
            <Route path="/magazine" element={<MagazinePage />} />
            <Route path="/hudvash" element={<HUDVASHPage />} />
            <Route path="/hpact" element={<HPACTPage />} />
            <Route path="/vetlegal" element={<VETLegalPage />} />
            <Route path="/vetscan" element={<VETScanPage />} />
            <Route path="/services" element={<VETServicesHubPage />} />
            <Route path="/case-dashboard" element={<CaseDashboardPage />} />
            <Route path="/vha-directives" element={<VHADirectivesPage />} />
            <Route path="/vetengine" element={<VETEnginePage />} />
            <Route path="/vetengine/intro" element={<VETEngineIntroPage />} />
            <Route path="/vetengine/challenges" element={<VETEngineChallengesPage />} />
            <Route path="/vetengine/deliver-justice" element={<VETEngineDeliverJusticePage />} />
            <Route path="/vetengine/domains" element={<VETEngineDomainCategoriesPage />} />
            <Route path="/vetengine/professions" element={<VETEngineProfessionsPage />} />
            <Route path="/vetengine/ethics" element={<VETEngineEthicsPage />} />
            <Route path="/vetengine/oversight" element={<VETEngineOversightPage />} />
            <Route path="/vetengine/drug-policy" element={<VETDrugPolicyPage />} />
            <Route path="/vetengine/legal-engine" element={<VETLegalEnginePage />} />
            <Route path="/vetengine/domain-tags" element={<VETDomainTagsPage />} />
            <Route path="/vetengine/entity-domains" element={<VETEntityDomainsPage />} />
            <Route path="/vetengine/boards" element={<VETBoardsPage />} />
            <Route path="/vetengine/housing-authorities" element={<VETHousingAuthoritiesPage />} />
            <Route path="/vetengine/medical" element={<VETMedicalPage />} />
            <Route path="/vetengine/emergency" element={<VETEmergencyPage />} />
            <Route path="/vetengine/law-references" element={<VETLawReferencesPage />} />
            <Route path="/vetengine/examples" element={<VETRealLifeExamplesPage />} />
            <Route path="/vetengine/guided-navigator" element={<VETGuidedNavigatorPage />} />
            <Route path="/vetengine/evidence-scanner" element={<EvidenceScannerPage />} />
            <Route path="/vetengine/case-advisor" element={<CaseAdvisorPage />} />
            <Route path="/vetengine/timeline-builder" element={<TimelineBuilderPage />} />
            <Route path="/vetengine/complaint-generator" element={<ComplaintGeneratorPage />} />
            <Route path="/facility-risk-index" element={<HUDVashFacilityIndexPage />} />
            <Route path="/hudvash-directory" element={<HUDVASHDirectoryPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/vetshield" element={<ProtectedRoute><DashboardLayout><VETShieldPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/timeline" element={<ProtectedRoute><DashboardLayout><TimelinePage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><DashboardLayout><DocumentsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/evidence" element={<ProtectedRoute><DashboardLayout><EvidencePage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/laws" element={<ProtectedRoute><DashboardLayout><LawsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/vetcommand" element={<ProtectedRoute><VETCommandPage /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
